import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import { useNavigate, useSearchParams } from "react-router-dom";
import DataTable from "datatables.net-bs5";
import $ from "jquery";
import "select2";
import "select2/dist/css/select2.min.css";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FilterListIcon from "@mui/icons-material/FilterList";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TableChartIcon from "@mui/icons-material/TableChart";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import { Button, Stack, CircularProgress } from "@mui/material";
import { complaintAPI, masterDataAPI } from "../services/api";
// Native date inputs are used for all calendar fields
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import "./AddEditUser.css";
import "./RaiseComplaintChecklist.css";
import "./RaiseComplaint.css";
import Swal from "sweetalert2";
import { showMissingInvoice, showError, showInvoiceNotFound, showSwal } from "../utils/swalHelper";
import { escapeHtml } from "../utils/xss";
import { useSidebar } from "../contexts/SidebarContext";
import InvoiceUpload from "../components/InvoiceUpload";
import {
  materialRowShowsComponentDetailsIcon,
  getBusinessVerticalFromRow,
  filterMaterialGroupsByBusinessVertical,
} from "../utils/componentDetailsBusinessVertical";
import { filterSrCategoryOptionsForMaterialRow } from "../utils/domesticSrCategoryBusinessVertical";
import { isSeUserType } from "../utils/auth";
import { isTouchDevice, isEditableElementFocusedWithin } from "../utils/touchDevice";
import { validateInvoiceAccessForUser, INITIATOR_GEO_MISMATCH_MESSAGE } from "../utils/raiseComplaintInvoiceAuth";

const COMPONENT_DETAILS_LS_KEY = "csrp_component_details";

/**
 * Material grid: Business Vertical column shows only SAP BusinessVertical (no DivisionName, no category).
 */
function getMaterialRowBusinessVerticalLabel(row) {
  return (row?.BusinessVertical ?? row?.businessVertical ?? "").toString().trim();
}

/** Normalize products array from invoice API (JSON string or nested object). */
function getInvoiceProductsList(invoiceData) {
  if (!invoiceData) return [];
  let products = invoiceData.products ?? invoiceData.Products ?? [];
  if (typeof products === "string") {
    try {
      products = JSON.parse(products);
    } catch {
      return [];
    }
  }
  if (products && !Array.isArray(products)) {
    if (Array.isArray(products.products)) products = products.products;
    else if (Array.isArray(products.data)) products = products.data;
    else products = [];
  }
  return Array.isArray(products) ? products : [];
}

/** First non-empty Business Vertical from invoice line items (Export commercial Product Category). */
function getBusinessVerticalFromInvoice(invoiceData) {
  for (const row of getInvoiceProductsList(invoiceData)) {
    const label = getMaterialRowBusinessVerticalLabel(row);
    if (label) return label;
  }
  return "";
}

/** Export invoice: auto-fill commercial fields from SAP header / line Business Vertical. */
function getExportCommercialAutoFields(invoiceData) {
  const invoiceCurrency = (
    invoiceData?.currency ??
    invoiceData?.Currency ??
    ""
  )
    .toString()
    .trim();
  return {
    productCategory: getBusinessVerticalFromInvoice(invoiceData),
    invoiceCurrency,
    documentCurrency: "INR",
  };
}

/** Monetary values on Raise Complaint: 2 decimal places, no round-up-to-integer rule. */
const formatRaiseComplaintMoney = (v) => {
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
};

const toRaiseComplaintMoneyNumber = (v) =>
  parseFloat(formatRaiseComplaintMoney(v)) || 0;

/**
 * Same unit price as Material Section "Invoice Unit Price" (2 decimals).
 * Prefer SAP Price when present; else ROUND(InvoiceValue / InvoiceQuantity, 2).
 */
const getInvoiceUnitPriceForMaterialRow = (row) => {
  const invoiceValue =
    parseFloat(
      row?.InvoiceValue ?? row?.invoiceValue ?? row?.Invoicevalue ?? 0
    ) || 0;
  const invoiceQty =
    parseFloat(
      row?.InvoiceQuantity ??
        row?.invoiceQuantity ??
        row?.Invoicequantity ??
        0
    ) || 0;
  let unitPrice = row?.Price ?? row?.price;
  if (unitPrice == null || unitPrice === "" || Number(unitPrice) === 0) {
    unitPrice = invoiceQty > 0 ? invoiceValue / invoiceQty : 0;
  } else {
    unitPrice = parseFloat(unitPrice) || 0;
  }
  return toRaiseComplaintMoneyNumber(unitPrice);
};

/** Comp/Component Value = Qty × displayed Invoice Unit Price (2 dp). */
const computeMaterialSrValueFromRow = (row, compQty) => {
  const qty = parseFloat(compQty) || 0;
  return formatRaiseComplaintMoney(qty * getInvoiceUnitPriceForMaterialRow(row));
};

/** Row inputs to enable/disable on checkbox — SR Category synced separately (Select2); Add/Remove stay clickable. */
const getMaterialRowEditableControls = ($row) =>
  $row
    .find("input, select, button")
    .not(
      ".product-select-checkbox, .material-row-add, .material-row-remove, .category-select"
    );

const isMaterialRowSelectedForAction = (rowId, $table, selectedRef) => {
  if (!rowId) return false;
  if (selectedRef?.current?.has?.(rowId)) return true;
  if ($table?.length) {
    return $table
      .find('.product-select-checkbox[data-product-key="' + rowId + '"]')
      .is(":checked");
  }
  return false;
};

const MATERIAL_DIFFERENT_PLANT_MESSAGE =
  "Materials from different manufacturing plants cannot be selected together. Please select rows from the same plant only.";

const MATERIAL_SELECT_ALL_DIFFERENT_PLANTS_MESSAGE =
  "Select All is not allowed because invoice materials belong to different manufacturing plants. Please select rows from the same plant only.";

/** Plant display name from row (SAP / draft field name variants). */
const getMaterialRowPlantName = (row) =>
  String(
    row?.PlantName ??
      row?.plantName ??
      row?.ProductPlantDescription ??
      row?.productPlantDescription ??
      ""
  ).trim();

/** Normalize plant fields so validation and Plant Name column use the same value. */
const normalizeMaterialProductRow = (row) => {
  if (!row) return row;
  const plantName = getMaterialRowPlantName(row);
  if (!plantName) return row;
  return {
    ...row,
    PlantName: plantName,
    plantName: plantName,
    ProductPlantDescription: plantName,
    productPlantDescription: plantName,
  };
};

/** Normalize draft API invoiceDetails to an array (handles camelCase, JSON string, nested products). */
const normalizeDraftInvoiceDetailsList = (raw) => {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  if (typeof raw === "object" && Array.isArray(raw.products)) return raw.products;
  return [];
};

/** Stable plant key for Material Section selection rules (ID preferred, then name). */
const getMaterialRowPlantKey = (row) => {
  if (!row) return "";
  const plantId = String(
    row.ProductPlantId ??
      row.productPlantId ??
      row.PlantId ??
      row.plantId ??
      ""
  ).trim();
  if (plantId) return `id:${plantId}`;
  const plantName = getMaterialRowPlantName(row);
  if (plantName && plantName.toUpperCase() !== "N/A") {
    return `name:${plantName.toUpperCase()}`;
  }
  return "";
};

const resolveMaterialRowDataByKey = (rowKey, productMap, rowsArray) => {
  const key = rowKey != null && rowKey !== "" ? String(rowKey) : "";
  if (!key) return null;
  let row = productMap?.[key];
  if (!row && Array.isArray(rowsArray)) {
    row = rowsArray.find((r, i) => {
      const code = r?.ProductCode ?? r?.productCode ?? r?.Productcode ?? "r";
      const id =
        r?._rowId != null && r._rowId !== ""
          ? String(r._rowId)
          : `${code}_row${i}`;
      return id === key;
    });
  }
  return normalizeMaterialProductRow(row);
};

const getMaterialPlantKeysFromRows = (rows) => {
  const plantKeys = new Set();
  (rows || []).forEach((row) => {
    if (!row || row._selectable === false) return;
    const plantKey = getMaterialRowPlantKey(row);
    if (plantKey) plantKeys.add(plantKey);
  });
  return plantKeys;
};

const getMaterialPlantKeysForSelection = (selectedKeys, productMap, rowsArray) => {
  const plantKeys = new Set();
  (selectedKeys || []).forEach((key) => {
    const plantKey = getMaterialRowPlantKey(
      resolveMaterialRowDataByKey(key, productMap, rowsArray)
    );
    if (plantKey) plantKeys.add(plantKey);
  });
  return plantKeys;
};

const getMaterialPlantMismatchMessage = (
  targetRow,
  selectedKeys,
  productMap,
  rowsArray
) => {
  const targetPlant = getMaterialRowPlantKey(normalizeMaterialProductRow(targetRow));
  if (!targetPlant) return null;
  const selectedPlants = getMaterialPlantKeysForSelection(
    selectedKeys,
    productMap,
    rowsArray
  );
  if (selectedPlants.size === 0 || selectedPlants.has(targetPlant)) return null;
  return MATERIAL_DIFFERENT_PLANT_MESSAGE;
};

/** Select2 v4 removed enable()/disable() — sync native select + container UI. */
const setCategorySelectInteractive = ($select, interactive) => {
  if (!$select?.length) return;
  $select.prop("disabled", !interactive);
  const $container = $select.next(".select2-container");
  if ($container.length) {
    $container.toggleClass("select2-container--disabled", !interactive);
    $container.find(".select2-selection").attr("aria-disabled", !interactive);
  }
};

const revertMaterialRowCheckboxSelection = (tableEl, rowKey) => {
  if (!tableEl || !rowKey) return;
  const $table = $(tableEl);
  const $row = $table
    .find('.product-select-checkbox[data-product-key="' + rowKey + '"]')
    .closest("tr");
  const $checkbox = $row.find(".product-select-checkbox").first();
  if ($checkbox.length) $checkbox.prop("checked", false);
  if ($row.length) {
    const $controls = getMaterialRowEditableControls($row);
    $row.removeClass("material-row-active").addClass("material-row-disabled");
    $controls.prop("disabled", true);
    const $cat = $row
      .find('.category-select[data-key="' + rowKey + '"]')
      .first();
    setCategorySelectInteractive($cat, false);
  }
};

const RaiseComplaint = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { collapseSidebar, restoreSidebar } = useSidebar();
  /** SE (UT0000001): Material Code (Plant Team) is view-only. Remarks are required and editable. */
  const isPlantTeamComponentFieldsReadOnly = isSeUserType();
  const isComponentIssueRemarksRequired = isSeUserType();
  const [formData, setFormData] = useState({
    invoiceNo: "",
  });
  /** When loading from draft link, keep SRCode for Save/Submit. */
  const [draftSrCode, setDraftSrCode] = useState("");
  /** Ref so DataTable column render always sees current draft srCode (avoids stale closure). */
  const draftSrCodeRef = useRef("");
  // When opening via ?srCode=..., preserve these IDs even if invoiceDetails gets overwritten by SAP invoice API response.
  const draftCustomerCodeRef = useRef(null);
  const draftCustomerIdRef = useRef(null);
  const draftShippingCustomerCodeRef = useRef(null);
  const draftShippingCustomerIdRef = useRef(null);
  /** True when draft loaded is already submitted -> show in View Mode. */
  const [isDraftViewMode, setIsDraftViewMode] = useState(false);
  /** "Invalid Draft Request" or similar when srCode not found. */
  const [draftLoadError, setDraftLoadError] = useState("");
  const [loadingDraft, setLoadingDraft] = useState(false);

  // Suppress benign ResizeObserver errors globally for this component
  useEffect(() => {
    const errorHandler = (event) => {
      if (
        event.message.includes("ResizeObserver loop") ||
        event.message.includes(
          "ResizeObserver loop completed with undelivered notifications"
        )
      ) {
        event.stopImmediatePropagation();
        event.preventDefault();
        return true;
      }
    };

    window.addEventListener("error", errorHandler);
    return () => {
      window.removeEventListener("error", errorHandler);
    };
  }, []);

  // Prevent scroll-to-top when clicking hash links or role="link" buttons (e.g. DataTables pagination)
  useEffect(() => {
    const preventScrollToTop = (e) => {
      const el = e.target && e.target.closest ? e.target.closest("a[href='#'], a[href=''], button[role='link'], [role='link']") : null;
      if (!el) return;
      const href = (el.getAttribute && el.getAttribute("href")) || "";
      const role = el.getAttribute && el.getAttribute("role");
      const isHashOrEmpty = href === "#" || href === "";
      const isButtonLink = el.tagName === "BUTTON" && role === "link";
      if (isHashOrEmpty || isButtonLink) {
        e.preventDefault();
      }
    };
    document.addEventListener("click", preventScrollToTop, true);
    return () => document.removeEventListener("click", preventScrollToTop, true);
  }, []);

  // Bulletproof scroll lock: for 800ms after any Material table pagination, force window back to saved position
  useEffect(() => {
    const LOCK_MS = 800;
    const guard = () => {
      const now = Date.now();
      if (now >= paginationScrollLockUntilRef.current) return;
      const targetY = paginationScrollLockYRef.current;
      const currentY = window.scrollY || window.pageYOffset || 0;
      if (Math.abs(currentY - targetY) > 2) {
        window.scrollTo({ top: targetY, behavior: "instant" });
      }

      // Also preserve Material DataTable horizontal scroll (X axis).
      // This table uses DataTables' `.dt-scroll-body` for scrollX.
          const targetX = paginationScrollLockXRef.current;
      try {
        const scrollBodyEl = productTableRef.current
          ? $(productTableRef.current).closest(".dataTables_wrapper").find(".dt-scroll-body")[0]
          : null;
        if (scrollBodyEl && Math.abs(scrollBodyEl.scrollLeft - targetX) > 2) {
          scrollBodyEl.scrollLeft = targetX;
        }
      } catch (e) {
        // Ignore - table may not be fully initialized during lock
      }
    };
    const releaseCompQtyMissingCategoryScrollLock = (event) => {
      const target = event?.target;
      const isMaterialRowAction = !!(
        target &&
        target.closest &&
        target.closest(".material-row-add, .material-row-remove")
      );
      if (isMaterialRowAction) {
        compQtyMissingCategoryLockActiveRef.current = false;
        paginationScrollLockUntilRef.current = 0;
        return;
      }
      if (!compQtyMissingCategoryLockActiveRef.current) return;
      const isCompQtyInput = !!(
        target &&
        target.closest &&
        target.closest(".complaint-qty-input")
      );
      // Keep lock for the initial blocked Comp Qty key press/click.
      if (event?.type === "keydown" && isCompQtyInput) return;

      compQtyMissingCategoryLockActiveRef.current = false;
      paginationScrollLockUntilRef.current = 0;
    };
    window.addEventListener("scroll", guard, true);
    window.addEventListener("wheel", releaseCompQtyMissingCategoryScrollLock, true);
    window.addEventListener("touchstart", releaseCompQtyMissingCategoryScrollLock, true);
    window.addEventListener("mousedown", releaseCompQtyMissingCategoryScrollLock, true);
    window.addEventListener("keydown", releaseCompQtyMissingCategoryScrollLock, true);
    const id = setInterval(guard, 50);
    return () => {
      window.removeEventListener("scroll", guard, true);
      window.removeEventListener("wheel", releaseCompQtyMissingCategoryScrollLock, true);
      window.removeEventListener("touchstart", releaseCompQtyMissingCategoryScrollLock, true);
      window.removeEventListener("mousedown", releaseCompQtyMissingCategoryScrollLock, true);
      window.removeEventListener("keydown", releaseCompQtyMissingCategoryScrollLock, true);
      clearInterval(id);
    };
  }, []);

  // Scroll to top only when page is already at top (avoids overriding user scroll / pagination)
  useEffect(() => {
    requestAnimationFrame(() => {
      const y = window.scrollY || window.pageYOffset || 0;
      if (y < 20) window.scrollTo({ top: 0, behavior: "instant" });
    });
  }, [searchParams]);

  // After draft loading completes, scroll to top only if still near top (don't override user scroll)
  useEffect(() => {
    if (!loadingDraft) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          const y = window.scrollY || window.pageYOffset || 0;
          if (y < 20) window.scrollTo({ top: 0, behavior: "instant" });
        }, 0);
      });
    }
  }, [loadingDraft]);

  // Load draft by srCode from query string (?srCode=SRD000001)
  useEffect(() => {
    const srCode = searchParams.get("srCode")?.trim() || "";
    if (!srCode) {
      setDraftLoadError("");
      setDraftSrCode("");
      draftSrCodeRef.current = "";
      return;
    }

    let mounted = true;
    setLoadingDraft(true);
    setDraftLoadError("");

    complaintAPI
      .getDraftByCode(srCode)
      .then((res) => {
        if (!mounted) return;
        const ok = res?.success ?? res?.Success ?? false;
        const data = res?.data ?? res?.Data ?? res;
        if (!ok || !data) {
          setDraftLoadError("Invalid Draft Request");
          setLoadingDraft(false);
          // Scroll to top even when draft is invalid
          requestAnimationFrame(() => {
            setTimeout(() => {
              window.scrollTo({ top: 0, behavior: "instant" });
            }, 100);
          });
          return;
        }

        const draft = typeof data === "object" ? data : {};
        const status = (draft.Status || draft.status || "").toLowerCase();

        if (status && status !== "draft") {
          setIsDraftViewMode(true);
          // Still bind data so user can view; existing save will not overwrite submitted
        }

        const srCodeVal = draft.SRCode || draft.srCode || srCode;
        setDraftSrCode(srCodeVal);
        draftSrCodeRef.current = srCodeVal;

        // Persist IDs from draft payload (so Save Draft doesn't lose them)

        // API may camelCase properties; note that ALL-CAPS names become fully lowercased (CUSTOMERID -> customerid).
        
        
        
        
        draftCustomerCodeRef.current =
          draft.CUSTOMERCODE ?? draft.customercode ?? draft.CustomerCode ?? draft.customerCode ?? null;
        
          draftCustomerIdRef.current =
          draft.CUSTOMERID ?? draft.customerid ?? draft.customerId ?? draft.CustomerID ?? null;
        // ShippingCustomerID typically becomes shippingCustomerID in camelCase


        draftShippingCustomerIdRef.current =
          draft.ShippingCustomerID ?? draft.shippingCustomerID ?? draft.shippingCustomerId ?? null;

        draftShippingCustomerCodeRef.current =
          draft.ShippingCustomerCode ??
          draft.shippingCustomerCode ??
          draft.shippingcustomercode ??
          null;







        setFormData((prev) => ({
          ...prev,
          invoiceNo: draft.InvoiceNumber || draft.invoiceNumber || "",
        }));

        setTypeOfComplaint(draft.SRTypeCode || draft.srTypeCode || "Quality");

        // Derive simple Market flag (Domestic / Export) from draft MARKETCODE and MarketName for draft load
        // Use both MARKETCODE and MarketName so SR Category dropdown gets correct market (Export vs Domestic)
        const draftMarketCode = (draft.MARKETCODE ?? draft.marketCode ?? draft.MarketCode ?? "").toString().trim();
        const draftMarketName = (draft.MarketName ?? draft.marketName ?? draft.MARKETNAME ?? draftMarketCode).toString().trim();
        const codeUpper = draftMarketCode.toUpperCase();
        const nameUpper = draftMarketName.toUpperCase();
        let draftMarketForSrTypes = null;
        // Check Export first so we don't mis-treat Export codes as Domestic
        if (
          codeUpper.includes("EXP") ||
          codeUpper === "EXPORT" ||
          nameUpper.includes("EXP") ||
          nameUpper === "EXPORT"
        ) {
          setMarket("Export");
          draftMarketForSrTypes = "Export";
        } else if (
          codeUpper.includes("DOM") ||
          codeUpper === "DOMESTIC" ||
          nameUpper.includes("DOM") ||
          nameUpper === "DOMESTIC"
        ) {
          setMarket("Domestic");
          draftMarketForSrTypes = "Domestic";
        } else {
          setMarket("Domestic");
          draftMarketForSrTypes = "Domestic";
        }

        // When loading by srCode, fetch SR Types for this market so SR Category dropdown shows correct list (Export vs Domestic)
        if (draftMarketForSrTypes) {
          fetchSRTypesForRaiseComplaint(draftMarketForSrTypes);
        }
        setCustomerType(draft.AddCustomerType || draft.addCustomerType || "");
        setDealerDistributor(
          draft.DealerDistributor || draft.dealerDistributor || ""
        );

        const consumerCountry = draft.ConsumerCountry || draft.consumerCountry || "";
        const consumerZone = draft.ConsumerZone || draft.consumerZone || "";
        const consumerState = draft.ConsumerState || draft.consumerState || "";
        if (draft.ConsumerName || draft.ConsumerAddress || consumerCountry || consumerZone || consumerState) {
          setShowEndCustomer(true);
        }
        setEndCustomerData({
          endCustomerCode: draft.EndCustomerCode || draft.endCustomerCode || "",
          name: draft.ConsumerName || draft.consumerName || "",
          address: draft.ConsumerAddress || draft.consumerAddress || "",
          city: draft.TerritoryName || draft.territoryName || draft.ConsumerState || draft.consumerState || "",
          state: draft.TerritoryName || draft.territoryName || draft.ConsumerState || draft.consumerState || "",
          countryCode: consumerCountry,
          zoneCode: consumerZone,
          stateCode: consumerState,
          cityCode: consumerState,
          pinCode: draft.ConsumerPostalCode || draft.consumerPostalCode || "",
          projectSiteDetails:
            draft.ProjectSiteDetails || draft.projectSiteDetails || "",
          contactPerson: "",
          mobileNumber: draft.ConsumerContactNumber || draft.consumerContactNumber || "",
          phoneNo: draft.ConsumerContactNumber || draft.consumerContactNumber || "",
        });
        endConsumerDraftCodesRef.current = {
          countryCode: consumerCountry,
          zoneCode: consumerZone,
          stateCode: consumerState,
          countryName: draft.CountryName || draft.countryName || "",
          zoneName: draft.ZoneName || draft.zoneName || "",
          stateName: draft.TerritoryName || draft.territoryName || "",
        };

        setSampleInfo({
          sampleSent: draft.SampleType || draft.sampleType || "No",
          courierAgency: draft.CourierAgency || draft.courierAgency || "",
          courierDate: toDateInputValue(draft.CourierDate || draft.courierDate) || "",
          docketNo: draft.DocketNo || draft.docketNo || "",
          sentTo: draft.SentTo || draft.sentTo || "",
          sampleDetails: draft.SampleDetails || draft.sampleDetails || "",
        });

        // Commercial Details (SRINVOICEMASTER new fields)
        setCommercialDetails({
          productCategory:
            draft.ProductCategory ??
            draft.productCategory ??
            "",
          shortPayment:
            draft.ShortPayment ||
            draft.shortPayment ||
            "",
          invoiceCurrency:
            draft.InvoiceCurrency ||
            draft.invoiceCurrency ||
            "",
          documentCurrency:
            draft.DocumentCurrency ||
            draft.documentCurrency ||
            "",
          reportingCurrency:
            draft.ReportingCurrency ||
            draft.reportingCurrency ||
            "",
          reportingAmount:
            draft.ReportingAmount !== undefined && draft.ReportingAmount !== null
              ? String(draft.ReportingAmount)
              : draft.reportingAmount !== undefined && draft.reportingAmount !== null
                ? String(draft.reportingAmount)
                : "",
          exchangeRate:
            draft.ExchangeRate ||
            draft.exchangeRate ||
            "",
          complaintValueUsd:
            draft.ComplaintValueUsd ||
            draft.complaintValueUsd ||
            "",
          approvedValue:
            draft.ApprovedValue !== undefined && draft.ApprovedValue !== null
              ? String(draft.ApprovedValue)
              : draft.approvedValue !== undefined && draft.approvedValue !== null
                ? String(draft.approvedValue)
                : "",
        });

        const details = normalizeDraftInvoiceDetailsList(
          draft.InvoiceDetails ?? draft.invoiceDetails
        );
        if (details.length === 0) {
          console.warn("Draft loaded but InvoiceDetails is empty for srCode:", srCodeVal);
        }
        const products = details.map((d, i) => {
              const code = d.ProductCode || d.productCode || "";
              const isSelected = d.IsSelected === 1 || d.isSelected === 1;
              const effectiveRemainingRaw =
                d.RemainingQty ??
                d.remainingQty ??
                d.InvoiceQuantity ??
                d.invoiceQuantity ??
                0;
              const effectiveRemaining = parseFloat(effectiveRemainingRaw) || 0;
              const isReadOnlyDueToRemaining =
                !!srCodeVal && effectiveRemaining <= 0;
              return normalizeMaterialProductRow({
                ...d,
                ProductCode: code,
                productCode: code,
                ProductName: d.MaterialDescription || d.materialDescription || d.ProductName || d.productName || "",
                productName: d.MaterialDescription || d.materialDescription || d.ProductName || d.productName || "",
                BusinessVertical: d.BusinessVertical || d.businessVertical || "",
                businessVertical: d.BusinessVertical || d.businessVertical || "",
                ProductCategory: d.ProductCategoryDescription || d.productCategoryDescription || d.ProductCategory || d.productCategory || "",
                productCategory: d.ProductCategoryDescription || d.productCategoryDescription || d.ProductCategory || d.productCategory || "",
                PlantName: d.ProductPlantDescription || d.productPlantDescription || d.PlantName || d.plantName || "",
                plantName: d.ProductPlantDescription || d.productPlantDescription || d.PlantName || d.plantName || "",
                ProductPlantDescription:
                  d.ProductPlantDescription || d.productPlantDescription || d.PlantName || d.plantName || "",
                productPlantDescription:
                  d.ProductPlantDescription || d.productPlantDescription || d.PlantName || d.plantName || "",
                InvoiceQuantity: d.InvoiceQuantity ?? d.invoiceQuantity ?? 0,
                invoiceQuantity: d.InvoiceQuantity ?? d.invoiceQuantity ?? 0,
                RemainingQty: effectiveRemaining,
                remainingQty: effectiveRemaining,
                InvoiceValue: d.InvoiceValue ?? d.invoiceValue ?? 0,
                invoiceValue: d.InvoiceValue ?? d.invoiceValue ?? 0,
                UOM: d.UOM || d.uom || "PCS",
                uom: d.UOM || d.uom || "PCS",
                BatchNo: d.BatchNo || d.batchNo,
                batchNo: d.BatchNo || d.batchNo,
                ProductCategoryId: d.ProductCategoryId || d.productCategoryId,
                productCategoryId: d.ProductCategoryId || d.productCategoryId,
                ProductPlantId: d.ProductPlantId || d.productPlantId,
                productPlantId: d.ProductPlantId || d.productPlantId,
                MaterialDescription: d.MaterialDescription || d.materialDescription,
                materialDescription: d.MaterialDescription || d.materialDescription,
                Division: d.Division || d.division,
                DivisionName: d.DivisionName || d.divisionName,
                DesignNo: d.DesignNo || d.designNo,
                DesignDescription: d.DesignDescription || d.designDescription,
                ProductDescription: d.ProductDescription || d.productDescription,
                ProductBrand: d.ProductBrand || d.productBrand,
                ProductFinish: d.ProductFinish || d.productFinish,
                ProductThickness: d.ProductThickness || d.productThickness,
                Size: d.Size || d.size,
                Territory: d.Territory || d.territory,
                TerritoryName: d.TerritoryName || d.territoryName,
                IsSelected: isSelected ? 1 : 0,
                isSelected: isSelected ? 1 : 0,
                _selectable: !isReadOnlyDueToRemaining,
                _remainingReadOnly: isReadOnlyDueToRemaining,
                _rowId: code ? code + "_row" + i : "row_" + i,
              });
            });

        details.forEach((d, i) => {
          const code = d.ProductCode || d.productCode || "";
          const rowKey = code ? code + "_row" + i : "";
          const srTypeCode =
            d.SRTypeCode || d.srTypeCode || draft.SRTypeCode || draft.srTypeCode || "";
          const srTypeDesc =
            d.SRTypeName || d.srTypeName || d.SRTypeDesc || d.srTypeDesc || "";
          const qtyStr = String(d.SRQuantity ?? d.srQuantity ?? "");
          if (code) {
            // Keep code-based keys for any legacy lookups
            categoryRef.current[code] = srTypeCode;
            categoryDescRef.current[code] = srTypeDesc;
            complaintQtyRef.current[code] = qtyStr;
          }
          if (rowKey) {
            // Also seed row-based keys used by the DataTable (matches _rowId = code + "_row" + index)
            categoryRef.current[rowKey] = srTypeCode;
            categoryDescRef.current[rowKey] = srTypeDesc;
            complaintQtyRef.current[rowKey] = qtyStr;
          }
        });
        // Populate component details from draft (per row) so Component Details popup shows saved data
        const compRef = componentDetailsRef.current;
        const invoiceKeyForComp = (
          draft.InvoiceNumber ||
          draft.invoiceNumber ||
          draft.InvoiceNo ||
          draft.invoiceNo ||
          ""
        )
          .toString()
          .trim()
          .toUpperCase();
        let compStore = {};
        try {
          compStore = JSON.parse(localStorage.getItem(COMPONENT_DETAILS_LS_KEY) || "{}");
        } catch (_) {
          compStore = {};
        }
        try {
        details.forEach((d, i) => {
          const code = d.ProductCode || d.productCode || "";
          const rowKey = code ? code + "_row" + i : "";
          const apiRows = parseComponentDetailsArray(d.ComponentDetails ?? d.componentDetails);
          let rows = [];
          if (apiRows.length) {
            rows = apiRows.map(mapComponentDetailRowFromApi);
          } else {
            const legacyRow = mapComponentDetailRowFromApi({
              ComponentMaterialGroupCode: d.ComponentMaterialGroupCode ?? d.componentMaterialGroupCode ?? d.MaterialGroup ?? d.materialGroup ?? "",
              MaterialGroupDesc: d.MaterialGroupDesc ?? d.materialGroupDesc ?? "",
              ComponentCategoryCode: d.ComponentCategoryCode ?? d.componentCategoryCode ?? "",
              ComponentCategory: d.ComponentCategory ?? d.componentCategory ?? "",
              ComponentListCode: d.ComponentListCode ?? d.componentListCode ?? "",
              ComponentList: d.ComponentList ?? d.componentList ?? "",
              ComponentQty: d.ComponentQty ?? d.componentQty ?? "",
              ComponentIssueCode: d.ComponentIssueCode ?? d.componentIssueCode ?? "",
              ComponentIssue: d.ComponentIssue ?? d.componentIssue ?? "",
              DoorMaterialCode: d.DoorMaterialCode ?? d.doorMaterialCode ?? "",
              ComponentIssueRemarks: d.ComponentIssueRemarks ?? d.componentIssueRemarks ?? "",
              ComponentValue: d.ComponentValue ?? d.componentValue ?? 0,
            });
            if (
              COMPONENT_DETAILS_FIELD_KEYS.some((k) => {
                const keyMap = {
                  materialGroupCode: legacyRow.materialGroupCode,
                  materialGroupDesc: legacyRow.materialGroupDesc,
                  componentCategoryCode: legacyRow.componentCategoryCode,
                  componentCategory: legacyRow.componentCategory,
                  componentListCode: legacyRow.componentListCode,
                  componentList: legacyRow.componentList,
                  componentQty: legacyRow.componentQty,
                  componentIssueCode: legacyRow.componentIssueCode,
                  componentIssue: legacyRow.componentIssue,
                  doorMaterialCode: legacyRow.doorMaterialCode,
                  componentIssueRemarks: legacyRow.componentIssueRemarks,
                };
                const v = keyMap[k];
                return v != null && String(v).trim() !== "";
              })
            ) {
              rows = [legacyRow];
            }
          }
          const compPayload = { rows };
          if (code) compRef[code] = compPayload;
          if (rowKey) {
            compRef[rowKey] = compPayload;
            const storeKey = invoiceKeyForComp ? `${invoiceKeyForComp}::${rowKey}` : rowKey;
            compStore[storeKey] = {
              rows: rows.map((r) => ({
                _rowId: r._rowId,
                materialGroupCode: r.materialGroupCode ?? "",
                materialGroupDesc: r.materialGroupDesc ?? "",
                componentCategoryCode: r.componentCategoryCode ?? "",
                componentCategory: r.componentCategory ?? "",
                componentListCode: r.componentListCode ?? "",
                componentList: r.componentList ?? "",
                componentQty:
                  r.componentQty != null && r.componentQty !== "" ? String(r.componentQty) : "",
                componentValue: parseFloat(r.componentValue) || 0,
                componentIssueCode: r.componentIssueCode ?? "",
                componentIssue: r.componentIssue ?? "",
                doorMaterialCode: r.doorMaterialCode ?? "",
                componentIssueRemarks: r.componentIssueRemarks ?? "",
              })),
            };
          }
        });
        try {
          localStorage.setItem(COMPONENT_DETAILS_LS_KEY, JSON.stringify(compStore));
        } catch (_) {}
        } catch (compErr) {
          console.warn("Component details preload from draft failed:", compErr);
        }
        setComplaintQty(
          details.reduce((acc, d, i) => {
            const code = d.ProductCode || d.productCode || "";
            const rowKey = code ? code + "_row" + i : "";
            const qtyStr = String(d.SRQuantity ?? d.srQuantity ?? "");
            if (code) acc[code] = qtyStr;
            if (rowKey) acc[rowKey] = qtyStr;
            return acc;
          }, {})
        );

        // Use same _rowId formula as table (ProductCode/productCode + "_row" + index) so checkboxes match.
        // Include rows that were IsSelected=1 AND rows with RemainingQty<=0 (disabled) so their checkboxes show checked.
        let selectedFromDraft = new Set();
        details.forEach((d, i) => {
          const code = d.ProductCode || d.productCode || "r";
          const rowKey = code ? code + "_row" + i : "";
          if (!rowKey) return;
          const isSelected = d.IsSelected === 1 || d.isSelected === 1;
          const remaining = parseFloat(d.RemainingQty ?? d.remainingQty ?? d.InvoiceQuantity ?? d.invoiceQuantity ?? 0) || 0;
          const isDisabledRow = remaining <= 0;
          if (isSelected || isDisabledRow) selectedFromDraft.add(rowKey);
        });
        // Validation: Different plants cannot be selected in the Material section.
        const draftSelectedKeys = Array.from(selectedFromDraft);
        const draftPlantKeys = getMaterialPlantKeysForSelection(
          draftSelectedKeys,
          {},
          products
        );
        if (draftPlantKeys.size > 1) {
          const primaryPlant = draftPlantKeys.values().next().value;
          const filteredDraftSelection = new Set();
          draftSelectedKeys.forEach((rowKey) => {
            const row = resolveMaterialRowDataByKey(rowKey, {}, products);
            const plantKey = getMaterialRowPlantKey(row);
            if (!plantKey || plantKey === primaryPlant) {
              filteredDraftSelection.add(rowKey);
            }
          });
          selectedFromDraft = filteredDraftSelection;
          setMaterialSectionError(MATERIAL_DIFFERENT_PLANT_MESSAGE);
        }
        // If exactly one selectable line item, default to selecting it and Select All checked
        const selectableCount = products.filter((p) => p._selectable).length;
        if (selectableCount === 1) {
          const idx = products.findIndex((p) => p._selectable);
          if (idx >= 0) {
            const code = products[idx].ProductCode || products[idx].productCode || "r";
            const oneKey = code + "_row" + idx;
            if (!selectedFromDraft.has(oneKey)) {
              selectedFromDraft.add(oneKey);
            }
          }
        }
        selectedProductsRef.current = selectedFromDraft;
        setSelectedProducts(selectedFromDraft);
        const selectedSelectableCount = products.filter((p, i) => {
          const isSelectable = p?._selectable !== false;
          if (!isSelectable) return false;
          const code = p.ProductCode || p.productCode || "r";
          const rowKey = code + "_row" + i;
          return selectedFromDraft.has(rowKey);
        }).length;
        setSelectAllProducts(
          selectableCount > 0 && selectedSelectableCount === selectableCount
        );

        setInvoiceDetails({
          invoiceNo: draft.InvoiceNumber || draft.invoiceNumber,
          invoiceNumber: draft.InvoiceNumber || draft.invoiceNumber,
          InvoiceNo: draft.InvoiceNumber || draft.invoiceNumber,
          customerName: draft.CustomerName || draft.customerName,
          CustomerName: draft.CustomerName || draft.customerName,
          representativeName: draft.RepresentativeName || draft.representativeName,
          customerAddress: draft.CustomerAddress || draft.customerAddress,
          customerCity: draft.CustomerCity || draft.customerCity,
          customerState: draft.CustomerState || draft.customerState,
          customerCountry: draft.CustomerCountry || draft.customerCountry,
          // Invoice Zone/Territory from SAPInvoiceHeader (via draft-by-code)
          salesDistrict:
            draft.SalesDistrict ||
            draft.salesDistrict ||
            draft.SALESDISTRICT ||
            "",
          SalesDistrict:
            draft.SalesDistrict ||
            draft.salesDistrict ||
            draft.SALESDISTRICT ||
            "",
          branchName:
            draft.BranchName || draft.branchName || draft.BRANCHNAME || "",
          BranchName:
            draft.BranchName || draft.branchName || draft.BRANCHNAME || "",
          customerPinCode: draft.CustomerPinCode || draft.customerPinCode,
          customerPhone: draft.CustomerPhone || draft.customerPhone,
          customerMobileNo: draft.CustomerPhone || draft.customerPhone,
          customerAlternateNo: draft.CustomerAlternateNo || draft.customerAlternateNo,
          customerEmail: draft.CustomerEmail || draft.customerEmail,
          customerType: draft.CustomerType || draft.customerType || null,
          CustomerType: draft.CustomerType || draft.customerType || null,
          AddCustomerType: draft.AddCustomerType || draft.addCustomerType || null,
          customerCode: draft.CUSTOMERCODE || draft.customerCode,
          customerId: draft.CUSTOMERID || draft.customerId,
          CustomerId: draft.CUSTOMERID || draft.customerId,
          CustomerID: draft.CUSTOMERID || draft.customerId,
          shippingCustomerCode: draft.ShippingCustomerCode || draft.shippingCustomerCode,
          ShippingCustomerCode: draft.ShippingCustomerCode || draft.shippingCustomerCode,
          shippingCustomerId: draft.ShippingCustomerID || draft.shippingCustomerId,
          ShippingCustomerID: draft.ShippingCustomerID || draft.shippingCustomerId,
          shippingCustomerName: draft.ShippingCustomerName || draft.shippingCustomerName,
          ShippingCustomerName: draft.ShippingCustomerName || draft.shippingCustomerName,
          shippingCustomerAddress: draft.ShippingCustomerAddress || draft.shippingCustomerAddress,
          ShippingCustomerAddress: draft.ShippingCustomerAddress || draft.shippingCustomerAddress,
          shippingCustomerCity: draft.ShippingCustomerCity || draft.shippingCustomerCity,
          ShippingCustomerCity: draft.ShippingCustomerCity || draft.shippingCustomerCity,
          shippingCustomerState: draft.ShippingCustomerState || draft.shippingCustomerState,
          ShippingCustomerState: draft.ShippingCustomerState || draft.shippingCustomerState,
          shippingCustomerCountry: draft.ShippingCustomerCountry || draft.shippingCustomerCountry,
          ShippingCustomerCountry: draft.ShippingCustomerCountry || draft.shippingCustomerCountry,
          shippingCustomerPinCode: draft.ShippingCustomerPinCode || draft.shippingCustomerPinCode,
          ShippingCustomerPinCode: draft.ShippingCustomerPinCode || draft.shippingCustomerPinCode,
          shippingCustomerPhone: draft.ShippingCustomerPhone || draft.shippingCustomerPhone,
          ShippingCustomerPhone: draft.ShippingCustomerPhone || draft.shippingCustomerPhone,
          shippingCustomerEmail: draft.ShippingCustomerEmail || draft.shippingCustomerEmail,
          ShippingCustomerEmail: draft.ShippingCustomerEmail || draft.shippingCustomerEmail,
          shippingCustomerType: draft.ShippingCustomerType || draft.shippingCustomerType,
          ShippingCustomerType: draft.ShippingCustomerType || draft.shippingCustomerType,
          InvoiceDate: draft.InvoiceDate || draft.invoiceDate,
          invoiceDate: draft.InvoiceDate || draft.invoiceDate,
          OrderNo: draft.OrderNumber || draft.orderNumber,
          orderNo: draft.OrderNumber || draft.orderNumber,
          OrderNumber: draft.OrderNumber || draft.orderNumber,
          orderDate: draft.OrderDate || draft.orderDate,
          OrderDate: draft.OrderDate || draft.orderDate,
          referenceInvoiceNo: draft.ReferenceInvoiceNumber || draft.referenceInvoiceNumber,
          referenceInvoiceNumber: draft.ReferenceInvoiceNumber || draft.referenceInvoiceNumber,
          SapOrder: draft.SapOrder || draft.sapOrder,
          sapOrder: draft.SapOrder || draft.sapOrder,
          ODNNumber: draft.ODNNumber || draft.odnNumber,
          odnNumber: draft.ODNNumber || draft.odnNumber,
          MarketName: draft.MARKETCODE || draft.marketCode,
          marketCode: draft.MARKETCODE || draft.marketCode,
          marketName: draft.MARKETCODE || draft.marketCode,
          Currency: draft.Currency || draft.currency,
          currency: draft.Currency || draft.currency,
          ExchangeRate: draft.ExchangeRate || draft.exchangeRate,
          exchangeRate: draft.ExchangeRate || draft.exchangeRate,
          products,
        });

        // When loading by srCode, fetch previous complaints for this draft's invoice so they are shown
        const draftInvoiceNo = (draft.InvoiceNumber || draft.invoiceNumber || "").toString().trim();
        if (draftInvoiceNo) {
          const upperCaseInvoiceNo = draftInvoiceNo.toUpperCase();
          // Pass UserIdCode for SP filtering/audit (fallback is cookie claims on backend)
          let prevUserIdCode = null;
          try {
            const u = localStorage.getItem("user");
            if (u) {
              const user = JSON.parse(u);
              prevUserIdCode = user?.userIdCode ?? user?.UserIdCode ?? null;
            }
          } catch (_) {}

          complaintAPI
            .getPreviousComplaintsByInvoice(upperCaseInvoiceNo, prevUserIdCode)
            .then((prevRes) => {
              if (!mounted) return;
              const list = prevRes?.data ?? prevRes?.Data;
              if (prevRes?.success && list && Array.isArray(list)) {
                setPreviousComplaintsData(list);
                setPreviousComplaintsCount(list.length);
                if (list.length === 1) setShowPreviousComplaintsTable(true);
              } else {
                setPreviousComplaintsData([]);
                setPreviousComplaintsCount(0);
              }
            })
            .catch(() => {
              if (!mounted) return;
              setPreviousComplaintsData([]);
              setPreviousComplaintsCount(0);
            });
        } else {
          setPreviousComplaintsData([]);
          setPreviousComplaintsCount(0);
        }

        if (draft.FloorForm && typeof draft.FloorForm === "object") {
          setFloorForm((prev) => ({ ...prev, ...draft.FloorForm }));
        }

        // SRATTACHMENTS from draft API only (do not use local storage for attachments in srCode mode)
        const atts = Array.isArray(draft.Attachments) ? draft.Attachments : (Array.isArray(draft.attachments) ? draft.attachments : []);
        const invoiceCopy = atts.filter(
          (a) => (a.CopyFor || a.copyFor || "").toLowerCase() === "invoice copy"
        );
        const secondary = atts.filter(
          (a) => (a.CopyFor || a.copyFor || "").toLowerCase() === "secondary invoice"
        );
        setInvoiceCopyFiles(
          invoiceCopy.map((a, i) => ({
            id: `draft-inv-${i}`,
            name: a.FileName || a.fileName || (a.FilePath || a.filePath || "").split("/").pop() || "attachment",
            path: a.FilePath || a.filePath || "",
            url: a.FilePath || a.filePath || "",
            size: a.FileSize ?? a.fileSize,
            type: a.FileType || a.fileType,
          }))
        );
        setSecondaryInvoiceFiles(
          secondary.map((a, i) => ({
            id: `draft-sec-${i}`,
            name: a.FileName || a.fileName || (a.FilePath || a.filePath || "").split("/").pop() || "attachment",
            path: a.FilePath || a.filePath || "",
            url: a.FilePath || a.filePath || "",
            size: a.FileSize ?? a.fileSize,
            type: a.FileType || a.fileType,
          }))
        );

        setLoadingDraft(false);
        // Scroll to top after draft data is loaded and state is updated
        requestAnimationFrame(() => {
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: "instant" });
          }, 100);
        });
      })
      .catch((err) => {
        if (!mounted) return;
        console.error("Draft load failed:", err);
        setDraftLoadError("Invalid Draft Request");
        setLoadingDraft(false);
        // Scroll to top even on error
        requestAnimationFrame(() => {
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: "instant" });
          }, 100);
        });
      });

    return () => {
      mounted = false;
    };
  }, [searchParams]);

  const [market, setMarket] = useState("Domestic");
  const [typeOfComplaint, setTypeOfComplaint] = useState("Quality");
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [invoiceNoError, setInvoiceNoError] = useState("");
  const [endCustomerPinCodeError, setEndCustomerPinCodeError] = useState("");
  const [materialSectionError, setMaterialSectionError] = useState("");
  const [previousComplaintsCount, setPreviousComplaintsCount] = useState(0);
  const [previousComplaintsData, setPreviousComplaintsData] = useState([]);
  const [showPreviousComplaintsTable, setShowPreviousComplaintsTable] = useState(false);
  const [showPreviousComplaintsDetailModal, setShowPreviousComplaintsDetailModal] = useState(false);
  const [selectedComplaintIndex, setSelectedComplaintIndex] = useState(null);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [checklistData, setChecklistData] = useState({
    category: "",
    productName: "",
    productCode: "",
    orderNo: "",
    quantity: "",
  });
  const [floorForm, setFloorForm] = useState({
    customerName: "",
    address: "",
    contactNumber: "",
    dateReceived: "",
    productType: { flooring: false, staircase: false, accessories: false },
    customization: "",
    transitProductDamage: "",
    transitPackagingDamage: "",
    installedStatus: {
      installed: false,
      dealerStock: false,
      siteStored: false,
      others: false,
    },
    sizeOrdered: "",
    sizeReceived: "",
    qtyOrdered: "",
    qtyReceived: "",
  });
  const productTableRef = useRef(null);
  const dataTableInstanceRef = useRef(null);
  /** Clone/remove update DataTable in-place; skip full destroy/reinit on the next materialRows effect. */
  const skipNextMaterialTableReinitRef = useRef(false);
  /** Lock window scroll position after Material DataTable pagination; scroll guard enforces this for 800ms. */
  const paginationScrollLockYRef = useRef(0);
  const paginationScrollLockXRef = useRef(0);
  const paginationScrollLockUntilRef = useRef(0);
  // Holds the redraw/scroll-fix deferred by the SR Qty blur handler until the click it
  // raced with (Enter/+/Save Draft/Next) has been processed. See scheduleDeferredMaterialRedraw.
  const pendingMaterialRedrawRef = useRef(null);
  // Set on mousedown of +/-/Save Draft/Next so the SR Qty blur that follows (mousedown moves
  // focus off the input before click) doesn't re-arm the scroll lock with a stale saved Y
  // (captured at focus/last-keystroke time, before the user scrolled down to reach the
  // button) - that stale target is what the 50ms scroll guard snaps back to mid-click,
  // moving the button out from under the cursor. See activateScrollLockOnly.
  const suppressNextScrollLockArmRef = useRef(false);
  // Set alongside suppressNextScrollLockArmRef so the deferred redraw's own draw.rcPaginateScrollFix
  // handler skips its unconditional window.scrollTo just once - otherwise, even after a button
  // click succeeds, the page would still visibly snap back to the (possibly stale) saved
  // position once the deferred draw() finally runs.
  const skipNextDrawScrollFixRef = useRef(false);
  // True only for "Comp Qty used before SR Category" lock; released on user interaction.
  const compQtyMissingCategoryLockActiveRef = useRef(false);
  // Track user's current horizontal scroll inside Material table so redraws can restore it.
  const materialScrollLeftRef = useRef(0);
  // Preserve the user's current window scroll position when Material table mutates
  // (e.g. Add more / Remove row), so DataTables drawCallback won't force scroll to top.
  const lockMaterialTableScrollForRowMutation = (durationMs = 800) => {
    paginationScrollLockYRef.current = window.scrollY || window.pageYOffset || 0;
    paginationScrollLockXRef.current = materialScrollLeftRef.current || 0;
    paginationScrollLockUntilRef.current = Date.now() + durationMs;

    // If DataTables scroll body exists right now, prefer its actual scrollLeft.
    try {
      const $table = productTableRef.current ? $(productTableRef.current) : null;
      const $scopedScrollBody = $table ? $table.closest(".dt-scroll").find(".dt-scroll-body").first() : null;
      if ($scopedScrollBody && $scopedScrollBody.length) {
        paginationScrollLockXRef.current = $scopedScrollBody[0].scrollLeft || 0;
      }
    } catch (_) {
      // ignore - table may not be fully initialized
    }
  };
  // When SweetAlert scroll lock sets body to `position: fixed`, window.scrollY becomes 0.
  // Use body.top offset to recover the actual visual scroll position.
  const getEffectiveWindowScrollY = () => {
    const body = document.body;
    if (body && body.style.position === "fixed" && body.style.top) {
      const topValue = parseInt(body.style.top, 10);
      if (!Number.isNaN(topValue)) return Math.abs(topValue);
    }
    return window.scrollY || window.pageYOffset || 0;
  };
  /** Clear table scroll locks and SweetAlert body-fixed state so action buttons work on first click. */
  const releaseRaiseComplaintActionLocks = () => {
    paginationScrollLockUntilRef.current = 0;
    compQtyMissingCategoryLockActiveRef.current = false;
    const body = document.body;
    if (!body || body.style.position !== "fixed") return;
    const topValue = parseInt(body.style.top || "0", 10);
    const scrollY = !Number.isNaN(topValue) ? Math.abs(topValue) : getEffectiveWindowScrollY();
    body.style.position = "";
    body.style.top = "";
    body.style.width = "";
    body.style.overflow = "";
    document.documentElement.style.overflow = "";
    requestAnimationFrame(() => {
      window.scrollTo({ top: scrollY, behavior: "instant" });
    });
  };
  /** Read latest SR Category / Comp Qty from the table DOM into refs (Select2/blur timing). */
  const syncMaterialSectionRefsFromDom = () => {
    if (!productTableRef.current) return;
    $(productTableRef.current)
      .find(".category-select")
      .each(function () {
        const key = $(this).data("key");
        if (!key) return;
        const value = ($(this).val() || "").toString().trim();
        categoryRef.current = { ...categoryRef.current, [key]: value };
      });
    productTableRef.current.querySelectorAll(".complaint-qty-input").forEach((el) => {
      const key = el.getAttribute("data-key");
      if (!key) return;
      complaintQtyRef.current = {
        ...complaintQtyRef.current,
        [key]: (el.value || "").toString().trim(),
      };
    });
  };
  const historyTableRef = useRef(null);
  const historyDataTableInstanceRef = useRef(null);
  const previousComplaintsToggleScrollYRef = useRef(null);
  const restorePreviousComplaintsScrollRef = useRef(null);
  const restoreWindowScrollPosition = (scrollY) => {
    if (typeof scrollY !== "number" || Number.isNaN(scrollY)) return;
    window.scrollTo({ top: scrollY, behavior: "instant" });
  };
  const handlePreviousComplaintsTableToggle = () => {
    const currentY = window.scrollY || window.pageYOffset || 0;
    previousComplaintsToggleScrollYRef.current = currentY;
    setShowPreviousComplaintsTable((prev) => !prev);

    // Guard against transient jumps caused by DataTable reflow while toggling.
    requestAnimationFrame(() => restoreWindowScrollPosition(currentY));
    setTimeout(() => restoreWindowScrollPosition(currentY), 80);
    setTimeout(() => restoreWindowScrollPosition(currentY), 220);
  };
  const productDataRef = useRef({});
  const [complaintQty, setComplaintQty] = useState({});
  const complaintQtyRef = useRef({});
  const categoryRef = useRef({});
  /** Per-product SR Type description (e.g. "Installation") for draft load so dropdown can show saved SRTYPE-001/002/003 when not in fetched list. */
  const categoryDescRef = useRef({});
  const [srTypesOptions, setSrTypesOptions] = useState([]);
  const srTypesOptionsRef = useRef([]);
  const getSrCategoryOptionsForMaterialRowRef = useRef(() => []);
  const [loadingSRTypes, setLoadingSRTypes] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const selectedProductsRef = useRef(selectedProducts);
  const handleProductSelectRef = useRef(null);
  /** Track invoice we last cleared selection for, so we only clear on new invoice not on selectAllProducts/draftSrCode change */
  const lastClearedInvoiceRef = useRef(null);
  useEffect(() => {
    selectedProductsRef.current = selectedProducts;
  }, [selectedProducts]);

  // Debounce redraw during complaint qty typing (prevents DataTables flicker)
  const materialCompQtyDrawDebounceTimerRef = useRef(null);
  const [selectAllProducts, setSelectAllProducts] = useState(false);
  const selectAllProductsRef = useRef(false);
  useEffect(() => {
    selectAllProductsRef.current = selectAllProducts;
  }, [selectAllProducts]);
  /** Guards Export+Commercial bulk apply during programmatic category updates / Select2 init. */
  const exportCommercialBulkApplyingRef = useRef(false);
  const categorySelect2InitPassRef = useRef(false);
  /** Row keys (all pages) whose SR Category is locked after Export+Commercial bulk apply. */
  const exportCommercialCategoryReadOnlyKeysRef = useRef(new Set());
  /** Row keys (all pages) whose SR Qty is locked after Export+Commercial bulk apply. */
  const exportCommercialQtyReadOnlyKeysRef = useRef(new Set());
  /** Trigger row that initiated Export+Commercial bulk apply (category stays editable). */
  const exportCommercialTriggerKeyRef = useRef("");
  /** True after Export+Commercial bulk apply until reverted or invoice cleared. */
  const exportCommercialBulkActiveRef = useRef(false);
  /** Material section rows: same as invoice products but can have cloned rows. Each row has _rowId and _isClone. */
  const [materialRows, setMaterialRows] = useState([]);
  const materialRowsRef = useRef(materialRows);
  useEffect(() => {
    materialRowsRef.current = materialRows;
  }, [materialRows]);
  /** Hide Component Details column (header + cells) when no row is Mikasa Doors / Mikasa Floor / Greenlam Cubicle (Sturdo). */
  const showMaterialComponentDetailsColumn = useMemo(() => {
    const raw = materialRows.length > 0 ? materialRows : invoiceDetails?.products;
    if (!Array.isArray(raw)) return false;
    return raw.some((row) => materialRowShowsComponentDetailsIcon(row));
  }, [materialRows, invoiceDetails?.products]);
  /**
   * Hide SR Qty + Sr VALUE when every material row is Mikasa Doors / Floors / Sturdo.
   * Mixed invoices keep the columns (other BVs still need qty entry).
   */
  const showMaterialSrQtyValueColumns = useMemo(() => {
    const raw = materialRows.length > 0 ? materialRows : invoiceDetails?.products;
    if (!Array.isArray(raw) || raw.length === 0) return true;
    return raw.some((row) => !materialRowShowsComponentDetailsIcon(row));
  }, [materialRows, invoiceDetails?.products]);
  /** Sanitize Comp Qty / Component Qty: allow only digits (integer only, no decimals). */
  const sanitizeCompQtyInput = (value) => {
    if (value == null || value === "") return "";
    return String(value).replace(/\D/g, "");
  };
  const newLine = () => ({ id: Date.now(), type: "", qty: "" });
  const [complaintLines, setComplaintLines] = useState([newLine()]);
  const [selectedProductKey, setSelectedProductKey] = useState(null);
  const draftStorageKey = "complaintChecklistDraft";
  const [complaintTypes, setComplaintTypes] = useState([]);
  const [loadingComplaintTypes, setLoadingComplaintTypes] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  /** Per-action loading: 'save' | 'next' | 'modal' so only the clicked control shows a spinner until the DB/API returns. */
  const [pendingDraftAction, setPendingDraftAction] = useState(null);
  const checklistModalRef = useRef(null);
  const modalInstanceRef = useRef(null);
  const [customerType, setCustomerType] = useState("B2B"); // B2B or B2C; default B2B
  const [dealerDistributor, setDealerDistributor] = useState("");
  const [showEndCustomer, setShowEndCustomer] = useState(false);
  const [endCustomerData, setEndCustomerData] = useState({
    endCustomerCode: "",
    name: "",
    address: "",
    city: "",
    state: "",
    countryCode: "",
    zoneCode: "",
    stateCode: "",
    cityCode: "",
    pinCode: "",
    projectSiteDetails: "",
    contactPerson: "",
    mobileNumber: "",
    phoneNo: "",
  });
  const [endConsumerLookupLoading, setEndConsumerLookupLoading] = useState(false);
  const endCustomerCountryRef = useRef(null);
  const endCustomerZoneRef = useRef(null);
  const endCustomerStateRef = useRef(null);
  const endCustomerCountryCodeRef = useRef("");
  /** When loading draft, hold Country/Zone/State codes and display names so Select2 can be pre-filled once. */
  const endConsumerDraftCodesRef = useRef(null);
  const [invoiceCopyFiles, setInvoiceCopyFiles] = useState([]);
  const [secondaryInvoiceFiles, setSecondaryInvoiceFiles] = useState([]);
  const [sampleInfo, setSampleInfo] = useState({
    sampleSent: "No",
    courierAgency: "",
    courierDate: "",
    docketNo: "",
    sentTo: "",
    sampleDetails: "",
  });
  const [srInfo, setSrInfo] = useState({
    plantName: "",
    correctiveAction: "",
    visitReportComments: "",
    postCommercialNotes: "",
  });
  const [commercialDetails, setCommercialDetails] = useState({
    productCategory: "",
    shortPayment: "",
    complaintValueUsd: "",
    invoiceCurrency: "",
    documentCurrency: "",
    reportingCurrency: "",
    reportingAmount: "",
    exchangeRate: "",
    approvedValue: "",
  });
  const [categorySelectionTick, setCategorySelectionTick] = useState(0);

  useEffect(() => {
    getSrCategoryOptionsForMaterialRowRef.current = (row) =>
      filterSrCategoryOptionsForMaterialRow(
        srTypesOptionsRef.current.length > 0 ? srTypesOptionsRef.current : srTypesOptions,
        market,
        getMaterialRowBusinessVerticalLabel(row)
      );
  }, [market, srTypesOptions]);

  const isExportMarketActive = useCallback(() => {
    const marketUpper = (market || "").toString().toUpperCase();
    if (marketUpper === "EXPORT") return true;
    const marketName =
      invoiceDetails?.marketName ||
      invoiceDetails?.MarketName ||
      invoiceDetails?.marketCode ||
      invoiceDetails?.MarketCode ||
      "";
    const nameUpper = marketName.toString().toUpperCase();
    return nameUpper.includes("EXP") || nameUpper === "EXPORT";
  }, [market, invoiceDetails]);

  const isExportCommercialCategoryReadOnly = (rowKey) => {
    const key = rowKey != null && rowKey !== "" ? String(rowKey) : "";
    if (!key) return false;
    return exportCommercialCategoryReadOnlyKeysRef.current.has(key);
  };

  const isExportCommercialQtyReadOnly = (rowKey) => {
    const key = rowKey != null && rowKey !== "" ? String(rowKey) : "";
    if (!key) return false;
    return exportCommercialQtyReadOnlyKeysRef.current.has(key);
  };

  /** Mikasa Doors / Floors / Sturdo — SR Qty is auto-filled and not editable. */
  const isComponentDetailsBvSrQtyReadOnly = (rowOrKey) => {
    if (rowOrKey && typeof rowOrKey === "object") {
      return materialRowShowsComponentDetailsIcon(rowOrKey);
    }
    const key = rowOrKey != null && rowOrKey !== "" ? String(rowOrKey) : "";
    if (!key) return false;
    return materialRowShowsComponentDetailsIcon(productDataRef.current?.[key]);
  };

  const lockComponentDetailsBvSrQtyInput = ($row, rowKey) => {
    if (!isComponentDetailsBvSrQtyReadOnly(rowKey)) return;
    const $qty = $row
      .find('.complaint-qty-input[data-key="' + rowKey + '"]')
      .first();
    if (!$qty.length) return;
    $qty.prop("disabled", true).prop("readonly", true);
  };

  const unlockExportCommercialCategorySelect = ($row, rowKey) => {
    const $cat = $row
      .find('.category-select[data-key="' + rowKey + '"]')
      .first();
    if (!$cat.length) return;
    setCategorySelectInteractive($cat, true);
  };

  const lockExportCommercialCategorySelect = ($row, rowKey) => {
    if (!isExportCommercialCategoryReadOnly(rowKey)) return;
    const $cat = $row
      .find('.category-select[data-key="' + rowKey + '"]')
      .first();
    if (!$cat.length) return;
    setCategorySelectInteractive($cat, false);
  };

  const lockExportCommercialQtyInput = ($row, rowKey) => {
    if (!isExportCommercialQtyReadOnly(rowKey)) return;
    const $qty = $row
      .find('.complaint-qty-input[data-key="' + rowKey + '"]')
      .first();
    if (!$qty.length) return;
    $qty.prop("disabled", true).prop("readonly", true);
  };

  const unlockExportCommercialQtyInput = ($row, rowKey) => {
    const $qty = $row
      .find('.complaint-qty-input[data-key="' + rowKey + '"]')
      .first();
    if (!$qty.length) return;
    $qty.prop("disabled", false).prop("readonly", false);
  };

  const syncMaterialRowCategorySelectEnabled = ($row, rowKey, shouldEnable) => {
    const key = rowKey != null && rowKey !== "" ? String(rowKey) : "";
    if (!key) return;
    const $cat = $row
      .find('.category-select[data-key="' + key + '"]')
      .first();
    if (!$cat.length) return;
    const rowData = productDataRef.current?.[key];
    const remainingReadOnly = rowData?._remainingReadOnly === true;
    const categoryLocked =
      !shouldEnable ||
      remainingReadOnly ||
      isExportCommercialCategoryReadOnly(key);
    setCategorySelectInteractive($cat, !categoryLocked);
  };

  const syncAllMaterialRowCategorySelects = useCallback(() => {
    if (!productTableRef.current) return;
    $(productTableRef.current)
      .find("tbody tr")
      .each(function () {
        const $row = $(this);
        const $checkbox = $row.find(".product-select-checkbox").first();
        const rowKey = $checkbox.data("product-key");
        if (!rowKey) return;
        const selectable = $checkbox.data("selectable");
        const notSelectable = selectable === false || selectable === "false";
        const isDisabledDraftRow =
          $checkbox.data("disabled-draft-row") === 1 ||
          $checkbox.attr("data-disabled-draft-row") === "1";
        const checked = $checkbox.is(":checked");
        if (notSelectable && isDisabledDraftRow) {
          syncMaterialRowCategorySelectEnabled($row, rowKey, false);
        } else {
          syncMaterialRowCategorySelectEnabled($row, rowKey, !!checked);
        }
      });
  }, []);

  const applyMaterialRowSelectionUi = ($row, rowKey, shouldEnable) => {
    const $controls = getMaterialRowEditableControls($row);
    if (shouldEnable) {
      $row.removeClass("material-row-disabled").addClass("material-row-active");
      $controls.prop("disabled", false);
      unlockExportCommercialCategorySelect($row, rowKey);
      unlockExportCommercialQtyInput($row, rowKey);
      lockExportCommercialCategorySelect($row, rowKey);
      lockExportCommercialQtyInput($row, rowKey);
      lockComponentDetailsBvSrQtyInput($row, rowKey);
      syncMaterialRowCategorySelectEnabled($row, rowKey, true);
    } else {
      $row.removeClass("material-row-active").addClass("material-row-disabled");
      $controls.prop("disabled", true);
      syncMaterialRowCategorySelectEnabled($row, rowKey, false);
    }
  };

  const clearExportCommercialBulkState = () => {
    exportCommercialCategoryReadOnlyKeysRef.current = new Set();
    exportCommercialQtyReadOnlyKeysRef.current = new Set();
    exportCommercialTriggerKeyRef.current = "";
    exportCommercialBulkActiveRef.current = false;
  };

  const getExportCommercialRowsToReset = (triggerKey) => {
    const normalizedTriggerKey =
      triggerKey != null && triggerKey !== "" ? String(triggerKey) : "";
    return getAllMaterialTableRowsData()
      .filter((row) => row && row._selectable !== false)
      .map((row) =>
        row._rowId != null && row._rowId !== "" ? String(row._rowId) : null
      )
      .filter((rowKey) => rowKey && rowKey !== normalizedTriggerKey);
  };

  const getSrTypesOptionsSnapshot = useCallback(() => {
    return srTypesOptionsRef.current.length > 0
      ? srTypesOptionsRef.current
      : srTypesOptions;
  }, [srTypesOptions]);

  const isCommercialSrCategoryCode = useCallback(
    (code) => {
      const normalized = String(code ?? "").trim();
      if (!normalized) return false;
      const options = getSrTypesOptionsSnapshot();
      const match = (options || []).find(
        (o) => String(o?.value ?? "").trim() === normalized
      );
      return String(match?.text ?? "").trim().toLowerCase() === "commercial";
    },
    [getSrTypesOptionsSnapshot]
  );

  const MATERIAL_COMMERCIAL_ADD_DISABLED_MESSAGE =
    "Add more (+) is not available when SR Category is Commercial.";
  const MATERIAL_SR_CATEGORY_REQUIRED_FOR_ADD_MESSAGE =
    "Please select SR Category before adding a new row.";
  const MATERIAL_COMPONENT_DETAILS_REQUIRED_FOR_ADD_MESSAGE =
    "Without Component Details you cannot add a new row. Please save Component Details first.";
  const MATERIAL_NO_SR_CATEGORY_LEFT_FOR_ADD_MESSAGE =
    "All SR Categories for this product are already used. Cannot add another row.";

  /** Filled later once Component Details helpers exist (Mikasa Doors/Floors/Sturdo add gate). */
  const materialRowAddBlockedByComponentDetailsRef = useRef(() => false);
  /** Filled later once SR Category exclusivity helpers exist. */
  const materialRowAddBlockedByNoSrCategoryLeftRef = useRef(() => false);

  const isMaterialRowAddBlockedForMissingSrCategory = useCallback((rowKey) => {
    return !String(categoryRef.current?.[rowKey] ?? "").trim();
  }, []);

  const isMaterialRowAddDisabledForCategory = useCallback(
    (rowKey, categoryCode) => {
      const code =
        categoryCode != null && categoryCode !== undefined
          ? categoryCode
          : categoryRef.current?.[rowKey];
      if (isCommercialSrCategoryCode(code)) return true;
      const desc = String(categoryDescRef.current?.[rowKey] ?? "")
        .trim()
        .toLowerCase();
      return desc === "commercial";
    },
    [isCommercialSrCategoryCode]
  );

  const applyMaterialRowAddButtonState = useCallback(
    ($row, rowKey) => {
      if (!$row?.length || !rowKey) return;
      const $addBtn = $row.find(".material-row-add").first();
      if (!$addBtn.length) return;
      let $wrap = $addBtn.closest(".material-row-add-wrap");
      if (!$wrap.length) {
        $addBtn.wrap('<span class="material-row-add-wrap"></span>');
        $wrap = $addBtn.parent();
      }
      const blockedByMissingSrCategory =
        isMaterialRowAddBlockedForMissingSrCategory(rowKey);
      const blockedByCommercial =
        !blockedByMissingSrCategory &&
        isMaterialRowAddDisabledForCategory(rowKey);
      const blockedByComponentDetails =
        typeof materialRowAddBlockedByComponentDetailsRef.current === "function" &&
        materialRowAddBlockedByComponentDetailsRef.current(rowKey);
      const blockedByNoSrCategoryLeft =
        !blockedByMissingSrCategory &&
        typeof materialRowAddBlockedByNoSrCategoryLeftRef.current === "function" &&
        materialRowAddBlockedByNoSrCategoryLeftRef.current(rowKey);
      const disabled =
        blockedByMissingSrCategory ||
        blockedByCommercial ||
        blockedByComponentDetails ||
        blockedByNoSrCategoryLeft;
      const tip = blockedByMissingSrCategory
        ? MATERIAL_SR_CATEGORY_REQUIRED_FOR_ADD_MESSAGE
        : blockedByCommercial
          ? MATERIAL_COMMERCIAL_ADD_DISABLED_MESSAGE
          : blockedByComponentDetails
            ? MATERIAL_COMPONENT_DETAILS_REQUIRED_FOR_ADD_MESSAGE
            : blockedByNoSrCategoryLeft
              ? MATERIAL_NO_SR_CATEGORY_LEFT_FOR_ADD_MESSAGE
              : "Add more (clone row)";
      $addBtn.prop("disabled", disabled);
      $addBtn.toggleClass("disabled", disabled);
      $addBtn.attr("title", tip);
      $wrap.attr("title", tip);
      $wrap.attr("aria-label", tip);
      if (disabled) {
        $addBtn.css({ opacity: 0.5, cursor: "not-allowed", pointerEvents: "none" });
        $wrap.css({ cursor: "not-allowed", display: "inline-block" });
        $wrap.addClass("is-add-blocked");
      } else {
        $addBtn.css({ opacity: "", cursor: "", pointerEvents: "" });
        $wrap.css({ cursor: "", display: "inline-block" });
        $wrap.removeClass("is-add-blocked");
      }
    },
    [
      isMaterialRowAddBlockedForMissingSrCategory,
      isMaterialRowAddDisabledForCategory,
    ]
  );

  const syncAllMaterialRowAddButtons = useCallback(() => {
    if (!productTableRef.current) return;
    $(productTableRef.current)
      .find("tbody tr")
      .each(function () {
        const $row = $(this);
        const $btn = $row.find(".material-row-add").first();
        if (!$btn.length) return;
        const rowKey = $btn.attr("data-row-id") || $btn.data("row-id");
        if (rowKey) applyMaterialRowAddButtonState($row, rowKey);
      });
  }, [applyMaterialRowAddButtonState]);

  const resetMaterialRowDataInDom = useCallback(
    (rowKey, $row, { keepDisabled = true } = {}) => {
      if (!rowKey) return;
      categoryRef.current[rowKey] = "";
      categoryDescRef.current[rowKey] = "";
      complaintQtyRef.current = { ...complaintQtyRef.current, [rowKey]: "" };
      delete componentDetailsRef.current[rowKey];

      if (!$row?.length) return;

      const $qtyInput = $row
        .find('.complaint-qty-input[data-key="' + rowKey + '"]')
        .first();
      if ($qtyInput.length) {
        $qtyInput.val("");
        if (keepDisabled) {
          $qtyInput.prop("disabled", true).prop("readonly", true);
        } else {
          $qtyInput.prop("disabled", false).prop("readonly", false);
        }
      }

      const $catSelect = $row
        .find('.category-select[data-key="' + rowKey + '"]')
        .first();
      if ($catSelect.length) {
        $catSelect.val("").trigger("change.select2");
        if (keepDisabled) {
          setCategorySelectInteractive($catSelect, false);
        } else {
          unlockExportCommercialCategorySelect($row, rowKey);
          lockExportCommercialCategorySelect($row, rowKey);
        }
      }

      $row
        .find("td.comp-value-cell")
        .text(
          computeMaterialSrValueFromRow(productDataRef.current?.[rowKey], 0)
        );
      applyMaterialRowAddButtonState($row, rowKey);
    },
    [applyMaterialRowAddButtonState]
  );

  const resetAllMaterialSectionRowsData = useCallback(() => {
    clearExportCommercialBulkState();
    const allKeys = getSelectableMaterialRowKeys();
    allKeys.forEach((rowKey) => {
      categoryRef.current[rowKey] = "";
      categoryDescRef.current[rowKey] = "";
      delete componentDetailsRef.current[rowKey];
    });
    const clearedQty = {};
    allKeys.forEach((rowKey) => {
      clearedQty[rowKey] = "";
    });
    complaintQtyRef.current = clearedQty;
    setComplaintQty(clearedQty);
    setCategorySelectionTick((v) => v + 1);

    if (productTableRef.current) {
      $(productTableRef.current).find("tbody tr").each(function () {
        const $row = $(this);
        const rowKey = String(
          $row.find(".product-select-checkbox").first().data("product-key") || ""
        );
        if (rowKey) resetMaterialRowDataInDom(rowKey, $row);
      });
    }

    if (dataTableInstanceRef.current) {
      try {
        dataTableInstanceRef.current.draw(false);
      } catch (_) {
        // ignore draw errors
      }
    }
    if (typeof syncComplaintValueUsdRef.current === "function") {
      syncComplaintValueUsdRef.current();
    }
  }, [resetMaterialRowDataInDom]);

  const getInvoiceQtyForMaterialRow = (product) => {
    const qty =
      parseFloat(
        product?.InvoiceQuantity ??
          product?.invoiceQuantity ??
          product?.Invoicequantity ??
          product?.Quantity ??
          product?.quantity ??
          0
      ) || 0;
    return String(Math.max(0, Math.floor(qty)));
  };

  /**
   * Mikasa Doors / Mikasa Floors / Sturdo: after checkbox + SR Category,
   * auto-fill SR Qty = Invoice Qty so Sr VALUE matches Total Price.
   */
  const autoFillSrQtyForComponentDetailsBvRow = useCallback(
    (rowKey, rowData, $row) => {
      const key = rowKey != null && rowKey !== "" ? String(rowKey) : "";
      if (!key || !rowData || !materialRowShowsComponentDetailsIcon(rowData)) {
        return false;
      }
      const invoiceQtyStr = getInvoiceQtyForMaterialRow(rowData);
      complaintQtyRef.current = {
        ...complaintQtyRef.current,
        [key]: invoiceQtyStr,
      };
      setComplaintQty((prev) => ({ ...prev, [key]: invoiceQtyStr }));

      const $targetRow =
        $row?.length
          ? $row
          : productTableRef.current
            ? $(productTableRef.current)
                .find(
                  '.product-select-checkbox[data-product-key="' + key + '"]'
                )
                .closest("tr")
            : null;
      if ($targetRow?.length) {
        const $qtyInput = $targetRow
          .find('.complaint-qty-input[data-key="' + key + '"]')
          .first();
        if ($qtyInput.length) {
          $qtyInput.val(invoiceQtyStr);
          $qtyInput.prop("disabled", true).prop("readonly", true);
        }
        $targetRow
          .find("td.comp-value-cell")
          .text(computeMaterialSrValueFromRow(rowData, invoiceQtyStr));
      }
      if (typeof syncComplaintValueUsdRef.current === "function") {
        syncComplaintValueUsdRef.current();
      }
      return true;
    },
    []
  );

  const getMaterialRowBaseProductCode = (row) =>
    String(row?.ProductCode ?? row?.productCode ?? row?.Productcode ?? "").trim();

  /** Commercial SR Qty: invoice qty once per ProductCode (matches footer invoice total logic). */
  const getCommercialSrQtyForMaterialRow = (product, seenBaseProducts) => {
    const baseCode = getMaterialRowBaseProductCode(product);
    if (baseCode) {
      if (seenBaseProducts.has(baseCode)) return "0";
      seenBaseProducts.add(baseCode);
    }
    return getInvoiceQtyForMaterialRow(product);
  };

  const getAllMaterialTableRowsData = () => {
    if (dataTableInstanceRef.current) {
      try {
        const rows = dataTableInstanceRef.current
          .rows({ page: "all" })
          .data()
          .toArray();
        if (rows?.length) return rows;
      } catch (e) {
        console.warn("Error reading all material table rows:", e);
      }
    }
    const fallback = materialRowsRef.current || [];
    return Array.isArray(fallback) ? fallback : [];
  };

  const getSelectableMaterialRowKeys = () => {
    return getAllMaterialTableRowsData()
      .filter((row) => row && row._selectable !== false)
      .map((row) =>
        row._rowId != null && row._rowId !== "" ? String(row._rowId) : null
      )
      .filter(Boolean);
  };

  /** Export market: checked row + Commercial → select all rows, Commercial category, SR Qty = Invoice Qty. */
  const applyExportCommercialBulkSelectionIfNeeded = useCallback(
    (triggerKey, categoryCode) => {
      if (
        exportCommercialBulkApplyingRef.current ||
        categorySelect2InitPassRef.current
      ) {
        return;
      }
      if (!isExportMarketActive()) return;
      if (!isCommercialSrCategoryCode(categoryCode)) return;

      const normalizedTriggerKey =
        triggerKey != null && triggerKey !== "" ? String(triggerKey) : "";
      if (!normalizedTriggerKey) return;

      const selected = selectedProductsRef.current || new Set();
      if (!selected.has(normalizedTriggerKey)) return;

      const options = getSrTypesOptionsSnapshot();
      const commercialOption = (options || []).find(
        (o) => String(o?.text ?? "").trim().toLowerCase() === "commercial"
      );
      if (!commercialOption?.value) return;

      const commercialCode = String(commercialOption.value).trim();
      const commercialDesc = String(commercialOption.text || commercialCode).trim();
      const allRows = getAllMaterialTableRowsData().filter(
        (row) => row && row._selectable !== false
      );
      const allKeys = allRows
        .map((row) =>
          row._rowId != null && row._rowId !== "" ? String(row._rowId) : null
        )
        .filter(Boolean);
      if (!allKeys.length) return;

      exportCommercialBulkApplyingRef.current = true;
      try {
        const allProductKeys = new Set(allKeys);
        const lockedCategoryKeys = new Set();
        const lockedQtyKeys = new Set();
        allKeys.forEach((rowKey) => {
          lockedQtyKeys.add(String(rowKey));
          if (String(rowKey) !== normalizedTriggerKey) {
            lockedCategoryKeys.add(String(rowKey));
          }
        });
        exportCommercialCategoryReadOnlyKeysRef.current = lockedCategoryKeys;
        exportCommercialQtyReadOnlyKeysRef.current = lockedQtyKeys;
        exportCommercialTriggerKeyRef.current = normalizedTriggerKey;
        exportCommercialBulkActiveRef.current = true;

        selectedProductsRef.current = allProductKeys;
        setSelectedProducts(allProductKeys);
        setSelectAllProducts(true);
        selectAllProductsRef.current = true;

        const nextComplaintQty = { ...complaintQtyRef.current };
        const seenCommercialBaseProducts = new Set();
        allRows.forEach((row) => {
          const rowKey =
            row._rowId != null && row._rowId !== "" ? String(row._rowId) : "";
          if (!rowKey) return;

          productDataRef.current[rowKey] = row;
          categoryRef.current[rowKey] = commercialCode;
          categoryDescRef.current[rowKey] = commercialDesc;
          nextComplaintQty[rowKey] = getCommercialSrQtyForMaterialRow(
            row,
            seenCommercialBaseProducts
          );
        });
        complaintQtyRef.current = nextComplaintQty;
        setComplaintQty(nextComplaintQty);

        if (productTableRef.current) {
          const $table = $(productTableRef.current);
          $table.find("tbody tr").each(function () {
            const $row = $(this);
            const $cb = $row.find(".product-select-checkbox").first();
            const rowKey = $cb.data("product-key");
            const selectable = $cb.data("selectable");
            const shouldCheck =
              selectable !== false &&
              selectable !== "false" &&
              allProductKeys.has(String(rowKey));
            $cb.prop("checked", !!shouldCheck);
            applyMaterialRowSelectionUi($row, rowKey, !!shouldCheck);

            if (!rowKey || !allProductKeys.has(String(rowKey))) return;

            const rowData = productDataRef.current?.[rowKey];
            const $catSelect = $row
              .find('.category-select[data-key="' + rowKey + '"]')
              .first();
            if ($catSelect.length) {
              $catSelect.val(commercialCode).trigger("change.select2");
              lockExportCommercialCategorySelect($row, rowKey);
            }

            applyMaterialRowAddButtonState($row, rowKey);

            const $qtyInput = $row
              .find('.complaint-qty-input[data-key="' + rowKey + '"]')
              .first();
            if ($qtyInput.length) {
              $qtyInput.val(nextComplaintQty[rowKey] || "");
              $qtyInput.prop("disabled", true).prop("readonly", true);
            }

            $row
              .find("td.comp-value-cell")
              .text(
                computeMaterialSrValueFromRow(
                  rowData,
                  nextComplaintQty[rowKey] || 0
                )
              );
          });

          const $wrapper = $table.closest(".dataTables_wrapper, .dt-container");
          $wrapper.find('input[id="select-all-products"]').prop("checked", true);
        }

        setCategorySelectionTick((v) => v + 1);
        if (dataTableInstanceRef.current) {
          try {
            dataTableInstanceRef.current.rows({ page: "all" }).invalidate();
          } catch (_) {
            // ignore invalidate errors
          }
          dataTableInstanceRef.current.draw(false);
        }
        if (typeof syncComplaintValueUsdRef.current === "function") {
          syncComplaintValueUsdRef.current();
        }
      } finally {
        exportCommercialBulkApplyingRef.current = false;
      }
    },
    [isCommercialSrCategoryCode, isExportMarketActive, getSrTypesOptionsSnapshot]
  );

  /** Export market: trigger row changed away from Commercial → reset other auto-filled rows. */
  const revertExportCommercialBulkSelection = useCallback(
    (triggerKey) => {
      if (
        exportCommercialBulkApplyingRef.current ||
        categorySelect2InitPassRef.current
      ) {
        return;
      }
      if (!isExportMarketActive()) return;
      if (!exportCommercialBulkActiveRef.current) return;

      const normalizedTriggerKey =
        triggerKey != null && triggerKey !== "" ? String(triggerKey) : "";
      if (
        exportCommercialTriggerKeyRef.current &&
        normalizedTriggerKey !== String(exportCommercialTriggerKeyRef.current)
      ) {
        return;
      }

      const rowsToReset = getExportCommercialRowsToReset(normalizedTriggerKey);
      if (!rowsToReset.length) {
        clearExportCommercialBulkState();
        return;
      }

      exportCommercialBulkApplyingRef.current = true;
      try {
        const nextSelected = new Set();
        if (
          normalizedTriggerKey &&
          selectedProductsRef.current?.has(normalizedTriggerKey)
        ) {
          nextSelected.add(normalizedTriggerKey);
        }
        selectedProductsRef.current = nextSelected;
        setSelectedProducts(nextSelected);
        setSelectAllProducts(false);
        selectAllProductsRef.current = false;

        const rowsToResetSet = new Set(rowsToReset.map((k) => String(k)));
        const nextComplaintQty = {};
        getAllMaterialTableRowsData()
          .filter((row) => row && row._selectable !== false)
          .forEach((row) => {
            const rowKey =
              row._rowId != null && row._rowId !== ""
                ? String(row._rowId)
                : "";
            if (!rowKey) return;

            if (rowKey === normalizedTriggerKey) {
              nextComplaintQty[rowKey] = "";
              return;
            }

            if (!rowsToResetSet.has(rowKey)) return;

            categoryRef.current[rowKey] = "";
            categoryDescRef.current[rowKey] = "";
            nextComplaintQty[rowKey] = "";
            delete componentDetailsRef.current[rowKey];
          });
        complaintQtyRef.current = nextComplaintQty;
        setComplaintQty(nextComplaintQty);
        clearExportCommercialBulkState();

        if (productTableRef.current) {
          const $table = $(productTableRef.current);
          $table.find("tbody tr").each(function () {
            const $row = $(this);
            const $cb = $row.find(".product-select-checkbox").first();
            const rowKey = String($cb.data("product-key") || "");
            if (!rowKey) return;

            const isTrigger = rowKey === normalizedTriggerKey;
            const shouldCheck = isTrigger && nextSelected.has(rowKey);
            $cb.prop("checked", !!shouldCheck);
            applyMaterialRowSelectionUi($row, rowKey, !!shouldCheck);

            if (!rowsToResetSet.has(rowKey)) return;

            const $catSelect = $row
              .find('.category-select[data-key="' + rowKey + '"]')
              .first();
            if ($catSelect.length) {
              setCategorySelectInteractive($catSelect, true);
              $catSelect.val("").trigger("change.select2");
            }

            $row.find('.complaint-qty-input[data-key="' + rowKey + '"]').val("");
            unlockExportCommercialQtyInput($row, rowKey);
            lockComponentDetailsBvSrQtyInput($row, rowKey);
            $row
              .find("td.comp-value-cell")
              .text(
                computeMaterialSrValueFromRow(
                  productDataRef.current?.[rowKey],
                  0
                )
              );
          });

          $table
            .closest(".dataTables_wrapper, .dt-container")
            .find('input[id="select-all-products"]')
            .prop("checked", false);
        }

        if (normalizedTriggerKey && productTableRef.current) {
          const $triggerRow = $(productTableRef.current)
            .find(
              '.product-select-checkbox[data-product-key="' +
                normalizedTriggerKey +
                '"]'
            )
            .closest("tr");
          if ($triggerRow.length) {
            const $triggerQty = $triggerRow
              .find(
                '.complaint-qty-input[data-key="' + normalizedTriggerKey + '"]'
              )
              .first();
            unlockExportCommercialQtyInput($triggerRow, normalizedTriggerKey);
            lockComponentDetailsBvSrQtyInput($triggerRow, normalizedTriggerKey);
            if ($triggerQty.length) {
              $triggerQty.val("");
            }
            $triggerRow
              .find("td.comp-value-cell")
              .text(
                computeMaterialSrValueFromRow(
                  productDataRef.current?.[normalizedTriggerKey],
                  0
                )
              );
          }
        }

        setCategorySelectionTick((v) => v + 1);
        if (dataTableInstanceRef.current) {
          try {
            dataTableInstanceRef.current.rows({ page: "all" }).invalidate();
          } catch (_) {
            // ignore invalidate errors
          }
          dataTableInstanceRef.current.draw(false);
        }
        if (typeof syncComplaintValueUsdRef.current === "function") {
          syncComplaintValueUsdRef.current();
        }
      } finally {
        exportCommercialBulkApplyingRef.current = false;
      }
    },
    [isExportMarketActive]
  );

  /** Sum of material-section Sr VALUE (Comp Qty × unit price) — same logic as table footer. */
  const calculateTotalSrValue = useCallback(() => {
    let totalCompValue = 0;
    const rowDataArray = dataTableInstanceRef.current
      ? dataTableInstanceRef.current.rows({ page: "all" }).data().toArray()
      : materialRows.length > 0
        ? materialRows
        : Array.isArray(invoiceDetails?.products)
          ? invoiceDetails.products
          : [];
    for (let i = 0; i < rowDataArray.length; i++) {
      const rowData = rowDataArray[i];
      const key =
        rowData?._rowId ?? rowData?.ProductCode ?? rowData?.productCode ?? "";
      const compQty = parseFloat(complaintQtyRef.current?.[key] ?? 0) || 0;
      totalCompValue += toRaiseComplaintMoneyNumber(
        compQty * getInvoiceUnitPriceForMaterialRow(rowData)
      );
    }
    return totalCompValue;
  }, [materialRows, invoiceDetails]);

  const syncComplaintValueUsdRef = useRef(() => {});

  /**
   * USD Value = total SR Value / Exchange Rate (from SAP invoice).
   * Reporting Amount = Exchange Rate × Invoice / Doc. Currency Amount (SR Value in $).
   */
  const syncComplaintValueUsdFromSrTotal = useCallback(() => {
    if (!isExportMarketActive()) return;
    const exchangeRate =
      parseFloat(
        invoiceDetails?.exchangeRate ?? invoiceDetails?.ExchangeRate ?? 0
      ) || 0;
    if (exchangeRate <= 0) {
      setCommercialDetails((prev) => ({
        ...prev,
        complaintValueUsd: "",
        reportingAmount: "",
      }));
      return;
    }

    const totalSrValue = calculateTotalSrValue();
    if (totalSrValue <= 0) {
      setCommercialDetails((prev) => ({
        ...prev,
        complaintValueUsd: "",
        reportingAmount: "",
      }));
      return;
    }

    const usdValue = totalSrValue / exchangeRate;
    const invoiceDocCurrencyAmount = Number.isFinite(usdValue) ? usdValue : 0;
    const reportingAmountValue = exchangeRate * invoiceDocCurrencyAmount;
    setCommercialDetails((prev) => ({
      ...prev,
      complaintValueUsd: invoiceDocCurrencyAmount
        ? invoiceDocCurrencyAmount.toFixed(2)
        : "",
      reportingAmount: Number.isFinite(reportingAmountValue)
        ? reportingAmountValue.toFixed(2)
        : "",
    }));
  }, [invoiceDetails, calculateTotalSrValue, isExportMarketActive]);

  useEffect(() => {
    syncComplaintValueUsdRef.current = syncComplaintValueUsdFromSrTotal;
  }, [syncComplaintValueUsdFromSrTotal]);

  useEffect(() => {
    syncComplaintValueUsdFromSrTotal();
  }, [complaintQty, materialRows, invoiceDetails, syncComplaintValueUsdFromSrTotal]);

  /** Export invoice search: Product Category = Business Vertical; Invoice / Doc. Currency = SAP header currency; Local Currency = INR. */
  useEffect(() => {
    if (!invoiceDetails || !isExportMarketActive()) return;
    const auto = getExportCommercialAutoFields(invoiceDetails);
    setCommercialDetails((prev) => ({
      ...prev,
      productCategory: auto.productCategory,
      invoiceCurrency: auto.invoiceCurrency,
      documentCurrency: auto.documentCurrency,
      // Keep existing reporting currency when reloading the same invoice; default USD for export.
      reportingCurrency: prev.reportingCurrency || "USD",
      exchangeRate: (
        invoiceDetails?.exchangeRate ??
        invoiceDetails?.ExchangeRate ??
        ""
      )
        .toString()
        .trim(),
    }));
  }, [invoiceDetails, market, isExportMarketActive]);

  const shouldShowShortPayment = useMemo(() => {
    const selectedKeys = Array.from(selectedProducts || []);
    const optionsByCode = new Map(
      (Array.isArray(srTypesOptions) ? srTypesOptions : []).map((o) => [
        String(o?.value ?? "").trim(),
        String(o?.text ?? "").trim().toLowerCase(),
      ])
    );
    for (const key of selectedKeys) {
      const code = String(categoryRef.current?.[key] ?? "").trim();
      if (!code) continue;
      const fromOptions = optionsByCode.get(code);
      const fromDraft = String(categoryDescRef.current?.[key] ?? "")
        .trim()
        .toLowerCase();
      if (fromOptions === "commercial" || fromDraft === "commercial") {
        return true;
      }
    }
    return String(typeOfComplaint || "").trim().toLowerCase() === "commercial";
  }, [selectedProducts, srTypesOptions, typeOfComplaint, categorySelectionTick]);

  useEffect(() => {
    if (shouldShowShortPayment) return;
    setCommercialDetails((prev) =>
      prev.shortPayment ? { ...prev, shortPayment: "" } : prev
    );
  }, [shouldShowShortPayment]);

  const [productDetailsOpen, setProductDetailsOpen] = useState(false);
  const [selectedProductData, setSelectedProductData] = useState(null);
  const productDetailsModalRef = useRef(null);
  const [componentDetailsOpen, setComponentDetailsOpen] = useState(false);
  const [selectedComponentProductKey, setSelectedComponentProductKey] = useState(null);
  const componentDetailsModalRef = useRef(null);
  const componentDetailsRef = useRef({});
  /** Mirrors componentDetailsData for reliable Save (avoids stale React state vs Select2). */
  const componentDetailsDataRef = useRef({
    materialGroupCode: "",
    materialGroupDesc: "",
    componentCategoryCode: "",
    componentCategory: "",
    componentListCode: "",
    componentList: "",
    componentQty: "",
    componentIssueCode: "",
    componentIssue: "",
    doorMaterialCode: "",
    componentIssueRemarks: "",
  });
  const componentCategoryCodeRef = useRef("");
  const materialGroupCodeRef = useRef("");
  const componentDetailsMaterialGroupRef = useRef(null);
  const componentDetailsCategoryRef = useRef(null);
  const componentDetailsComponentListRef = useRef(null);
  const componentDetailsIssueRef = useRef(null);
  const [componentDetailsData, setComponentDetailsData] = useState({
    materialGroupCode: "",
    materialGroupDesc: "",
    componentCategoryCode: "",
    componentCategory: "",
    componentListCode: "",
    componentList: "",
    componentQty: "",
    componentIssueCode: "",
    componentIssue: "",
    doorMaterialCode: "",
    componentIssueRemarks: "",
  });
  /** Inline editable component rows in the modal grid. */
  const [componentDetailsRowsList, setComponentDetailsRowsList] = useState([]);
  /** Always mirrors componentDetailsRowsList so Save is never stale. */
  const componentDetailsRowsListRef = useRef([]);
  useEffect(() => {
    componentDetailsRowsListRef.current = componentDetailsRowsList;
  }, [componentDetailsRowsList]);
  /** Per-grid-row cascading dropdown options (category / component name). */
  const [componentRowOptionsMap, setComponentRowOptionsMap] = useState({});
  /** Always mirrors componentRowOptionsMap for sync reads in change handlers. */
  const componentRowOptionsMapRef = useRef({});
  useEffect(() => {
    componentRowOptionsMapRef.current = componentRowOptionsMap;
  }, [componentRowOptionsMap]);
  const setComponentDetailsDataBoth = useCallback((updater) => {
    setComponentDetailsData((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      componentDetailsDataRef.current = next;
      return next;
    });
  }, []);
  // Dropdown options for Component Details modal (from MATERIALGROUPMASTER, COMPONANTCATGBYMATERIAL, COMPONANTNAMEMASTER, COMPONANTISSUEMASTER)
  const [materialGroupsOptions, setMaterialGroupsOptions] = useState([]);
  /** Material Group Desc. options filtered by the open row's business vertical. */
  const filteredMaterialGroupsOptions = useMemo(() => {
    const row = selectedComponentProductKey
      ? productDataRef.current?.[selectedComponentProductKey]
      : null;
    const bv = getBusinessVerticalFromRow(row);
    return filterMaterialGroupsByBusinessVertical(materialGroupsOptions, bv);
  }, [materialGroupsOptions, selectedComponentProductKey, componentDetailsOpen]);
  const [componentCategoriesOptions, setComponentCategoriesOptions] = useState([]);
  const [componentNamesOptions, setComponentNamesOptions] = useState([]);
  const [componentIssuesOptions, setComponentIssuesOptions] = useState([]);
  const [loadingComponentDropdowns, setLoadingComponentDropdowns] = useState(false);

  // Drop Material Group selection if it is not valid for the row's business vertical
  useEffect(() => {
    if (!componentDetailsOpen || loadingComponentDropdowns) return;
    const code = componentDetailsData.materialGroupCode;
    if (!code) return;
    const stillValid = filteredMaterialGroupsOptions.some(
      (o) => (o.code || o.Code) === code
    );
    if (stillValid) return;
    setComponentDetailsDataBoth((prev) => ({
      ...prev,
      materialGroupCode: "",
      materialGroupDesc: "",
      componentCategoryCode: "",
      componentCategory: "",
      componentListCode: "",
      componentList: "",
    }));
  }, [
    componentDetailsOpen,
    loadingComponentDropdowns,
    filteredMaterialGroupsOptions,
    componentDetailsData.materialGroupCode,
    setComponentDetailsDataBoth,
  ]);

  // Normalize date strings (DD/MM/YYYY, DD-MM-YYYY, or ISO) to ISO for native date inputs
  const toISODate = (value) => {
    if (!value || typeof value !== "string") return "";
    const trimmed = value.trim();

    const slashMatch = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (slashMatch) return `${slashMatch[3]}-${slashMatch[2]}-${slashMatch[1]}`;

    const dashMatch = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dashMatch) return `${dashMatch[3]}-${dashMatch[2]}-${dashMatch[1]}`;

    if (trimmed.match(/^\d{4}-\d{2}-\d{2}$/)) return trimmed;

    const parsed = new Date(trimmed);
    if (!isNaN(parsed.getTime())) {
      const y = parsed.getFullYear();
      const m = String(parsed.getMonth() + 1).padStart(2, "0");
      const d = String(parsed.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }

    return "";
  };

  // Convert date (Date object or ISO/date string) to YYYY-MM-DD for input type="date"
  const toDateInputValue = (value) => {
    if (value == null || value === "") return "";
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  // Format date from ISO or any format to DD-MM-YYYY for display
  const formatDateDisplay = (value) => {
    if (!value) return "";

    // Convert to ISO first
    const isoDate = toISODate(value);
    if (!isoDate) return "";

    // Parse ISO date (YYYY-MM-DD)
    const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      return `${match[3]}-${match[2]}-${match[1]}`; // DD-MM-YYYY
    }

    return "";
  };
  const isFloorCategory = (cat) => {
    const c = (cat || "").toLowerCase();
    return c.includes("floor");
  };

  // Initialize Bootstrap modal instance if available (optional enhancement)
  useEffect(() => {
    if (!checklistModalRef.current) return;

    const modalElement = checklistModalRef.current;

    // Try to initialize Bootstrap modal for enhanced behavior (fade animations, etc.)
    const initBootstrapModal = () => {
      if (typeof window === "undefined") return;

      // Check if Bootstrap is available
      let BootstrapModal = null;
      if (window.bootstrap && window.bootstrap.Modal) {
        BootstrapModal = window.bootstrap.Modal;
      }

      if (BootstrapModal && !modalInstanceRef.current) {
        try {
          modalInstanceRef.current =
            BootstrapModal.getInstance(modalElement) ||
            new BootstrapModal(modalElement, {
              backdrop: false, // We're managing backdrop manually
              keyboard: true,
            });

          // Handle modal hidden event to sync state
          const handleHidden = () => {
            setChecklistOpen(false);
          };

          modalElement.addEventListener("hidden.bs.modal", handleHidden);
          modalElement._handleHidden = handleHidden;
        } catch (error) {
          // Bootstrap not available or error - that's okay, CSS will handle it
          console.debug("Bootstrap Modal not available, using CSS fallback");
        }
      }
    };

    // Try to initialize, but don't block if Bootstrap isn't available
    setTimeout(initBootstrapModal, 100);

    // Cleanup
    return () => {
      if (modalElement && modalElement._handleHidden) {
        modalElement.removeEventListener(
          "hidden.bs.modal",
          modalElement._handleHidden
        );
        delete modalElement._handleHidden;
      }
    };
  }, []); // Run only once on mount

  // Track previous modal state to avoid unnecessary sidebar toggles
  const prevChecklistOpenRef = useRef(checklistOpen);
  const modalOpenRef = useRef(checklistOpen);
  const prevProductDetailsOpenRef = useRef(productDetailsOpen);

  // Update ref whenever checklistOpen changes
  useEffect(() => {
    modalOpenRef.current = checklistOpen;
  }, [checklistOpen]);

  // Handle ESC key to close modal and sidebar collapse/restore
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && checklistOpen) {
        handleChecklistClose();
      }
    };

    // Only update sidebar if state actually changed
    const prevState = prevChecklistOpenRef.current;
    const currentState = checklistOpen;

    if (currentState && !prevState) {
      // Modal just opened
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      collapseSidebar();
    } else if (!currentState && prevState) {
      // Modal just closed
      document.body.style.overflow = "";
      restoreSidebar();
    }

    // Update ref for next render
    prevChecklistOpenRef.current = currentState;

    return () => {
      document.removeEventListener("keydown", handleEscape);
      // Only restore body overflow, don't restore sidebar here
      // Sidebar restoration is handled by the state change above
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checklistOpen]); // Only depend on checklistOpen, functions are stable

  // Handle ESC key to close product details modal and sidebar collapse/restore
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && productDetailsOpen) {
        handleProductDetailsClose();
      }
    };

    // Only update sidebar if state actually changed
    const prevState = prevProductDetailsOpenRef.current;
    const currentState = productDetailsOpen;

    if (currentState && !prevState) {
      // Modal just opened
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      collapseSidebar();
    } else if (!currentState && prevState) {
      // Modal just closed
      document.body.style.overflow = "";
      restoreSidebar();
    }

    // Update ref for next render
    prevProductDetailsOpenRef.current = currentState;

    return () => {
      document.removeEventListener("keydown", handleEscape);
      // Only restore body overflow, don't restore sidebar here
      // Sidebar restoration is handled by the state change above
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productDetailsOpen]); // Only depend on productDetailsOpen, functions are stable

  // Handle ESC key to close component details modal and sidebar collapse/restore
  const prevComponentDetailsOpenRef = useRef(componentDetailsOpen);
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && componentDetailsOpen) {
        handleComponentDetailsCancel();
      }
    };

    // Only update sidebar if state actually changed
    const prevState = prevComponentDetailsOpenRef.current;
    const currentState = componentDetailsOpen;

    if (currentState && !prevState) {
      // Modal just opened
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      collapseSidebar();
    } else if (!currentState && prevState) {
      // Modal just closed
      document.body.style.overflow = "";
      restoreSidebar();
    }

    // Update ref for next render
    prevComponentDetailsOpenRef.current = currentState;

    return () => {
      document.removeEventListener("keydown", handleEscape);
      // Only restore body overflow, don't restore sidebar here
      // Sidebar restoration is handled by the state change above
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentDetailsOpen]); // Only depend on componentDetailsOpen, functions are stable

  // Cleanup on unmount - restore sidebar if modal was open
  useEffect(() => {
    return () => {
      if (modalOpenRef.current || productDetailsOpen || componentDetailsOpen) {
        restoreSidebar();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on unmount

  // Debug: Log complaintTypes whenever it changes
  useEffect(() => {

   // console.log("ComplaintTypes state updated:", complaintTypes);
    //console.log("ComplaintTypes count:", complaintTypes.length);

    if (complaintTypes.length > 0) {
     // console.log("First complaint type:", complaintTypes[0]);
      //console.log("All complaint types:", complaintTypes);
    }
  }, [complaintTypes]);


  const getDraftsFromMemory = () => {
    try {
      const raw = localStorage.getItem(draftStorageKey);
      return raw ? JSON.parse(raw) : {};
    } catch (err) {
      console.error("Failed to read draft", err);
      return {};
    }
  };

  const loadDraftForProduct = (productKey) => {
    const drafts = getDraftsFromMemory();
    const storedLines = drafts?.[productKey];
    if (Array.isArray(storedLines) && storedLines.length > 0) {
      setComplaintLines(
        storedLines.map((line) => ({
          id: line.id || Date.now(),
          type: line.type || "",
          qty: line.qty || "",
        }))
      );
    } else {
      setComplaintLines([newLine()]);
    }
  };

  const fetchComplaintTypes = async () => {
    console.log(
      "fetchComplaintTypes called. Current complaintTypes.length:",
      complaintTypes.length
    );
    // Don't return early - always fetch to ensure we have the latest data
    // if (complaintTypes.length > 0) return; // Already fetched

    setLoadingComplaintTypes(true);
    try {
      // Map MarketCode/MarketName to "Domestic" or "Export"
      // If market cannot be determined, pass null to get all complaint types
      const marketCode =
        invoiceDetails?.MarketCode || invoiceDetails?.marketCode || "";
      const marketName =
        invoiceDetails?.MarketName || invoiceDetails?.marketName || "";
      let market = null;

      if (marketCode || marketName) {
        const codeUpper = marketCode.toUpperCase();
        const nameUpper = marketName.toUpperCase();

        // Check if it's Domestic
        if (
          codeUpper.includes("DOM") ||
          nameUpper.includes("DOMESTIC") ||
          codeUpper === "DOMESTIC" ||
          nameUpper === "DOMESTIC"
        ) {
          market = "Domestic";
        }
        // Check if it's Export
        else if (
          codeUpper.includes("EXP") ||
          nameUpper.includes("EXPORT") ||
          codeUpper === "EXPORT" ||
          nameUpper === "EXPORT"
        ) {
          market = "Export";
        }
      }

      // Fetch all complaint types filtered by market (no pagination needed)
      const response = await masterDataAPI.getComplaintTypes(market);

      console.log("Complaint types API response:", response);
      console.log("Response structure:", {
        success: response?.success,
        data: response?.data,
        complaintTypes: response?.data?.ComplaintTypes,
        isArray: Array.isArray(response?.data?.ComplaintTypes),
        length: response?.data?.ComplaintTypes?.length,
      });

      if (response && response.success && response.data?.complaintTypes) {
        // API returns camelCase: complaintTypes (not ComplaintTypes)
        const types =
          response.data.complaintTypes || response.data.ComplaintTypes || [];
        console.log("Raw complaint types from API:", types);
        console.log("First type structure:", types[0]);
        console.log(
          "First type keys:",
          types[0] ? Object.keys(types[0]) : "No types"
        );

        // Filter only active complaint types (though stored procedure already filters)
        // API returns camelCase: isActive
        const activeTypes = types.filter((type) => {
          const isActive =
            type.isActive !== undefined ? type.isActive : type.IsActive;
          return isActive === true;
        });

        console.log("Active types after filtering:", activeTypes);

        // Normalize property names - API returns camelCase, convert to PascalCase for consistency
        const normalizedTypes = activeTypes.map((type) => {
          const normalized = {
            ComplaintTypeId:
              type.complaintTypeId || type.ComplaintTypeId || type.id,
            ComplaintTypeName:
              type.complaintTypeName ||
              type.ComplaintTypeName ||
              type.name ||
              "Unknown",
            ComplaintTypeCode:
              type.complaintTypeCode || type.ComplaintTypeCode || type.code,
            Description: type.description || type.Description,
            IsForDomestic:
              type.isForDomestic !== undefined
                ? type.isForDomestic
                : type.IsForDomestic !== undefined
                  ? type.IsForDomestic
                  : false,
            IsForExport:
              type.isForExport !== undefined
                ? type.isForExport
                : type.IsForExport !== undefined
                  ? type.IsForExport
                  : false,
            IsActive:
              type.isActive !== undefined
                ? type.isActive
                : type.IsActive !== undefined
                  ? type.IsActive
                  : true,
          };
          console.log("Normalizing type:", type, "->", normalized);
          return normalized;
        });

        console.log("Normalized complaint types:", normalizedTypes);
        console.log(
          "Setting complaint types state with",
          normalizedTypes.length,
          "items"
        );
        console.log("Normalized types sample:", normalizedTypes.slice(0, 2));
        setComplaintTypes(normalizedTypes);
      } else {
        console.warn("No complaint types in response:", response);
        // Try to set empty array if response structure is different
        // Check for both camelCase and PascalCase
        if (response && response.data) {
          const types =
            response.data.complaintTypes || response.data.ComplaintTypes;
          if (Array.isArray(types)) {
            console.log(
              "Trying to use response.data.complaintTypes/ComplaintTypes directly"
            );
            setComplaintTypes(types);
          } else if (Array.isArray(response.data)) {
            console.log("Response.data is array, using it directly");
            setComplaintTypes(response.data);
          } else {
            console.warn("Setting empty array - no valid data found");
            setComplaintTypes([]);
          }
        } else {
          console.warn("Setting empty array - no valid data found");
          setComplaintTypes([]);
        }
      }
    } catch (err) {
      console.error("Error fetching complaint types:", err);
      console.error("Error details:", err.response?.data || err.message);
      // Set empty array on error so UI doesn't show loading forever
      setComplaintTypes([]);
    } finally {
      setLoadingComplaintTypes(false);
    }
  };

  const openChecklist = async (productKey, qty) => {
    // Validate category selection before opening checklist
    const selectedCategory = categoryRef.current[productKey] || "";
    if (!selectedCategory || selectedCategory.trim() === "") {
      setMaterialSectionError("Please select SR Type to proceed.");
      showMaterialSectionError("Please select SR Type to proceed.");
      return;
    }

    // Clear error if category is selected
    setMaterialSectionError("");

    // Get products and handle different data formats (same as DataTable initialization)
    let products = invoiceDetails?.products || [];

    // If products is a string, try to parse it
    if (typeof products === "string") {
      try {
        products = JSON.parse(products);
      } catch (e) {
        console.error(
          "Error parsing products JSON string in openChecklist:",
          e
        );
        products = [];
      }
    }

    // If products is not an array, try to extract it from nested structure
    if (products && !Array.isArray(products)) {
      if (Array.isArray(products.products)) {
        products = products.products;
      } else if (Array.isArray(products.data)) {
        products = products.data;
      } else {
        products = [];
      }
    }

    const matched = products.find(
      (p) =>
        p?.ProductCode === productKey ||
        p?.productCode === productKey ||
        p?.Productcode === productKey
    );

    setChecklistData({
      category:
        matched?.ProductCategory ||
        matched?.productCategory ||
        matched?.Productcategory ||
        "",
      productName:
        matched?.ProductName ||
        matched?.productName ||
        matched?.Productname ||
        "",
      productCode:
        matched?.ProductCode ||
        matched?.productCode ||
        matched?.Productcode ||
        productKey ||
        "",
      orderNo:
        matched?.OrderNo ||
        matched?.orderNo ||
        matched?.Orderno ||
        invoiceDetails?.orderNo ||
        "",
      quantity: qty || "",
    });
    setSelectedProductKey(productKey);
    loadDraftForProduct(productKey);

    // Fetch complaint types when popup opens
    await fetchComplaintTypes();

    setChecklistOpen(true);
  };

  const openChecklistForCategory = async (category) => {
    // Get products and handle different data formats
    let products = invoiceDetails?.products || [];

    if (typeof products === "string") {
      try {
        products = JSON.parse(products);
      } catch (e) {
        console.error(
          "Error parsing products JSON string in openChecklistForCategory:",
          e
        );
        products = [];
      }
    }

    if (products && !Array.isArray(products)) {
      if (Array.isArray(products.products)) {
        products = products.products;
      } else if (Array.isArray(products.data)) {
        products = products.data;
      } else {
        products = [];
      }
    }

    const match = products.find(
      (p) =>
        (
          p?.ProductCategory ||
          p?.productCategory ||
          p?.Productcategory ||
          ""
        ).toLowerCase() === (category || "").toLowerCase()
    );
    if (match) {
      const key = match.ProductCode || match.productCode || match.Productcode;
      const qty = complaintQtyRef.current[key] ?? "";
      await openChecklist(key, qty);
    }
  };

  const handleDraftSave = async () => {
    setPendingDraftAction("modal");
    setSavingDraft(true);
    try {
      const payload = {
        invoiceNo: formData.invoiceNo,
        productCode: checklistData.productCode,
        category: checklistData.category,
        complaintQty: checklistData.quantity,
        floorForm,
        complaintLines,
      };
      await complaintAPI.saveChecklistDraft(payload);

      // Small delay before closing to allow pending operations to complete
      requestAnimationFrame(() => {
        setChecklistOpen(false);
      });
    } catch (err) {
      console.error("Error saving checklist draft:", err);
      alert(err?.response?.data?.message || "Failed to save draft");
    } finally {
      setSavingDraft(false);
      setPendingDraftAction(null);
    }
  };

  const handleChecklistClose = () => {
    if (modalInstanceRef.current) {
      try {
        modalInstanceRef.current.hide();
      } catch (error) {
        console.error("Error closing modal:", error);
      }
    }

    // Use requestAnimationFrame to ensure smooth closing and prevent ResizeObserver issues
    requestAnimationFrame(() => {
      setChecklistOpen(false);
    });
  };

  const handleProductDetailsOpen = (productData) => {
    setSelectedProductData(productData);
    setProductDetailsOpen(true);
  };

  const handleProductDetailsClose = () => {
    requestAnimationFrame(() => {
      setProductDetailsOpen(false);
      setSelectedProductData(null);
    });
  };

  const fetchComponentDetailsDropdowns = async (
    componentCatgCode = null,
    materialGroupCode = null
  ) => {
    setLoadingComponentDropdowns(true);
    try {
      const [mgRes, catRes, namesRes, issuesRes] = await Promise.all([
        masterDataAPI.getMaterialGroups(true),
        materialGroupCode
          ? masterDataAPI.getComponentCategories(true, materialGroupCode)
          : Promise.resolve({ data: [] }),
        componentCatgCode
          ? masterDataAPI.getComponentNames(componentCatgCode, true)
          : Promise.resolve({ data: [] }),
        masterDataAPI.getComponentIssues(true),
      ]);
      const mgList = (mgRes?.data ?? mgRes) || [];
      const catList = (catRes?.data ?? catRes) || [];
      const namesList = (namesRes?.data ?? namesRes) || [];
      const issuesList = (issuesRes?.data ?? issuesRes) || [];
      setMaterialGroupsOptions(Array.isArray(mgList) ? mgList : []);
      setComponentCategoriesOptions(Array.isArray(catList) ? catList : []);
      setComponentNamesOptions(Array.isArray(namesList) ? namesList : []);
      setComponentIssuesOptions(Array.isArray(issuesList) ? issuesList : []);
    } catch (err) {
      console.error("Error fetching component details dropdowns:", err);
      setMaterialGroupsOptions([]);
      setComponentCategoriesOptions([]);
      setComponentNamesOptions([]);
      setComponentIssuesOptions([]);
    } finally {
      setLoadingComponentDropdowns(false);
    }
  };

  const getProductCodeFromKey = (productKey) => {
    const row = productDataRef.current?.[productKey];
    return (row?.ProductCode ?? row?.productCode ?? row?.Productcode ?? (productKey || "")).toString().trim();
  };

  const getInvoiceKeyForComponentDetails = () =>
    (
      invoiceDetails?.InvoiceNumber ||
      invoiceDetails?.invoiceNumber ||
      invoiceDetails?.invoiceNo ||
      invoiceDetails?.InvoiceNo ||
      formData?.invoiceNo ||
      ""
    )
      .toString()
      .trim()
      .toUpperCase();

  /** Row-scoped LS key so each material row keeps its own Component Details. */
  const getComponentDetailsRowStorageKey = (rowKey) => {
    const inv = getInvoiceKeyForComponentDetails();
    const key = String(rowKey || "").trim();
    return inv ? `${inv}::${key}` : key;
  };

  const emptyComponentDetailsForm = () => ({
    materialGroupCode: "",
    materialGroupDesc: "",
    componentCategoryCode: "",
    componentCategory: "",
    componentListCode: "",
    componentList: "",
    componentQty: "",
    componentIssueCode: "",
    componentIssue: "",
    doorMaterialCode: "",
    componentIssueRemarks: "",
  });

  const COMPONENT_DETAILS_FIELD_KEYS = [
    "materialGroupCode",
    "materialGroupDesc",
    "componentCategoryCode",
    "componentCategory",
    "componentListCode",
    "componentList",
    "componentQty",
    "componentIssueCode",
    "componentIssue",
    "doorMaterialCode",
    "componentIssueRemarks",
  ];

  const createComponentDetailRowId = (() => {
    let seq = 0;
    return () => {
      seq += 1;
      return `comp_${Date.now()}_${seq}_${Math.random().toString(36).slice(2, 8)}`;
    };
  })();

  const createEmptyComponentDetailRow = () => ({
    _rowId: createComponentDetailRowId(),
    ...emptyComponentDetailsForm(),
    componentValue: 0,
  });

  const isComponentDetailRowFilled = (r) =>
    COMPONENT_DETAILS_FIELD_KEYS.some(
      (k) => String(r?.[k] ?? "").trim() !== ""
    );

  const countFilledComponentDetailRows = (rows) =>
    (Array.isArray(rows) ? rows : []).filter(isComponentDetailRowFilled).length;

  const normalizeComponentDetailRow = (row) => {
    if (!row || typeof row !== "object") return createEmptyComponentDetailRow();
    // Preserve identity — do not allocate a throwaway id then overwrite.
    const preservedId = row._rowId || row.rowId || createComponentDetailRowId();
    const base = {
      _rowId: preservedId,
      ...emptyComponentDetailsForm(),
      componentValue: 0,
    };
    applyComponentDetailsFields(base, row);
    base._rowId = preservedId;
    const cv = row.componentValue ?? row.ComponentValue;
    base.componentValue = cv != null && cv !== "" ? parseFloat(cv) || 0 : 0;
    return base;
  };

  const normalizeComponentDetailsStorage = (data) => {
    if (!data) return { rows: [] };
    if (Array.isArray(data.rows)) {
      return { rows: data.rows.map(normalizeComponentDetailRow) };
    }
    if (Array.isArray(data)) {
      return { rows: data.map(normalizeComponentDetailRow) };
    }
    const hasLegacy = COMPONENT_DETAILS_FIELD_KEYS.some((k) => {
      const v = data[k];
      return v != null && String(v).trim() !== "";
    });
    if (hasLegacy || data.componentValue != null || data.ComponentValue != null) {
      return { rows: [normalizeComponentDetailRow(data)] };
    }
    return { rows: [] };
  };

  const mapComponentDetailRowFromApi = (row) =>
    normalizeComponentDetailRow({
      _rowId: row._rowId || row.rowId,
      materialGroupCode:
        row.ComponentMaterialGroupCode ??
        row.componentMaterialGroupCode ??
        row.materialGroupCode ??
        row.MaterialGroup ??
        row.materialGroup ??
        "",
      materialGroupDesc: row.MaterialGroupDesc ?? row.materialGroupDesc ?? "",
      componentCategoryCode: row.ComponentCategoryCode ?? row.componentCategoryCode ?? "",
      componentCategory: row.ComponentCategory ?? row.componentCategory ?? "",
      componentListCode: row.ComponentListCode ?? row.componentListCode ?? "",
      componentList: row.ComponentList ?? row.componentList ?? "",
      componentQty:
        row.ComponentQty != null && row.ComponentQty !== ""
          ? String(row.ComponentQty)
          : row.componentQty != null && row.componentQty !== ""
            ? String(row.componentQty)
            : "",
      componentIssueCode: row.ComponentIssueCode ?? row.componentIssueCode ?? "",
      componentIssue: row.ComponentIssue ?? row.componentIssue ?? "",
      doorMaterialCode: row.DoorMaterialCode ?? row.doorMaterialCode ?? "",
      componentIssueRemarks: row.ComponentIssueRemarks ?? row.componentIssueRemarks ?? "",
      componentValue: row.ComponentValue ?? row.componentValue ?? 0,
    });

  const parseComponentDetailsArray = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : parsed ? [parsed] : [];
      } catch {
        return [];
      }
    }
    if (typeof raw === "object") return [raw];
    return [];
  };

  /** Keep saved code visible in dropdown even before async options load (edit/draft mode). */
  const mergeSavedSelectOption = (options, code, label) => {
    const list = Array.isArray(options) ? [...options] : [];
    const trimmedCode = String(code || "").trim();
    if (!trimmedCode) return list;
    if (list.some((o) => String(o.code || o.Code || "").trim() === trimmedCode)) {
      return list;
    }
    const trimmedLabel = String(label || trimmedCode).trim();
    return [
      { code: trimmedCode, Code: trimmedCode, name: trimmedLabel, Name: trimmedLabel },
      ...list,
    ];
  };

  const mapComponentDetailRowToApi = (row) => ({
    ComponentMaterialGroupCode: row.materialGroupCode || null,
    MaterialGroupDesc: row.materialGroupDesc || null,
    ComponentCategoryCode: row.componentCategoryCode || null,
    ComponentCategory: row.componentCategory || null,
    ComponentListCode: row.componentListCode || null,
    ComponentList: row.componentList || null,
    ComponentQty: row.componentQty != null && row.componentQty !== "" ? String(row.componentQty) : null,
    ComponentIssueCode: row.componentIssueCode || null,
    ComponentIssue: row.componentIssue || null,
    DoorMaterialCode: row.doorMaterialCode || null,
    ComponentIssueRemarks: row.componentIssueRemarks || null,
    ComponentValue: row.componentValue != null ? parseFloat(row.componentValue) || 0 : null,
  });

  const computeComponentRowValue = (productKey, compQty) => {
    const product = productDataRef.current?.[productKey];
    const compQtyNum = parseFloat(compQty) || 0;
    return toRaiseComplaintMoneyNumber(
      compQtyNum * getInvoiceUnitPriceForMaterialRow(product)
    );
  };

  const resetComponentDetailsForm = () => {
    const empty = emptyComponentDetailsForm();
    componentDetailsDataRef.current = empty;
    setComponentDetailsData(empty);
    componentCategoryCodeRef.current = "";
    materialGroupCodeRef.current = "";
  };

  const loadComponentRowCategoryOptions = async (
    rowId,
    materialGroupCode,
    { resetNames = true } = {}
  ) => {
    if (!materialGroupCode) {
      setComponentRowOptionsMap((prev) => {
        const next = {
          ...prev,
          [rowId]: {
            ...(prev[rowId] || {}),
            categories: [],
            names: resetNames ? [] : prev[rowId]?.names || [],
          },
        };
        componentRowOptionsMapRef.current = next;
        return next;
      });
      return [];
    }
    try {
      const res = await masterDataAPI.getComponentCategories(true, materialGroupCode);
      const list = Array.isArray(res?.data ?? res) ? res?.data ?? res : [];
      setComponentRowOptionsMap((prev) => {
        const next = {
          ...prev,
          [rowId]: {
            ...(prev[rowId] || {}),
            categories: list,
            names: resetNames ? [] : prev[rowId]?.names || [],
          },
        };
        componentRowOptionsMapRef.current = next;
        return next;
      });
      return list;
    } catch (_) {
      setComponentRowOptionsMap((prev) => {
        const next = {
          ...prev,
          [rowId]: {
            ...(prev[rowId] || {}),
            categories: [],
            names: resetNames ? [] : prev[rowId]?.names || [],
          },
        };
        componentRowOptionsMapRef.current = next;
        return next;
      });
      return [];
    }
  };

  const loadComponentRowNameOptions = async (rowId, categoryCode) => {
    if (!categoryCode) {
      setComponentRowOptionsMap((prev) => {
        const next = {
          ...prev,
          [rowId]: { ...(prev[rowId] || {}), names: [] },
        };
        componentRowOptionsMapRef.current = next;
        return next;
      });
      return [];
    }
    try {
      const res = await masterDataAPI.getComponentNames(categoryCode, true);
      const list = Array.isArray(res?.data ?? res) ? res?.data ?? res : [];
      setComponentRowOptionsMap((prev) => {
        const next = {
          ...prev,
          [rowId]: { ...(prev[rowId] || {}), names: list },
        };
        componentRowOptionsMapRef.current = next;
        return next;
      });
      return list;
    } catch (_) {
      setComponentRowOptionsMap((prev) => {
        const next = {
          ...prev,
          [rowId]: { ...(prev[rowId] || {}), names: [] },
        };
        componentRowOptionsMapRef.current = next;
        return next;
      });
      return [];
    }
  };

  const componentGridPreloadSeqRef = useRef(0);
  const preloadComponentGridRowOptions = async (rows) => {
    const seq = ++componentGridPreloadSeqRef.current;
    for (const row of rows || []) {
      if (seq !== componentGridPreloadSeqRef.current) return; // newer preload started
      if (!row?.materialGroupCode) continue;
      // Keep existing name options while categories load so multi-row view doesn't flash blank.
      await loadComponentRowCategoryOptions(row._rowId, row.materialGroupCode, {
        resetNames: false,
      });
      if (seq !== componentGridPreloadSeqRef.current) return;
      if (row.componentCategoryCode) {
        await loadComponentRowNameOptions(row._rowId, row.componentCategoryCode);
      }
    }
  };

  const updateComponentGridRow = (rowId, patch) => {
    setComponentDetailsRowsList((prev) => {
      const next = prev.map((r) => {
        if (r._rowId !== rowId) return r;
        const updated = normalizeComponentDetailRow({ ...r, ...patch });
        if (selectedComponentProductKey && patch.componentQty !== undefined) {
          updated.componentValue = computeComponentRowValue(
            selectedComponentProductKey,
            updated.componentQty
          );
        }
        return updated;
      });
      componentDetailsRowsListRef.current = next;
      return next;
    });
  };

  const handleComponentGridMaterialGroupChange = async (rowId, code) => {
    const mgOpt = filteredMaterialGroupsOptions.find((o) => (o.code || o.Code) === code);
    updateComponentGridRow(rowId, {
      materialGroupCode: code,
      materialGroupDesc: mgOpt ? mgOpt.name || mgOpt.Name || "" : "",
      componentCategoryCode: "",
      componentCategory: "",
      componentListCode: "",
      componentList: "",
    });
    await loadComponentRowCategoryOptions(rowId, code);
  };

  const handleComponentGridCategoryChange = async (rowId, code, label) => {
    const rowOpts = componentRowOptionsMapRef.current[rowId]?.categories || [];
    const catOpt = rowOpts.find((o) => (o.code || o.Code) === code);
    const categoryLabel = catOpt
      ? catOpt.name || catOpt.Name || ""
      : label && !/^select category$/i.test(String(label))
        ? label
        : "";
    updateComponentGridRow(rowId, {
      componentCategoryCode: code,
      componentCategory: categoryLabel,
      componentListCode: "",
      componentList: "",
    });
    await loadComponentRowNameOptions(rowId, code);
  };

  const handleComponentGridListChange = (rowId, code, label) => {
    const rowOpts = componentRowOptionsMapRef.current[rowId]?.names || [];
    const listOpt = rowOpts.find((o) => (o.code || o.Code) === code);
    const listLabel = listOpt
      ? listOpt.name || listOpt.Name || ""
      : label && !/^select component$/i.test(String(label))
        ? label
        : "";
    updateComponentGridRow(rowId, {
      componentListCode: code,
      componentList: listLabel,
    });
  };

  const handleComponentGridIssueChange = (rowId, code) => {
    const issueOpt = componentIssuesOptions.find((o) => (o.code || o.Code) === code);
    updateComponentGridRow(rowId, {
      componentIssueCode: code,
      componentIssue: issueOpt ? issueOpt.name || issueOpt.Name || "" : "",
    });
  };

  const addComponentGridRow = (afterRowId) => {
    const newRow = createEmptyComponentDetailRow();
    setComponentDetailsRowsList((prev) => {
      const idx = prev.findIndex((r) => r._rowId === afterRowId);
      const next =
        idx < 0 ? [...prev, newRow] : [...prev.slice(0, idx + 1), newRow, ...prev.slice(idx + 1)];
      componentDetailsRowsListRef.current = next;
      return next;
    });
  };

  const removeComponentGridRow = (rowId) => {
    setComponentDetailsRowsList((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((r) => r._rowId !== rowId);
      componentDetailsRowsListRef.current = next;
      return next;
    });
    setComponentRowOptionsMap((prev) => {
      const next = { ...prev };
      delete next[rowId];
      componentRowOptionsMapRef.current = next;
      return next;
    });
  };

  const persistComponentDetailsRows = (productKey, rows) => {
    if (!productKey) return;
    const normalizedRows = (rows || []).map(normalizeComponentDetailRow);
    const productCode = getProductCodeFromKey(productKey);
    const payload = { rows: normalizedRows };
    try {
      const stored = readComponentDetailsLocalStore();
      const rowStoreKey = getComponentDetailsRowStorageKey(productKey);
      stored[rowStoreKey] = {
        rows: normalizedRows.map((r) => ({
          _rowId: r._rowId,
          materialGroupCode: r.materialGroupCode ?? "",
          materialGroupDesc: r.materialGroupDesc ?? "",
          componentCategoryCode: r.componentCategoryCode ?? "",
          componentCategory: r.componentCategory ?? "",
          componentListCode: r.componentListCode ?? "",
          componentList: r.componentList ?? "",
          componentQty:
            r.componentQty != null && r.componentQty !== "" ? String(r.componentQty) : "",
          componentValue: parseFloat(r.componentValue) || 0,
          componentIssueCode: r.componentIssueCode ?? "",
          componentIssue: r.componentIssue ?? "",
          doorMaterialCode: r.doorMaterialCode ?? "",
          componentIssueRemarks: r.componentIssueRemarks ?? "",
        })),
      };
      if (productCode && Object.prototype.hasOwnProperty.call(stored, productCode)) {
        delete stored[productCode];
      }
      writeComponentDetailsLocalStore(stored);
    } catch (_) {}
    componentDetailsRef.current = {
      ...componentDetailsRef.current,
      [productKey]: payload,
    };
    // Enable Material Section + after Component Details are saved (Mikasa Doors/Floors/Sturdo).
    try {
      syncAllMaterialRowAddButtons();
    } catch (_) {}
  };

  const applyComponentDetailsFields = (target, source) => {
    if (!source || typeof source !== "object") return target;
    COMPONENT_DETAILS_FIELD_KEYS.forEach((k) => {
      if (!Object.prototype.hasOwnProperty.call(source, k)) return;
      const v = source[k];
      target[k] = v === 0 ? "0" : v != null ? String(v) : "";
    });
    return target;
  };

  const readComponentDetailsLocalStore = () => {
    try {
      return JSON.parse(localStorage.getItem(COMPONENT_DETAILS_LS_KEY) || "{}");
    } catch {
      return {};
    }
  };

  const writeComponentDetailsLocalStore = (stored) => {
    try {
      localStorage.setItem(COMPONENT_DETAILS_LS_KEY, JSON.stringify(stored || {}));
    } catch (_) {}
  };

  /** True when the material row has at least one saved non-blank Component Details line. */
  const materialRowHasFilledComponentDetails = (rowKey) => {
    const key = String(rowKey || "").trim();
    if (!key) return false;
    let storage = normalizeComponentDetailsStorage(componentDetailsRef.current[key]);
    if (countFilledComponentDetailRows(storage.rows) > 0) return true;
    try {
      const stored = readComponentDetailsLocalStore();
      const rowStoreKey = getComponentDetailsRowStorageKey(key);
      let byRow = stored[rowStoreKey];
      if ((!byRow || typeof byRow !== "object") && getProductCodeFromKey(key)) {
        byRow = stored[getProductCodeFromKey(key)];
      }
      if (byRow && typeof byRow === "object") {
        storage = normalizeComponentDetailsStorage(byRow);
      }
    } catch (_) {}
    return countFilledComponentDetailRows(storage.rows) > 0;
  };

  /**
   * Mikasa Doors / Floors / Sturdo: block Add More (+) until Component Details are saved.
   * Other BVs: never blocked by this rule.
   */
  const isMaterialRowAddBlockedForMissingComponentDetails = (rowKey) => {
    const key = String(rowKey || "").trim();
    if (!key) return false;
    const row =
      productDataRef.current?.[key] ||
      (materialRowsRef.current || []).find(
        (r) => String(r._rowId || "") === key
      );
    if (!materialRowShowsComponentDetailsIcon(row)) return false;
    return !materialRowHasFilledComponentDetails(key);
  };

  // Keep add-button UI in sync with Component Details gate.
  materialRowAddBlockedByComponentDetailsRef.current =
    isMaterialRowAddBlockedForMissingComponentDetails;

  /** Prefer live Select2/DOM values so Save is not stale vs React state. */
  const collectComponentDetailsFromForm = () => {
    const base = {
      ...emptyComponentDetailsForm(),
      ...(componentDetailsDataRef.current || {}),
    };
    const readSelectVal = (ref) => {
      if (!ref?.current) return "";
      try {
        if ($(ref.current).data("select2")) {
          return ($(ref.current).val() || "").toString();
        }
      } catch (_) {}
      return (ref.current.value || "").toString();
    };
    const materialGroupCode = readSelectVal(componentDetailsMaterialGroupRef) || base.materialGroupCode || "";
    const componentCategoryCode = readSelectVal(componentDetailsCategoryRef) || base.componentCategoryCode || "";
    const componentListCode = readSelectVal(componentDetailsComponentListRef) || base.componentListCode || "";
    const componentIssueCode = readSelectVal(componentDetailsIssueRef) || base.componentIssueCode || "";

    const mgOpt = filteredMaterialGroupsOptions.find((o) => (o.code || o.Code) === materialGroupCode);
    const catOpt = componentCategoriesOptions.find((o) => (o.code || o.Code) === componentCategoryCode);
    const listOpt = componentNamesOptions.find((o) => (o.code || o.Code) === componentListCode);
    const issueOpt = componentIssuesOptions.find((o) => (o.code || o.Code) === componentIssueCode);

    return {
      materialGroupCode,
      materialGroupDesc: mgOpt ? (mgOpt.name || mgOpt.Name || "") : base.materialGroupDesc || "",
      componentCategoryCode,
      componentCategory: catOpt ? (catOpt.name || catOpt.Name || "") : base.componentCategory || "",
      componentListCode,
      componentList: listOpt ? (listOpt.name || listOpt.Name || "") : base.componentList || "",
      componentQty: base.componentQty ?? "",
      componentIssueCode,
      componentIssue: issueOpt ? (issueOpt.name || issueOpt.Name || "") : base.componentIssue || "",
      doorMaterialCode: base.doorMaterialCode ?? "",
      componentIssueRemarks: base.componentIssueRemarks ?? "",
    };
  };

  const handleComponentDetailsOpen = (productKey) => {
    const rowKey = String(productKey || "").trim();
    if (!rowKey) return;
    setSelectedComponentProductKey(rowKey);
    const productCode = getProductCodeFromKey(rowKey);
    let memoryStorage = normalizeComponentDetailsStorage(
      componentDetailsRef.current[rowKey]
    );
    let storage = memoryStorage;

    try {
      const stored = readComponentDetailsLocalStore();
      const rowStoreKey = getComponentDetailsRowStorageKey(rowKey);
      let byRow = stored[rowStoreKey];
      const hasInMemoryRow = Boolean(memoryStorage.rows.length);
      if ((!byRow || typeof byRow !== "object") && !hasInMemoryRow && productCode) {
        byRow = stored[productCode];
      }
      if (byRow && typeof byRow === "object") {
        const lsStorage = normalizeComponentDetailsStorage(byRow);
        const lsCount = countFilledComponentDetailRows(lsStorage.rows);
        const memCount = countFilledComponentDetailRows(memoryStorage.rows);
        // Prefer the source with more filled rows (fixes 4–5 row reopen picking stale LS).
        // On tie, prefer in-memory (latest Save in this session).
        if (lsCount > memCount) {
          storage = lsStorage;
        } else if (memCount > 0) {
          storage = memoryStorage;
        } else if (lsCount > 0) {
          storage = lsStorage;
        }
      }
    } catch (_) {}

    const loadedRows = (storage.rows || []).map(normalizeComponentDetailRow);
    const rows = loadedRows.length ? loadedRows : [createEmptyComponentDetailRow()];
    // Keep a stable copy in both ref + memory so reopen always sees all rows.
    componentDetailsRef.current = {
      ...componentDetailsRef.current,
      [rowKey]: { rows },
    };
    componentDetailsRowsListRef.current = rows;
    componentGridPreloadedKeyRef.current = "";
    setComponentDetailsRowsList(rows);
    setComponentRowOptionsMap({});
    componentRowOptionsMapRef.current = {};
    resetComponentDetailsForm();
    setComponentDetailsOpen(true);
    fetchComponentDetailsDropdowns(null, null).then(() => {
      preloadComponentGridRowOptions(rows);
    });
  };

  const componentGridPreloadedKeyRef = useRef("");
  useEffect(() => {
    if (!componentDetailsOpen || loadingComponentDropdowns) return;
    const preloadKey = `${selectedComponentProductKey || ""}::${componentDetailsRowsList
      .map((r) => r._rowId)
      .join("|")}`;
    if (!componentDetailsRowsList.length || componentGridPreloadedKeyRef.current === preloadKey) {
      return;
    }
    componentGridPreloadedKeyRef.current = preloadKey;
    preloadComponentGridRowOptions(componentDetailsRowsList);
  }, [
    componentDetailsOpen,
    loadingComponentDropdowns,
    selectedComponentProductKey,
    componentDetailsRowsList,
  ]);

  useEffect(() => {
    if (!componentDetailsOpen) {
      componentGridPreloadedKeyRef.current = "";
    }
  }, [componentDetailsOpen]);

  // Draft/edit: resolve missing dropdown codes from saved labels so fields stay editable.
  useEffect(() => {
    if (!componentDetailsOpen || loadingComponentDropdowns) return;
    if (!filteredMaterialGroupsOptions.length) return;

    setComponentDetailsRowsList((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        let mgCode = String(row.materialGroupCode || "").trim();
        let mgDesc = String(row.materialGroupDesc || "").trim();
        if (!mgCode && mgDesc) {
          const mg = filteredMaterialGroupsOptions.find(
            (o) =>
              String(o.name || o.Name || "").trim() === mgDesc ||
              String(o.code || o.Code || "").trim() === mgDesc
          );
          if (mg) {
            mgCode = String(mg.code || mg.Code || "").trim();
            mgDesc = String(mg.name || mg.Name || mgDesc).trim();
          }
        }
        if (mgCode !== String(row.materialGroupCode || "").trim() || mgDesc !== String(row.materialGroupDesc || "").trim()) {
          changed = true;
          return normalizeComponentDetailRow({
            ...row,
            materialGroupCode: mgCode,
            materialGroupDesc: mgDesc,
          });
        }
        return row;
      });
      if (changed) {
        componentGridPreloadedKeyRef.current = "";
        componentDetailsRowsListRef.current = next;
        preloadComponentGridRowOptions(next);
      }
      return changed ? next : prev;
    });
  }, [componentDetailsOpen, loadingComponentDropdowns, filteredMaterialGroupsOptions]);

  const handleComponentDetailsClose = () => {
    requestAnimationFrame(() => {
      setComponentDetailsOpen(false);
      setSelectedComponentProductKey(null);
      componentDetailsRowsListRef.current = [];
      setComponentDetailsRowsList([]);
      setComponentRowOptionsMap({});
      resetComponentDetailsForm();
    });
  };

  /** Comparable snapshot of filled Component Details rows (ignores blank trailing rows / row ids). */
  const snapshotComponentDetailsForCompare = (rows) =>
    (Array.isArray(rows) ? rows : [])
      .filter(isComponentDetailRowFilled)
      .map((r) => {
        const o = {};
        COMPONENT_DETAILS_FIELD_KEYS.forEach((k) => {
          o[k] = String(r?.[k] ?? "").trim();
        });
        return o;
      });

  /** True when current grid matches what is already saved in localStorage for this material row. */
  const areComponentDetailsSyncedWithLocalStorage = (productKey) => {
    const key = String(productKey || "").trim();
    if (!key) return true;
    const rowsToCheck =
      Array.isArray(componentDetailsRowsListRef.current) &&
      componentDetailsRowsListRef.current.length
        ? componentDetailsRowsListRef.current
        : componentDetailsRowsList;
    const currentSnap = snapshotComponentDetailsForCompare(rowsToCheck);
    if (currentSnap.length === 0) return true;

    try {
      const stored = readComponentDetailsLocalStore();
      const rowStoreKey = getComponentDetailsRowStorageKey(key);
      let byRow = stored[rowStoreKey];
      if ((!byRow || typeof byRow !== "object") && getProductCodeFromKey(key)) {
        byRow = stored[getProductCodeFromKey(key)];
      }
      const lsSnap = snapshotComponentDetailsForCompare(
        normalizeComponentDetailsStorage(byRow).rows
      );
      return JSON.stringify(currentSnap) === JSON.stringify(lsSnap);
    } catch {
      return false;
    }
  };

  // Persist all inline grid rows for the material line, then close.
  const handleComponentDetailsSave = () => {
    const productKey = selectedComponentProductKey;
    if (productKey) {
      // Prefer ref (always current) over possibly stale closure state.
      const rowsToSave =
        Array.isArray(componentDetailsRowsListRef.current) &&
        componentDetailsRowsListRef.current.length
          ? componentDetailsRowsListRef.current
          : componentDetailsRowsList;
      // Ignore trailing/blank rows (e.g. empty row kept for + button).
      const filledRows = (rowsToSave || []).filter(isComponentDetailRowFilled);

      if (isComponentIssueRemarksRequired) {
        const missingRemarks = filledRows.some(
          (r) => !String(r.componentIssueRemarks ?? "").trim()
        );
        if (missingRemarks) {
          Swal.fire({
            title: "Required",
            text: "Component Issue Remarks is mandatory for Service Executive on every component row.",
            icon: "warning",
            confirmButtonColor: "#ff9800",
          });
          return;
        }
      }
      const rowsWithValue = filledRows.map((r) =>
        normalizeComponentDetailRow({
          ...r,
          componentValue: computeComponentRowValue(productKey, r.componentQty),
        })
      );
      // Sync ref immediately so reopen before LS read still has every row.
      componentDetailsRowsListRef.current = rowsWithValue;
      persistComponentDetailsRows(productKey, rowsWithValue);
    }
    handleComponentDetailsClose();
  };

  /**
   * Close without persisting. Warn when filled data is not yet saved in localStorage
   * (Cancel, header X, backdrop, Escape).
   */
  const handleComponentDetailsCancel = async () => {
    const productKey = selectedComponentProductKey;
    if (productKey && !areComponentDetailsSyncedWithLocalStorage(productKey)) {
      const result = await Swal.fire({
        title: "Data not saved",
        text: "Your Component Details data is not saved in local storage. If you close now, unsaved changes will be lost.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Close anyway",
        cancelButtonText: "Keep editing",
        confirmButtonColor: "#dc3545",
        cancelButtonColor: "#6c757d",
      });
      if (!result.isConfirmed) return;
    }
    handleComponentDetailsClose();
  };

  const handleComponentDetailsChange = (field) => (e) => {
    if (isPlantTeamComponentFieldsReadOnly && field === "doorMaterialCode") {
      return;
    }
    const value = e.target.value;
    setComponentDetailsDataBoth((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "componentCategoryCode") {
        const opt = componentCategoriesOptions.find((o) => (o.code || o.Code) === value);
        next.componentCategory = opt ? (opt.name || opt.Name || "") : "";
      } else if (field === "materialGroupCode") {
        const opt = filteredMaterialGroupsOptions.find((o) => (o.code || o.Code) === value);
        next.materialGroupDesc = opt ? (opt.name || opt.Name || "") : "";
        if (prev.materialGroupCode !== value) {
          next.componentCategoryCode = "";
          next.componentCategory = "";
          next.componentListCode = "";
          next.componentList = "";
        }
      } else if (field === "componentListCode") {
        const opt = componentNamesOptions.find((o) => (o.code || o.Code) === value);
        next.componentList = opt ? (opt.name || opt.Name || "") : "";
      } else if (field === "componentIssueCode") {
        const opt = componentIssuesOptions.find((o) => (o.code || o.Code) === value);
        next.componentIssue = opt ? (opt.name || opt.Name || "") : "";
      }
      return next;
    });
  };

  // When Material Group changes, load Component Category for that group (CSV mapping).
  useEffect(() => {
    if (!componentDetailsOpen || !selectedComponentProductKey) return;
    const code = componentDetailsData.materialGroupCode;
    if (materialGroupCodeRef.current === code) return;
    materialGroupCodeRef.current = code;
    if (!code) {
      setComponentCategoriesOptions([]);
      return;
    }
    masterDataAPI
      .getComponentCategories(true, code)
      .then((res) => {
        const list = (res?.data ?? res) || [];
        setComponentCategoriesOptions(Array.isArray(list) ? list : []);
      })
      .catch(() => setComponentCategoriesOptions([]));
  }, [componentDetailsOpen, selectedComponentProductKey, componentDetailsData.materialGroupCode]);

  // When category changes in Component Details modal, refetch component names for that category
  useEffect(() => {
    if (!componentDetailsOpen || !selectedComponentProductKey) return;
    const code = componentDetailsData.componentCategoryCode;
    if (componentCategoryCodeRef.current === code) return;
    componentCategoryCodeRef.current = code;
    if (!code) {
      setComponentNamesOptions([]);
      return;
    }
    masterDataAPI.getComponentNames(code, true).then((res) => {
      const list = (res?.data ?? res) || [];
      setComponentNamesOptions(Array.isArray(list) ? list : []);
    }).catch(() => setComponentNamesOptions([]));
  }, [componentDetailsOpen, componentDetailsData.componentCategoryCode]);

  const handleComponentQtyKeyDown = (e) => {
    const key = e.key;
    if (key === "Backspace" || key === "Tab" || key === "Enter" || key === "ArrowLeft" || key === "ArrowRight" || key === "Delete") return;
    if (/^[0-9]$/.test(key)) return;
    e.preventDefault();
  };

  // Initialize Select2 on Component Details dropdowns (same style as End Consumer Country)
  useEffect(() => {
    if (!componentDetailsOpen) {
      [componentDetailsMaterialGroupRef, componentDetailsCategoryRef, componentDetailsComponentListRef, componentDetailsIssueRef].forEach((ref) => {
        if (ref.current && $(ref.current).data("select2")) {
          $(ref.current).select2("destroy");
        }
      });
      return;
    }
    if (loadingComponentDropdowns) return;
    const t = setTimeout(() => {
      const data = componentDetailsData;
      if (componentDetailsMaterialGroupRef.current) componentDetailsMaterialGroupRef.current.value = data.materialGroupCode || "";
      if (componentDetailsCategoryRef.current) componentDetailsCategoryRef.current.value = data.componentCategoryCode || "";
      if (componentDetailsComponentListRef.current) componentDetailsComponentListRef.current.value = data.componentListCode || "";
      if (componentDetailsIssueRef.current) componentDetailsIssueRef.current.value = data.componentIssueCode || "";

      const initSelect2 = (ref, placeholder) => {
        if (!ref.current) return;
        const $el = $(ref.current);
        if ($el.data("select2")) $el.select2("destroy");
        $el.select2({
          width: "100%",
          placeholder: placeholder,
          allowClear: true,
        });
      };
      initSelect2(componentDetailsMaterialGroupRef, "Select Material Group");
      initSelect2(componentDetailsCategoryRef, "Select Category");
      initSelect2(componentDetailsComponentListRef, "Select Component");
      initSelect2(componentDetailsIssueRef, "Select Issue");

      const syncState = (ref, field) => {
        if (!ref.current) return;
        $(ref.current).off("change.componentDetails").on("change.componentDetails", function () {
          const value = $(this).val() || "";
          setComponentDetailsDataBoth((prev) => {
            const next = { ...prev, [field]: value };
            if (field === "materialGroupCode") {
              const opt = filteredMaterialGroupsOptions.find((o) => (o.code || o.Code) === value);
              next.materialGroupDesc = opt ? (opt.name || opt.Name || "") : "";
              if (prev.materialGroupCode !== value) {
                next.componentCategoryCode = "";
                next.componentCategory = "";
                next.componentListCode = "";
                next.componentList = "";
              }
            } else if (field === "componentCategoryCode") {
              const opt = componentCategoriesOptions.find((o) => (o.code || o.Code) === value);
              next.componentCategory = opt ? (opt.name || opt.Name || "") : "";
            } else if (field === "componentListCode") {
              const opt = componentNamesOptions.find((o) => (o.code || o.Code) === value);
              next.componentList = opt ? (opt.name || opt.Name || "") : "";
            } else if (field === "componentIssueCode") {
              const opt = componentIssuesOptions.find((o) => (o.code || o.Code) === value);
              next.componentIssue = opt ? (opt.name || opt.Name || "") : "";
            }
            return next;
          });
        });
      };
      syncState(componentDetailsMaterialGroupRef, "materialGroupCode");
      syncState(componentDetailsCategoryRef, "componentCategoryCode");
      syncState(componentDetailsComponentListRef, "componentListCode");
      syncState(componentDetailsIssueRef, "componentIssueCode");

      if (componentDetailsMaterialGroupRef.current) $(componentDetailsMaterialGroupRef.current).val(componentDetailsData.materialGroupCode || null).trigger("change");
      if (componentDetailsCategoryRef.current) $(componentDetailsCategoryRef.current).val(componentDetailsData.componentCategoryCode || null).trigger("change");
      if (componentDetailsComponentListRef.current) $(componentDetailsComponentListRef.current).val(componentDetailsData.componentListCode || null).trigger("change");
      if (componentDetailsIssueRef.current) $(componentDetailsIssueRef.current).val(componentDetailsData.componentIssueCode || null).trigger("change");
    }, 100);
    return () => clearTimeout(t);
  }, [componentDetailsOpen, loadingComponentDropdowns, filteredMaterialGroupsOptions, componentCategoriesOptions, componentNamesOptions, componentIssuesOptions]);

  // Handle DataTable column adjustment on layout changes
  useEffect(() => {
    let mutationObserver = null;
    let timeoutId = null;
    let lastViewportWidth =
      typeof window !== "undefined" ? window.innerWidth : 0;

    const adjustDataTable = () => {
      if (dataTableInstanceRef.current) {
        try {
          // Force recalculation of column widths
          dataTableInstanceRef.current.columns.adjust();
          // Mobile/tablet only: draw() regenerates every cell's HTML, so the SR Qty
          // input the user just tapped is replaced by a new node and the soft keyboard
          // closes. columns.adjust() above is safe (widths only, no re-render). The
          // pending redraw still happens on blur via scheduleDeferredMaterialRedraw().
          // Desktop keeps the original adjust + redraw behaviour.
          if (
            isTouchDevice() &&
            isEditableElementFocusedWithin(productTableRef.current)
          ) {
            return;
          }
          // Redraw without resetting pagination
          dataTableInstanceRef.current.draw(false);
        } catch (e) {
          console.error("Error adjusting DataTable columns:", e);
        }
      }
    };

    // Debounced adjustment
    const debouncedAdjust = () => {
      // Mobile/tablet only: opening/closing the soft keyboard fires resize with an
      // unchanged width. Column widths depend on width alone, so there is nothing to
      // recalculate and the redraw would only destroy the focused input. Width changes
      // (rotation, real resize) still go through. Desktop is unaffected.
      if (isTouchDevice()) {
        const viewportWidth = window.innerWidth;
        if (viewportWidth === lastViewportWidth) return;
        lastViewportWidth = viewportWidth;
      }
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(adjustDataTable, 100);
    };

    // Watch for class changes on body/main content that indicate sidebar toggle
    if (window.MutationObserver) {
      const targetNode = document.body;
      mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "class"
          ) {
            // Sidebar likely toggled, adjust with delay for animation
            setTimeout(adjustDataTable, 400);
          }
        });
      });

      mutationObserver.observe(targetNode, {
        attributes: true,
        attributeFilter: ["class"],
      });

      // Also observe the main content wrapper if it exists
      const mainContent = document.querySelector(
        ".main-content, main, .content-wrapper"
      );
      if (mainContent) {
        mutationObserver.observe(mainContent, {
          attributes: true,
          attributeFilter: ["class", "style"],
        });
      }
    }

    // Window resize listener
    window.addEventListener("resize", debouncedAdjust);

    return () => {
      window.removeEventListener("resize", debouncedAdjust);
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  // Fetch SR Types by MarketName for RaiseComplaint Material Section
  const fetchSRTypesForRaiseComplaint = async (marketName) => {
    try {
      setLoadingSRTypes(true);
      
      // Map MarketName to "Domestic" or "Export" for filtering
      // ISFORDOMESTIC = 1 for marketName = 'Domestic'
      // ISFOREXPORT = 1 for marketName = 'Export'
      let mappedMarketName = null;
      if (marketName) {
        const nameUpper = marketName.toUpperCase();
        
        // Check if it's Domestic
        if (
          nameUpper.includes("DOMESTIC") ||
          nameUpper === "DOMESTIC" ||
          nameUpper.includes("DOM")
        ) {
          mappedMarketName = "Domestic";
        }
        // Check if it's Export
        else if (
          nameUpper.includes("EXPORT") ||
          nameUpper === "EXPORT" ||
          nameUpper.includes("EXP")
        ) {
          mappedMarketName = "Export";
        }
      }
      
      //console.log("Fetching SR Types for marketName:", marketName, "mapped to:", mappedMarketName);
      const response = await masterDataAPI.getSRTypesByMarketNameForRaiseComplaint(mappedMarketName, true);
      //console.log("SR Types API Response:", response);
      
      if (response?.success) {
        let srTypes = [];
        if (Array.isArray(response.data)) {
          srTypes = response.data;
        } else if (response.data && Array.isArray(response.data)) {
          srTypes = response.data;
        }
        
        //console.log("SR Types extracted:", srTypes);

        // Filter by SRTYPES table flags:
        // - ISACTIVE = 1
        // - If Domestic: ISFORDOMESTIC = 1
        // - If Export: ISFOREXPORT = 1
        const truthyFlag = (v) => v === true || v === 1 || v === "1";
        const filteredSrTypes = srTypes.filter((st) => {
          const isActive =
            st.IsActive ??
            st.ISACTIVE ??
            st.isActive ??
            st.isactive;
          if (!truthyFlag(isActive)) return false;

          if (mappedMarketName === "Domestic") {
            const isForDomestic =
              st.IsForDomestic ??
              st.ISFORDOMESTIC ??
              st.isForDomestic ??
              st.isfordomestic;
            return truthyFlag(isForDomestic);
          }
          if (mappedMarketName === "Export") {
            const isForExport =
              st.IsForExport ??
              st.ISFOREXPORT ??
              st.isForExport ??
              st.isforexport;
            return truthyFlag(isForExport);
          }
          // If market is unknown, fall back to active-only list
          return true;
        });
        
        // Map SR Types to options format: show Name (e.g. "Packaging / Dispatch", "Quality", "Installation"), fallback to Description then code
        const mappedOptions = filteredSrTypes.map((st) => {
          const code = st.SRTypeCode || st.SRTYPECODE || st.srTypeCode || st.SrTypeCode || "";
          const name = st.Name || st.NAME || st.name || "";
          const desc = st.Description || st.DESCRIPTION || st.description || "";
          const text = (name || desc || code).trim();
          return { value: code, text: text || code };
        }).filter((st) => st.value);
        
        //console.log("SR Types mapped options:", mappedOptions);
        setSrTypesOptions(mappedOptions);
        srTypesOptionsRef.current = mappedOptions; // Update ref as well
        //console.log("SR Types loaded for RaiseComplaint:", mappedOptions);
      } else {
        setSrTypesOptions([]);
        srTypesOptionsRef.current = [];
      }
    } catch (error) {
      //console.error("Error loading SR Types for RaiseComplaint:", error);
      setSrTypesOptions([]);
      srTypesOptionsRef.current = [];
    } finally {
      setLoadingSRTypes(false);
    }
  };

  // Initialize Select2 for category dropdowns
  const getUsedSrCategoriesForSameProduct = (baseCode, excludeRowKey) => {
    const used = new Set();
    const code = String(baseCode || "").trim();
    if (!code) return used;
    const rows = materialRowsRef.current || [];
    const visit = (rowKey, prod) => {
      if (!rowKey || String(rowKey) === String(excludeRowKey || "")) return;
      const codeForRow = String(
        prod?.ProductCode || prod?.productCode || prod?.Productcode || ""
      ).trim();
      if (codeForRow !== code) return;
      const catVal = (categoryRef.current?.[rowKey] || "").toString().trim();
      if (catVal) used.add(catVal);
    };
    if (rows.length) {
      rows.forEach((r) => {
        const rowKey = String(
          r?._rowId || r?.ProductCode || r?.productCode || r?.Productcode || ""
        ).trim();
        visit(rowKey, r);
      });
    } else if (productDataRef.current) {
      Object.keys(productDataRef.current).forEach((rowKey) => {
        visit(rowKey, productDataRef.current[rowKey] || {});
      });
    }
    return used;
  };

  /**
   * Block Add More (+) when every SR Category available for this product
   * is already selected on a sibling row (e.g. Packaging + Quality + Installation).
   */
  const isMaterialRowAddBlockedForNoRemainingSrCategory = (rowKey) => {
    const key = String(rowKey || "").trim();
    if (!key) return false;
    const row =
      productDataRef.current?.[key] ||
      (materialRowsRef.current || []).find(
        (r) => String(r._rowId || "") === key
      );
    if (!row) return false;
    const baseCode = String(
      row.ProductCode || row.productCode || row.Productcode || ""
    ).trim();
    if (!baseCode) return false;
    const available =
      typeof getSrCategoryOptionsForMaterialRowRef.current === "function"
        ? getSrCategoryOptionsForMaterialRowRef.current(row) || []
        : [];
    const availableValues = available
      .map((o) => String(o?.value ?? "").trim())
      .filter(Boolean);
    if (!availableValues.length) return false;
    const used = getUsedSrCategoriesForSameProduct(baseCode, null);
    return availableValues.every((v) => used.has(v));
  };

  materialRowAddBlockedByNoSrCategoryLeftRef.current =
    isMaterialRowAddBlockedForNoRemainingSrCategory;

  /** Rebuild SR Category <option>s so each product's rows cannot reuse another row's category. */
  const refreshMaterialRowSrCategoryOptions = () => {
    if (!productTableRef.current) return;
    $(productTableRef.current)
      .find(".category-select")
      .each(function () {
        const $select = $(this);
        const key = $select.data("key");
        if (!key) return;
        const row = productDataRef.current[key];
        const baseCode =
          row?.ProductCode || row?.productCode || row?.Productcode || "";
        const savedValue = (categoryRef.current[key] || "").toString().trim();
        const savedDesc = categoryDescRef.current[key] || "";
        const used = getUsedSrCategoriesForSameProduct(baseCode, key);
        const rowSrOptions =
          getSrCategoryOptionsForMaterialRowRef.current(row) || [];

        $select.find("option:not(:first)").remove();

        if (savedValue && !rowSrOptions.some((o) => o.value === savedValue)) {
          $select.append(
            $("<option></option>")
              .attr("value", savedValue)
              .text(savedDesc || savedValue)
              .prop("selected", true)
          );
        }
        rowSrOptions.forEach((option) => {
          if (!option?.value) return;
          if (used.has(option.value) && option.value !== savedValue) return;
          const $option = $("<option></option>")
            .attr("value", option.value)
            .text(option.text);
          if (savedValue === option.value) $option.prop("selected", true);
          $select.append($option);
        });

        if (savedValue) {
          $select.val(savedValue);
        } else {
          $select.val("");
        }
        if ($select.data("select2")) {
          $select.trigger("change.select2");
        }
      });
  };

  const initializeCategorySelect2 = () => {
    if (!productTableRef.current) return;
    categorySelect2InitPassRef.current = true;

    // Destroy existing Select2 instances first
    $(productTableRef.current)
      .find(".category-select")
      .each(function () {
        const $select = $(this);
        if ($select.data("select2")) {
          $select.select2("destroy");
        }
      });

    // Initialize Select2 on category dropdowns
    $(productTableRef.current)
      .find(".category-select")
      .each(function () {
        const $select = $(this);
        if (!$select.data("select2")) {
          const key = $select.data("key");
          const savedValue = categoryRef.current[key] || "";
          const categoryReadOnly = isExportCommercialCategoryReadOnly(key);
          const isSelected =
            key &&
            selectedProductsRef.current &&
            typeof selectedProductsRef.current.has === "function" &&
            selectedProductsRef.current.has(String(key));

          setCategorySelectInteractive($select, !categoryReadOnly && !!isSelected);

          $select.select2({
            width: "100%",
            placeholder: "Select SR Category",
            allowClear: false,
            dropdownParent: $(productTableRef.current).closest(
              ".table-responsive, .card-panel"
            ),
            minimumResultsForSearch: Infinity, // Disable search for small list
          });

          const $row = $select.closest("tr");
          syncMaterialRowCategorySelectEnabled($row, key, !!isSelected);

          // Set the saved value if it exists
          if (savedValue) {
            $select.val(savedValue).trigger("change");
          }

          // Handle change event
          $select.off("change.category").on("change.category", function () {
            const key = $(this).data("key");
            const value = $(this).val() || "";
            categoryRef.current = { ...categoryRef.current, [key]: value };
            if (value) {
              const options = getSrTypesOptionsSnapshot();
              const match = (options || []).find(
                (o) => String(o?.value ?? "").trim() === String(value).trim()
              );
              if (match?.text) {
                categoryDescRef.current[key] = match.text;
              }
            } else {
              categoryDescRef.current[key] = "";
            }
            setCategorySelectionTick((v) => v + 1);
            // Clear error message when category is selected
            if (value && value.trim() !== "") {
              setMaterialSectionError("");
            }
            if (
              isExportMarketActive() &&
              exportCommercialBulkActiveRef.current &&
              String(key) === String(exportCommercialTriggerKeyRef.current) &&
              !isCommercialSrCategoryCode(value)
            ) {
              revertExportCommercialBulkSelection(key);
            } else {
              applyExportCommercialBulkSelectionIfNeeded(key, value);
            }
            const $row = $select.closest("tr");
            applyMaterialRowAddButtonState($row, key);
            // Re-evaluate + on all rows of this product (last category used → disable +)
            syncAllMaterialRowAddButtons();

            // Hide this category on sibling rows of the same product
            if (!categorySelect2InitPassRef.current) {
              refreshMaterialRowSrCategoryOptions();
            }

            // Mikasa Doors / Floors / Sturdo: after checkbox + SR Category →
            // SR Qty = Invoice Qty, Sr VALUE = Total Price (skip Select2 init pass)
            if (
              !categorySelect2InitPassRef.current &&
              value &&
              String(value).trim() !== ""
            ) {
              const isRowSelected =
                (selectedProductsRef.current &&
                  typeof selectedProductsRef.current.has === "function" &&
                  selectedProductsRef.current.has(String(key))) ||
                $(productTableRef.current)
                  .find(
                    '.product-select-checkbox[data-product-key="' + key + '"]'
                  )
                  .is(":checked");
              if (isRowSelected) {
                let rowsArray = materialRowsRef.current;
                if (dataTableInstanceRef.current) {
                  try {
                    rowsArray = dataTableInstanceRef.current
                      .rows()
                      .data()
                      .toArray();
                  } catch (_) {
                    // fall back to materialRowsRef
                  }
                }
                const rowData = resolveMaterialRowDataByKey(
                  key,
                  productDataRef.current,
                  rowsArray
                );
                if (rowData) {
                  productDataRef.current[key] = rowData;
                  autoFillSrQtyForComponentDetailsBvRow(key, rowData, $row);
                }
              }
            }
          });
        }
      });
    categorySelect2InitPassRef.current = false;
    syncAllMaterialRowAddButtons();
  };

  // Error swal for Material Section — identical scroll-lock mechanism to
  // showSwalWithScrollPrevention but anchored at the current scroll position
  // instead of the top. savedScrollY is captured once and never mutated.
  const showMaterialSectionError = (message) => {
    const savedScrollY = window.scrollY || window.pageYOffset || 0;
    let scrollLocked = false;
    let scrollMonitorInterval = null;

    const preventScrollHandler = (e) => {
      if (!scrollLocked) return;
      if (e.type === 'wheel') {
        enableScroll();
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      window.scrollTo({ top: savedScrollY, behavior: 'instant' });
      return false;
    };

    const disableScroll = () => {
      scrollLocked = true;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${savedScrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      window.addEventListener('scroll',    preventScrollHandler, { passive: false, capture: true });
      window.addEventListener('wheel',     preventScrollHandler, { passive: false, capture: true });
      window.addEventListener('touchmove', preventScrollHandler, { passive: false, capture: true });
      document.addEventListener('scroll',  preventScrollHandler, { passive: false, capture: true });
    };

    const enableScroll = () => {
      scrollLocked = false;
      if (scrollMonitorInterval) { clearInterval(scrollMonitorInterval); scrollMonitorInterval = null; }
      window.removeEventListener('scroll',    preventScrollHandler, { capture: true });
      window.removeEventListener('wheel',     preventScrollHandler, { capture: true });
      window.removeEventListener('touchmove', preventScrollHandler, { capture: true });
      document.removeEventListener('scroll',  preventScrollHandler, { capture: true });
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      requestAnimationFrame(() => {
        window.scrollTo({ top: savedScrollY, behavior: 'instant' });
      });
      document.removeEventListener('click',     enableScrollHandler);
      document.removeEventListener('wheel',     enableScrollHandler);
      document.removeEventListener('touchmove', enableScrollHandler);
      document.removeEventListener('keydown',   enableScrollHandler);
    };

    const enableScrollHandler = () => enableScroll();

    const startScrollMonitor = () => {
      if (scrollMonitorInterval) clearInterval(scrollMonitorInterval);
      scrollMonitorInterval = setInterval(() => {
        if (scrollLocked) {
          const y = window.scrollY || window.pageYOffset;
          if (Math.abs(y - savedScrollY) > 1) {
            window.scrollTo({ top: savedScrollY, behavior: 'instant' });
          }
        }
      }, 10);
    };

    const stopScrollMonitor = () => {
      if (scrollMonitorInterval) { clearInterval(scrollMonitorInterval); scrollMonitorInterval = null; }
    };

    // Lock at current position before swal opens (no scroll-to-top).
    disableScroll();
    startScrollMonitor();

    const swalPromise = Swal.fire({
      title: "Error",
      html: `<div class="modern-swal-content">${escapeHtml(message)}</div>`,
      icon: "error",
      confirmButtonText: "OK",
      showCloseButton: true,
      backdrop: "rgba(3,10,26,0.6)",
      showClass: { popup: "swal2-zoom-in" },
      hideClass: { popup: "swal2-zoom-out" },
      buttonsStyling: false,
      customClass: {
        popup: "modern-swal-popup",
        title: "modern-swal-title",
        content: "modern-swal-body",
        confirmButton: "modern-swal-confirm",
        closeButton: "modern-swal-close",
      },
      allowOutsideClick: false,
      scrollbarPadding: false,
      didOpen: () => {
        requestAnimationFrame(() => { disableScroll(); startScrollMonitor(); });
      },
      willClose: () => {
        enableScroll();
        stopScrollMonitor();
      },
      didClose: () => {
        enableScroll();
        stopScrollMonitor();
        releaseRaiseComplaintActionLocks();
      },
    });

    swalPromise.finally(() => {
      stopScrollMonitor();
      enableScroll();
      releaseRaiseComplaintActionLocks();
    });
    return swalPromise;
  };

  // Function to sync dt-scroll-headInner width with dt-scroll-body width
  const syncScrollHeadWidth = (tableRef) => {
    if (!tableRef?.current) return;

    try {
      const $table = $(tableRef.current);
      const $wrapper = $table.closest(".dataTables_wrapper");
      if (!$wrapper.length) return;

      const $headInner = $wrapper.find(".dt-scroll-headInner");
      const $body = $wrapper.find(".dt-scroll-body");

      if ($headInner.length && $body.length) {
        // Use scrollWidth to get the actual content width, not just visible width
        const bodyWidth = $body[0]?.scrollWidth || $body.width() || 0;
        if (bodyWidth > 0) {
          $headInner.css("width", bodyWidth + "px");
        }
      }
    } catch (e) {
      // Silently handle errors - table might not be fully initialized
      console.debug("Error syncing scroll head width:", e);
    }
  };

  // Sync material rows from invoice details (so we can add cloned rows without losing source)
  useEffect(() => {
    const products = invoiceDetails?.products;
    if (!products || !Array.isArray(products)) {
      setMaterialRows([]);
      return;
    }
    setMaterialRows(
      products.map((p, i) => {
        const code = p.ProductCode || p.productCode || p?.Productcode || "";
        const _rowId = code ? code + "_row" + i : "row_" + i;
        const _isClone = i > 0 && products.slice(0, i).some((prev) => (prev.ProductCode || prev.productCode || prev?.Productcode) === code);
        return normalizeMaterialProductRow({ ...p, _rowId, _isClone });
      })
    );
  }, [invoiceDetails?.products]);

  // Initialize / re-init product DataTable when invoice or SR types change (clone/remove update in-place for smooth UX)
  useEffect(() => {
    if (skipNextMaterialTableReinitRef.current) {
      skipNextMaterialTableReinitRef.current = false;
      return () => {};
    }

    // Blur used to call dataTableInstanceRef.current.draw(false) synchronously, which
    // recreates the row's DOM (including the +/− buttons) and, via the draw.rcPaginateScrollFix
    // listener below, does an instant window.scrollTo — both happening mid-click when the
    // blur was caused by pressing another button. That destroys the click's target (or moves
    // it out from under the cursor) so the click is silently dropped and only works on a
    // second, separate click. Deferring the redraw until after the in-flight click has been
    // handled (via a one-time document click listener, which fires after the target's own
    // click handler since it's bubbled up to) fixes that without touching the scroll-lock
    // system that prevents jumping while the user is still typing.
    // Declared here (not inside the `if` block below) so the unmount cleanup can reach it too.
    const debugAnyClick = (e) => {
      console.log(
        "[DEBUG] ANY click reached document (capture)",
        Date.now(),
        "target=",
        e.target?.className || e.target?.tagName
      );
    };
    const debugAnyMouseup = (e) => {
      console.log(
        "[DEBUG] ANY mouseup reached document (capture)",
        Date.now(),
        "target=",
        e.target?.className || e.target?.tagName
      );
    };
    document.addEventListener("click", debugAnyClick, true);
    document.addEventListener("mouseup", debugAnyMouseup, true);

    const onDocumentClickFlush = () => flushPendingMaterialRedraw("click");
    const flushPendingMaterialRedraw = (source) => {
      console.log("[DEBUG] flushPendingMaterialRedraw invoked, source=", source, Date.now());
      const fn = pendingMaterialRedrawRef.current;
      if (!fn) {
        console.log("[DEBUG] flush: nothing pending");
        return;
      }
      pendingMaterialRedrawRef.current = null;
      console.log("[DEBUG] flush: running deferred draw() now");
      fn();
    };
    const scheduleDeferredMaterialRedraw = () => {
      console.log("[DEBUG] scheduleDeferredMaterialRedraw called", Date.now());
      pendingMaterialRedrawRef.current = () => {
        if (materialCompQtyDrawDebounceTimerRef.current) {
          clearTimeout(materialCompQtyDrawDebounceTimerRef.current);
          materialCompQtyDrawDebounceTimerRef.current = null;
        }
        // Mobile/tablet only: on touch, focus lands on the tapped element before the
        // click that flushes this deferred redraw, so the click can arrive while another
        // SR Qty input is already focused (tapping straight from one qty field to the
        // next). draw() would recreate that input and close the soft keyboard, so skip
        // it here - the newly focused field schedules its own redraw when it blurs.
        // Desktop still redraws on the flushing click exactly as before.
        if (
          isTouchDevice() &&
          isEditableElementFocusedWithin(productTableRef.current)
        ) {
          syncComplaintValueUsdRef.current?.();
          return;
        }
        if (dataTableInstanceRef.current) dataTableInstanceRef.current.draw(false);
        syncComplaintValueUsdRef.current?.();
      };
      document.addEventListener("click", onDocumentClickFlush, { once: true });
      // Fallback for blur with no following click at all (e.g. Tab/Escape) so totals still
      // refresh eventually. Must be well longer than any real mousedown->mouseup gesture
      // (commonly 100-200ms+) or it fires first and destroys the row before the pending
      // click's mouseup/click can reach it - recreating the exact bug this is fixing.
      setTimeout(() => flushPendingMaterialRedraw("timeout"), 700);
    };

    // Destroy previous instance
    if (dataTableInstanceRef.current) {
      dataTableInstanceRef.current.destroy();
      dataTableInstanceRef.current = null;
    }

    // Use materialRows when available (supports clones); else fall back to products with _rowId for refs
    const rawProducts = invoiceDetails?.products || [];
    const tableData = materialRows.length > 0 ? materialRows : rawProducts.map((p, i) => ({
      ...p,
      _rowId: (p.ProductCode || p.productCode || p?.Productcode || "r") + "_row" + i,
      _isClone: false,
    }));
    // Normalize _selectable and _remainingReadOnly: in draft mode, rows with RemainingQty <= 0 are disabled and read-only
    const isDraftMode = !!draftSrCode;
    let products = Array.isArray(tableData)
      ? tableData.map((row) => {
          const remaining = parseFloat(row?.RemainingQty ?? row?.remainingQty ?? row?.InvoiceQuantity ?? row?.invoiceQuantity ?? 0) || 0;
          const remainingReadOnly = row._remainingReadOnly === true || (isDraftMode && remaining <= 0);
          return normalizeMaterialProductRow({
            ...row,
            _remainingReadOnly: remainingReadOnly,
            _selectable: remainingReadOnly ? false : (row._selectable === false ? false : true),
          });
        })
      : tableData;

    // If products is a string, try to parse it
    if (typeof products === "string") {
      try {
        products = JSON.parse(products);
      } catch (e) {
        console.error("Error parsing products JSON string:", e);
        products = null;
      }
    }

    // If products is not an array, try to extract it from nested structure
    if (products && !Array.isArray(products)) {
      if (Array.isArray(products.products)) {
        products = products.products;
      } else if (Array.isArray(products.data)) {
        products = products.data;
      } else {
        console.warn("Products is not an array:", products);
        products = null;
      }
    }

    // Helpers: Material Section qty = integer only; monetary values = 2 decimal places (no round-up).
    const formatMaterialQty = (v) => String(Math.floor(parseFloat(v) || 0));
    const formatMaterialValue = formatRaiseComplaintMoney;
    const formatMaterialValueForTotals = formatRaiseComplaintMoney;

    if (
      productTableRef.current &&
      Array.isArray(products)
    ) {
      const showComponentDetailsColumn = products.some((row) =>
        materialRowShowsComponentDetailsIcon(row)
      );
      // Hide SR Qty / Sr VALUE when all rows are Mikasa Doors / Floors / Sturdo
      const showSrQtyValueColumns = products.some(
        (row) => !materialRowShowsComponentDetailsIcon(row)
      );

      // Initialize DataTable even with empty products array
      // This shows the table headers with no data rows when array is empty
      // Ensure DataTable plugin is registered on jQuery (avoid window mutation)
      if (!$.fn.DataTable) {
        DataTable($);
      }

      // Inject CSS for numeric column headers
      const styleId = "dt-numeric-header-style";
      if (!document.getElementById(styleId)) {
        const style = document.createElement("style");
        style.id = styleId;
        style.textContent = `
          .dt-numeric-col .dt-column-header {
            flex-direction: row !important;
          }
        `;
        document.head.appendChild(style);
      }

      const bindMaterialRowActionHandlers = () => {
        if (!productTableRef.current) return;
        const $table = $(productTableRef.current);
        const $root = $table.closest(".dataTables_wrapper");
        const $delegateTarget = $root.length ? $root : $table;

        const releaseLocksForMaterialRowAction = () => {
          releaseRaiseComplaintActionLocks();
          compQtyMissingCategoryLockActiveRef.current = false;
          paginationScrollLockUntilRef.current = 0;
          suppressNextScrollLockArmRef.current = true;
        };

        $delegateTarget
          .off("mousedown.materialRowAdd pointerdown.materialRowAdd", ".material-row-add")
          .on("mousedown.materialRowAdd pointerdown.materialRowAdd", ".material-row-add", function () {
            console.log("[DEBUG] + mousedown", Date.now());
            releaseLocksForMaterialRowAction();
          })
          .off("click.materialRowAddBlocked", ".material-row-add-wrap.is-add-blocked")
          .on("click.materialRowAddBlocked", ".material-row-add-wrap.is-add-blocked", function (e) {
            e.preventDefault();
            e.stopPropagation();
            releaseLocksForMaterialRowAction();
            const rowId =
              $(this).find(".material-row-add").attr("data-row-id") ||
              $(this).find(".material-row-add").data("row-id");
            if (!rowId) return;
            if (isMaterialRowAddBlockedForMissingSrCategory(rowId)) {
              setMaterialSectionError(MATERIAL_SR_CATEGORY_REQUIRED_FOR_ADD_MESSAGE);
              showMaterialSectionError(MATERIAL_SR_CATEGORY_REQUIRED_FOR_ADD_MESSAGE);
              return;
            }
            if (isMaterialRowAddDisabledForCategory(rowId)) {
              setMaterialSectionError(MATERIAL_COMMERCIAL_ADD_DISABLED_MESSAGE);
              showMaterialSectionError(MATERIAL_COMMERCIAL_ADD_DISABLED_MESSAGE);
              return;
            }
            if (
              typeof materialRowAddBlockedByComponentDetailsRef.current === "function" &&
              materialRowAddBlockedByComponentDetailsRef.current(rowId)
            ) {
              setMaterialSectionError(MATERIAL_COMPONENT_DETAILS_REQUIRED_FOR_ADD_MESSAGE);
              showMaterialSectionError(MATERIAL_COMPONENT_DETAILS_REQUIRED_FOR_ADD_MESSAGE);
              return;
            }
            if (
              typeof materialRowAddBlockedByNoSrCategoryLeftRef.current === "function" &&
              materialRowAddBlockedByNoSrCategoryLeftRef.current(rowId)
            ) {
              setMaterialSectionError(MATERIAL_NO_SR_CATEGORY_LEFT_FOR_ADD_MESSAGE);
              showMaterialSectionError(MATERIAL_NO_SR_CATEGORY_LEFT_FOR_ADD_MESSAGE);
            }
          })
          .off("click.materialRowAdd", ".material-row-add")
          .on("click.materialRowAdd", ".material-row-add", function (e) {
            console.log("[DEBUG] + click handler fired", Date.now());
            e.preventDefault();
            e.stopPropagation();
            releaseLocksForMaterialRowAction();
            const rowId = $(this).attr("data-row-id") || $(this).data("row-id");
            if (!rowId) return;
            if (!isMaterialRowSelectedForAction(rowId, $table, selectedProductsRef)) {
              const $cb = $table
                .find('.product-select-checkbox[data-product-key="' + rowId + '"]')
                .first();
              if ($cb.length && !$cb.prop("disabled")) {
                $cb.prop("checked", true);
                if (typeof handleProductSelectRef.current === "function") {
                  handleProductSelectRef.current(rowId, true);
                }
              }
            }
            if (!isMaterialRowSelectedForAction(rowId, $table, selectedProductsRef)) {
              return;
            }
            if (isMaterialRowAddBlockedForMissingSrCategory(rowId)) {
              setMaterialSectionError(MATERIAL_SR_CATEGORY_REQUIRED_FOR_ADD_MESSAGE);
              showMaterialSectionError(MATERIAL_SR_CATEGORY_REQUIRED_FOR_ADD_MESSAGE);
              return;
            }
            if (isMaterialRowAddDisabledForCategory(rowId)) {
              setMaterialSectionError(MATERIAL_COMMERCIAL_ADD_DISABLED_MESSAGE);
              showMaterialSectionError(MATERIAL_COMMERCIAL_ADD_DISABLED_MESSAGE);
              return;
            }
            if (
              typeof materialRowAddBlockedByComponentDetailsRef.current === "function" &&
              materialRowAddBlockedByComponentDetailsRef.current(rowId)
            ) {
              setMaterialSectionError(MATERIAL_COMPONENT_DETAILS_REQUIRED_FOR_ADD_MESSAGE);
              showMaterialSectionError(MATERIAL_COMPONENT_DETAILS_REQUIRED_FOR_ADD_MESSAGE);
              return;
            }
            if (
              typeof materialRowAddBlockedByNoSrCategoryLeftRef.current === "function" &&
              materialRowAddBlockedByNoSrCategoryLeftRef.current(rowId)
            ) {
              setMaterialSectionError(MATERIAL_NO_SR_CATEGORY_LEFT_FOR_ADD_MESSAGE);
              showMaterialSectionError(MATERIAL_NO_SR_CATEGORY_LEFT_FOR_ADD_MESSAGE);
              return;
            }
            lockMaterialTableScrollForRowMutation();
            cloneMaterialRow(rowId);
          });

        $delegateTarget
          .off("mousedown.materialRowRemove pointerdown.materialRowRemove", ".material-row-remove")
          .on("mousedown.materialRowRemove pointerdown.materialRowRemove", ".material-row-remove", function () {
            releaseLocksForMaterialRowAction();
          })
          .off("click.materialRowRemove", ".material-row-remove")
          .on("click.materialRowRemove", ".material-row-remove", function (e) {
            e.preventDefault();
            e.stopPropagation();
            releaseLocksForMaterialRowAction();
            const rowId = $(this).attr("data-row-id") || $(this).data("row-id");
            if (rowId) {
              lockMaterialTableScrollForRowMutation();
              removeMaterialRow(rowId);
            }
          });
      };

      dataTableInstanceRef.current = $(productTableRef.current).DataTable({
        data: products,
        paging: true,
        searching: true,
        info: true,
        lengthChange: true,
        lengthMenu: [5, 10, 25, 50, 100],
        pageLength: 5,
        dom: "lfrtip",
        ordering: true,
        destroy: true,
        scrollX: true,
        scrollY: "50vh",
        scrollCollapse: true,
        autoWidth: false,
        fixedColumns: false,
        // Sort by Business Vertical (Product Category is hidden)
        order: [[1, "asc"]],
        columns: [
          {
            title: "",
            data: null,
            orderable: false,
            render: (data, type, row, meta) => {
              if (type === "display") {
                // Use stable _rowId as the single source of truth for selection keys
                const productKey = row?._rowId
                  ? String(row._rowId)
                  : `row_${meta.row}`;
                productDataRef.current[productKey] = normalizeMaterialProductRow(row);
                const currentSelected = selectedProductsRef.current;
                const inSet = currentSelected && currentSelected.has && currentSelected.has(productKey);
                // For disabled draft rows (RemainingQty 0): row is disabled and checkbox must show checked
                const remainingQty = parseFloat(row?.RemainingQty ?? row?.remainingQty ?? 0) || 0;
                const inDraftMode = !!draftSrCodeRef.current;
                const isDisabledDraftRow = row?._remainingReadOnly === true || (inDraftMode && remainingQty <= 0);
                const isChecked = inSet || isDisabledDraftRow;
                const notSelectable = row?._selectable === false;
                const disabledAttr = notSelectable ? " disabled" : "";
                const cursor = notSelectable
                  ? "cursor: not-allowed;"
                  : "cursor: pointer;";
                const checkedAttr = isChecked ? ' checked="checked"' : "";
                const disabledRowAttr = isDisabledDraftRow ? ' data-disabled-draft-row="1"' : "";
                return `<input type="checkbox" class="form-check-input product-select-checkbox" data-product-key="${productKey}" data-selectable="${!notSelectable}"${checkedAttr}${disabledAttr}${disabledRowAttr} style="${cursor}">`;
              }
              return "";
            },
            createdCell: (td) => {
              td.classList.add("text-center");
            },
          },
          {
            title: "Business Vertical",
            data: null,
            render: (data, type, row) => getMaterialRowBusinessVerticalLabel(row),
          },
          {
            title: "Product Category",
            data: null,
            visible: false,
            render: (data, type, row) => {
              // 'row' contains the full product object from the data array
              // Handle both camelCase and PascalCase property names
              let category =
                row?.ProductCategory ||
                row?.productCategory ||
                row?.Productcategory ||
                "";

              // Only apply mapping for display, filter, and type render modes
              if (type === "display" || type === "filter" || type === "type") {
                // Normalize category for comparison (trim and lowercase)
                const normalizedCategory = (category || "").trim().toLowerCase();

                // Map category values (case-insensitive matching)
                if (normalizedCategory === "makasaply" || normalizedCategory === "mikasaply") {
                  category = "Plain Particle Board";
                } else if (normalizedCategory === "floors") {
                  category = "Prelam";
                }
              }

              return category;
            },
          },
          {
            title: "Plant Name",
            data: null,
            render: (data, type, row, meta) => {
              // Plant name from SAP / draft field variants (ProductPlantDescription, PlantName)
              return getMaterialRowPlantName(row) || "";
            },
          },
          {
            title: "Item Description",
            data: null,
            render: (data, type, row, meta) => {
              let itemDescription =
                row?.ProductName || row?.productName || row?.Productname || "";

              // For SNo 1 (first row, index 0), change "Plain Particle Board 8mm" to "Prelam Board 8mm"
              if (meta.row === 0 && itemDescription === "Plain Particle Board 8mm") {
                itemDescription = "Prelam Board 8mm";
              }

              return itemDescription;
            },
          },
          {
            title: "Product Details",
            data: null,
            orderable: false,
            render: (data, type, row, meta) => {
              if (type === "display") {
                // Store row data in ref using the same stable key as the checkbox
                const productKey = row?._rowId
                  ? String(row._rowId)
                  : `row_${(meta && meta.row) != null ? meta.row : 0}`;
                productDataRef.current[productKey] = normalizeMaterialProductRow(row);
                return `<div class="product-details-icon" data-product-key="${productKey}" style="cursor: pointer; display: inline-block; text-align: center;">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1976d2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle;">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                </div>`;
              }
              return "";
            },
            className: "text-center",
          },
          {
            title: "Invoice Quantity",
            data: null,
            render: (data, type, row) => {
              const qty = row?.InvoiceQuantity ?? row?.invoiceQuantity ?? row?.Invoicequantity ?? 0;
              return formatMaterialQty(qty);
            },
            className: "text-end dt-numeric-col",
          },
          {
            title: "UOM",
            data: null,
            render: (data, type, row) => {
              const uom = row?.UOM || row?.uom || row?.Uom || "PCS";
              return uom;
            },
            className: "text-center",
          },
          {
            title: "Invoice Unit Price",
            data: null,
            render: (data, type, row) => {
              return formatMaterialValue(getInvoiceUnitPriceForMaterialRow(row));
            },
            className: "text-end dt-numeric-col",
          },
          {
            title: "Total Price",
            data: null,
            render: (data, type, row) => {
              const qty = parseFloat(
                row?.InvoiceQuantity ?? row?.invoiceQuantity ?? row?.Invoicequantity ?? 0
              );
              return formatMaterialValue(
                toRaiseComplaintMoneyNumber(qty * getInvoiceUnitPriceForMaterialRow(row))
              );
            },
            className: "text-end dt-numeric-col",
          },
          {
            title: "Component Details",
            data: null,
            orderable: false,
            visible: showComponentDetailsColumn,
            render: (data, type, row, meta) => {
              if (type === "display") {
                if (!materialRowShowsComponentDetailsIcon(row)) {
                  return "";
                }
                const key =
                  row?._rowId ||
                  row?.ProductCode ||
                  row?.productCode ||
                  row?.Productcode ||
                  `row-${meta.row}`;
                const isRowDisabled = row?._remainingReadOnly === true;
                const disabledStyle = isRowDisabled ? "opacity: 0.5; cursor: not-allowed; pointer-events: none;" : "cursor: pointer;";
                return `
                  <div class="component-details-icon" data-product-key="${key}" data-remaining-read-only="${isRowDisabled ? "1" : "0"}" style="${disabledStyle} display: inline-block; text-align: center;">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1976d2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle;">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </div>
                `;
              }
              return "";
            },
            className: "text-center",
          },
          {
            title: 'SR Category <span class="text-danger">*</span>',
            data: null,
            orderable: false,
            render: (data, type, row, meta) => {
              // 'row' contains the full product object (may have _rowId for clones)
              const key =
                row?._rowId ||
                row?.ProductCode ||
                row?.productCode ||
                row?.Productcode ||
                `row-${meta.row}`;
              const val = (categoryRef.current[key] ?? "").toString().trim();
              const savedDesc = categoryDescRef.current[key] ?? "";

              // SR Types for this row (domestic Laminate/MikasaPly/PPB/Prelam/Veneer → Packaging/Dispatch + Product Quality only)
              const options = getSrCategoryOptionsForMaterialRowRef.current(row);

              // Same product (incl. clones): hide SR Categories already chosen on other rows
              const baseCode =
                row?.ProductCode ||
                row?.productCode ||
                row?.Productcode ||
                "";
              const usedForThisProduct = new Set();
              if (baseCode && productDataRef.current) {
                Object.keys(productDataRef.current).forEach((rowKey) => {
                  if (String(rowKey) === String(key)) return; // keep this row's own selection
                  const prod = productDataRef.current[rowKey] || {};
                  const codeForRow =
                    prod.ProductCode || prod.productCode || prod.Productcode || "";
                  if (codeForRow !== baseCode) return;
                  const catVal = (categoryRef.current?.[rowKey] || "").toString().trim();
                  if (catVal) {
                    usedForThisProduct.add(catVal);
                  }
                });
              }

              const optionsForRow = [];

              // If current value is not in options list (e.g. from draft), add it first
              const valInOptions = val && options.some((opt) => opt.value === val);
              if (val && !valInOptions) {
                optionsForRow.push({ value: val, text: savedDesc || val });
              }

              options.forEach((opt) => {
                const optValue = opt.value;
                if (!optValue) return;
                // Skip categories already used on another row of the same product
                if (usedForThisProduct.has(optValue) && optValue !== val) {
                  return;
                }
                optionsForRow.push(opt);
              });

              const optionsHtml = optionsForRow
                .map((opt) => {
                  const valueAttr = (opt.value || "").replace(/"/g, "&quot;");
                  const text = (opt.text || "").replace(/</g, "&lt;");
                  const selectedAttr = val === opt.value ? "selected" : "";
                  return `<option value="${valueAttr}" ${selectedAttr}>${text}</option>`;
                })
                .join("");

              const isSelected =
                selectedProductsRef.current &&
                typeof selectedProductsRef.current.has === "function" &&
                selectedProductsRef.current.has(String(key));
              const isRowDisabled =
                row?._remainingReadOnly === true ||
                isExportCommercialCategoryReadOnly(key) ||
                !isSelected;
              const disabledAttr = isRowDisabled ? " disabled" : "";
              return `
                <select
                  class="form-select form-select-sm category-select"
                  data-key="${(key || "").replace(/"/g, "&quot;")}"
                  style="min-width: 150px;"
                  ${disabledAttr}
                >
                  <option value="">Select SR Category</option>
                  ${optionsHtml}
                </select>
              `;
            },
            className: "text-center category-cell",
          },
          {
            title: "Remain Qty",
            data: null,
            orderable: false,
            visible: false,
            render: (data, type, row) => {
              const raw =
                row?.RemainingQty ??
                row?.remainingQty ??
                row?.InvoiceQuantity ??
                row?.invoiceQuantity ??
                row?.Invoicequantity ??
                0;
              return formatMaterialQty(raw);
            },
            className: "text-end dt-numeric-col",
          },
          {
            title: "SR Qty",
            data: null,
            orderable: false,
            visible: showSrQtyValueColumns,
            render: (data, type, row, meta) => {
              // Mikasa Doors / Floors / Sturdo: qty not shown (auto-filled in background).
              if (materialRowShowsComponentDetailsIcon(row)) {
                return "";
              }
              const key =
                row?._rowId ||
                row?.ProductCode ||
                row?.productCode ||
                row?.Productcode ||
                `row-${meta.row}`;
              const baseCode =
                row?.ProductCode ||
                row?.productCode ||
                row?.Productcode ||
                "";
              const limitQty =
                parseFloat(
                  row?.InvoiceQuantity ??
                    row?.invoiceQuantity ??
                    row?.Invoicequantity ??
                    0
                ) || 0;
              const val = complaintQtyRef.current[key] ?? "";
              const isSelected =
                selectedProductsRef.current &&
                typeof selectedProductsRef.current.has === "function" &&
                selectedProductsRef.current.has(String(key));
              const isRowDisabled =
                row?._remainingReadOnly === true ||
                isExportCommercialQtyReadOnly(key) ||
                !isSelected;
              const disabledAttr = isRowDisabled ? " disabled readonly" : "";
              return `
                <input
                  type="text"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  class="form-control form-control-sm complaint-qty-input"
                  data-key="${key}"
                  data-base-code="${(baseCode || "").replace(/"/g, "&quot;")}"
                  data-invoice-qty="${limitQty}"
                  value="${val}"
                  style="max-width: 120px; text-align: right;"
                  ${disabledAttr}
                />
              `;
            },
            className: "text-end complaint-qty-cell",
          },
          {
            title: "Action",
            data: null,
            orderable: false,
            render: (data, type, row, meta) => {
              const isRowDisabled = row?._remainingReadOnly === true;
              if (isRowDisabled) return "";
              const key =
                row?._rowId ||
                row?.ProductCode ||
                row?.productCode ||
                row?.Productcode ||
                `row-${meta.row}`;
              const escapedKey = (key || "").replace(/"/g, "&quot;");
              const baseCode = String(
                row?.ProductCode || row?.productCode || row?.Productcode || ""
              ).trim();
              const addIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="pointer-events: none;"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>`;
              const removeIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="pointer-events: none;"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`;

              // + on last row of this product group; X on earlier rows (same as Component Details).
              let allRows = [];
              try {
                if (dataTableInstanceRef.current) {
                  allRows = dataTableInstanceRef.current
                    .rows({ page: "all" })
                    .data()
                    .toArray();
                }
              } catch (_) {
                allRows = [];
              }
              if (!allRows.length) {
                allRows = Array.isArray(materialRowsRef.current)
                  ? materialRowsRef.current
                  : [];
              }
              const groupKeys = [];
              allRows.forEach((r) => {
                const code = String(
                  r?.ProductCode || r?.productCode || r?.Productcode || ""
                ).trim();
                const rowKey =
                  r?._rowId ||
                  r?.ProductCode ||
                  r?.productCode ||
                  r?.Productcode ||
                  "";
                if (!rowKey) return;
                if (baseCode) {
                  if (code === baseCode) groupKeys.push(String(rowKey));
                } else if (String(rowKey) === String(key)) {
                  groupKeys.push(String(rowKey));
                }
              });
              const isLastInGroup =
                groupKeys.length > 0 &&
                groupKeys[groupKeys.length - 1] === String(key);

              if (!isLastInGroup) {
                return `<button type="button" class="btn btn-sm btn-outline-danger material-row-remove d-inline-flex align-items-center justify-content-center" data-row-id="${escapedKey}" title="Remove row" style="padding: 6px 10px; min-width: 40px; min-height: 40px; border-radius: 6px;">${removeIcon}</button>`;
              }

              const categoryCode = (categoryRef.current[key] ?? "").toString().trim();
              const categoryDesc = String(categoryDescRef.current?.[key] ?? "")
                .trim()
                .toLowerCase();
              const blockedByMissingSrCategory = !categoryCode;
              const isCommercialDisabled =
                !blockedByMissingSrCategory &&
                (categoryDesc === "commercial" ||
                  (() => {
                    const options = srTypesOptionsRef.current.length
                      ? srTypesOptionsRef.current
                      : srTypesOptions;
                    const match = (options || []).find(
                      (o) => String(o?.value ?? "").trim() === categoryCode
                    );
                    return (
                      String(match?.text ?? "").trim().toLowerCase() === "commercial"
                    );
                  })());
              const blockedByComponentDetails =
                typeof materialRowAddBlockedByComponentDetailsRef.current === "function" &&
                materialRowAddBlockedByComponentDetailsRef.current(key);
              const blockedByNoSrCategoryLeft =
                !blockedByMissingSrCategory &&
                typeof materialRowAddBlockedByNoSrCategoryLeftRef.current === "function" &&
                materialRowAddBlockedByNoSrCategoryLeftRef.current(key);
              const isAddDisabled =
                blockedByMissingSrCategory ||
                isCommercialDisabled ||
                blockedByComponentDetails ||
                blockedByNoSrCategoryLeft;
              const disabledAttr = isAddDisabled ? " disabled" : "";
              const addTitle = blockedByMissingSrCategory
                ? MATERIAL_SR_CATEGORY_REQUIRED_FOR_ADD_MESSAGE
                : isCommercialDisabled
                  ? "Add more (+) is not available when SR Category is Commercial."
                  : blockedByComponentDetails
                    ? MATERIAL_COMPONENT_DETAILS_REQUIRED_FOR_ADD_MESSAGE
                    : blockedByNoSrCategoryLeft
                      ? MATERIAL_NO_SR_CATEGORY_LEFT_FOR_ADD_MESSAGE
                      : "Add more (clone row)";
              const escapedTitle = String(addTitle)
                .replace(/&/g, "&amp;")
                .replace(/"/g, "&quot;")
                .replace(/</g, "&lt;");
              const addStyle = isAddDisabled
                ? "padding: 6px 10px; min-width: 40px; min-height: 40px; border-radius: 6px; opacity: 0.5; cursor: not-allowed; pointer-events: none;"
                : "padding: 6px 10px; min-width: 40px; min-height: 40px; border-radius: 6px;";
              const wrapClass = isAddDisabled
                ? "material-row-add-wrap is-add-blocked"
                : "material-row-add-wrap";
              return `<span class="${wrapClass}" title="${escapedTitle}" aria-label="${escapedTitle}" data-bs-toggle="tooltip" data-bs-placement="top"><button type="button" class="btn btn-sm btn-outline-primary material-row-add d-inline-flex align-items-center justify-content-center" data-row-id="${escapedKey}" title="${escapedTitle}" style="${addStyle}"${disabledAttr}>${addIcon}</button></span>`;
            },
            className: "text-center material-add-remove-cell",
          },
          {
            title: "Sr VALUE",
            data: null,
            visible: showSrQtyValueColumns,
            render: (data, type, row, meta) => {
              if (materialRowShowsComponentDetailsIcon(row)) {
                return "";
              }
              const key =
                row?._rowId || row?.ProductCode || row?.productCode || row?.Productcode || (meta ? `row-${meta.row}` : "");
              const complaintQty = complaintQtyRef.current[key] || 0;
              return computeMaterialSrValueFromRow(row, complaintQty);
            },
            className: "text-end dt-numeric-col comp-value-cell",
          },
        ],
        headerCallback: function (thead, data, start, end, display) {
          try {
            const $thead = $(thead);
            const $firstTh = $thead.find("th").first();
            if (!$firstTh.length) return;

            const checkboxHtml = `
              <label class="material-select-all-label" title="Select / Deselect all rows">
                <input
                  type="checkbox"
                  id="select-all-products"
                  class="form-check-input"
                  ${selectAllProductsRef.current ? "checked" : ""}
                  style="cursor: pointer;"
                />
                <span class="material-select-all-text"></span>
              </label>
            `;

            // Ensure header cell contains the Select All checkbox markup
            if (!$firstTh.find("#select-all-products").length) {
              $firstTh.html(checkboxHtml);
            }

            // Wire up change handler directly on the header checkbox
            const $headerCheckbox = $firstTh.find("#select-all-products");
            if ($headerCheckbox.length) {
              $headerCheckbox.prop("checked", selectAllProductsRef.current);
              $headerCheckbox
                .off("change.materialSelectAll")
                .on("change.materialSelectAll", function (e) {
                  e.stopPropagation();
                  const checked = $(this).is(":checked");
                  handleSelectAllProducts(checked);
                });
            }
          } catch (e) {
            console.error("Error in headerCallback for material section:", e);
          }
        },
        footerCallback: function (row, data, start, end, display) {
          const api = this.api();
          const rowDataArray = api.rows({ page: "all" }).data().toArray();
          let totalInvoiceQty = 0;
          let totalTotalPrice = 0;
          let totalRemainQty = 0;
          let totalCompQty = 0;
          let totalCompValue = 0;
          // When rows are cloned ("Add More"), invoice-related values repeat per clone.
          // Totals for Invoice Qty / Total Price / Remain Qty should count once per base product.
          const seenBaseProducts = new Set();
          for (let i = 0; i < rowDataArray.length; i++) {
            const rowData = rowDataArray[i];
            const baseCodeRaw =
              rowData?.ProductCode ??
              rowData?.productCode ??
              rowData?.Productcode ??
              rowData?.PRODUCTCODE ??
              "";
            const baseCode = String(baseCodeRaw || "").trim();
            const isDuplicateBase = baseCode ? seenBaseProducts.has(baseCode) : false;

            const invoiceQty =
              parseFloat(
                rowData?.InvoiceQuantity ??
                  rowData?.invoiceQuantity ??
                  rowData?.Invoicequantity ??
                  0
              ) || 0;
            const remainQty =
              parseFloat(
                rowData?.RemainingQty ??
                  rowData?.remainingQty ??
                  rowData?.InvoiceQuantity ??
                  rowData?.invoiceQuantity ??
                  0
              ) || 0;

            if (!isDuplicateBase) {
              if (baseCode) seenBaseProducts.add(baseCode);
              totalInvoiceQty += invoiceQty;
              // Same per-row value as the Total Price cell (qty × displayed unit price, 2 dp), so the total equals the sum of the rows.
              totalTotalPrice += toRaiseComplaintMoneyNumber(
                invoiceQty * getInvoiceUnitPriceForMaterialRow(rowData)
              );
              totalRemainQty += remainQty;
            }

            const key = rowData?._rowId ?? rowData?.ProductCode ?? rowData?.productCode ?? "";
            const compQty = parseFloat(complaintQtyRef.current?.[key] ?? 0) || 0;
            totalCompQty += compQty;
            // Same per-row value as the Sr VALUE cell (qty × unit price rounded to 2 dp), so the total matches the rows.
            totalCompValue += toRaiseComplaintMoneyNumber(
              compQty * getInvoiceUnitPriceForMaterialRow(rowData)
            );
          }
          $(api.column(6).footer()).html(formatMaterialQty(totalInvoiceQty));
          $(api.column(9).footer()).html(formatMaterialValueForTotals(totalTotalPrice));
          $(api.column(12).footer()).html(formatMaterialQty(totalRemainQty));
          $(api.column(13).footer()).html(formatMaterialQty(totalCompQty));
          $(api.column(15).footer()).html(formatMaterialValueForTotals(totalCompValue));
          if (typeof syncComplaintValueUsdRef.current === "function") {
            syncComplaintValueUsdRef.current();
          }
        },
        initComplete: function (settings, json) {
          try {
            // Wrap dt-length and dt-search into a single top filter wrapper
            const wrapper = $(productTableRef.current).closest(
              ".dataTables_wrapper, .dt-container"
            );
            if (wrapper.length > 0) {
              paginationScrollLockYRef.current = window.scrollY || window.pageYOffset || 0;
              paginationScrollLockXRef.current = materialScrollLeftRef.current || 0;
              const pagingBtnSelector =
                ".dt-paging-button button.page-link, .dt-paging-button .page-link, .dt-paging button.page-link";
              const saveAndLockScroll = () => {
                paginationScrollLockYRef.current = window.scrollY || window.pageYOffset || 0;
                paginationScrollLockXRef.current = materialScrollLeftRef.current || 0;
                paginationScrollLockUntilRef.current = Date.now() + 800;
              };
              const wrapperEl = wrapper[0];
              if (wrapperEl) {
                wrapperEl.addEventListener("click", function (e) {
                  if ($(e.target).closest(pagingBtnSelector).length) {
                    saveAndLockScroll();
                    e.preventDefault();
                  }
                }, true);
              }
              wrapper
                .off("pointerdown.rcPaginateScrollFix mousedown.rcPaginateScrollFix", pagingBtnSelector)
                .on("pointerdown.rcPaginateScrollFix mousedown.rcPaginateScrollFix", pagingBtnSelector, saveAndLockScroll)
                .off("click.rcPaginateScrollFix", pagingBtnSelector)
                .on("click.rcPaginateScrollFix", pagingBtnSelector, function (e) {
                  e.preventDefault();
                  e.stopPropagation();
                  saveAndLockScroll();
                  const api = dataTableInstanceRef.current;
                  const btn = e.target.closest ? e.target.closest("button") : e.currentTarget;
                  if (api && btn) {
                    const text = (btn.textContent || "").trim();
                    if (text === "Previous") {
                      api.page("previous").draw("page");
                    } else if (text === "Next") {
                      api.page("next").draw("page");
                    } else {
                      const num = parseInt(text, 10);
                      if (!isNaN(num) && num >= 1) {
                        api.page(num - 1).draw("page");
                      }
                    }
                  }
                  window.scrollTo({ top: paginationScrollLockYRef.current, behavior: "instant" });
                  if (document.activeElement && wrapper[0].contains(document.activeElement)) {
                    document.activeElement.blur();
                  }
                });

              if (dataTableInstanceRef.current && typeof dataTableInstanceRef.current.on === "function") {
                dataTableInstanceRef.current.off("draw.rcPaginateScrollFix");
                dataTableInstanceRef.current.on("draw.rcPaginateScrollFix", function () {
                  if (skipNextDrawScrollFixRef.current) {
                    skipNextDrawScrollFixRef.current = false;
                    return;
                  }
                  window.scrollTo({ top: paginationScrollLockYRef.current, behavior: "instant" });
                  paginationScrollLockUntilRef.current = Date.now() + 800;
                });
              }

              const lengthEl = wrapper.find(".dt-length");
              const searchEl = wrapper.find(".dt-search");
              if (lengthEl.length > 0 || searchEl.length > 0) {
                let topWrap = wrapper.find(".dtFltrTop");
                if (topWrap.length === 0) {
                  topWrap = $("<div class='dtFltrTop d-flex flex-wrap'></div>");
                  wrapper.prepend(topWrap);
                }
                lengthEl.each(function () {
                  $(this).appendTo(topWrap);
                });
                searchEl.each(function () {
                  $(this).appendTo(topWrap);
                });
              }

              // Wrap dt-info and dt-paging into a single bottom filter wrapper
              const infoEl = wrapper.find(".dt-info");
              const pagingEl = wrapper.find(".dt-paging");
              if (infoEl.length > 0 || pagingEl.length > 0) {
                let bottomWrap = wrapper.find(".dtFltrBotm");
                if (bottomWrap.length === 0) {
                  bottomWrap = $(
                    "<div class='dtFltrBotm d-flex flex-wrap'></div>"
                  );
                  wrapper.append(bottomWrap);
                }
                infoEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
                pagingEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
              }
            }

            // Wrap dt-scroll with an inner wrapper and ensure outer wrapper has dtScrlWrpr
            const dtScrollDiv = $(productTableRef.current).closest(
              ".dt-scroll"
            );
            if (dtScrollDiv.length > 0) {
              if (!dtScrollDiv.parent().hasClass("dtScrlWrprInner")) {
                dtScrollDiv.wrap('<div class="dtScrlWrprInner"></div>');
              }
              const outer = dtScrollDiv.parent().parent();
              if (outer.length > 0) outer.addClass("dtScrlWrpr");
            } else {
              const container = $(productTableRef.current).closest(
                ".table-responsive"
              );
              if (container.length > 0) {
                const dtScroll = container.find(".dt-scroll");
                if (dtScroll.length > 0) {
                  dtScroll.each(function () {
                    const $this = $(this);
                    if (!$this.parent().hasClass("dtScrlWrprInner")) {
                      $this.wrap('<div class="dtScrlWrprInner"></div>');
                    }
                    const outer = $this.parent().parent();
                    if (outer.length > 0) outer.addClass("dtScrlWrpr");
                  });
                }
              }
            }

            // Initial column adjustment
            setTimeout(() => {
              if (dataTableInstanceRef.current) {
                dataTableInstanceRef.current.columns.adjust();
                // Sync scroll head width with body width
                syncScrollHeadWidth(productTableRef);
              }
            }, 100);

            // Add Select All checkbox only to dt-scroll-headInner header (material section header)
            setTimeout(() => {
              if (productTableRef.current) {
                const $table = $(productTableRef.current);
                const $wrapper = $table.closest(".dataTables_wrapper");
                const $headInner = $wrapper.find(".dt-scroll-headInner");
                const $scrollHeadTh = $headInner.find("thead tr:first th:first");
                const checkboxHtml = `
                  <label class="material-select-all-label" title="Select / Deselect all rows">
                    <input
                      type="checkbox"
                      id="select-all-products"
                      class="form-check-input"
                      ${selectAllProducts ? "checked" : ""}
                      style="cursor: pointer;"
                    />
                    <span class="material-select-all-text">All</span>
                  </label>
                `;
                // Strictly add to dt-scroll-headInner header cell only
                if ($scrollHeadTh.length > 0 && !$scrollHeadTh.find('#select-all-products').length) {
                  $scrollHeadTh.html(checkboxHtml);
                }
              }
            }, 150);

            // Apply row enable/disable + highlight based on checkbox selection
            const applyRowSelectionStyling = () => {
              if (!productTableRef.current) return;
              const $table = $(productTableRef.current);
              $table.find("tbody tr").each(function () {
                const $row = $(this);
                const $checkbox = $row.find(".product-select-checkbox").first();
                const rowKey = $checkbox.data("product-key");
                const checked = $checkbox.is(":checked");
                const selectable = $checkbox.data("selectable");
                const notSelectable = selectable === false || selectable === "false";
                const isDisabledDraftRow =
                  $checkbox.data("disabled-draft-row") === 1 ||
                  $checkbox.attr("data-disabled-draft-row") === "1";
                if (notSelectable && isDisabledDraftRow) {
                  applyMaterialRowSelectionUi($row, rowKey, false);
                } else {
                  applyMaterialRowSelectionUi($row, rowKey, !!checked);
                }
              });
            };

            applyRowSelectionStyling();

            // Initialize Select2 after row selection — SR Category follows checkbox like SR Qty
            initializeCategorySelect2();
            syncAllMaterialRowCategorySelects();
            setTimeout(() => syncAllMaterialRowCategorySelects(), 0);

            // Attach click handler for product details icon
            $(productTableRef.current).off("click", ".product-details-icon");
            $(productTableRef.current).on(
              "click",
              ".product-details-icon",
              function (e) {
                e.preventDefault();
                e.stopPropagation();
                const productKey = $(this).data("product-key");
                const productData = productDataRef.current[productKey];
                if (productData) {
                  // Prevent page-jump caused by Material DataTable redraw logic.
                  paginationScrollLockYRef.current = window.scrollY || window.pageYOffset || 0;
                  paginationScrollLockXRef.current = materialScrollLeftRef.current || 0;
                  paginationScrollLockUntilRef.current = Date.now() + 350;
                  handleProductDetailsOpen(productData);
                }
              }
            );

            // Attach click handler for component details icon (only open for selected checkbox row)
            $(productTableRef.current).off("click", ".component-details-icon");
            $(productTableRef.current).on(
              "click",
              ".component-details-icon",
              function (e) {
                e.preventDefault();
                e.stopPropagation();
                if ($(this).attr("data-remaining-read-only") === "1") return;
                const productKey = String($(this).data("product-key") || "").trim();
                if (!productKey) return;
                const isSelected =
                  (selectedProductsRef.current &&
                    typeof selectedProductsRef.current.has === "function" &&
                    selectedProductsRef.current.has(productKey)) ||
                  $(productTableRef.current)
                    .find('.product-select-checkbox[data-product-key="' + productKey + '"]')
                    .is(":checked");
                if (!isSelected) {
                  setMaterialSectionError("Please select the row (checkbox) before opening Component Details.");
                  showMaterialSectionError("Please select the row (checkbox) before opening Component Details.");
                  return;
                }
                // Prevent page-jump caused by Material DataTable redraw logic.
                paginationScrollLockYRef.current = window.scrollY || window.pageYOffset || 0;
                paginationScrollLockXRef.current = materialScrollLeftRef.current || 0;
                paginationScrollLockUntilRef.current = Date.now() + 350;
                handleComponentDetailsOpen(productKey);
              }
            );
          } catch (e) {
            console.error("Error in DataTable initComplete wrapper:", e);
          }
        },
        drawCallback: function (settings) {
          const savedY = paginationScrollLockYRef.current;
          const now = Date.now();
          const currentY = window.scrollY || window.pageYOffset || 0;
          // Only enforce vertical snap while lock window is active.
          // This prevents stale savedY (e.g. 0 after modal/swal lifecycle) from forcing top jump.
          if (now < paginationScrollLockUntilRef.current) {
            if (Math.abs(currentY - savedY) > 2) {
              window.scrollTo({ top: savedY, behavior: "instant" });
            }
            paginationScrollLockUntilRef.current = now + 350;
          }
          // Preserve horizontal scroll while the Material table redraws.
          const restoreX = () => {
            try {
              const x = paginationScrollLockXRef.current;
              if (typeof x !== "number") return;

              // Use the scroll body that belongs to this table's dt-scroll container.
              // Fallback to the first dt-scroll-body in the wrapper if the scoped query fails.
              const $table = productTableRef.current ? $(productTableRef.current) : null;
              const $wrapper = $table ? $table.closest(".dataTables_wrapper") : $();
              const $scopedScrollBody = $table ? $table.closest(".dt-scroll").find(".dt-scroll-body").first() : null;
              const scrollBodyEl =
                ($scopedScrollBody && $scopedScrollBody.length ? $scopedScrollBody[0] : null) ||
                ($wrapper && $wrapper.length ? $wrapper.find(".dt-scroll-body").first()[0] : null);

              if (scrollBodyEl) scrollBodyEl.scrollLeft = x;
            } catch (e) {
              // ignore
            }
          };
          restoreX();
          requestAnimationFrame(restoreX);
          // Track horizontal scroll as the user moves the table.
          // We use this value when restoring after redraw (typing in qty input triggers redraw).
          try {
            const $table = productTableRef.current ? $(productTableRef.current) : $();
            const $scopedScrollBody = $table.closest(".dt-scroll").find(".dt-scroll-body").first();
            if ($scopedScrollBody && $scopedScrollBody.length) {
              materialScrollLeftRef.current = $scopedScrollBody[0].scrollLeft || 0;
              $scopedScrollBody
                .off("scroll.rcMaterialX")
                .on("scroll.rcMaterialX", function () {
                  materialScrollLeftRef.current = this.scrollLeft || 0;
                });
            }
          } catch (e) {
            // ignore
          }
          syncAllMaterialRowAddButtons();
          // Sync scroll head width with body width
          syncScrollHeadWidth(productTableRef);
          // DataTables sometimes recalculates widths after this, which can reset horizontal scroll.
          // Re-apply scrollLeft after layout work.
          setTimeout(restoreX, 0);
          setTimeout(restoreX, 50);
          setTimeout(restoreX, 100);

          // Update Select All checkbox in header (re-inject in dt-scroll-headInner if needed after draw)
          const $table = $(productTableRef.current);
          const wrapper = $table.closest(".dataTables_wrapper");
          const $headInner = wrapper.find(".dt-scroll-headInner");
          const $scrollHeadTh = $headInner.find("thead tr:first th:first");
          const checkboxHtml = `
            <label class="material-select-all-label" title="Select / Deselect all rows">
              <input type="checkbox" id="select-all-products" class="form-check-input" ${selectAllProductsRef.current ? "checked" : ""} style="cursor: pointer;" />
              <span class="material-select-all-text">All</span>
            </label>
          `;
          if ($scrollHeadTh.length > 0 && !$scrollHeadTh.find('#select-all-products').length) {
            $scrollHeadTh.html(checkboxHtml);
          }
          const selectAllCheckbox = $headInner.find('input[id="select-all-products"]');
          if (selectAllCheckbox.length > 0) {
            const $rowCheckboxes = $table
              .find("tbody .product-select-checkbox")
              .filter(function () {
                const selectable = $(this).data("selectable");
                return !(selectable === false || selectable === "false");
              });
            const allVisibleChecked =
              $rowCheckboxes.length > 0 &&
              $rowCheckboxes.filter(":checked").length === $rowCheckboxes.length;
            let globalSelectableCount = 0;
            try {
              if (dataTableInstanceRef.current) {
                globalSelectableCount = dataTableInstanceRef.current
                  .rows({ page: "all" })
                  .data()
                  .toArray()
                  .filter((row) => row && row._selectable !== false).length;
              }
            } catch (_) {
              globalSelectableCount = 0;
            }
            const selectedCount = selectedProductsRef.current?.size || 0;
            const globalSelectAll =
              globalSelectableCount > 0 && selectedCount === globalSelectableCount;
            const nextSelectAll =
              globalSelectAll ||
              selectAllProductsRef.current ||
              ($rowCheckboxes.length > 0 ? allVisibleChecked : selectAllProductsRef.current);
            selectAllCheckbox.prop("checked", nextSelectAll);
            setSelectAllProducts(nextSelectAll);
            selectAllProductsRef.current = nextSelectAll;
          }

          // Apply row enable/disable + highlight from selection ref after draw (requestAnimationFrame so DOM is ready)
          if (productTableRef.current) {
            const $table = $(productTableRef.current);
            const applySelectionFromRef = () => {
              const currentSelected = selectedProductsRef.current;
              if (!$table || !$table.length) return;
              $table.find("tbody tr").each(function () {
                const $row = $(this);
                const $checkbox = $row.find(".product-select-checkbox").first();
                const key = $checkbox.data("product-key");
                const selectable = $checkbox.data("selectable");
                const notSelectable = selectable === false || selectable === "false";
                const isDisabledDraftRow = $checkbox.data("disabled-draft-row") === 1 || $checkbox.attr("data-disabled-draft-row") === "1";
                if (notSelectable) {
                  $checkbox.prop("disabled", true);
                  // Disabled draft rows (RemainingQty 0): show checkbox as checked
                  $checkbox.prop("checked", !!isDisabledDraftRow);
                } else {
                  $checkbox.prop("disabled", false);
                  const isChecked =
                    key && currentSelected && typeof currentSelected.has === "function"
                      ? currentSelected.has(key)
                      : false;
                  $checkbox.prop("checked", !!isChecked);
                }
                const $controls = getMaterialRowEditableControls($row);
                const checked = $checkbox.is(":checked");
                // For disabled draft rows: keep row/controls disabled even though checkbox is checked
                if (notSelectable && isDisabledDraftRow) {
                  $row.removeClass("material-row-active").addClass("material-row-disabled");
                  $controls.prop("disabled", true);
                } else if (checked) {
                  applyMaterialRowSelectionUi($row, key, true);
                } else {
                  applyMaterialRowSelectionUi($row, key, false);
                }
              });
            };
            applySelectionFromRef();
          }

          // Init Select2 after row selection — SR Category follows checkbox like SR Qty
          initializeCategorySelect2();
          syncAllMaterialRowCategorySelects();
          setTimeout(() => syncAllMaterialRowCategorySelects(), 0);

          // Re-attach click handler for product details icon after redraw
          $(productTableRef.current).off("click", ".product-details-icon");
          $(productTableRef.current).on(
            "click",
            ".product-details-icon",
            function (e) {
              e.preventDefault();
              e.stopPropagation();
              const productKey = $(this).data("product-key");
              const productData = productDataRef.current[productKey];
              if (productData) {
                // Prevent page-jump caused by Material DataTable redraw logic.
                paginationScrollLockYRef.current = window.scrollY || window.pageYOffset || 0;
                paginationScrollLockXRef.current = materialScrollLeftRef.current || 0;
                paginationScrollLockUntilRef.current = Date.now() + 350;
                handleProductDetailsOpen(productData);
              }
            }
          );

          // Re-attach click handler for component details icon after redraw (only open for selected checkbox row)
          $(productTableRef.current).off("click", ".component-details-icon");
          $(productTableRef.current).on(
            "click",
            ".component-details-icon",
            function (e) {
              e.preventDefault();
              e.stopPropagation();
              if ($(this).attr("data-remaining-read-only") === "1") return;
              const productKey = String($(this).data("product-key") || "").trim();
              if (!productKey) return;
              const isSelected =
                (selectedProductsRef.current &&
                  typeof selectedProductsRef.current.has === "function" &&
                  selectedProductsRef.current.has(productKey)) ||
                $(productTableRef.current)
                  .find('.product-select-checkbox[data-product-key="' + productKey + '"]')
                  .is(":checked");
              if (!isSelected) {
                setMaterialSectionError("Please select the row (checkbox) before opening Component Details.");
                showMaterialSectionError("Please select the row (checkbox) before opening Component Details.");
                return;
              }
              // Prevent page-jump caused by Material DataTable redraw logic.
              paginationScrollLockYRef.current = window.scrollY || window.pageYOffset || 0;
              paginationScrollLockXRef.current = materialScrollLeftRef.current || 0;
              paginationScrollLockUntilRef.current = Date.now() + 350;
              handleComponentDetailsOpen(productKey);
            }
          );

          bindMaterialRowActionHandlers();

          // Re-attach click handler for Select All checkbox
          $(productTableRef.current).closest('.dataTables_wrapper').off("change", "#select-all-products");
          $(productTableRef.current).closest('.dataTables_wrapper').on("change", "#select-all-products", function (e) {
            e.stopPropagation();
            const checked = $(this).is(":checked");
            handleSelectAllProducts(checked);
          });

          // Re-attach click handlers for individual product checkboxes
          $(productTableRef.current).off("change", ".product-select-checkbox");
          $(productTableRef.current).on("change", ".product-select-checkbox", function (e) {
            e.stopPropagation();
            const productKey = $(this).data("product-key");
            const checked = $(this).is(":checked");
            handleProductSelect(productKey, checked);
            // Keep header Select All in sync with row checkbox state after every row toggle.
            // This mirrors Raise SR behavior and prevents stale header state in view mode.
            try {
              let selectableCount = 0;
              let selectedSelectableCount = 0;
              if (dataTableInstanceRef.current) {
                const rowsData = dataTableInstanceRef.current.rows().data().toArray();
                rowsData.forEach((row) => {
                  if (!row || row._selectable === false) return;
                  selectableCount += 1;
                  const key =
                    row._rowId != null && row._rowId !== ""
                      ? String(row._rowId)
                      : null;
                  if (key && selectedProductsRef.current?.has(key)) {
                    selectedSelectableCount += 1;
                  }
                });
              }
              const newSelectAll =
                selectableCount > 0 && selectedSelectableCount === selectableCount;
              setSelectAllProducts(newSelectAll);
              selectAllProductsRef.current = newSelectAll;
              const $wrapper = $(productTableRef.current).closest(".dataTables_wrapper, .dt-container");
              $wrapper.find('input[id="select-all-products"]').prop("checked", newSelectAll);
            } catch (_) {}
          });
        },
      });

      // Prevent DataTables internal links (`href="#"`) from triggering hash
      // navigation which can scroll the page to the top (pagination/sort controls).
      $(productTableRef.current)
        .closest(".dataTables_wrapper")
        .off("click.rcHashFix", 'a[href="#"]')
        .on("click.rcHashFix", 'a[href="#"]', function (e) {
          e.preventDefault();
        });

      // Refresh Material table scroll snapshot on user interactions so later redraws
      // don't use stale Y/X values (prevents jump-to-top after modal/swal close).
      $(productTableRef.current)
        .closest(".dataTables_wrapper")
        .off("pointerdown.rcScrollSnapshot mousedown.rcScrollSnapshot click.rcScrollSnapshot")
        .on(
          "pointerdown.rcScrollSnapshot mousedown.rcScrollSnapshot click.rcScrollSnapshot",
          ".dt-scroll-body, tbody tr, tbody td, .product-select-checkbox, .complaint-qty-input, .category-select, .product-details-icon, .component-details-icon, .material-row-add, .material-row-remove",
          function () {
            paginationScrollLockYRef.current = window.scrollY || window.pageYOffset || 0;
            paginationScrollLockXRef.current = materialScrollLeftRef.current || 0;
          }
        );

      // Helper: compute Comp Value = Comp Qty × Invoice Unit Price (qty integer, value 2 decimals)
      const getCompValueForRow = (key, compQtyVal) => {
        const product = productDataRef.current?.[key];
        if (!product) return "0.00";
        return computeMaterialSrValueFromRow(product, compQtyVal);
      };

      // Helper: cap complaint qty so that SUM of all Comp Qty rows for the same product never exceeds Invoice Qty.
      // Uses DOM data attributes on inputs (data-base-code, data-invoice-qty). data-invoice-qty is set from InvoiceQuantity.
      // Returns { value, wasCapped, maxForThisRow, invoiceQty } so callers can show validation without changing cap behavior.
      const getCappedComplaintQty = (key, rawVal, inputEl) => {
        const raw = String(rawVal).trim();
        const el = inputEl;
        if (!el) return { value: raw, wasCapped: false, maxForThisRow: null, invoiceQty: null };

        const baseCode = (el.getAttribute("data-base-code") || "").trim();
        const maxQtyAttr = el.getAttribute("data-invoice-qty");
        const maxQty = parseFloat(maxQtyAttr || "0") || 0;

        // If no base code, don't cap (safety guard)
        if (!baseCode) {
          return { value: raw, wasCapped: false, maxForThisRow: null, invoiceQty: maxQty };
        }

        // Sum Comp Qty of all OTHER SELECTED rows for the same product (same baseCode)
        let otherRowsTotal = 0;
        const allInputs = document.querySelectorAll(".complaint-qty-input");
        allInputs.forEach((inp) => {
          if (inp === el) return;
          const bc = (inp.getAttribute("data-base-code") || "").trim();
          if (bc !== baseCode) return;
          const otherKey = (inp.getAttribute("data-key") || "").trim();
          if (otherKey) {
            const otherCheckbox = document.querySelector(
              '.product-select-checkbox[data-product-key="' + otherKey + '"]'
            );
            if (!otherCheckbox || !otherCheckbox.checked) {
              return;
            }
          }
          const v = parseInt((inp.value || "").toString().trim(), 10);
          if (!Number.isNaN(v) && v > 0) {
            otherRowsTotal += v;
          }
        });

        const numVal = parseInt(raw, 10);
        if (Number.isNaN(numVal) || numVal <= 0) {
          return { value: raw, wasCapped: false, maxForThisRow: null, invoiceQty: maxQty };
        }

        // Max we can allocate to this row without exceeding remaining quantity (integer)
        const maxForThisRow = Math.max(0, Math.floor(maxQty - otherRowsTotal));
        if (numVal > maxForThisRow) {
          return {
            value: String(maxForThisRow),
            wasCapped: true,
            maxForThisRow,
            invoiceQty: maxQty,
          };
        }

        return {
          value: String(numVal),
          wasCapped: false,
          maxForThisRow,
          invoiceQty: maxQty,
        };
      };

      const SR_QTY_EXCEEDS_INVOICE_MSG =
        "SR Qty cannot be greater than Invoice Quantity.";

      // Remember rows capped while typing so blur can show Swal (input already reduces the value).
      let srQtyExceedPendingKey = null;

      const notifySrQtyExceedsInvoice = (showSwal) => {
        setMaterialSectionError(SR_QTY_EXCEEDS_INVOICE_MSG);
        if (showSwal) {
          showMaterialSectionError(SR_QTY_EXCEEDS_INVOICE_MSG);
        }
      };

      const clearSrQtyExceedPending = (key) => {
        if (srQtyExceedPendingKey === key) {
          srQtyExceedPendingKey = null;
        }
      };

      // Helper: show SR Category required validation, clear Comp Qty, focus SR Category dropdown
      const enforceSRCategoryBeforeCompQty = (key, inputEl) => {
        // Anchor viewport before showing error + focusing SR Category dropdown.
        applyCompQtyMissingCategoryScrollPrevention();
        setMaterialSectionError("Please select SR Category before entering Comp Qty.");
        showMaterialSectionError("Please select SR Category before entering Comp Qty.");
        if (inputEl) inputEl.value = "";
        complaintQtyRef.current = { ...complaintQtyRef.current, [key]: "" };
        setComplaintQty((prev) => ({ ...prev, [key]: "" }));
        const $catSelect = $(productTableRef.current).find('.category-select[data-key="' + key + '"]').first();
        if ($catSelect.length) {
          try {
            if ($catSelect.data("select2")) $catSelect.select2("open");
            else $catSelect[0].focus({ preventScroll: true });
          } catch (err) {
            $catSelect[0].focus({ preventScroll: true });
          }
        }
      };

      // Lock window scroll while DataTables redraws due to Comp Qty interactions.
      const lockMaterialTableScroll = () => {
        paginationScrollLockYRef.current = getEffectiveWindowScrollY();
        paginationScrollLockXRef.current = materialScrollLeftRef.current || 0;
        paginationScrollLockUntilRef.current = Date.now() + 800;
      };
      // Save position only (no lock). Use when field is focused – blur fires after focus moves, so we need pre-saved position.
      const saveMaterialScrollPosition = () => {
        paginationScrollLockYRef.current = getEffectiveWindowScrollY();
        paginationScrollLockXRef.current = materialScrollLeftRef.current || 0;
      };
      // Activate lock only (use pre-saved Y/X). Use in blur – by then focus moved, so current scroll can be wrong.
      const activateScrollLockOnly = () => {
        if (suppressNextScrollLockArmRef.current) {
          // Blur was caused by mousedown on a known action button (+/-/Save Draft/Next):
          // don't arm the 50ms scroll guard against a possibly-stale saved Y, or it can snap
          // the page back mid-click and move the button out from under the cursor.
          suppressNextScrollLockArmRef.current = false;
          skipNextDrawScrollFixRef.current = true;
          return;
        }
        paginationScrollLockUntilRef.current = Date.now() + 800;
      };

      // Scroll prevention for Comp Qty interactions when SR Category is missing.
      // Restores current viewport position without altering validation/business logic.
      const applyCompQtyMissingCategoryScrollPrevention = () => {
        const savedY = getEffectiveWindowScrollY();
        paginationScrollLockYRef.current = savedY;
        paginationScrollLockXRef.current = materialScrollLeftRef.current || 0;
        paginationScrollLockUntilRef.current = Date.now() + 1400;
        compQtyMissingCategoryLockActiveRef.current = true;

        // Restore multiple times because focus/select2 reflows can trigger a late scroll.
        window.scrollTo({ top: savedY, behavior: "instant" });
        requestAnimationFrame(() => {
          window.scrollTo({ top: savedY, behavior: "instant" });
        });
        setTimeout(() => {
          window.scrollTo({ top: savedY, behavior: "instant" });
        }, 80);
      };

      const maybeApplyCompQtyMissingCategoryScrollPrevention = (rowKey) => {
        const selectedCategory = (categoryRef.current[rowKey] || "").trim();
        if (!selectedCategory) applyCompQtyMissingCategoryScrollPrevention();
      };

      // Attach handler for complaint qty - allow only digits and one decimal; no special characters
      // Namespaced + off-before-on: when a row clone/remove sets skipNextMaterialTableReinitRef,
      // this effect's own cleanup no-ops (see the cleanup below), but this setup still runs again
      // right after - a plain .on() here would stack a duplicate handler on every clone, so
      // subsequent blurs fired the validation/redraw logic multiple times (some intermittently
      // showing a duplicate warning dialog that then ate the next click on Save Draft/Next).
      $(productTableRef.current).off("keydown.complaintQty", ".complaint-qty-input").on(
        "keydown.complaintQty",
        ".complaint-qty-input",
        function (e) {
          const rowKey = $(this).data("key");
          if (isExportCommercialQtyReadOnly(rowKey) || isComponentDetailsBvSrQtyReadOnly(rowKey)) {
            e.preventDefault();
            return;
          }
          const key = e.key;
          if (e.key === "Enter" || e.keyCode === 13) {
            e.preventDefault();
            const key = $(this).data("key");
            const isSelected = $(productTableRef.current)
              .find('.product-select-checkbox[data-product-key="' + key + '"]')
              .is(":checked");
            if (!isSelected) {
              return;
            }
            let val = sanitizeCompQtyInput(this.value);
            if (val !== this.value) this.value = val;
            const selectedCategory = (categoryRef.current[key] || "").trim();

            if (!selectedCategory) {
              enforceSRCategoryBeforeCompQty(key, this);
              return;
            }

            const cappedResult = getCappedComplaintQty(key, val, this);
            val = cappedResult.value;
            if (val !== this.value.trim()) {
              this.value = val;
            }
            if (cappedResult.wasCapped) {
              srQtyExceedPendingKey = null;
              notifySrQtyExceedsInvoice(true);
            } else {
              clearSrQtyExceedPending(key);
              setMaterialSectionError("");
            }

            complaintQtyRef.current = {
              ...complaintQtyRef.current,
              [key]: val,
            };
            setComplaintQty((prev) => ({ ...prev, [key]: val }));
            lockMaterialTableScroll();
            if (materialCompQtyDrawDebounceTimerRef.current) {
              clearTimeout(materialCompQtyDrawDebounceTimerRef.current);
              materialCompQtyDrawDebounceTimerRef.current = null;
            }
            if (dataTableInstanceRef.current) dataTableInstanceRef.current.draw(false);
            return;
          }
          // Integer only: allow only digits and navigation/editing keys (no decimal)
          if (key === "Backspace" || key === "Tab" || key === "ArrowLeft" || key === "ArrowRight" || key === "Delete") return;
          if (/^[0-9]$/.test(key)) {
            const rowKey = $(this).data("key");
            const isSelected = $(productTableRef.current)
              .find('.product-select-checkbox[data-product-key="' + rowKey + '"]')
              .is(":checked");
            if (!isSelected) return;

            const selectedCategory = (categoryRef.current[rowKey] || "").trim();
            if (!selectedCategory) {
              // Hard-stop on keydown itself so no native/input side-effects can scroll first.
              e.preventDefault();
              e.stopPropagation();
              if (typeof e.stopImmediatePropagation === "function") {
                e.stopImmediatePropagation();
              }
              applyCompQtyMissingCategoryScrollPrevention();
              enforceSRCategoryBeforeCompQty(rowKey, this);
              return;
            }
            return;
          }
          e.preventDefault();
        }
      );

      // On input: sanitize (digits only, integer), then validate SR Category; cap to Invoice Qty; update Comp Value.
      $(productTableRef.current).off("input.complaintQtySanitize", ".complaint-qty-input").on(
        "input.complaintQtySanitize",
        ".complaint-qty-input",
        function () {
          const key = $(this).data("key");
          if (isExportCommercialQtyReadOnly(key) || isComponentDetailsBvSrQtyReadOnly(key)) {
            return;
          }
          const isSelected = $(productTableRef.current)
            .find('.product-select-checkbox[data-product-key="' + key + '"]')
            .is(":checked");
          if (!isSelected) {
            return;
          }
          let val = sanitizeCompQtyInput(this.value);
          if (val !== this.value) this.value = val;
          const selectedCategory = (categoryRef.current[key] || "").trim();

          if (val !== "" && !selectedCategory) {
            enforceSRCategoryBeforeCompQty(key, this);
            return;
          }

          const cappedResult = getCappedComplaintQty(key, val, this);
          if (cappedResult.wasCapped) {
            val = cappedResult.value;
            this.value = val;
            srQtyExceedPendingKey = key;
            // Banner while typing — Swal on blur/Enter (value is already capped here).
            notifySrQtyExceedsInvoice(false);
          } else if (val !== "") {
            clearSrQtyExceedPending(key);
            setMaterialSectionError((prev) =>
              prev === SR_QTY_EXCEEDS_INVOICE_MSG ? "" : prev
            );
          }
          complaintQtyRef.current = { ...complaintQtyRef.current, [key]: val };
          setComplaintQty((prev) => ({ ...prev, [key]: val }));

          // Auto-calculate Comp Value = Comp Qty × Invoice Unit Price (instant, no draw)
          const compValueText = getCompValueForRow(key, val);
          $(this).closest("tr").find("td.comp-value-cell").text(compValueText);
          syncComplaintValueUsdRef.current?.();
          // Do NOT call draw() during typing – it recreates the input and blocks multi-digit entry.
          // Footer totals update on blur or Enter.
        }
      );

      // On blur: validate SR Category; if missing, clear Comp Qty and focus SR Category; else cap to Invoice Qty; validate Comp Qty > 0
      $(productTableRef.current).off("blur.complaintQty", ".complaint-qty-input").on(
        "blur.complaintQty",
        ".complaint-qty-input",
        function () {
          console.log("[DEBUG] qty blur fired", Date.now());
          const key = $(this).data("key");
          if (isExportCommercialQtyReadOnly(key) || isComponentDetailsBvSrQtyReadOnly(key)) {
            return;
          }
          const isSelected = $(productTableRef.current)
            .find('.product-select-checkbox[data-product-key="' + key + '"]')
            .is(":checked");
          if (!isSelected) {
            return;
          }
          let val = sanitizeCompQtyInput(this.value);
          if (val !== this.value) this.value = val;
          const selectedCategory = (categoryRef.current[key] || "").trim();

          if (val !== "" && !selectedCategory) {
            enforceSRCategoryBeforeCompQty(key, this);
            return;
          }

          const numVal = parseInt(val, 10);
          if (val !== "" && (isNaN(numVal) || numVal <= 0)) {
            setMaterialSectionError("Comp Qty must be a whole number greater than 0.");
            showMaterialSectionError("Comp Qty must be a whole number greater than 0.");
            this.value = "";
            complaintQtyRef.current = { ...complaintQtyRef.current, [key]: "" };
            setComplaintQty((prev) => ({ ...prev, [key]: "" }));
            activateScrollLockOnly();
            scheduleDeferredMaterialRedraw();
            return;
          }

          const cappedResult = getCappedComplaintQty(key, val, this);
          if (cappedResult.wasCapped || srQtyExceedPendingKey === key) {
            if (cappedResult.wasCapped) {
              this.value = cappedResult.value;
              complaintQtyRef.current = {
                ...complaintQtyRef.current,
                [key]: cappedResult.value,
              };
              setComplaintQty((prev) => ({
                ...prev,
                [key]: cappedResult.value,
              }));
            }
            srQtyExceedPendingKey = null;
            notifySrQtyExceedsInvoice(true);
          } else if (val !== "") {
            setMaterialSectionError((prev) =>
              prev === SR_QTY_EXCEEDS_INVOICE_MSG ? "" : prev
            );
          }
          activateScrollLockOnly();
          scheduleDeferredMaterialRedraw();
        }
      );

      // Save scroll when focused (blur fires after focus moves, so we need pre-saved position for restore)
      $(productTableRef.current).off("mousedown.complaintQty", ".complaint-qty-input").on("mousedown.complaintQty", ".complaint-qty-input", function () {
        const rowKey = $(this).data("key");
        maybeApplyCompQtyMissingCategoryScrollPrevention(rowKey);
      });
      $(productTableRef.current).off("focus.complaintQty", ".complaint-qty-input").on("focus.complaintQty", ".complaint-qty-input", function () {
        saveMaterialScrollPosition();
        const rowKey = $(this).data("key");
        maybeApplyCompQtyMissingCategoryScrollPrevention(rowKey);
      });
      $(productTableRef.current).off("input.complaintQtyScroll", ".complaint-qty-input").on("input.complaintQtyScroll", ".complaint-qty-input", function () {
        if ($(this).is(":focus")) saveMaterialScrollPosition();
      });

      // Attach click handler for Select All checkbox
      $(productTableRef.current).closest('.dataTables_wrapper').on("change", "#select-all-products", function (e) {
        e.stopPropagation();
        const checked = $(this).is(":checked");
        handleSelectAllProducts(checked);
      });

      // Attach click handlers for individual product checkboxes
      $(productTableRef.current).off("change", ".product-select-checkbox");
      $(productTableRef.current).on("change", ".product-select-checkbox", function (e) {
        e.stopPropagation();
        const productKey = $(this).data("product-key");
        const checked = $(this).is(":checked");
        handleProductSelect(productKey, checked);
        // Keep header Select All in sync with row checkbox state after every row toggle.
        // This mirrors Raise SR behavior and prevents stale header state in view mode.
        try {
          let selectableCount = 0;
          let selectedSelectableCount = 0;
          if (dataTableInstanceRef.current) {
            const rowsData = dataTableInstanceRef.current.rows().data().toArray();
            rowsData.forEach((row) => {
              if (!row || row._selectable === false) return;
              selectableCount += 1;
              const key =
                row._rowId != null && row._rowId !== ""
                  ? String(row._rowId)
                  : null;
              if (key && selectedProductsRef.current?.has(key)) {
                selectedSelectableCount += 1;
              }
            });
          }
          const newSelectAll =
            selectableCount > 0 && selectedSelectableCount === selectableCount;
          setSelectAllProducts(newSelectAll);
          selectAllProductsRef.current = newSelectAll;
          const $wrapper = $(productTableRef.current).closest(".dataTables_wrapper, .dt-container");
          $wrapper.find('input[id="select-all-products"]').prop("checked", newSelectAll);
        } catch (_) {}
      });

      bindMaterialRowActionHandlers();

      // Add ResizeObserver for this specific table with debouncing
      let resizeObserver = null;
      let resizeTimeout = null;
      if (window.ResizeObserver) {
        const tableContainer = $(productTableRef.current).closest(
          ".card-panel, .table-responsive"
        )[0];
        if (tableContainer) {
          let lastObservedWidth = 0;
          resizeObserver = new ResizeObserver((entries) => {
            // Mobile/tablet only: the soft keyboard changes the panel's height, not its
            // width. Redrawing on that replaces the focused SR Qty input and dismisses
            // the keyboard as soon as the field is tapped, so height-only changes are
            // ignored here. Desktop keeps reacting to every resize as before.
            if (isTouchDevice()) {
              const observedWidth = Math.round(
                entries?.[0]?.contentRect?.width || 0
              );
              if (observedWidth === lastObservedWidth) return;
              lastObservedWidth = observedWidth;
            }
            // Debounce the resize adjustments to prevent rapid firing
            if (resizeTimeout) clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
              if (dataTableInstanceRef.current) {
                try {
                  // Use requestAnimationFrame to ensure smooth rendering
                  requestAnimationFrame(() => {
                    if (dataTableInstanceRef.current) {
                      // Mobile/tablet only: never draw() while a cell input holds focus -
                      // it recreates the input and closes the keyboard. Widths are still
                      // recalculated; the redraw follows on blur.
                      if (
                        isTouchDevice() &&
                        isEditableElementFocusedWithin(productTableRef.current)
                      ) {
                        dataTableInstanceRef.current.columns.adjust();
                        syncScrollHeadWidth(productTableRef);
                        return;
                      }
                      dataTableInstanceRef.current.columns.adjust().draw(false);
                      // Sync scroll head width with body width after resize
                      syncScrollHeadWidth(productTableRef);
                    }
                  });
                } catch (e) {
                  // Silently handle errors - ResizeObserver errors are usually benign
                  if (!e.message.includes("ResizeObserver")) {
                    console.error("Error in ResizeObserver adjust:", e);
                  }
                }
              }
            }, 150); // 150ms debounce
          });
          resizeObserver.observe(tableContainer);
        }
      }

      // Store resizeObserver and timeout for cleanup
      if (productTableRef.current) {
        productTableRef.current._resizeObserver = resizeObserver;
        productTableRef.current._resizeTimeout = resizeTimeout;
      }
    }

    // Cleanup on unmount
    return () => {
      if (skipNextMaterialTableReinitRef.current) {
        skipNextMaterialTableReinitRef.current = false;
        return;
      }
      // Cleanup ResizeObserver and timeout
      if (productTableRef.current) {
        if (productTableRef.current._resizeTimeout) {
          clearTimeout(productTableRef.current._resizeTimeout);
          delete productTableRef.current._resizeTimeout;
        }
        if (productTableRef.current._resizeObserver) {
          productTableRef.current._resizeObserver.disconnect();
          delete productTableRef.current._resizeObserver;
        }
      }
      if (dataTableInstanceRef.current) {
        try {
          dataTableInstanceRef.current.off("draw.dt");
        } catch (e) { }
        dataTableInstanceRef.current.destroy();
        dataTableInstanceRef.current = null;
      }
      document.removeEventListener("click", onDocumentClickFlush);
      document.removeEventListener("click", debugAnyClick, true);
      document.removeEventListener("mouseup", debugAnyMouseup, true);
      pendingMaterialRedrawRef.current = null;
      if (productTableRef.current) {
        $(productTableRef.current).off("keydown", ".complaint-qty-input");
        $(productTableRef.current).off("input", ".complaint-qty-input");
        $(productTableRef.current).off("blur", ".complaint-qty-input");
        $(productTableRef.current).off("mousedown", ".complaint-qty-input");
        $(productTableRef.current).off("focus", ".complaint-qty-input");
        const $table = $(productTableRef.current);
        $table.off("click", ".material-row-add");
        $table.off("click", ".material-row-remove");
        $table
          .closest(".dataTables_wrapper")
          .off(
            "click.materialRowAdd click.materialRowAddBlocked click.materialRowRemove mousedown.materialRowAdd mousedown.materialRowRemove pointerdown.materialRowAdd pointerdown.materialRowRemove"
          );
        // Destroy Select2 instances
        $(productTableRef.current)
          .find(".category-select")
          .each(function () {
            const $select = $(this);
            if ($select.data("select2")) {
              $select.off("change.category");
              $select.select2("destroy");
            }
          });
      }
    };
  }, [invoiceDetails, materialRows, srTypesOptions, draftSrCode]);

  // Refresh DataTable when SR Types are loaded to update category dropdowns
  useEffect(() => {
    if (dataTableInstanceRef.current && srTypesOptions.length > 0 && productTableRef.current) {
      try {
        //console.log("Updating category dropdowns with SR Types:", srTypesOptions);
        
        // Update all category select dropdowns with new options; restore value from categoryRef (draft load)
        $(productTableRef.current)
          .find(".category-select")
          .each(function () {
            const $select = $(this);
            const key = $select.data("key");
            const savedValue = key ? (categoryRef.current[key] || "") : ($select.val() || "");
            const savedDesc = key ? (categoryDescRef.current[key] || "") : "";
            
            // Clear existing options except the first "Select SR Category"
            $select.find("option:not(:first)").remove();
            
            // If draft has a saved value (e.g. SRTYPE-001) not in fetched list, add it so dropdown can display it
            const row = key ? productDataRef.current[key] : null;
            const rowSrOptions = getSrCategoryOptionsForMaterialRowRef.current(row);
            const baseCode =
              row?.ProductCode || row?.productCode || row?.Productcode || "";
            const usedForThisProduct = getUsedSrCategoriesForSameProduct(
              baseCode,
              key
            );

            const savedInList = rowSrOptions.some((opt) => opt.value === savedValue);
            if (savedValue && !savedInList) {
              const $opt = $("<option></option>").attr("value", savedValue).text(savedDesc || savedValue);
              $opt.prop("selected", true);
              $select.append($opt);
            }
            
            // Add options for this row; skip categories already used on sibling rows
            rowSrOptions.forEach((option) => {
              if (!option?.value) return;
              if (
                usedForThisProduct.has(option.value) &&
                option.value !== savedValue
              ) {
                return;
              }
              const $option = $("<option></option>")
                .attr("value", option.value)
                .text(option.text);
              if (savedValue === option.value) {
                $option.prop("selected", true);
              }
              $select.append($option);
            });
            
            if (savedValue) {
              $select.val(savedValue);
            }
            // Trigger change to update Select2 if it's initialized
            if ($select.data("select2")) {
              $select.trigger("change");
            }
          });
        
        // Re-draw table so SR Category column re-renders with full options list (same logic as column render)
        if (dataTableInstanceRef.current) {
          try {
            dataTableInstanceRef.current.draw(false);
          } catch (e) {
            console.warn("DataTable draw after SR Types refresh:", e);
          }
        }
        // Reinitialize Select2 on category dropdowns after draw
        setTimeout(() => {
          initializeCategorySelect2();
        }, 100);
      } catch (error) {
        console.error("Error refreshing DataTable with SR Types:", error);
      }
    }
  }, [srTypesOptions, market]);

  // Clear selections only when invoice changes (new invoice). Do NOT clear when selectAllProducts/draftSrCode change or selections are lost on pagination.
  const invoiceId = invoiceDetails
    ? (invoiceDetails.InvoiceNumber || invoiceDetails.invoiceNumber || "").toString().trim()
    : "";
  useEffect(() => {
    if (invoiceDetails && !draftSrCode && invoiceId) {
      if (lastClearedInvoiceRef.current !== invoiceId) {
        lastClearedInvoiceRef.current = invoiceId;
        const emptySet = new Set();
        selectedProductsRef.current = emptySet;
        setSelectedProducts(emptySet);
        setSelectAllProducts(false);
        clearExportCommercialBulkState();
        componentDetailsRef.current = {};
        try {
          localStorage.removeItem(COMPONENT_DETAILS_LS_KEY);
          localStorage.removeItem(draftStorageKey);
        } catch (_) {}
        // If exactly one selectable line item, default to selecting it and Select All checked
        const products = invoiceDetails?.products;
        if (Array.isArray(products) && products.length === 1) {
          const p = products[0];
          const remaining = parseFloat(p?.RemainingQty ?? p?.remainingQty ?? p?.InvoiceQuantity ?? p?.invoiceQuantity ?? 0) || 0;
          if (remaining > 0) {
            const code = p.ProductCode || p.productCode || "r";
            const oneKey = code + "_row0";
            const singleSet = new Set([oneKey]);
            selectedProductsRef.current = singleSet;
            setSelectedProducts(singleSet);
            setSelectAllProducts(true);
          }
        }
      }
    }
    if (!invoiceId) lastClearedInvoiceRef.current = null;
  }, [invoiceDetails, draftSrCode, invoiceId]);

  // Sync Select All checkbox DOM with state (no clear) when selectAllProducts or table is ready
  useEffect(() => {
    if (productTableRef.current && dataTableInstanceRef.current) {
      const selectAllCheckbox = $(productTableRef.current)
        .closest(".dataTables_wrapper")
        .find('input[id="select-all-products"]');
      if (selectAllCheckbox.length > 0) {
        selectAllCheckbox.prop('checked', selectAllProducts);
      }
    }
  }, [selectAllProducts]);

  // Initialize Previous Complaints DataTable
  useEffect(() => {
    // Destroy previous instance
    if (historyDataTableInstanceRef.current) {
      historyDataTableInstanceRef.current.destroy();
      historyDataTableInstanceRef.current = null;
    }

    if (historyTableRef.current && invoiceDetails) {
      // Ensure DataTable plugin is registered on jQuery
      if (!$.fn.DataTable) {
        DataTable($);
      }

      // Transform the data from backend to match DataTable format
      // If previousComplaintsData is empty, use empty array to show headers with no data
      const historyData = previousComplaintsData.length > 0
        ? previousComplaintsData.map((complaint, index) => ({
          originalIndex: index, // Store original index for View button
          srNo: complaint.srNumber || "",
          srDate: complaint.srDate || "",
          productsInvolved: complaint.productsInvolved || "",
          srQty: complaint.srQuantity || 0,
          srValue: complaint.srValue || 0,
          status: complaint.statusName || complaint.StatusName || complaint.status || "",
          actionTaken: complaint.actionTaken || "",
          latestComment: complaint.latestComment || "",
        }))
        : []; // Empty array when no data - shows headers but no rows

      historyDataTableInstanceRef.current = $(
        historyTableRef.current
      ).DataTable({
        data: historyData,
        paging: true,
        searching: true,
        info: true,
        lengthChange: true,
        lengthMenu: [5, 10, 25, 50, 100],
        pageLength: 5,
        dom: "lfrtip",
        ordering: true,
        destroy: true,
        scrollX: true,
        autoWidth: false,
        fixedColumns: false,
        order: [[0, "asc"]],
        columns: [
          {
            title: "SR No",
            data: "srNo",
            render: (data, type, row) => {
              const srNo = (data || "").trim();
              if (!srNo) return "";
              const originalIndex = row.originalIndex !== undefined ? row.originalIndex : 0;
              return `<a href="#" class="sr-link-view" data-original-index="${escapeHtml(String(originalIndex))}" style="color: #007bff; text-decoration: underline; cursor: pointer; font-weight: 500;" onmouseover="this.style.color='#0056b3';" onmouseout="this.style.color='#007bff';">${escapeHtml(srNo)}</a>`;
            },
            createdCell: (td) => {
              td.classList.add("text-center");
            },
          },
          {
            title: "SR Date",
            data: "srDate",
            render: (data) => {
              if (!data) return "";
              const date = new Date(data);
              const day = String(date.getDate()).padStart(2, '0');
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const year = date.getFullYear();
              return `${day}-${month}-${year}`;
            },
            createdCell: (td) => {
              td.classList.add("text-center");
            },
          },
          {
            title: "Product(s) involved",
            data: "productsInvolved",
            createdCell: (td) => {
              td.classList.add("text-start");
            },
            render: (data, type) => {
              const fullText = (data ?? "").toString();
              if (type !== "display") return fullText;

              // Escape to keep DataTable HTML safe when backend contains special chars.
              const escapeHtml = (str) =>
                str
                  .replace(/&/g, "&amp;")
                  .replace(/</g, "&lt;")
                  .replace(/>/g, "&gt;")
                  .replace(/"/g, "&quot;")
                  .replace(/'/g, "&#039;");

              const escaped = escapeHtml(fullText);
              return `<span class="sr-product-involved-truncate" title="${escaped}">${escaped}</span>`;
            },
          },
          {
            title: "SR QTY",
            data: "srQty",
            className: "text-end dt-numeric-col",
          },
          {
            title: "SR VALUE",
            data: "srValue",
            className: "text-end dt-numeric-col",
            render: (data) => {
              if (!data) return "";
              return parseFloat(data).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
            },
          },
          {
            title: "Resolution status",
            data: "status",
            createdCell: (td) => {
              td.classList.add("text-center");
            },
            render: (data, type) => {
              const status = String(data ?? "").trim();
              if (!status) return "";
              if (type !== "display") return status;

              let statusClass = "";
              const lower = status.toLowerCase();
              if (lower === "open") {
                statusClass = "status-open";
              } else if (lower === "closed") {
                statusClass = "status-closed";
              } else if (lower === "approved") {
                statusClass = "status-approved";
              } else if (lower === "rejected") {
                statusClass = "status-rejected";
              }

              return statusClass
                ? `<span class="${statusClass}">${status}</span>`
                : status;
            },
          },
          {
            title: "Action",
            data: null,
            orderable: false,
            createdCell: (td) => {
              td.classList.add("text-center");
            },
            render: (data, type, row) => {
              if (type === "display") {
                const originalIndex = row.originalIndex !== undefined ? row.originalIndex : 0;
                return `<button class="btn-view-complaint" data-original-index="${originalIndex}" style="
                  background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
                  border: none;
                  color: white;
                  padding: 4px 12px;
                  border-radius: 4px;
                  cursor: pointer;
                  font-size: 12px;
                  font-weight: 500;
                  transition: all 0.3s ease;
                  box-shadow: 0 2px 4px rgba(23, 162, 184, 0.3);
                " onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 4px 8px rgba(23, 162, 184, 0.5)';" onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 2px 4px rgba(23, 162, 184, 0.3)';">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle; margin-right: 4px; display: inline-block;">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  View
                </button>`;
              }
              return "";
            },
          },
        ],
        initComplete: function (settings, json) {
          try {
            // Wrap dt-length and dt-search into a single top filter wrapper
            const wrapper = $(historyTableRef.current).closest(
              ".dataTables_wrapper, .dt-container"
            );
            if (wrapper.length > 0) {
              const lengthEl = wrapper.find(".dt-length");
              const searchEl = wrapper.find(".dt-search");
              if (lengthEl.length > 0 || searchEl.length > 0) {
                let topWrap = wrapper.find(".dtFltrTop");
                if (topWrap.length === 0) {
                  topWrap = $("<div class='dtFltrTop d-flex flex-wrap'></div>");
                  wrapper.prepend(topWrap);
                }
                lengthEl.each(function () {
                  $(this).appendTo(topWrap);
                });
                searchEl.each(function () {
                  $(this).appendTo(topWrap);
                });
              }

              // Wrap dt-info and dt-paging into a single bottom filter wrapper
              const infoEl = wrapper.find(".dt-info");
              const pagingEl = wrapper.find(".dt-paging");
              if (infoEl.length > 0 || pagingEl.length > 0) {
                let bottomWrap = wrapper.find(".dtFltrBotm");
                if (bottomWrap.length === 0) {
                  bottomWrap = $(
                    "<div class='dtFltrBotm d-flex flex-wrap'></div>"
                  );
                  wrapper.append(bottomWrap);
                }
                infoEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
                pagingEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
              }
            }

            // Wrap dt-scroll with an inner wrapper and ensure outer wrapper has dtScrlWrpr
            const dtScrollDiv = $(historyTableRef.current).closest(
              ".dt-scroll"
            );
            if (dtScrollDiv.length > 0) {
              if (!dtScrollDiv.parent().hasClass("dtScrlWrprInner")) {
                dtScrollDiv.wrap('<div class="dtScrlWrprInner"></div>');
              }
              const outer = dtScrollDiv.parent().parent();
              if (outer.length > 0) outer.addClass("dtScrlWrpr");
            } else {
              const container = $(historyTableRef.current).closest(
                ".table-responsive"
              );
              if (container.length > 0) {
                const dtScroll = container.find(".dt-scroll");
                if (dtScroll.length > 0) {
                  dtScroll.each(function () {
                    const $this = $(this);
                    if (!$this.parent().hasClass("dtScrlWrprInner")) {
                      $this.wrap('<div class="dtScrlWrprInner"></div>');
                    }
                    const outer = $this.parent().parent();
                    if (outer.length > 0) outer.addClass("dtScrlWrpr");
                  });
                }
              }
            }

            // Initial column adjustment
            setTimeout(() => {
              if (historyDataTableInstanceRef.current) {
                historyDataTableInstanceRef.current.columns.adjust();
                // Sync scroll head width with body width
                syncScrollHeadWidth(historyTableRef);
              }
            }, 100);
          } catch (e) {
            console.error(
              "Error in History DataTable initComplete wrapper:",
              e
            );
          }
        },
        drawCallback: function (settings) {
          // Re-attach click handler for View button
          $(historyTableRef.current)
            .off("click", ".btn-view-complaint")
            .on("click", ".btn-view-complaint", function (e) {
              e.preventDefault();
              const originalIndex = parseInt($(this).data("original-index"));
              setSelectedComplaintIndex(originalIndex);
              setShowPreviousComplaintsDetailModal(true);
            });

          // Re-attach click handler for SR Number link
          $(historyTableRef.current)
            .off("click", ".sr-link-view")
            .on("click", ".sr-link-view", function (e) {
              e.preventDefault();
              const originalIndex = parseInt($(this).data("original-index"));
              setSelectedComplaintIndex(originalIndex);
              setShowPreviousComplaintsDetailModal(true);
            });

          // Sync scroll head width with body width
          syncScrollHeadWidth(historyTableRef);
        },
      });

      // Attach click handler for View button
      $(historyTableRef.current).on("click", ".btn-view-complaint", function (e) {
        e.preventDefault();
        const originalIndex = parseInt($(this).data("original-index"));
        setSelectedComplaintIndex(originalIndex);
        setShowPreviousComplaintsDetailModal(true);
      });

      // Attach click handler for SR Number link
      $(historyTableRef.current).on("click", ".sr-link-view", function (e) {
        e.preventDefault();
        const originalIndex = parseInt($(this).data("original-index"));
        setSelectedComplaintIndex(originalIndex);
        setShowPreviousComplaintsDetailModal(true);
      });

      // Add ResizeObserver for this specific table with debouncing
      let resizeObserver = null;
      let resizeTimeout = null;
      if (window.ResizeObserver) {
        const tableContainer = $(historyTableRef.current).closest(
          ".card-panel, .table-responsive"
        )[0];
        if (tableContainer) {
          let lastObservedWidth = 0;
          resizeObserver = new ResizeObserver((entries) => {
            // Mobile/tablet only: ignore the height-only resize caused by the soft
            // keyboard - a redraw there only shifts the layout under the user while they
            // type in the Material section. Desktop behaviour is unchanged.
            if (isTouchDevice()) {
              const observedWidth = Math.round(
                entries?.[0]?.contentRect?.width || 0
              );
              if (observedWidth === lastObservedWidth) return;
              lastObservedWidth = observedWidth;
            }
            // Debounce the resize adjustments to prevent rapid firing
            if (resizeTimeout) clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
              if (historyDataTableInstanceRef.current) {
                try {
                  // Use requestAnimationFrame to ensure smooth rendering
                  requestAnimationFrame(() => {
                    if (historyDataTableInstanceRef.current) {
                      historyDataTableInstanceRef.current.columns
                        .adjust()
                        .draw(false);
                      // Sync scroll head width with body width after resize
                      syncScrollHeadWidth(historyTableRef);
                    }
                  });
                } catch (e) {
                  // Silently handle errors - ResizeObserver errors are usually benign
                  if (!e.message.includes("ResizeObserver")) {
                    console.error("Error in ResizeObserver adjust:", e);
                  }
                }
              }
            }, 150); // 150ms debounce
          });
          resizeObserver.observe(tableContainer);
        }
      }

      // Store resizeObserver and timeout for cleanup
      if (historyTableRef.current) {
        historyTableRef.current._resizeObserver = resizeObserver;
        historyTableRef.current._resizeTimeout = resizeTimeout;
      }
    }

    // Cleanup on unmount
    return () => {
      // Cleanup ResizeObserver and timeout
      if (historyTableRef.current) {
        if (historyTableRef.current._resizeTimeout) {
          clearTimeout(historyTableRef.current._resizeTimeout);
          delete historyTableRef.current._resizeTimeout;
        }
        if (historyTableRef.current._resizeObserver) {
          historyTableRef.current._resizeObserver.disconnect();
          delete historyTableRef.current._resizeObserver;
        }
      }
      if (historyDataTableInstanceRef.current) {
        try {
          historyDataTableInstanceRef.current.off("draw.dt");
        } catch (e) { }
        historyDataTableInstanceRef.current.destroy();
        historyDataTableInstanceRef.current = null;
      }
      // Remove click handler
      if (historyTableRef.current) {
        $(historyTableRef.current).off("click", ".sr-link");
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceDetails, previousComplaintsData]);

  // Adjust DataTable when table visibility changes
  useEffect(() => {
    const targetY = previousComplaintsToggleScrollYRef.current;
    if (typeof targetY === "number" && !Number.isNaN(targetY)) {
      requestAnimationFrame(() => restoreWindowScrollPosition(targetY));
      if (restorePreviousComplaintsScrollRef.current) {
        clearTimeout(restorePreviousComplaintsScrollRef.current);
      }
      restorePreviousComplaintsScrollRef.current = setTimeout(() => {
        restoreWindowScrollPosition(targetY);
      }, 180);
    }

    if (showPreviousComplaintsTable && historyDataTableInstanceRef.current) {
      // Use setTimeout to ensure the table is visible before adjusting
      setTimeout(() => {
        if (historyDataTableInstanceRef.current) {
          try {
            historyDataTableInstanceRef.current.columns.adjust();
            if (historyTableRef.current) {
              syncScrollHeadWidth(historyTableRef);
            }
            if (typeof targetY === "number" && !Number.isNaN(targetY)) {
              restoreWindowScrollPosition(targetY);
            }
          } catch (e) {
            console.error("Error adjusting history table columns:", e);
          }
        }
      }, 100);
    }

    return () => {
      if (restorePreviousComplaintsScrollRef.current) {
        clearTimeout(restorePreviousComplaintsScrollRef.current);
        restorePreviousComplaintsScrollRef.current = null;
      }
    };
  }, [showPreviousComplaintsTable]);

  // Min lengths: Invoice 10, ODN 12 (same search box accepts either)
  const MIN_INVOICE_LENGTH = 10;
  const MIN_ODN_LENGTH = 12;
  const MAX_SEARCH_KEY_LENGTH = 20;

  /** ODN numbers typically start with 2 letters (e.g. IN, EX, ET) and are at least 12 chars. */
  const isLikelyOdnSearch = (trimmed) =>
    /^[A-Z]{2}/i.test(trimmed) && /[0-9]/.test(trimmed);

  const resolveInvoiceNumberFromDetails = (details) => {
    if (!details) return "";
    return (
      details.invoiceNumber ||
      details.invoiceNo ||
      details.InvoiceNo ||
      details.InvoiceNumber ||
      ""
    )
      .toString()
      .trim()
      .toUpperCase();
  };

  // Validation for Invoice No. / ODN No. (single search field)
  const validateInvoiceNumber = (value) => {
    if (!value || value.trim() === "") {
      return ""; // Empty is allowed during typing, will be validated on submit
    }

    const trimmed = value.trim();
    if (isLikelyOdnSearch(trimmed)) {
      if (trimmed.length < MIN_ODN_LENGTH) {
        return `ODN number must be at least ${MIN_ODN_LENGTH} characters.`;
      }
    } else if (trimmed.length < MIN_INVOICE_LENGTH) {
      return `Invoice number must be at least ${MIN_INVOICE_LENGTH} characters.`;
    }

    // Convert to uppercase for validation
    const upperValue = value.toUpperCase();

    // Check for spaces
    if (/\s/.test(value)) {
      return "Spaces are not allowed.";
    }

    // Check for special characters (only allow A-Z, a-z, 0-9)
    if (!/^[A-Za-z0-9]*$/.test(value)) {
      return "Special characters are not allowed.";
    }

    // Check for decimal values
    if (/\./.test(value)) {
      return "Decimal values are not allowed.";
    }

    // After conversion, check if only uppercase letters and digits
    if (!/^[A-Z0-9]*$/.test(upperValue)) {
      return "Only uppercase/Lowercase letters and digits are allowed.";
    }

    return ""; // Valid
  };

  // Helper function to check if invoice number is valid
  const isInvoiceNumberValid = () => {
    const originalValue = formData.invoiceNo || "";
    const trimmedValue = originalValue.trim();
    if (!trimmedValue) {
      return false;
    }
    // Validate the original value (not trimmed) to catch spaces
    return validateInvoiceNumber(originalValue) === "";
  };

  const handleInputChange = (field) => (event) => {
    let value = event.target.value;

    // Special handling for invoiceNo field
    if (field === "invoiceNo") {
      // Auto-convert to uppercase
      value = value.toUpperCase();

      // Validate the input
      const validationError = validateInvoiceNumber(value);
      setInvoiceNoError(validationError);
    }

    setFormData({
      ...formData,
      [field]: value,
    });
    setError("");
  };

  const handleSearch = async () => {
    // Trim and validate invoice number
    const trimmedInvoiceNo = formData.invoiceNo?.trim() || "";

    if (!trimmedInvoiceNo) {
      const message = "Please enter Invoice No. or ODN No.";
      await showMissingInvoice(message);
      return;
    }

    // Validate search key format
    const validationError = validateInvoiceNumber(trimmedInvoiceNo);
    if (validationError) {
      setInvoiceNoError(validationError);
      await showError("Invalid search value", validationError);
      return;
    }

    // Ensure invoice number is uppercase before search
    const upperCaseInvoiceNo = trimmedInvoiceNo.toUpperCase();
    if (upperCaseInvoiceNo !== formData.invoiceNo) {
      setFormData({
        ...formData,
        invoiceNo: upperCaseInvoiceNo,
      });
    }

    setLoading(true);
    setError("");
    setInvoiceNoError("");
    // New invoice/ODN search: clear uploaded invoice files (in-memory only; no localStorage)
    setInvoiceCopyFiles([]);
    setSecondaryInvoiceFiles([]);
    setInvoiceDetails(null);
    setPreviousComplaintsCount(0);
    setPreviousComplaintsData([]);
    setShowPreviousComplaintsTable(false);
    // Reset customer type and end customer when loading new invoice
    setCustomerType("B2B");
    setShowEndCustomer(false);
    // Reset Material Section selections/values so SR Category does not carry over
    setMaterialRows([]);
    setMaterialSectionError("");
    const emptyForInvoice = new Set();
    selectedProductsRef.current = emptyForInvoice;
    setSelectedProducts(emptyForInvoice);
    setSelectAllProducts(false);
    setComplaintQty({});
    complaintQtyRef.current = {};
    categoryRef.current = {};
    categoryDescRef.current = {};
    clearExportCommercialBulkState();
    componentDetailsRef.current = {};
    // Clear Material Section / card panel data from localStorage so previous invoice data is not shown
    try {
      localStorage.removeItem(COMPONENT_DETAILS_LS_KEY);
      localStorage.removeItem(draftStorageKey);
    } catch (_) {}
    // Reset SR Types list until market-based list is loaded for this invoice
    setSrTypesOptions([]);
    srTypesOptionsRef.current = [];

    try {
      const response = await complaintAPI.getInvoiceDetails(
        upperCaseInvoiceNo,
        null
      );

      // console.log("Invoice Details Response:", response);
      console.log("Invoice Details Data:", response.data);

      //console.log("API Response Success:", response.success);
      //console.log("API Response Data:", response.data);
      //console.log("API Response Message:", response.message);

      if (response.success && response.data) {
        // Validate Domestic/Export + Business Vertical against logged-in user before loading
        const accessError = validateInvoiceAccessForUser(response.data);
        if (accessError) {
          setInvoiceDetails(null);
          setError(accessError);
          await showSwal({
            title: "Access Denied",
            message: accessError,
            icon: "error",
            showCloseButton: true,
            confirmText: "OK",
            allowOutsideClick: false,
          });
          return;
        }

        setInvoiceDetails(response.data);

        // Derive simple Market flag (Domestic / Export) from invoice details
        const marketCodeFromInvoice =
          response.data?.MarketCode || response.data?.marketCode || "";
        const marketNameFromInvoice =
          response.data?.MarketName || response.data?.marketName || "";
        const codeUpper = (marketCodeFromInvoice || "").toString().toUpperCase();
        const nameUpper = (marketNameFromInvoice || "").toString().toUpperCase();

        if (
          codeUpper.includes("DOM") ||
          nameUpper.includes("DOMESTIC") ||
          codeUpper === "DOMESTIC" ||
          nameUpper === "DOMESTIC"
        ) {
          setMarket("Domestic");
        } else if (
          codeUpper.includes("EXP") ||
          nameUpper.includes("EXPORT") ||
          codeUpper === "EXPORT" ||
          nameUpper === "EXPORT"
        ) {
          setMarket("Export");
        }

        // Fetch SR Types based on marketName for Material Section dropdown
        const marketName = marketNameFromInvoice;
        if (marketName) {
          fetchSRTypesForRaiseComplaint(marketName);
        }

        // Fetch previous complaints (use resolved invoice no. when search was by ODN)
        try {
          let prevUserIdCode = null;
          try {
            const u = localStorage.getItem("user");
            if (u) {
              const user = JSON.parse(u);
              prevUserIdCode = user?.userIdCode ?? user?.UserIdCode ?? null;
            }
          } catch (_) {}

          const invoiceNoForHistory =
            resolveInvoiceNumberFromDetails(response.data) || upperCaseInvoiceNo;

          const previousComplaintsResponse = await complaintAPI.getPreviousComplaintsByInvoice(
            invoiceNoForHistory,
            prevUserIdCode
          );
          //console.log("Previous Complaints Response:", previousComplaintsResponse);

          const list = previousComplaintsResponse.data ?? previousComplaintsResponse.Data;
          if (previousComplaintsResponse.success && list && Array.isArray(list)) {
            const complaints = list;
            setPreviousComplaintsData(complaints);
            setPreviousComplaintsCount(complaints.length);
            // Auto-show grid if only 1 complaint exists
            if (complaints.length === 1) {
              setShowPreviousComplaintsTable(true);
            }
          }
        } catch (prevErr) {
          console.error("Error fetching previous complaints:", prevErr);
          // Don't show error to user for previous complaints - just log it
          setPreviousComplaintsData([]);
          setPreviousComplaintsCount(0);
        }
      } else {
        // Invoice not found - response is not successful or no data
        // Show blocking popup with retry/dashboard options
        console.log("Invoice not found - showing blocking popup");
        const result = await showInvoiceNotFound();

        if (result.isConfirmed) {
          // User clicked "Retry Search" - just close popup, stay on page
          // Invoice number field is still populated for retry
          console.log("User chose to retry search");
        } else if (result.isDismissed) {
          // User clicked "Return to Dashboard" - navigate away
          console.log("User chose to return to dashboard");
          navigate("/dashboard");
        }
      }
    } catch (err) {
      console.error("Error fetching invoice details:", err);
      console.error("Error response:", err.response);
      console.error("Error status:", err.response?.status);
      console.error("Error message:", err.response?.data?.message);

      // Check if this is a "Not Found" error
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.Message ||
        err.message ||
        "";
      const status = err.response?.status;
      const isAccessDenied =
        errorMessage.toLowerCase().includes("assigned customer type") ||
        errorMessage
          .toLowerCase()
          .includes("not authorized to raise a complaint for this business vertical");

      if (isAccessDenied) {
        setInvoiceDetails(null);
        setError(errorMessage);
        await showSwal({
          title: "Access Denied",
          message: errorMessage,
          icon: "error",
          showCloseButton: true,
          confirmText: "OK",
          allowOutsideClick: false,
        });
        return;
      }

      const isNotFoundError =
        status === 404 ||
        errorMessage.toLowerCase().includes("not found") ||
        errorMessage.toLowerCase().includes("invoice not found") ||
        (errorMessage.toLowerCase().includes("invoice number") &&
          errorMessage.toLowerCase().includes("not found"));

      if (isNotFoundError) {
        // Invoice not found - show blocking popup
        console.log("Invoice not found error detected - showing blocking popup");
        const result = await showInvoiceNotFound();

        if (result.isConfirmed) {
          // User clicked "Retry Search"
          console.log("User chose to retry search");
        } else if (result.isDismissed) {
          // User chose to return to dashboard
          console.log("User chose to return to dashboard");
          navigate("/dashboard");
        }
      } else {
        // Other errors (network, server error, etc.) - show generic error
        const msg =
          errorMessage ||
          "An error occurred while fetching invoice details";
        console.log("Showing generic error:", msg);
        showError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    const hadInvoiceDetails = !!invoiceDetails;

    // Reset button clears ALL data values.
    // If invoice details were never loaded (ex: ODN/Invoice not found), keep panels hidden.
    // If invoice details were loaded earlier, keep the existing behavior (render sections with blank fields).

    // Clear invoice search section - invoice number to blank
    setFormData({
      invoiceNo: "",
    });

    setInvoiceDetails(
      hadInvoiceDetails
        ? {
            sapOrder: "",
            SAPOrder: "",
            odnNumber: "",
            ODNNumber: "",
            customerName: "",
            representativeName: "",
            customerAddress: "",
            customerCity: "",
            customerState: "",
            customerPinCode: "",
            customerMobileNo: "",
            customerPhone: "",
            customerEmail: "",
            products: [], // Empty products array keeps product section visible but empty
          }
        : null
    );

    setError("");
    setInvoiceNoError("");

    // Clear previous complaints data
    // Grid is hidden by default, will show when "View All Complaints" is clicked
    setPreviousComplaintsCount(0);
    setPreviousComplaintsData([]);
    setShowPreviousComplaintsTable(false); // Hide grid by default

    // Reset complaint lines to initial state (one empty line)
    setComplaintLines([newLine()]);

    // Clear selected product
    setSelectedProductKey(null);

    // Clear complaint types
    setComplaintTypes([]);

    // Reset customer type and end customer visibility
    setCustomerType("B2B");
    setDealerDistributor("");
    setShowEndCustomer(false);

    // Clear end customer data
    setEndCustomerData({
      endCustomerCode: "",
      name: "",
      address: "",
      city: "",
      state: "",
      countryCode: "",
      zoneCode: "",
      stateCode: "",
      cityCode: "",
      pinCode: "",
      projectSiteDetails: "",
      contactPerson: "",
      mobileNumber: "",
      phoneNo: "",
    });
    setEndCustomerPinCodeError("");

    // Clear invoice files (both uploads)
    setInvoiceCopyFiles([]);
    setSecondaryInvoiceFiles([]);

    // Note: Market, TypeOfComplaint, and all grid/table visibility states are preserved
    // Page structure, headers, and layout remain intact - only data is cleared

    // Clear selected products
    const emptySetClear = new Set();
    selectedProductsRef.current = emptySetClear;
    setSelectedProducts(emptySetClear);
    setSelectAllProducts(false);
    clearExportCommercialBulkState();
  };

  // Handle Select All products
  const handleSelectAllProducts = (checked) => {
    if (checked) {
      let rowsData = [];
      if (dataTableInstanceRef.current) {
        try {
          rowsData = dataTableInstanceRef.current.rows().data().toArray();
        } catch (e) {
          console.warn(
            "Error reading DataTable rows for Select All plant validation; falling back to productDataRef:",
            e
          );
        }
      }
      if (!rowsData.length) {
        rowsData = Object.values(productDataRef.current || {});
      }
      const plantKeys = getMaterialPlantKeysFromRows(rowsData);
      if (plantKeys.size > 1) {
        setSelectAllProducts(false);
        selectAllProductsRef.current = false;
        if (productTableRef.current) {
          const $wrapper = $(productTableRef.current).closest(
            ".dataTables_wrapper, .dt-container"
          );
          $wrapper.find('input[id="select-all-products"]').prop("checked", false);
        }
        setMaterialSectionError(MATERIAL_SELECT_ALL_DIFFERENT_PLANTS_MESSAGE);
        showMaterialSectionError(MATERIAL_SELECT_ALL_DIFFERENT_PLANTS_MESSAGE);
        return;
      }
    }

    setSelectAllProducts(checked);
    if (checked) {
      // Build full list of selectable product keys across ALL pages using DataTables API when available
      let selectableKeys = [];
      if (dataTableInstanceRef.current) {
        try {
          const rowsData = dataTableInstanceRef.current.rows().data().toArray();
          selectableKeys = rowsData
            .filter((row) => row && row._selectable !== false)
            .map((row) =>
              row._rowId != null && row._rowId !== ""
                ? String(row._rowId)
                : null
            )
            .filter(Boolean);
        } catch (e) {
          console.warn(
            "Error building selectableKeys from DataTable rows; falling back to productDataRef:",
            e
          );
        }
      }

      // Fallback: use productDataRef when DataTables API is not available
      if (!selectableKeys.length) {
        selectableKeys = Object.keys(productDataRef.current).filter(
          (k) => productDataRef.current[k]._selectable !== false
        );
      }

      const allProductKeys = new Set(selectableKeys);
      selectedProductsRef.current = allProductKeys;
      setSelectedProducts(allProductKeys);

      // Update all checkboxes in the table (only check selectable ones; disabled stay unchecked)
      if (productTableRef.current && dataTableInstanceRef.current) {
        const $table = $(productTableRef.current);
        $table.find("tbody tr").each(function () {
          const $row = $(this);
          const $cb = $row.find(".product-select-checkbox").first();
          const key = $cb.data("product-key");
          const selectable = $cb.data("selectable");
          const shouldCheck = selectable && allProductKeys.has(key);
          $cb.prop("checked", !!shouldCheck);
          applyMaterialRowSelectionUi($row, key, !!shouldCheck);
        });
      }
    } else {
      const emptySet = new Set();
      selectedProductsRef.current = emptySet;
      setSelectedProducts(emptySet);
      setSelectAllProducts(false);
      selectAllProductsRef.current = false;
      // Uncheck all checkboxes and disable row inputs
      if (productTableRef.current && dataTableInstanceRef.current) {
        const $table = $(productTableRef.current);
        $table.find(".product-select-checkbox").prop("checked", false);
        $table.find("tbody tr").each(function () {
          const $row = $(this);
          const $cb = $row.find(".product-select-checkbox").first();
          const key = $cb.data("product-key");
          applyMaterialRowSelectionUi($row, key, false);
        });
      }
      resetAllMaterialSectionRowsData();
    }
  };

  // Handle individual product selection
  const handleProductSelect = (productKey, checked) => {
    const normalizedKey =
      productKey != null && productKey !== "" ? String(productKey) : "";
    if (!normalizedKey) return;
    let rowsArray = materialRowsRef.current;
    if (dataTableInstanceRef.current) {
      try {
        rowsArray = dataTableInstanceRef.current.rows().data().toArray();
      } catch (_) {
        // fall back to materialRowsRef
      }
    }
    const rowData = resolveMaterialRowDataByKey(
      normalizedKey,
      productDataRef.current,
      rowsArray
    );
    if (rowData) {
      productDataRef.current[normalizedKey] = rowData;
    }
    if (checked && rowData?._selectable === false) return;
    // Always base updates on the latest ref so selections persist across pagination/draws
    const newSelected = new Set(selectedProductsRef.current || selectedProducts);
    if (checked) {
      const plantMismatchMessage = getMaterialPlantMismatchMessage(
        rowData,
        Array.from(newSelected),
        productDataRef.current,
        rowsArray
      );
      if (plantMismatchMessage) {
        revertMaterialRowCheckboxSelection(productTableRef.current, normalizedKey);
        setMaterialSectionError(plantMismatchMessage);
        showMaterialSectionError(plantMismatchMessage);
        return;
      }
      newSelected.add(normalizedKey);
    } else {
      newSelected.delete(normalizedKey);
    }
    selectedProductsRef.current = newSelected; // keep ref in sync so drawCallback sees selection across pagination
    setSelectedProducts(newSelected);

    // Update select all checkbox state: compare with selectable count so Select All reflects "all selectable rows" only
    if (productTableRef.current && dataTableInstanceRef.current) {
      let selectableCount = 0;
      try {
        const rowsData = dataTableInstanceRef.current.rows().data().toArray();
        selectableCount = rowsData.filter((row) => row && row._selectable !== false).length;
      } catch (e) {
        console.warn(
          "Error determining selectableCount from DataTable; falling back to productDataRef:",
          e
        );
        selectableCount = Object.keys(productDataRef.current || {}).filter(
          (k) => productDataRef.current[k]._selectable !== false
        ).length;
      }
      let selectedSelectableCount = 0;
      try {
        const rowsData = dataTableInstanceRef.current.rows().data().toArray();
        selectedSelectableCount = rowsData.filter((row) => {
          if (!row || row._selectable === false) return false;
          const key =
            row._rowId != null && row._rowId !== ""
              ? String(row._rowId)
              : null;
          return !!key && newSelected.has(key);
        }).length;
      } catch (e) {
        selectedSelectableCount = Object.keys(productDataRef.current || {}).filter(
          (k) =>
            productDataRef.current[k]?._selectable !== false && newSelected.has(k)
        ).length;
      }
      let newSelectAll =
        selectableCount > 0 && selectedSelectableCount === selectableCount;
      // Keep parity with visible row checkbox state in Service Request Details view table.
      const $tableForSelectAll = $(productTableRef.current);
      const $visibleSelectableCheckboxes = $tableForSelectAll
        .find("tbody .product-select-checkbox")
        .filter(function () {
          const selectable = $(this).data("selectable");
          return !(selectable === false || selectable === "false");
        });
      if ($visibleSelectableCheckboxes.length > 0) {
        const visibleAllChecked =
          $visibleSelectableCheckboxes.filter(":checked").length ===
          $visibleSelectableCheckboxes.length;
        const visibleSelectableCount = $visibleSelectableCheckboxes.length;
        // If this page contains all selectable rows (common in view mode), trust DOM truth.
        if (visibleSelectableCount === selectableCount) {
          newSelectAll = visibleAllChecked;
        }
      }
      setSelectAllProducts(newSelectAll);
      selectAllProductsRef.current = newSelectAll;
      // Sync Select All checkbox DOM immediately so it reflects individual checkbox changes
      const $wrapper = $(productTableRef.current).closest(".dataTables_wrapper, .dt-container");
      const $selectAllCheckbox = $wrapper.find('input[id="select-all-products"]');
      if ($selectAllCheckbox.length > 0) {
        $selectAllCheckbox.prop("checked", newSelectAll);
      }
    }

    // Toggle row enabled/disabled + highlight (SR Category, SR Qty, etc.)
    if (productTableRef.current) {
      const $table = $(productTableRef.current);
      const $row = $table
        .find(
          '.product-select-checkbox[data-product-key="' + normalizedKey + '"]'
        )
        .closest("tr");
      if ($row && $row.length) {
        if (checked) {
          applyMaterialRowSelectionUi($row, normalizedKey, true);
        } else {
          resetMaterialRowDataInDom(normalizedKey, $row, { keepDisabled: true });
          applyMaterialRowSelectionUi($row, normalizedKey, false);
          setComplaintQty((prev) => ({ ...prev, [normalizedKey]: "" }));

          if (newSelected.size === 0) {
            clearExportCommercialBulkState();
          } else if (
            exportCommercialBulkActiveRef.current &&
            String(normalizedKey) ===
              String(exportCommercialTriggerKeyRef.current)
          ) {
            revertExportCommercialBulkSelection(normalizedKey);
          }

          if (dataTableInstanceRef.current) {
            try {
              dataTableInstanceRef.current.draw(false);
            } catch (_) {
              // ignore draw errors
            }
          }
          if (typeof syncComplaintValueUsdRef.current === "function") {
            syncComplaintValueUsdRef.current();
          }
        }
      }
    }
  };
  handleProductSelectRef.current = handleProductSelect;

  // Clone a material row (Add More): insert new row below via DataTable API for smooth update (no full re-init)
  const cloneMaterialRow = (rowId) => {
    releaseRaiseComplaintActionLocks();
    compQtyMissingCategoryLockActiveRef.current = false;
    paginationScrollLockUntilRef.current = 0;
    if (isMaterialRowAddBlockedForMissingSrCategory(rowId)) {
      setMaterialSectionError(MATERIAL_SR_CATEGORY_REQUIRED_FOR_ADD_MESSAGE);
      showMaterialSectionError(MATERIAL_SR_CATEGORY_REQUIRED_FOR_ADD_MESSAGE);
      return;
    }
    if (isMaterialRowAddDisabledForCategory(rowId)) {
      setMaterialSectionError(MATERIAL_COMMERCIAL_ADD_DISABLED_MESSAGE);
      showMaterialSectionError(MATERIAL_COMMERCIAL_ADD_DISABLED_MESSAGE);
      return;
    }
    if (isMaterialRowAddBlockedForMissingComponentDetails(rowId)) {
      setMaterialSectionError(MATERIAL_COMPONENT_DETAILS_REQUIRED_FOR_ADD_MESSAGE);
      showMaterialSectionError(MATERIAL_COMPONENT_DETAILS_REQUIRED_FOR_ADD_MESSAGE);
      return;
    }
    if (isMaterialRowAddBlockedForNoRemainingSrCategory(rowId)) {
      setMaterialSectionError(MATERIAL_NO_SR_CATEGORY_LEFT_FOR_ADD_MESSAGE);
      showMaterialSectionError(MATERIAL_NO_SR_CATEGORY_LEFT_FOR_ADD_MESSAGE);
      return;
    }
    setMaterialRows((prev) => {
      const idx = prev.findIndex((r) => r._rowId === rowId);
      if (idx < 0) return prev;
      const row = prev[idx];

      // Guard: if sum of existing Comp Qty for this product already equals Invoice Qty, do NOT add a new row.
      // Mikasa Doors / Floors / Sturdo: qty validation not required — allow add more freely.
      const baseCode =
        row.ProductCode || row.productCode || row.Productcode || null;
      const limitQty =
        parseFloat(
          row.InvoiceQuantity ??
            row.invoiceQuantity ??
            row.Invoicequantity ??
            0
        ) || 0;
      if (
        baseCode &&
        limitQty > 0 &&
        !materialRowShowsComponentDetailsIcon(row)
      ) {
        let currentTotal = 0;
        prev.forEach((r) => {
          const codeForRow =
            r.ProductCode || r.productCode || r.Productcode || null;
          if (codeForRow !== baseCode) return;
          const qtyKey =
            r._rowId ||
            r.ProductCode ||
            r.productCode ||
            r.Productcode ||
            null;
          if (!qtyKey) return;
          const v = parseFloat(
            (complaintQtyRef.current?.[qtyKey] ?? "").toString().trim()
          );
          if (!Number.isNaN(v) && v > 0) {
            currentTotal += v;
          }
        });
        if (currentTotal >= limitQty - 1e-6) {
          setMaterialSectionError(
            "Total Comp Qty for this item already equals allowed quantity. Cannot add more rows."
          );
          showMaterialSectionError(
            "Total Comp Qty for this item already equals allowed quantity. Cannot add more rows."
          );
          return prev;
        }
      }

      const newRowId = rowId + "_clone_" + Date.now();
      const newRow = { ...row, _rowId: newRowId, _isClone: true };
      // Cloned rows start empty; SR Category already used on sibling rows of this product is hidden.
      categoryRef.current[newRowId] = "";
      categoryDescRef.current[newRowId] = "";
      // Start cloned row with empty Comp Qty so user must enter fresh quantity
      complaintQtyRef.current[newRowId] = "";
      productDataRef.current[newRowId] = { ...(productDataRef.current[rowId] || row), _rowId: newRowId, _isClone: true };
      setSelectedProducts((prevSel) => {
        const next = new Set([...prevSel, newRowId]);
        selectedProductsRef.current = next;
        return next;
      });
      const nextRows = [...prev.slice(0, idx + 1), newRow, ...prev.slice(idx + 1)];
      skipNextMaterialTableReinitRef.current = true;
      const api = dataTableInstanceRef.current;
      if (api && typeof api.rows === "function" && typeof api.clear === "function") {
        try {
          api.clear();
          api.rows.add(nextRows);
          api.draw(false);
          setTimeout(() => initializeCategorySelect2(), 0);
        } catch (e) {
          console.warn("DataTable clone row:", e);
        }
      }
      return nextRows;
    });
  };

  // Remove a cloned material row via DataTable API for smooth update
  const removeMaterialRow = (rowId) => {
    setMaterialRows((prev) => {
      const idx = prev.findIndex((r) => r._rowId === rowId);
      if (idx < 0) return prev;
      const filtered = prev.filter((r) => r._rowId !== rowId);
      delete categoryRef.current[rowId];
      delete complaintQtyRef.current[rowId];
      delete categoryDescRef.current[rowId];
      delete productDataRef.current[rowId];
      delete componentDetailsRef.current[rowId];
      setComplaintQty((q) => {
        const next = { ...q };
        delete next[rowId];
        return next;
      });
      // If the removed row was selected, remove it from selection set so validations don't fail.
      setSelectedProducts((prevSel) => {
        if (!prevSel || !prevSel.size || !prevSel.has(rowId)) return prevSel;
        const nextSel = new Set(prevSel);
        nextSel.delete(rowId);
        selectedProductsRef.current = nextSel;
        return nextSel;
      });
      skipNextMaterialTableReinitRef.current = true;
      const api = dataTableInstanceRef.current;
      if (api && typeof api.rows === "function" && typeof api.clear === "function") {
        try {
          api.clear();
          api.rows.add(filtered);
          api.draw(false);
          setTimeout(() => initializeCategorySelect2(), 0);
        } catch (e) {
          console.warn("DataTable remove row:", e);
        }
      }
      return filtered;
    });
  };

  // Copy Invoice Quantity to Complaint Quantity for selected products
  const handleCopyInvoiceQuantity = () => {
    if (selectedProducts.size === 0) {
      Swal.fire({
        title: "No Products Selected",
        text: "Please select at least one product to copy invoice quantity.",
        icon: "warning",
        confirmButtonColor: "#007bff",
      });
      return;
    }

    // Copy invoice quantity to complaint quantity for selected products
    selectedProducts.forEach((productKey) => {
      const product = productDataRef.current[productKey];

      if (product) {
        const invoiceQty = product?.Quantity ?? product?.quantity ?? product?.Quantity ?? 0;
        const key = productKey;

        // Update complaint quantity
        complaintQtyRef.current = {
          ...complaintQtyRef.current,
          [key]: invoiceQty.toString(),
        };
        setComplaintQty((prev) => ({ ...prev, [key]: invoiceQty.toString() }));

        // Update the input field in the table
        if (productTableRef.current) {
          $(productTableRef.current)
            .find(`.complaint-qty-input[data-key="${key}"]`)
            .val(invoiceQty.toString());
        }
      }
    });

    // Redraw the table to update Comp Value column
    if (dataTableInstanceRef.current) {
      dataTableInstanceRef.current.draw(false);
    }

    Swal.fire({
      title: "Success!",
      text: `Invoice quantity copied to complaint quantity for ${selectedProducts.size} product(s).`,
      icon: "success",
      confirmButtonColor: "#4caf50",
      timer: 2000,
      showConfirmButton: false,
    });
  };

  // Helper function to show Swal with scroll prevention
  const showSwalWithScrollPrevention = (swalConfig) => {
    const {
      lockToTop: rawLockToTop,
      anchorScrollY: rawAnchorScrollY,
      ...safeSwalConfig
    } = swalConfig || {};
    const lockToTop = rawLockToTop !== false;
    const hasFixedAnchor =
      !lockToTop &&
      Number.isFinite(rawAnchorScrollY) &&
      rawAnchorScrollY >= 0;
    const fixedAnchorScrollY = hasFixedAnchor ? rawAnchorScrollY : null;
    // Store scroll position (top or current based on lockToTop)
    let savedScrollPosition = lockToTop
      ? 0
      : (fixedAnchorScrollY ?? (window.scrollY || window.pageYOffset || 0));
    let scrollLocked = false;
    let scrollMonitorInterval = null;
    
    // Prevent scroll event handler
    const preventScrollHandler = (e) => {
      if (!scrollLocked) return;
      
      // If it's a wheel event, enable scroll first to allow mouse wheel scrolling
      if (e.type === 'wheel') {
        enableScroll();
        return; // Don't prevent, allow the wheel event to proceed
      }
      
      e.preventDefault();
      e.stopPropagation();
      // Force scroll back to saved position (top)
      window.scrollTo({ top: savedScrollPosition, behavior: 'instant' });
      return false;
    };
    
    // Function to disable scrolling completely
    const disableScroll = () => {
      scrollLocked = true;
      // Store lock position (or fixed anchor from click-time)
      const scrollY = hasFixedAnchor
        ? fixedAnchorScrollY
        : (window.scrollY || window.pageYOffset);
      savedScrollPosition = scrollY;
      
      // Lock body position
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      
      // Prevent all scroll events
      window.addEventListener('scroll', preventScrollHandler, { passive: false, capture: true });
      window.addEventListener('wheel', preventScrollHandler, { passive: false, capture: true });
      window.addEventListener('touchmove', preventScrollHandler, { passive: false, capture: true });
      document.addEventListener('scroll', preventScrollHandler, { passive: false, capture: true });
    };
    
    // Function to enable scrolling
    const enableScroll = () => {
      scrollLocked = false;
      // Stop scroll monitor
      if (scrollMonitorInterval) {
        clearInterval(scrollMonitorInterval);
        scrollMonitorInterval = null;
      }
      // Remove all scroll prevention
      window.removeEventListener('scroll', preventScrollHandler, { capture: true });
      window.removeEventListener('wheel', preventScrollHandler, { capture: true });
      window.removeEventListener('touchmove', preventScrollHandler, { capture: true });
      document.removeEventListener('scroll', preventScrollHandler, { capture: true });
      
      // Restore body styles
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      
      // Keep current lock position when re-enabling
      requestAnimationFrame(() => {
        window.scrollTo({ top: savedScrollPosition, behavior: 'instant' });
      });
      
      // Remove event listeners for re-enabling
      document.removeEventListener('click', enableScrollHandler);
      document.removeEventListener('wheel', enableScrollHandler);
      document.removeEventListener('touchmove', enableScrollHandler);
      document.removeEventListener('keydown', enableScrollHandler);
    };
    
    // Handler to re-enable scroll on user interaction
    const enableScrollHandler = () => {
      enableScroll();
    };
    
    // Move to top only when explicitly requested
    if (lockToTop) {
      window.scrollTo({ top: 0, behavior: 'instant' });
      savedScrollPosition = 0;
    } else {
      savedScrollPosition = fixedAnchorScrollY ?? (window.scrollY || window.pageYOffset || savedScrollPosition);
    }
    disableScroll(); // Lock scroll before swal opens
    
    // Monitor and force scroll position continuously (keep at top)
    const startScrollMonitor = () => {
      if (scrollMonitorInterval) clearInterval(scrollMonitorInterval);
      scrollMonitorInterval = setInterval(() => {
        if (scrollLocked) {
          const currentScroll = window.scrollY || window.pageYOffset;
          // If scroll is not at top (0), force it back to top
          if (Math.abs(currentScroll - savedScrollPosition) > 1) {
            window.scrollTo({ top: savedScrollPosition, behavior: 'instant' });
          }
        }
      }, 10);
    };
    
    const stopScrollMonitor = () => {
      if (scrollMonitorInterval) {
        clearInterval(scrollMonitorInterval);
        scrollMonitorInterval = null;
      }
    };
    
    startScrollMonitor();
    
    const swalPromise = Swal.fire({
      ...safeSwalConfig,
      scrollbarPadding: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        // Ensure scroll stays locked when swal opens
        requestAnimationFrame(() => {
          disableScroll();
          startScrollMonitor();
        });
        // Intercept confirm button click to lock scroll immediately
        setTimeout(() => {
          const confirmButton = document.querySelector('.swal2-confirm');
          if (confirmButton) {
            confirmButton.addEventListener('click', (e) => {
              // Lock scroll immediately when OK button is clicked
              if (lockToTop) {
                savedScrollPosition = 0;
                window.scrollTo({ top: 0, behavior: 'instant' });
              } else {
                savedScrollPosition = fixedAnchorScrollY ?? (window.scrollY || window.pageYOffset || savedScrollPosition);
              }
              disableScroll();
              startScrollMonitor();
            }, { capture: true, once: true });
          }
        }, 100);
      },
      // Disable scroll BEFORE swal closes (when OK button is clicked)
      willClose: () => {
        // Immediately disable scrolling before swal closes - this happens when OK is clicked
        if (lockToTop) {
          savedScrollPosition = 0;
          window.scrollTo({ top: 0, behavior: 'instant' });
        } else {
          savedScrollPosition = fixedAnchorScrollY ?? (window.scrollY || window.pageYOffset || savedScrollPosition);
        }
        disableScroll();
        startScrollMonitor();
      },
      didClose: () => {
        enableScroll();
        stopScrollMonitor();
      },
    });
    
    // Clean up monitor when promise resolves
    swalPromise.finally(() => {
      stopScrollMonitor();
      enableScroll();
    });
    
    return swalPromise;
  };

  const stripDigitsFromEndConsumerText = (value) =>
    String(value ?? "").replace(/\d/g, "");

  const handleEndConsumerNoDigitKeyDown = (e) => {
    const key = e.key;
    if (
      key === "Backspace" ||
      key === "Tab" ||
      key === "Enter" ||
      key === "ArrowLeft" ||
      key === "ArrowRight" ||
      key === "Delete" ||
      key === "Home" ||
      key === "End"
    ) {
      return;
    }
    if (e.ctrlKey || e.metaKey) return;
    if (/^[0-9]$/.test(key)) {
      e.preventDefault();
    }
  };

  const validateEndConsumerNoDigitFields = ({ lockToTop = true, anchorScrollY = null } = {}) => {
    const noDigitFields = [
      { key: "countryCode", label: "Country" },
      { key: "zoneCode", label: "Zone/Province" },
      { key: "stateCode", label: "State/City" },
    ];
    const invalid = noDigitFields
      .filter(({ key }) => /\d/.test(String(endCustomerData[key] ?? "")))
      .map(({ label }) => label);

    if (invalid.length === 0) return true;

    showSwalWithScrollPrevention({
      title: "Invalid Input",
      html: `<div style="text-align:left">The following fields must not contain numbers:<ul>${invalid
        .map((item) => `<li>${item}</li>`)
        .join("")}</ul></div>`,
      icon: "warning",
      confirmButtonColor: "#ff9800",
      lockToTop,
      anchorScrollY,
    });
    return false;
  };

  // Look up end consumer by mobile; if found auto-fill details, else user enters new
  const handleEndConsumerMobileLookup = async () => {
    const mobile = (endCustomerData.mobileNumber || "").trim().replace(/\D/g, "");
    if (!mobile || mobile.length !== 10) return;
    setEndConsumerLookupLoading(true);
    try {
      const res = await complaintAPI.getEndConsumerByMobile(mobile);
      const data = res?.data;
      if (data && (data.Name || data.name || data.EndCustomerCode || data.endCustomerCode)) {
        const countryCode = data.CountryCode || data.countryCode || "";
        const countryName = data.CountryName || data.countryName || countryCode;
        const zoneName = data.ZoneName || data.zoneName || "";
        const territoryName = data.TerritoryName || data.territoryName || "";
        setEndCustomerData((prev) => ({
          ...prev,
          endCustomerCode: data.EndCustomerCode || data.endCustomerCode || "",
          name: data.Name || data.name || "",
          address: data.Address || data.address || "",
          city: data.City || data.city || "",
          pinCode: String(data.PinCode || data.pinCode || "")
            .replace(/\D/g, "")
            .slice(0, 6),
          phoneNo: data.Phone || data.phone || data.MobileNo || data.mobileNo || "",
          countryCode: countryName || countryCode || "",
          zoneCode: zoneName,
          stateCode: territoryName,
        }));
        setEndCustomerPinCodeError("");
        Swal.fire({ icon: "success", title: "Found", text: "End consumer details loaded. You can edit if needed.", timer: 2000, showConfirmButton: false });
      } else {
        setEndCustomerData((prev) => ({
          ...prev,
          endCustomerCode: "",
          name: "",
          address: "",
          city: "",
          countryCode: "",
          zoneCode: "",
          stateCode: "",
          cityCode: "",
          pinCode: "",
          phoneNo: "",
        }));
        Swal.fire({ icon: "info", title: "No record found", text: "Enter new end consumer details below." });
      }
    } catch (e) {
      console.error("End consumer lookup failed:", e);
      Swal.fire({ icon: "error", title: "Lookup failed", text: "Could not search by mobile. Enter details manually." });
    } finally {
      setEndConsumerLookupLoading(false);
    }
  };

  // Validate End Consumer card before moving ahead
  const validateEndCustomerDetails = ({ lockToTop = true, anchorScrollY = null } = {}) => {
    // First, check if Customer Type is selected (mandatory)
    if (!customerType) {
      showSwalWithScrollPrevention({
        title: "Missing Required Details",
        html: `<div style="text-align:left">Please select a Customer Type (Channel Partner or End Consumer) to proceed.</div>`,
        icon: "warning",
        confirmButtonColor: "#ff9800",
        lockToTop,
        anchorScrollY,
      });
      return false;
    }

    // If B2B is selected, End Consumer details are optional
    if (customerType === "B2B") {
      // If End Consumer section is shown, validate it, otherwise it's optional
      if (!showEndCustomer) return true;
    }

    // If B2C is selected, End Consumer details are mandatory
    if (customerType === "B2C") {
      if (!showEndCustomer) {
        showSwalWithScrollPrevention({
          title: "Missing Required Details",
          html: `<div style="text-align:left">End Consumer Details are mandatory for End Consumer customer type. Please add End Consumer details.</div>`,
          icon: "warning",
          confirmButtonColor: "#ff9800",
          lockToTop,
          anchorScrollY,
        });
        return false;
      }
      // Secondary Invoice Upload is mandatory for B2C
      if (secondaryInvoiceFiles.length === 0) {
        showSwalWithScrollPrevention({
          title: "Missing Required Details",
          html: `<div style="text-align:left">Secondary Invoice Upload is mandatory for End Consumer customers.</div>`,
          icon: "warning",
          confirmButtonColor: "#ff9800",
          lockToTop,
          anchorScrollY,
        });
        return false;
      }
    }

    // Validate End Consumer fields if section is shown
    if (!showEndCustomer) return true;

    const pinCode = String(endCustomerData.pinCode ?? "").trim();
    const pinCodeValid = /^\d{6}$/.test(pinCode);

    // For B2B, End Consumer details are optional; if postal code is entered, it must be 6 digits
    if (customerType === "B2B") {
      if (!validateEndConsumerNoDigitFields({ lockToTop, anchorScrollY })) {
        return false;
      }
      if (pinCode && !pinCodeValid) {
        setEndCustomerPinCodeError("Postal Code must be exactly 6 digits (numbers only).");
        showSwalWithScrollPrevention({
          title: "Invalid Postal Code",
          html: `<div style="text-align:left">Postal Code must be exactly 6 digits (numbers only).</div>`,
          icon: "warning",
          confirmButtonColor: "#ff9800",
          lockToTop,
          anchorScrollY,
        });
        return false;
      }
      return true;
    }

    // For B2C, all End Consumer fields are mandatory (Mobile Number is the searchable key)
    const requiredFields = [
      { key: "mobileNumber", label: "Mobile Number" },
      { key: "name", label: "Consumer Name" },
      { key: "address", label: "Consumer Address" },
      { key: "countryCode", label: "Country" },
      { key: "zoneCode", label: "Zone/Province" },
      { key: "stateCode", label: "State/City" },
      { key: "pinCode", label: "Postal Code" },
    ];

    const missing = requiredFields
      .filter(({ key }) => !String(endCustomerData[key] ?? "").trim())
      .map(({ label }) => label);

    // Secondary Invoice is validated above with dedicated message for B2C

    if (missing.length > 0) {
      showSwalWithScrollPrevention({
        title: "Missing Required Details",
        html: `<div style="text-align:left">Please fill the following before continuing:<ul>${missing
          .map((item) => `<li>${item}</li>`)
          .join("")}</ul></div>`,
        icon: "warning",
        confirmButtonColor: "#ff9800",
        lockToTop,
        anchorScrollY,
      });
      return false;
    }

    if (!validateEndConsumerNoDigitFields({ lockToTop, anchorScrollY })) {
      return false;
    }

    const mobile = String(endCustomerData.mobileNumber ?? "").trim();
    if (!/^\d{10}$/.test(mobile)) {
      showSwalWithScrollPrevention({
        title: "Invalid Mobile Number",
        html: `<div style="text-align:left">Mobile Number must be exactly 10 digits (numbers only).</div>`,
        icon: "warning",
        confirmButtonColor: "#ff9800",
        lockToTop,
        anchorScrollY,
      });
      return false;
    }

    if (!pinCodeValid) {
      setEndCustomerPinCodeError("Postal Code must be exactly 6 digits (numbers only).");
      showSwalWithScrollPrevention({
        title: "Invalid Postal Code",
        html: `<div style="text-align:left">Postal Code must be exactly 6 digits (numbers only).</div>`,
        icon: "warning",
        confirmButtonColor: "#ff9800",
        lockToTop,
        anchorScrollY,
      });
      return false;
    }

    setEndCustomerPinCodeError("");
    return true;
  };

  // Helper function to safely parse and format dates for API
  const parseDateForAPI = (dateValue) => {
    if (!dateValue) return null;
    
    // If it's already a Date object
    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? null : dateValue.toISOString();
    }
    
    // If it's a string, try to parse it
    if (typeof dateValue === 'string') {
      const trimmed = dateValue.trim();
      if (!trimmed) return null;
      
      // Try parsing as ISO string first
      let parsed = new Date(trimmed);
      if (!isNaN(parsed.getTime())) {
        return parsed.toISOString();
      }
      
      // Try parsing DD/MM/YYYY format
      const slashMatch = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (slashMatch) {
        parsed = new Date(`${slashMatch[3]}-${slashMatch[2]}-${slashMatch[1]}`);
        if (!isNaN(parsed.getTime())) {
          return parsed.toISOString();
        }
      }
      
      // Try parsing DD-MM-YYYY format
      const dashMatch = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/);
      if (dashMatch) {
        parsed = new Date(`${dashMatch[3]}-${dashMatch[2]}-${dashMatch[1]}`);
        if (!isNaN(parsed.getTime())) {
          return parsed.toISOString();
        }
      }
      
      // Try using the existing toISODate helper
      const isoDate = toISODate(trimmed);
      if (isoDate) {
        parsed = new Date(isoDate);
        if (!isNaN(parsed.getTime())) {
          return parsed.toISOString();
        }
      }
    }
    
    return null;
  };

  // Shared logic: validate + build payload + save draft via API. Used by both Save Draft and Next.
  // Caller must set setSavingDraft(true/false). Returns { success: true, srCode } or { success: false }.
  const performSaveDraft = async ({ lockValidationSwalToTop = true, validationAnchorScrollY = null } = {}) => {
    try {
      releaseRaiseComplaintActionLocks();
      syncMaterialSectionRefsFromDom();
      if (!invoiceDetails || !formData.invoiceNo) {
        Swal.fire({
          title: "Error",
          text: "Please search for an invoice first",
          icon: "error",
          confirmButtonColor: "#ff9800",
          allowOutsideClick: false,
        });
        return { success: false };
      }

      const invoiceNumberValue =
        invoiceDetails?.InvoiceNo ||
        invoiceDetails?.invoiceNo ||
        invoiceDetails?.invoiceNumber ||
        formData.invoiceNo ||
        null;

      if (!invoiceNumberValue) {
        Swal.fire({
          title: "Error",
          text: "Invoice number is required. Please search for an invoice first.",
          icon: "error",
          confirmButtonColor: "#ff9800",
          allowOutsideClick: false,
        });
        return { success: false };
      }

      // Authoritative geo check: SAPInvoiceHeader by invoice/ODN (not draft UI fields).
      try {
        const geoValRes = await complaintAPI.validateInitiatorInvoiceGeo(invoiceNumberValue);
        const geoValOk = geoValRes?.success ?? geoValRes?.Success ?? false;
        if (!geoValOk) {
          const geoValMsg =
            geoValRes?.message ??
            geoValRes?.Message ??
            INITIATOR_GEO_MISMATCH_MESSAGE;
          showSwalWithScrollPrevention({
            title: "Cannot Submit",
            text: geoValMsg,
            icon: "warning",
            confirmButtonColor: "#ff9800",
            lockToTop: lockValidationSwalToTop,
            anchorScrollY: validationAnchorScrollY,
          });
          return { success: false };
        }
      } catch (geoValErr) {
        const geoValMsg =
          geoValErr?.response?.data?.message ??
          geoValErr?.response?.data?.Message ??
          "Could not validate invoice geography. Please try again or contact your administrator.";
        showSwalWithScrollPrevention({
          title: "Cannot Submit",
          text: geoValMsg,
          icon: "warning",
          confirmButtonColor: "#ff9800",
          lockToTop: lockValidationSwalToTop,
          anchorScrollY: validationAnchorScrollY,
        });
        return { success: false };
      }

      if (!invoiceCopyFiles || invoiceCopyFiles.length === 0) {
        showSwalWithScrollPrevention({
          title: "Missing Required Details",
          html: `<div style="text-align:left">Invoice Copy Upload is mandatory. Please attach at least one invoice file (PDF, JPG, or PNG) to proceed.</div>`,
          icon: "warning",
          confirmButtonColor: "#ff9800",
          lockToTop: lockValidationSwalToTop,
          anchorScrollY: validationAnchorScrollY,
        });
        return { success: false };
      }

      // At least one material row must be selected to save any complaint
      if (!selectedProducts || selectedProducts.size === 0) {
        showSwalWithScrollPrevention({
          title: "No Material Selected",
          text: "Please select at least one material to proceed.",
          icon: "warning",
          confirmButtonColor: "#ff9800",
          lockToTop: lockValidationSwalToTop,
          anchorScrollY: validationAnchorScrollY,
        });
        setMaterialSectionError("Please select at least one material to proceed.");
        return { success: false };
      }

      // Domestic/Export + Business Vertical vs logged-in user (selected lines)
      const selectedMaterialRows = Array.from(selectedProducts)
        .map((key) => productDataRef.current?.[key])
        .filter(Boolean);
      const accessError = validateInvoiceAccessForUser(
        invoiceDetails,
        null,
        selectedMaterialRows
      );
      if (accessError) {
        showSwalWithScrollPrevention({
          title: "Cannot Submit",
          text: accessError,
          icon: "warning",
          confirmButtonColor: "#ff9800",
          lockToTop: lockValidationSwalToTop,
          anchorScrollY: validationAnchorScrollY,
        });
        return { success: false };
      }

      const selectedPlantKeys = getMaterialPlantKeysForSelection(
        Array.from(selectedProducts),
        productDataRef.current,
        materialRowsRef.current?.length
          ? materialRowsRef.current
          : invoiceDetails?.products
      );
      if (selectedPlantKeys.size > 1) {
        showSwalWithScrollPrevention({
          title: "Different Manufacturing Plants",
          text: MATERIAL_DIFFERENT_PLANT_MESSAGE,
          icon: "warning",
          confirmButtonColor: "#ff9800",
          lockToTop: lockValidationSwalToTop,
          anchorScrollY: validationAnchorScrollY,
        });
        setMaterialSectionError(MATERIAL_DIFFERENT_PLANT_MESSAGE);
        return { success: false };
      }

      // SR Category must be selected for each selected material row
      const missingSrCategory = [];
      selectedProducts.forEach((productKey) => {
        const v = (categoryRef.current?.[productKey] || "").toString().trim();
        if (!v) missingSrCategory.push(productKey);
      });
      if (missingSrCategory.length > 0) {
        const firstKey = missingSrCategory[0];
        showSwalWithScrollPrevention({
          title: "Missing Required Details",
          html: `<div style="text-align:left">Please select an SR Category for each chosen material to proceed.</div>`,
          icon: "warning",
          confirmButtonColor: "#ff9800",
          lockToTop: lockValidationSwalToTop,
          anchorScrollY: validationAnchorScrollY,
        }).then(() => {
          requestAnimationFrame(() => {
            const inputs = productTableRef.current?.querySelectorAll(".category-select");
            if (inputs) {
              for (let i = 0; i < inputs.length; i++) {
                if ((inputs[i].getAttribute("data-key") || "") === firstKey) {
                  inputs[i].focus({ preventScroll: false });
                  inputs[i].scrollIntoView({ behavior: "smooth", block: "center" });
                  break;
                }
              }
            }
          });
        });
        setMaterialSectionError("Please select an SR Category for each chosen material to proceed.");
        return { success: false };
      }

      // Comp Qty must be entered for each selected material row (SR Category selected but Comp Qty not entered).
      // Mikasa Doors / Floors / Sturdo: qty is hidden + auto-filled — skip this validation.
      const missingQty = [];
      selectedProducts.forEach((productKey) => {
        const row =
          productDataRef.current[productKey] ||
          (materialRowsRef.current || []).find(
            (r) => String(r._rowId || r.ProductCode || r.productCode) === String(productKey)
          );
        if (materialRowShowsComponentDetailsIcon(row)) return;
        const qtyVal = (complaintQtyRef.current?.[productKey] ?? "").toString().trim();
        const num = parseFloat(qtyVal);
        if (qtyVal === "" || Number.isNaN(num) || num <= 0) {
          missingQty.push(productKey);
        }
      });
      if (missingQty.length > 0) {
        const firstKey = missingQty[0];
        showSwalWithScrollPrevention({
          title: "Comp Qty Required",
          html: `<div style="text-align:left">Please enter a value greater than 0 in Comp Quantity for all selected materials to proceed.</div>`,
          icon: "warning",
          confirmButtonColor: "#ff9800",
          lockToTop: lockValidationSwalToTop,
          anchorScrollY: validationAnchorScrollY,
        }).then(() => {
          requestAnimationFrame(() => {
            const inputs = productTableRef.current?.querySelectorAll(".complaint-qty-input");
            if (inputs) {
              for (let i = 0; i < inputs.length; i++) {
                if ((inputs[i].getAttribute("data-key") || "") === firstKey) {
                  inputs[i].focus({ preventScroll: false });
                  inputs[i].scrollIntoView({ behavior: "smooth", block: "center" });
                  break;
                }
              }
            }
          });
        });
        setMaterialSectionError("Please enter a value greater than 0 in Comp Quantity for all selected materials to proceed.");
        return { success: false };
      }

      // Sum of Comp Qty per product (by ProductCode) must not exceed Invoice Qty.
      // Skipped for Mikasa Doors / Floors / Sturdo (qty hidden / not validated).
      // This validation is skipped for existing draft SRs (when draftSrCode is present), so that saved draft rows for a product
      // do not start failing if data has changed for that product.
      if (!draftSrCode) {
        const rowsToCheck =
          materialRows.length > 0
            ? materialRows
            : (invoiceDetails?.products || []).map((p, i) => ({
                ...p,
                _rowId: (p.ProductCode || p.productCode || "r") + "_row" + i,
                _isClone: false,
              }));
        const compQtyByProduct = {}; // baseCode -> { limitQty, totalCompQty }
        selectedProducts.forEach((productKey) => {
          const row =
            productDataRef.current[productKey] ||
            rowsToCheck.find(
              (r) => (r._rowId || r.ProductCode || r.productCode) === productKey
            );
          if (!row) return;
          if (materialRowShowsComponentDetailsIcon(row)) return;
          const baseCode =
            (row.ProductCode ||
              row.productCode ||
              row.Productcode ||
              "").toString().trim() || productKey;
          const limitQty =
            parseFloat(
              row.InvoiceQuantity ??
                row.invoiceQuantity ??
                row.Invoicequantity ??
                0
            ) || 0;
          const compQty =
            parseFloat(
              (complaintQtyRef.current[productKey] ?? "")
                .toString()
                .trim()
            ) || 0;
          if (!compQtyByProduct[baseCode]) {
            compQtyByProduct[baseCode] = { limitQty, totalCompQty: 0 };
          }
          compQtyByProduct[baseCode].totalCompQty += compQty;
        });
        const exceedsLimit = Object.entries(compQtyByProduct).find(
          ([_, { limitQty, totalCompQty }]) => totalCompQty > limitQty
        );
        if (exceedsLimit) {
          const [baseCode, { limitQty, totalCompQty }] = exceedsLimit;
          Swal.fire({
            title: "Comp Qty Exceeds Allowed Qty",
            html: `<div style="text-align:left">Sum of Comp Qty (${totalCompQty}) for this product cannot be greater than invoice qty (${limitQty}). Please reduce Comp Qty for the selected rows.</div>`,
            icon: "warning",
            confirmButtonColor: "#ff9800",
          });
          setMaterialSectionError(
            `Sum of Comp Qty cannot exceed invoice qty (${limitQty}) for the selected product. Current total Comp Qty: ${totalCompQty}.`
          );
          return { success: false };
        }
      }

      // When Sample Sent is Yes, all Sample Information fields are mandatory (Save Draft and Next)
      if (sampleInfo?.sampleSent === "Yes") {
        const courierAgency = (sampleInfo.courierAgency || "").toString().trim();
        const courierDate = (sampleInfo.courierDate || "").toString().trim();
        const docketNo = (sampleInfo.docketNo || "").toString().trim();
        const sentTo = (sampleInfo.sentTo || "").toString().trim();
        const sampleDetails = (sampleInfo.sampleDetails || "").toString().trim();
        const missing = [];
        if (!courierAgency) missing.push("Courier Agency");
        if (!courierDate) missing.push("Courier Date");
        if (!docketNo) missing.push("Docket No.");
        if (!sentTo) missing.push("Sent To");
        if (!sampleDetails) missing.push("Sample Details");
        if (missing.length > 0) {
          Swal.fire({
            title: "Sample Information Required",
            html: `<div style="text-align:left">Sample Sent is "Yes". Please fill all sample details:<br/><ul style="margin: 0.5rem 0 0 1rem;">${missing.map((m) => `<li>${m}</li>`).join("")}</ul></div>`,
            icon: "warning",
            confirmButtonColor: "#ff9800",
          });
          return { success: false };
        }
      }

      // Collect invoice details from material rows (order preserved; includes cloned rows)
      const invoiceDetailsList = [];
      const rowsToSave =
        materialRows.length > 0
          ? materialRows
          : (invoiceDetails?.products || []).map((p, i) => ({
              ...p,
              _rowId: (p.ProductCode || p.productCode || "r") + "_row" + i,
              _isClone: false,
            }));

      // Ensure each selected row's Component Details from LocalStorage is in the in-memory ref before final save
      try {
        const stored = readComponentDetailsLocalStore();
        rowsToSave.forEach((row) => {
          const productKey = row._rowId || row?.ProductCode || row?.productCode;
          if (!productKey || !selectedProducts.has(productKey)) return;
          if (componentDetailsRef.current[productKey]) return;
          const rowStoreKey = getComponentDetailsRowStorageKey(productKey);
          const fromStore = stored[rowStoreKey];
          if (fromStore && typeof fromStore === "object") {
            componentDetailsRef.current[productKey] = normalizeComponentDetailsStorage(fromStore);
          }
        });
      } catch (_) {}

      rowsToSave.forEach((row) => {
        const productKey = row._rowId || row?.ProductCode || row?.productCode;
        // Only save rows whose checkbox is selected
        if (!selectedProducts.has(productKey)) {
          return;
        }
        const product = productDataRef.current[productKey] || row;
        if (product) {
          const complaintQty = complaintQtyRef.current[productKey] || 0;
          const category = categoryRef.current[productKey] || "";
          const componentStorage = normalizeComponentDetailsStorage(
            componentDetailsRef.current[productKey] || {}
          );
          const componentRows = (componentStorage.rows || []).filter(
            isComponentDetailRowFilled
          );
          const componentData = componentRows[0] || {};
          const componentDetailsApi = componentRows.map(mapComponentDetailRowToApi);
          const totalComponentValue = componentRows.reduce(
            (sum, r) => sum + (parseFloat(r.componentValue) || 0),
            0
          );
          
          // Map fields from API response (which uses ProductCategory, PlantName, ProductName, etc.)
          // to the expected draft save format - Property names must match C# InvoiceDetailItem model exactly
          // SRTypeCode = actual SR Category from Material Section dropdown for this row (categoryRef)
          // Component fields: prefer per-row Component Details (modal → localStorage/ref), then SAP product
          invoiceDetailsList.push({
            SRTypeCode: category || null,
            BusinessVertical:
              (product.BusinessVertical || product.businessVertical || "").trim() || null,
            // ProductCategory from API maps to ProductCategoryDescription; use per-row category first so it does not change when saving draft/final
            ProductCategoryId: product.ProductCategoryId || product.productCategoryId || null,
            ProductCategoryDescription:
              (product.ProductCategory || product.productCategory || product.ProductCategoryDescription || product.productCategoryDescription || "").trim() ||
              commercialDetails.productCategory?.trim() ||
              null,
            // PlantName from API maps to ProductPlantDescription
            ProductPlantId: product.ProductPlantId || product.productPlantId || product.PlantId || product.plantId || null,
            ProductPlantDescription: product.PlantName || product.plantName || product.ProductPlantDescription || product.productPlantDescription || null,
            ProductCode: product.ProductCode || product.productCode || null,
            // ProductName from API is actually MaterialDescription
            MaterialDescription: product.ProductName || product.productName || product.MaterialDescription || product.materialDescription || null,
            BatchNo: product.BatchNo || product.batchNo || product.BATCHNO || null,
            MfgDate: product.MFGDate || product.mfgDate || product.MfgDate || null,
            Division: product.Division || product.division || null,
            DivisionName: product.DivisionName || product.divisionName || null,
            DistChannel: product.DistChannel || product.distChannel || null,
            MaterialGroup: componentData.materialGroupCode || product.MaterialGroup || product.materialGroup || null,
            MaterialGroupDesc: componentData.materialGroupDesc || product.MaterialGroupDesc || product.materialGroupDesc || null,
            MaterialType: product.MaterialType || product.materialType || null,
            MaterialTypeDesc: product.MaterialTypeDesc || product.materialTypeDesc || null,
            DesignNo: product.DesignNo || product.designNo || product.DesignNumber || product.designNumber || null,
            DesignDescription: product.DesignDescription || product.designDescription || null,
            UOM: product.UOM || product.uom || null,
            Territory: product.Territory || product.territory || null,
            TerritoryName: product.TerritoryName || product.territoryName || null,
            WmsApplicableFlag: product.WmsApplicableFlag || product.wmsApplicableFlag || null,
            PH1: product.PH1 || product.ph1 || null,
            ProductDescription: product.ProductDescription || product.productDescription || null,
            PH2: product.PH2 || product.ph2 || null,
            ProductBrand: product.ProductBrand || product.productBrand || null,
            PH3: product.PH3 || product.ph3 || null,
            ProductFinish: product.ProductFinish || product.productFinish || null,
            PH4: product.PH4 || product.ph4 || null,
            ProductThickness: product.ProductThickness || product.productThickness ? parseFloat(product.ProductThickness || product.productThickness) : null,
            PH5: product.PH5 || product.ph5 || null,
            Size: product.Size || product.size || null,
            PH6: product.PH6 || product.ph6 || null,
            Film: product.Film || product.film || null,
            PH7: product.PH7 || product.ph7 || null,
            FireRated: product.FireRated || product.fireRated || null,
            ProjectName: product.ProjectName || product.projectName || null,
            ProjectAddress: product.ProjectAddress || product.projectAddress || null,
            ProductVariantCode: product.ProductVariantCode || product.productVariantCode || null,
            ProductVariantName: product.ProductVariantName || product.productVariantName || null,
            ModelName: product.ModelName || product.modelName || null,
            HardwareColour: product.HardwareColour || product.hardwareColour || null,
            SpeciesName: product.SpeciesName || product.speciesName || null,
            Substrate: product.Substrate || product.substrate || null,
            FactoryInvoiceDetails: product.FactoryInvoiceDetails || product.factoryInvoiceDetails || null,
            Emission: product.Emission || product.emission || null,
            ComponentCategory: componentData.componentCategory || componentData.ComponentCategory || product.ComponentCategory || product.componentCategory || null,
            ComponentList: componentData.componentList || componentData.ComponentList || product.ComponentList || product.componentList || null,
            ComponentQty: componentData.componentQty ?? componentData.ComponentQty ?? product.ComponentQty ?? product.componentQty ?? null,
            ComponentIssue: componentData.componentIssue || componentData.ComponentIssue || product.ComponentIssue || product.componentIssue || null,
            DoorMaterialCode: componentData.doorMaterialCode || componentData.DoorMaterialCode || product.DoorMaterialCode || product.doorMaterialCode || null,
            ComponentIssueRemarks: componentData.componentIssueRemarks || componentData.ComponentIssueRemarks || product.ComponentIssueRemarks || product.componentIssueRemarks || null,
            ComponentCategoryCode: componentData.componentCategoryCode || componentData.ComponentCategoryCode || product.ComponentCategoryCode || product.componentCategoryCode || null,
            ComponentListCode: componentData.componentListCode || componentData.ComponentListCode || product.ComponentListCode || product.componentListCode || null,
            ComponentIssueCode: componentData.componentIssueCode || componentData.ComponentIssueCode || product.ComponentIssueCode || product.componentIssueCode || null,
            ComponentMaterialGroupCode: componentData.materialGroupCode || componentData.MaterialGroupCode || product.ComponentMaterialGroupCode || product.componentMaterialGroupCode || null,
            ComponentDetails: componentDetailsApi.length ? componentDetailsApi : null,
            ComponentValue: (() => {
              if (totalComponentValue > 0) return totalComponentValue;
              if (componentData.componentValue != null && componentData.componentValue !== "") {
                const n = parseFloat(componentData.componentValue);
                if (!Number.isNaN(n)) return n;
              }
              const qty = parseFloat(componentData.componentQty || componentData.ComponentQty || 0) || 0;
              return toRaiseComplaintMoneyNumber(
                qty * getInvoiceUnitPriceForMaterialRow(product)
              );
            })(),
            // Persist remaining quantity used for Material Section validation (hidden field in UI)
            RemainingQty: parseFloat(
              product.RemainingQty ??
                product.remainingQty ??
                product.InvoiceQuantity ??
                product.invoiceQuantity ??
                0
            ),
            InvoiceQuantity: parseFloat(product.InvoiceQuantity || product.invoiceQuantity || 0),
            InvoiceValue: parseFloat(product.InvoiceValue || product.invoiceValue || 0),
            SRQuantity: parseFloat(complaintQty) || 0,
            // SRValue = Comp Qty × Invoice Unit Price (same as Material Section Comp Value)
            SRValue: toRaiseComplaintMoneyNumber(
              (parseFloat(complaintQty) || 0) *
                getInvoiceUnitPriceForMaterialRow(product)
            ),
            ApprvQuantity: parseFloat(product.ApprvQuantity || product.apprvQuantity || 0),
            ApprvValue: parseFloat(product.ApprvValue || product.apprvValue || 0),
          });
        }
      });

      // Upload any new files (no path yet) to server; get unique file path for each, then build attachments JSON.
      const uploadedPaths = {}; // item.id -> { filePath, fileName, fileSize, fileType }
      const uploadOne = async (item, copyFor) => {
        if (!item || !item.name) return;
        const hasPath = (item.path || item.url || "").trim() !== "";
        if (hasPath) return;
        const fileToUpload = item.file;
        if (!fileToUpload || !(fileToUpload instanceof File)) return;
        const uploadRes = await complaintAPI.uploadInvoiceAttachment(fileToUpload, copyFor);
        const data = uploadRes?.data ?? uploadRes;
        const filePath = data?.filePath ?? data?.FilePath;
        if (filePath) {
          uploadedPaths[item.id] = {
            filePath,
            fileName: data?.fileName ?? data?.FileName ?? item.name,
            fileSize: data?.fileSize ?? data?.FileSize ?? fileToUpload.size,
            fileType: data?.fileType ?? data?.FileType ?? fileToUpload.type,
          };
        }
      };
      for (const item of invoiceCopyFiles) {
        try {
          await uploadOne(item, "Invoice Copy");
        } catch (err) {
          console.error("Upload failed for file:", item?.name, err);
          throw new Error(`Failed to upload "${item?.name}". ${err?.response?.data?.message || err?.message || "Please try again."}`);
        }
      }
      for (const item of secondaryInvoiceFiles) {
        try {
          await uploadOne(item, "Secondary Invoice");
        } catch (err) {
          console.error("Upload failed for file:", item?.name, err);
          throw new Error(`Failed to upload "${item?.name}". ${err?.response?.data?.message || err?.message || "Please try again."}`);
        }
      }
      // Update local state so items have path (avoid re-upload on next save)
      if (Object.keys(uploadedPaths).length > 0) {
        setInvoiceCopyFiles((prev) =>
          prev.map((f) => (f.id in uploadedPaths ? { ...f, path: uploadedPaths[f.id].filePath, url: uploadedPaths[f.id].filePath } : f))
        );
        setSecondaryInvoiceFiles((prev) =>
          prev.map((f) => (f.id in uploadedPaths ? { ...f, path: uploadedPaths[f.id].filePath, url: uploadedPaths[f.id].filePath } : f))
        );
      }
      // Build attachments JSON with file paths and CopyFor (Invoice Copy / Secondary Invoice)
      const resolvedCustomerCode =
        invoiceDetails?.customerCode ??
        invoiceDetails?.CustomerCode ??
        invoiceDetails?.CUSTOMERCODE ??
        invoiceDetails?.customercode ??
        draftCustomerCodeRef.current ??
        null;





      const attachmentsList = [];
      const pushAttachment = (file, copyFor) => {
        if (!file || !file.name) return;
        const path = (file.path || file.url || "").trim() || (uploadedPaths[file.id]?.filePath ?? "");
        if (!path) return;
        const size = file.size != null ? Number(file.size) : (file.file && file.file.size != null ? Number(file.file.size) : null);
        const type = file.type || (file.file && file.file.type) || null;
        const uploaded = uploadedPaths[file.id];
        attachmentsList.push({
          CatgCode: null,
          CustomerCode: resolvedCustomerCode,
          EndCustomerCode: endCustomerData?.endCustomerCode || endCustomerData?.name || null,
          FileName: String(uploaded?.fileName ?? file.name),
          FilePath: path,
          FileSize: (uploaded?.fileSize != null ? Number(uploaded.fileSize) : null) ?? (size != null && Number.isFinite(size) ? size : null),
          FileType: (uploaded?.fileType || type) != null ? String(uploaded?.fileType || type) : null,
          CopyFor: copyFor || "",
        });
      };
      invoiceCopyFiles.forEach((file) => pushAttachment(file, "Invoice Copy"));
      secondaryInvoiceFiles.forEach((file) => pushAttachment(file, "Secondary Invoice"));

      // Resolve createdBy same as submit-final: send so backend can use when auth claims are not received
      let createdBy = null;
      try {
        const u = localStorage.getItem("user");
        if (u) {
          const user = JSON.parse(u);
          createdBy = user?.userIdCode ?? user?.UserIdCode ?? null;
        }
      } catch (_) {}

      // Prepare draft data with all new fields - Property names must match C# model exactly
      const resolvedCustomerId =
        invoiceDetails?.customerId ??
        invoiceDetails?.CustomerID ??
        invoiceDetails?.CustomerId ??
        invoiceDetails?.CUSTOMERID ??
        invoiceDetails?.customerid ??
        draftCustomerIdRef.current ??
        null;




      const resolvedShippingCustomerCode =
        invoiceDetails?.shippingCustomerCode ??
        invoiceDetails?.ShippingCustomerCode ??
        invoiceDetails?.shippingcustomercode ??
        draftShippingCustomerCodeRef.current ??
        null;




      const resolvedShippingCustomerId =
        invoiceDetails?.shippingCustomerId ??
        invoiceDetails?.ShippingCustomerID ??
        invoiceDetails?.shippingCustomerID ??
        draftShippingCustomerIdRef.current ??
        null;

      const resolvedCustomerType =
        invoiceDetails?.customerType ??
        invoiceDetails?.CustomerType ??
        invoiceDetails?.CUSTOMERTYPE ??
        null;

      const resolvedAddCustomerType =
        customerType ??
        invoiceDetails?.AddCustomerType ??
        invoiceDetails?.addCustomerType ??
        null;





      const draftData = {
        ...(createdBy ? { CreatedBy: createdBy } : {}),
        ...(draftSrCode ? { SRCode: draftSrCode } : {}),
        
        InvoiceNumber: invoiceNumberValue, // Use the validated invoice number
        SRTypeCode: typeOfComplaint || null,
        SRStatusCODE: "Draft",
        CATGCODE: null, // Will be set from products if available
        COUNTRYCODE: invoiceDetails?.customerCountry || null,
        CUSTOMERCODE: resolvedCustomerCode,
        CUSTOMERID: resolvedCustomerId,












        ENDCUSTOMERCODE: endCustomerData?.endCustomerCode || endCustomerData?.name || null,
        SRDescription: null, // Can be added if needed
        SRValue: invoiceDetailsList.reduce((sum, item) => sum + (item.SRValue || 0), 0),
        SRRaisedDate: new Date().toISOString(),
        MARKETCODE: invoiceDetails?.marketCode || invoiceDetails?.marketName || invoiceDetails?.MarketName || market || null,
        
        // SRINVOICEMASTER - Invoice Details
        InvoiceDate: parseDateForAPI(invoiceDetails?.InvoiceDate || invoiceDetails?.invoiceDate),
        OrderNumber: invoiceDetails?.OrderNo || invoiceDetails?.orderNo || invoiceDetails?.orderNumber || invoiceDetails?.OrderNumber || null,
        OrderDate: parseDateForAPI(invoiceDetails?.OrderDate || invoiceDetails?.orderDate),
        ReferenceInvoiceNumber: invoiceDetails?.referenceInvoiceNo || invoiceDetails?.referenceInvoiceNumber || null,
        SapOrder: invoiceDetails?.sapOrder || invoiceDetails?.SAPOrder || null,
        ODNNumber: invoiceDetails?.odnNo || invoiceDetails?.OdnNo || invoiceDetails?.odnNumber || invoiceDetails?.ODNNumber || null,
        
        // SRINVOICEMASTER - Customer Details
        CustomerName: invoiceDetails?.customerName || null,
        RepresentativeName: invoiceDetails?.representativeName || null,
        CustomerAddress: invoiceDetails?.customerAddress || null,
        CustomerCity: invoiceDetails?.customerCity || null,
        CustomerState: invoiceDetails?.customerState || null,
        CustomerCountry: invoiceDetails?.customerCountry || null,
        CustomerPinCode: invoiceDetails?.customerPinCode || null,
        CustomerPhone: invoiceDetails?.customerPhone || null,
        CustomerAlternateNo: invoiceDetails?.customerAlternateNo || null,
        CustomerEmail: invoiceDetails?.customerEmail || null,
        CustomerType: resolvedCustomerType,
        AddCustomerType: resolvedAddCustomerType,
        DealerDistributor:
          customerType === "B2B"
            ? (dealerDistributor || "").trim().slice(0, 250) || null
            : null,
        
        // SRINVOICEMASTER - Shipping Customer Details (from invoiceDetails)
        ShippingCustomerCode: resolvedShippingCustomerCode,
        ShippingCustomerID: resolvedShippingCustomerId,
        ShippingCustomerName: invoiceDetails?.shippingCustomerName || null,
        ShippingCustomerAddress: invoiceDetails?.shippingCustomerAddress || null,
        ShippingCustomerCity: invoiceDetails?.shippingCustomerCity || null,
        ShippingCustomerState: invoiceDetails?.shippingCustomerState || null,
        ShippingCustomerCountry: invoiceDetails?.shippingCustomerCountry || null,
        ShippingCustomerPinCode: invoiceDetails?.shippingCustomerPinCode || null,
        ShippingCustomerPhone: invoiceDetails?.shippingCustomerPhone || null,
        ShippingCustomerEmail: invoiceDetails?.shippingCustomerEmail || null,
        ShippingCustomerType: invoiceDetails?.shippingCustomerType || null,
        
        // SRINVOICEMASTER - Consumer Details (from end customer)
        ConsumerName: endCustomerData?.name || null,
        ConsumerAddress: endCustomerData?.address || null,
        ConsumerCountry: endCustomerData?.countryCode || null,
        ConsumerZone: endCustomerData?.zoneCode || null,
        ConsumerState: endCustomerData?.stateCode || null,
        ConsumerPostalCode: endCustomerData?.pinCode || null,
        ConsumerContactNumber: endCustomerData?.mobileNumber || endCustomerData?.phoneNo || null,
        ProjectSiteDetails:
          customerType === "B2C" || showEndCustomer
            ? (endCustomerData?.projectSiteDetails || "").trim().slice(0, 250) || null
            : null,
        
        // SRINVOICEMASTER - Billing Details
        BillingInvoiceNo: invoiceDetails?.billingInvoiceNo || invoiceDetails?.BillingInvoiceNo || null,
        BillingInvoiceDate: parseDateForAPI(invoiceDetails?.billingInvoiceDate || invoiceDetails?.BillingInvoiceDate),
        PONo: invoiceDetails?.CustomerPONo || invoiceDetails?.customerPONo || invoiceDetails?.customerPoPiNo || invoiceDetails?.CustomerPoPiNo || null,
        PODate: invoiceDetails?.CustomerPODate || invoiceDetails?.customerPODate || invoiceDetails?.customerPoPiDate || invoiceDetails?.CustomerPoPiDate || null,
        
        // SRINVOICEMASTER - Sample Details
        SampleType: sampleInfo?.sampleSent || null,
        CourierAgency: sampleInfo?.courierAgency || null,
        CourierDate: parseDateForAPI(sampleInfo?.courierDate),
        DocketNo: sampleInfo?.docketNo || null,
        SentTo: sampleInfo?.sentTo || null,
        SampleDetails: sampleInfo?.sampleDetails || null,
        
        // SRINVOICEMASTER - Order & Commercial Details
        Currency: invoiceDetails?.currency || invoiceDetails?.Currency || null,
        ExchangeRate: invoiceDetails?.exchangeRate || invoiceDetails?.ExchangeRate || null,
        ProductCategory: commercialDetails?.productCategory?.trim() || null,
        ShortPayment: commercialDetails?.shortPayment || null,
        InvoiceCurrency: commercialDetails?.invoiceCurrency || null,
        DocumentCurrency: commercialDetails?.documentCurrency || null,
        ReportingCurrency: commercialDetails?.reportingCurrency || null,
        ReportingAmount: commercialDetails?.reportingAmount
          ? parseFloat(commercialDetails.reportingAmount)
          : null,
        ComplaintValueUsd: commercialDetails?.complaintValueUsd
          ? parseFloat(commercialDetails.complaintValueUsd)
          : null,
        ApprovedValue: commercialDetails?.approvedValue
          ? parseFloat(commercialDetails.approvedValue)
          : null,
        
        InvoiceDetails: invoiceDetailsList.length > 0 ? invoiceDetailsList : null,
        Attachments: attachmentsList.length > 0 ? attachmentsList : null,
      };

      const response = await complaintAPI.saveComplaintDraft(draftData);

      if (response.success) {
        const srCode = response.data ?? response.srCode ?? "";
        return { success: true, srCode };
      }
      throw new Error(response.message || "Failed to save draft");
    } catch (err) {
      console.error("Error saving draft:", err);
      Swal.fire({
        title: "Error",
        text: err?.response?.data?.message || err?.message || "Failed to save draft",
        icon: "error",
        confirmButtonColor: "#ff9800",
        allowOutsideClick: false,
      });
      return { success: false };
    }
  };

  const handleSaveDraft = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    releaseRaiseComplaintActionLocks();
    syncMaterialSectionRefsFromDom();
    const clickAnchorScrollY = getEffectiveWindowScrollY();
    setPendingDraftAction("save");
    setSavingDraft(true);
    try {
      const result = await performSaveDraft({
        lockValidationSwalToTop: false,
        validationAnchorScrollY: clickAnchorScrollY,
      });
      if (result.success) {
        const srCode = result.srCode ?? "";
        const escapedCode = String(srCode).replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
        const successText = srCode
          ? "Draft saved successfully. Your Draft Code is: " + escapedCode + "."
          : "Draft saved successfully.";
        
        // Scroll to top and lock before showing the swal
        window.scrollTo({ top: 0, behavior: 'instant' });
        
        // Store scroll position at top (0)
        let savedScrollPosition = 0;
        let scrollLocked = false;
        
        // Function to enable scrolling
        const enableScroll = () => {
          scrollLocked = false;
          // Remove all scroll prevention
          window.removeEventListener('scroll', preventScrollHandler, { capture: true });
          window.removeEventListener('wheel', preventScrollHandler, { capture: true });
          window.removeEventListener('touchmove', preventScrollHandler, { capture: true });
          document.removeEventListener('scroll', preventScrollHandler, { capture: true });
          
          // Restore body styles
          document.body.style.position = '';
          document.body.style.top = '';
          document.body.style.width = '';
          document.body.style.overflow = '';
          document.documentElement.style.overflow = '';
          
          // Keep at top when re-enabling (don't scroll to bottom)
          requestAnimationFrame(() => {
            window.scrollTo({ top: 0, behavior: 'instant' });
          });
          
          // Remove event listeners for re-enabling
          document.removeEventListener('click', enableScrollHandler);
          document.removeEventListener('wheel', enableScrollHandler);
          document.removeEventListener('touchmove', enableScrollHandler);
          document.removeEventListener('keydown', enableScrollHandler);
        };
        
        // Prevent scroll event handler
        const preventScrollHandler = (e) => {
          if (!scrollLocked) return;
          
          // If it's a wheel event, enable scroll first to allow mouse wheel scrolling
          if (e.type === 'wheel') {
            enableScroll();
            return; // Don't prevent, allow the wheel event to proceed
          }
          
          e.preventDefault();
          e.stopPropagation();
          // Force scroll back to saved position (top)
          window.scrollTo({ top: savedScrollPosition, behavior: 'instant' });
          return false;
        };
        
        // Function to disable scrolling completely
        const disableScroll = () => {
          scrollLocked = true;
          // Store current scroll position (should be at top/0)
          const scrollY = window.scrollY || window.pageYOffset;
          savedScrollPosition = scrollY;
          
          // Lock body position
          document.body.style.position = 'fixed';
          document.body.style.top = `-${scrollY}px`;
          document.body.style.width = '100%';
          document.body.style.overflow = 'hidden';
          document.documentElement.style.overflow = 'hidden';
          
          // Prevent all scroll events
          window.addEventListener('scroll', preventScrollHandler, { passive: false, capture: true });
          window.addEventListener('wheel', preventScrollHandler, { passive: false, capture: true });
          window.addEventListener('touchmove', preventScrollHandler, { passive: false, capture: true });
          document.addEventListener('scroll', preventScrollHandler, { passive: false, capture: true });
        };
        
        // Handler to re-enable scroll on user interaction
        const enableScrollHandler = () => {
          enableScroll();
        };
        
        // Lock scroll before swal opens
        disableScroll();
        
        Swal.fire({
          title: "Draft Saved",
          html: successText,
          icon: "success",
          confirmButtonColor: "#ff9800",
          confirmButtonText: "OK",
          scrollbarPadding: false,
          allowOutsideClick: false,
          allowEscapeKey: false,
          // Disable scroll BEFORE swal closes
          willClose: () => {
            // Immediately disable scrolling before swal closes - ensure at top
            savedScrollPosition = 0;
            window.scrollTo({ top: 0, behavior: 'instant' });
            disableScroll();
          },
          didClose: () => {
            // Ensure scroll is still disabled and add re-enable listeners
            requestAnimationFrame(() => {
              disableScroll(); // Re-apply in case it was reset
              // Add event listeners to re-enable scroll on user interaction
              document.addEventListener('click', enableScrollHandler, { once: true });
              document.addEventListener('wheel', enableScrollHandler, { once: true, passive: true });
              document.addEventListener('touchmove', enableScrollHandler, { once: true, passive: true });
              document.addEventListener('keydown', enableScrollHandler, { once: true });
            });
          }
        }).then(() => {
          // Re-enable scroll before navigation
          enableScroll();
          navigate("/my-draft-service-requests");
        });
      }
    } finally {
      setSavingDraft(false);
      setPendingDraftAction(null);
    }
  };

  /** Sum SR Qty (Comp Qty) for all checked material rows. */
  const getSelectedMaterialSrQtyTotal = () => {
    const selected = selectedProductsRef.current;
    if (!selected || selected.size === 0) return 0;
    let total = 0;
    selected.forEach((productKey) => {
      const srQty = parseFloat(String(complaintQtyRef.current[productKey] ?? "").trim());
      if (!Number.isNaN(srQty) && srQty >= 0) total += srQty;
    });
    return Math.floor(total);
  };

  /** Sum invoice qty for selected rows; cloned (+) rows with same ProductCode count once. */
  const getSelectedMaterialInvoiceQtyTotal = () => {
    const selected = selectedProductsRef.current;
    if (!selected || selected.size === 0) return 0;
    const seenBaseProducts = new Set();
    let total = 0;
    selected.forEach((productKey) => {
      const row = productDataRef.current[productKey];
      const baseCode = String(
        row?.ProductCode ?? row?.productCode ?? row?.Productcode ?? ""
      ).trim();
      if (baseCode && seenBaseProducts.has(baseCode)) return;
      if (baseCode) seenBaseProducts.add(baseCode);
      total +=
        parseFloat(row?.InvoiceQuantity ?? row?.invoiceQuantity ?? row?.Invoicequantity ?? 0) ||
        0;
    });
    return Math.floor(total);
  };

  /** @deprecated Use getSelectedMaterialSrQtyTotal */
  const getSelectedMaterialQtyTotal = () => getSelectedMaterialSrQtyTotal();

  const handleNextChecklist = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    releaseRaiseComplaintActionLocks();
    syncMaterialSectionRefsFromDom();
    const clickAnchorScrollY = getEffectiveWindowScrollY();
    if (!validateEndCustomerDetails({ lockToTop: false, anchorScrollY: clickAnchorScrollY })) return;

    setPendingDraftAction("next");
    setSavingDraft(true);
    try {
      const result = await performSaveDraft({
        lockValidationSwalToTop: false,
        validationAnchorScrollY: clickAnchorScrollY,
      });
      if (result.success) {
        const selectedKeys = Array.from(selectedProductsRef.current || []);
        const productKeys = Object.keys(productDataRef.current);
        const primaryKey =
          selectedKeys[0] ||
          (selectedProductKey && selectedProductsRef.current?.has(selectedProductKey)
            ? selectedProductKey
            : null) ||
          productKeys.find((k) => selectedProductsRef.current?.has(k)) ||
          productKeys[0];
        const sourceProduct =
          (primaryKey && productDataRef.current[primaryKey]) ||
          invoiceDetails?.products?.[0];
        let productCategoryId = "";
        let productCategoryDescription = "";
        let productCode = "";
        let businessVerticalForChecklist = "";
        if (sourceProduct) {
          productCategoryId = sourceProduct.ProductCategoryId ?? sourceProduct.productCategoryId ?? "";
          productCategoryDescription = sourceProduct.ProductCategory ?? sourceProduct.productCategory ?? sourceProduct.ProductCategoryDescription ?? sourceProduct.productCategoryDescription ?? "";
          productCode = sourceProduct.ProductCode ?? sourceProduct.productCode ?? sourceProduct.Productcode ?? "";
          const bvRaw =
            (sourceProduct.BusinessVertical ?? sourceProduct.businessVertical ?? "").trim() ||
            (sourceProduct.DivisionName ?? sourceProduct.divisionName ?? "").trim();
          const bvLower = bvRaw.toLowerCase();
          businessVerticalForChecklist =
            bvRaw &&
            bvLower.includes("sturdo") &&
            !bvLower.includes("non-sturdo")
              ? "Sturdo"
              : bvRaw;
        }
        const selectedSrQtyTotal = getSelectedMaterialSrQtyTotal();
        const selectedInvoiceQtyTotal = getSelectedMaterialInvoiceQtyTotal();
        const marketCodeForChecklist = invoiceDetails?.marketCode ?? invoiceDetails?.MarketCode ?? invoiceDetails?.marketName ?? invoiceDetails?.MarketName ?? market ?? "";
        const params = new URLSearchParams();
        params.set("invoiceNo", formData.invoiceNo || "");
        if (productCode) params.set("productCode", productCode);
        if (productCategoryId) params.set("productCategoryId", productCategoryId);
        if (productCategoryDescription) params.set("productCategory", productCategoryDescription);
        if (result.srCode) params.set("srCode", result.srCode);
        if (marketCodeForChecklist) params.set("MarketCode", marketCodeForChecklist);
        if (businessVerticalForChecklist) params.set("businessVertical", businessVerticalForChecklist);
        if (selectedSrQtyTotal > 0) {
          params.set("selectedSrQty", String(selectedSrQtyTotal));
          params.set("selectedQty", String(selectedSrQtyTotal));
        }
        if (selectedInvoiceQtyTotal > 0) {
          params.set("selectedInvoiceQty", String(selectedInvoiceQtyTotal));
        }
        const { buildEncryptedChecklistPath } = await import("../utils/encryptedQuery");
        const checklistPath = await buildEncryptedChecklistPath(params);
        navigate(checklistPath);
      }
    } finally {
      setSavingDraft(false);
      setPendingDraftAction(null);
    }
  };

  // Close checklist dialog without prompting to save draft
  const handleCloseWithDraftPrompt = () => {
    // Use requestAnimationFrame to ensure smooth closing and prevent ResizeObserver issues
    requestAnimationFrame(() => {
      setChecklistOpen(false);
    });
  };

  const addComplaintLine = () => {
    setComplaintLines((prev) => [...prev, newLine()]);
  };

  const removeComplaintLine = (id) => {
    setComplaintLines((prev) =>
      prev.length > 1 ? prev.filter((l) => l.id !== id) : prev
    );
  };

  const updateComplaintLine = (id, field, value) => {
    setComplaintLines((prev) =>
      prev.map((line) => (line.id === id ? { ...line, [field]: value } : line))
    );
  };

  const saveDraftToMemory = () => {
    if (!selectedProductKey) return;
    try {
      const drafts = getDraftsFromMemory();
      drafts[selectedProductKey] = complaintLines;
      localStorage.setItem(draftStorageKey, JSON.stringify(drafts));
    } catch (err) {
      console.error("Failed to save draft", err);
    }
  };

  const handleSampleSentChange = (e) => {
    const value = e.target.value;
    setSampleInfo((prev) => ({
      ...prev,
      sampleSent: value,
      ...(value !== "Yes"
        ? {
          courierAgency: "",
          courierDate: "",
          docketNo: "",
          sentTo: "",
          sampleDetails: "",
        }
        : {
          courierDate: toDateInputValue(new Date()),
        }),
    }));
  };

  const handleSampleInfoChange = (field) => (e) => {
    const value = e.target.value;
    setSampleInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSrInfoChange = (field) => (e) => {
    const value = e.target.value;
    setSrInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCommercialDetailsChange = (field) => (e) => {
    if (
      field === "reportingCurrency" ||
      (isExportMarketActive() &&
        (field === "productCategory" ||
          field === "invoiceCurrency" ||
          field === "documentCurrency"))
    ) {
      return;
    }
    const value = e.target.value;
    setCommercialDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearDraftForProduct = () => {
    if (!selectedProductKey) return;
    try {
      const drafts = getDraftsFromMemory();
      if (drafts[selectedProductKey]) {
        delete drafts[selectedProductKey];
        localStorage.setItem(draftStorageKey, JSON.stringify(drafts));
      }
    } catch (err) {
      console.error("Failed to clear draft", err);
    }
  };

  // Initialize Select2 on modal selects when modal opens
  useEffect(() => {
    if (checklistOpen) {
      // Small delay to ensure modal is fully rendered
      const timer = setTimeout(() => {
        $(".checklist-modal select.form-select").each(function () {
          const $select = $(this);

          // Destroy existing select2 if any
          if ($select.data("select2")) {
            $select.select2("destroy");
          }

          // Initialize select2
          $select.select2({
            width: "100%",
            placeholder: "Select an option",
            allowClear: false,
            dropdownParent: $(".checklist-modal"),
            minimumResultsForSearch: 5,
          });

          // Handle change event to update React state
          $select.on("change", function () {
            const event = new Event("change", { bubbles: true });
            this.dispatchEvent(event);
          });
        });
      }, 100);

      return () => {
        clearTimeout(timer);
        $(".checklist-modal select.form-select").each(function () {
          const $select = $(this);
          if ($select.data("select2")) {
            $select.off("change");
            $select.select2("destroy");
          }
        });
      };
    }
  }, [checklistOpen, complaintLines.length, complaintTypes.length]);

  return (
    <div className="raise-page">
      <div className="card-panel mb-3">
        <div className="section-heading d-flex align-items-center gap-2">
          <FilterListIcon fontSize="small" className="text-muted" />
          <span>
            Enter Invoice No. or ODN No. below to fetch invoice details
          </span>
        </div>

        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            {error}
          </div>
        )}

        {draftLoadError && (
          <div className="alert alert-warning mb-3" role="alert">
            {draftLoadError}
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary ms-2"
              onClick={() => {
                setDraftLoadError("");
                navigate("/my-draft-service-requests");
              }}
            >
              Back to Drafts
            </button>
          </div>
        )}

        {loadingDraft && (
          <div className="mb-3 d-flex align-items-center gap-2 text-muted">
            <CircularProgress size={20} />
            <span>Loading draft...</span>
          </div>
        )}

        {isDraftViewMode && !loadingDraft && (
          <div className="alert alert-info mb-3" role="alert">
            This draft has already been submitted. You are viewing in read-only mode.
          </div>
        )}

        {draftSrCode && (
          <input type="hidden" name="draftSrCode" value={draftSrCode} />
        )}

        <div className="row g-3">
          <div className="col-lg-6 col-md-7">
            <label className="input-label required">
              Search via Invoice No. / ODN No.
            </label>
            <input
              type="text"
              className={`form-control form-control-light ${invoiceNoError ? "is-invalid" : ""}`}
              placeholder="Search via Invoice No. / ODN No."
              value={formData.invoiceNo}
              onChange={handleInputChange("invoiceNo")}
              maxLength={MAX_SEARCH_KEY_LENGTH}
            />
            {invoiceNoError && (
              <div className="invalid-feedback d-block">
                {invoiceNoError}
              </div>
            )}
          </div>
          {/* <div className="col-lg-4 col-md-6">
            <label className="input-label required">Market</label>
            <select
              className="form-select form-control-light"
              value={market}
              onChange={(e) => setMarket(e.target.value)}
            >
              <option value="Domestic">Domestic</option>
              <option value="Export">Export</option>
            </select>
          </div> */}
          {/* <div className="col-lg-4 col-md-6">
            <label className="input-label required">Type of SR</label>
            <select
              className="form-select form-control-light"
              value={typeOfComplaint}
              onChange={(e) => setTypeOfComplaint(e.target.value)}
            >
              <option value="Quality">Quality</option>
              <option value="Logistics">Logistics</option>
              <option value="Other">Other</option>
            </select>
          </div> */}
          <div className="col-4 d-flex gap-2 mt-2">
            <button
              type="button"
              className="btn btn-search btnsrchHt"
              onClick={handleSearch}
              disabled={loading || !isInvoiceNumberValid()}
              style={{
                background:
                  "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)",
                border: "none",
                color: "white",
                fontWeight: 600,
                boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                borderRadius: "4px",
                padding: "8px 24px",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.background =
                    "linear-gradient(135deg, #0f00f5 0%, #0f00f5 100%)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 20px rgba(102, 126, 234, 0.6)";
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.background =
                    "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 15px rgba(102, 126, 234, 0.4)";
                }
              }}
              onMouseDown={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = "translateY(0)";
                }
              }}
              onMouseUp={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = "translateY(-2px)";
                }
              }}
            >
              {loading ? (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
              ) : (
                <SearchIcon fontSize="small" className="me-2" />
              )}
              Search
            </button>
            <button
              type="button"
              className="btn btn-reset btnRstHt"
              onClick={handleReset}
              style={{
                background:
                  "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
                border: "none",
                color: "white",
                fontWeight: 600,
                boxShadow: "0 4px 15px rgba(132, 250, 176, 0.4)",
                borderRadius: "4px",
                padding: "8px 16px",
                transition: "all 0.3s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 6px 20px rgba(132, 250, 176, 0.6)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 4px 15px rgba(132, 250, 176, 0.4)";
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
            >
              <RestartAltIcon fontSize="small" />
            </button>
          </div>
        </div>
      </div>

      {invoiceDetails && previousComplaintsCount > 0 && (
        <>
          <div className="card-panel mt-3">
            <div className="section-heading previous-complaints-heading" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px" }}>
              <span>
                {previousComplaintsCount == 1
                  ? "Previous SR(s) exist for this invoice. Please review before proceeding"
                  : `Multiple previous SRs exist for this invoice (Total: ${previousComplaintsCount}). Please review before proceeding`}
              </span>
              {previousComplaintsCount > 1 && (
                <button
                  type="button"
                  onClick={handlePreviousComplaintsTableToggle}
                  style={{
                    background: showPreviousComplaintsTable
                      ? "linear-gradient(135deg, #4caf50 0%, #45a049 100%)"
                      : "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 500,
                    fontSize: "14px",
                    borderRadius: "4px",
                    padding: "6px 16px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    transition: "all 0.3s ease",
                    boxShadow: "0 2px 8px rgba(0, 123, 255, 0.3)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 123, 255, 0.5)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 123, 255, 0.3)";
                  }}
                >
                  <TableChartIcon fontSize="small" />
                  {showPreviousComplaintsTable ? "Hide SRs" : "View All SRs"}
                </button>
              )}
            </div>
            <div
              style={{
                maxWidth: "100%",
                overflowX: "auto",
                display: previousComplaintsCount === 1 || showPreviousComplaintsTable ? "block" : "none"
              }}
            >
              <div className="table-responsive">
                <table
                  ref={historyTableRef}
                  className="table table-striped table-bordered complaint-table srTablewrpr"
                  style={{ width: "100%" }}
                >
                  <thead>
                    <tr>
                      <th className="text-center">SR No</th>
                      <th className="text-center">SR Date</th>
                      <th className="text-start">Product(s) involved</th>
                      <th className="text-end">SR QTY</th>
                      <th className="text-end">SR VALUE</th>
                      <th className="text-center">Resolution status</th>
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody></tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {invoiceDetails && (
        <>
          <div className="card-panel mt-3">
            <div className="section-heading">Invoice Details</div>
            <div className="row g-3">
              <div className="col-lg-3 col-md-6">
                <label className="input-label">Invoice Number</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  value={
                    invoiceDetails.invoiceNumber ||
                    invoiceDetails.invoiceNo ||
                    formData.invoiceNo ||
                    ""
                  }
                  readOnly
                />
              </div>

              <div className="col-lg-3 col-md-6" style={{ display: "none" }}>
                <label className="input-label">Internal Reference No.</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  value={
                    invoiceDetails.internalReferenceNo ||
                    invoiceDetails.InternalReferenceNo ||
                    ""
                  }
                  readOnly
                />
              </div>

              <div className="col-lg-3 col-md-6">
                <label className="input-label">SAP Order</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  value={
                    invoiceDetails.sapOrder ||
                    invoiceDetails.SAPOrder ||
                    invoiceDetails.orderNo ||
                    invoiceDetails.OrderNo ||
                    invoiceDetails.orderNumber ||
                    invoiceDetails.OrderNumber ||
                    ""
                  }
                  readOnly
                />
              </div>

              <div className="col-lg-3 col-md-6">
                <label className="input-label">ODN Number</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  value={
                    invoiceDetails.odnNumber || invoiceDetails.ODNNumber || ""
                  }
                  readOnly
                />
              </div>

              <div className="col-12">
                <InvoiceUpload
                  className="file-upload-panel-layout--invoice"
                  title="Invoice Copy Upload"
                  required
                  value={invoiceCopyFiles}
                  onChange={setInvoiceCopyFiles}
                />
              </div>
            </div>
          </div>

          <div className="card-panel mt-3">
            <div className="section-heading">Customer Details</div>
            <div className="row g-3">
              {/* Customer Type Radio Button - Mandatory - Prominent Section */}
              <div className="col-12 mb-3">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    flexWrap: "wrap",
                  }}
                >
                  <label
                    className="input-label required"
                    style={{
                      margin: 0,
                      display: "inline-flex",
                      alignItems: "center",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#333",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Customer Type
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      margin: 0,
                      padding: "4px 8px",
                      backgroundColor: customerType === "B2B" ? "#e3f2fd" : "transparent",
                      borderRadius: "6px",
                      border: customerType === "B2B" ? "1px solid #0b6bff" : "1px solid #dee2e6",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (customerType !== "B2B") {
                        e.currentTarget.style.borderColor = "#0b6bff";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (customerType !== "B2B") {
                        e.currentTarget.style.borderColor = "#dee2e6";
                      }
                    }}
                  >
                    <input
                      type="radio"
                      name="customerType"
                      value="B2B"
                      checked={customerType === "B2B"}
                      onChange={(e) => {
                        setCustomerType(e.target.value);
                        // For B2B, keep End Consumer optional and collapsed by default
                        // (user can still open it via "ADD END CONSUMER")
                        if (e.target.value === "B2B" && showEndCustomer) {
                          setShowEndCustomer(false);
                        }
                      }}
                      style={{
                        marginRight: "8px",
                        cursor: "pointer",
                        width: "14px",
                        height: "14px",
                        accentColor: "#0b6bff",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: customerType === "B2B" ? "600" : "500",
                        color: customerType === "B2B" ? "#0b6bff" : "#666",
                      }}
                    >
                      <strong>Channel Partner</strong>
                    </span>
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      margin: 0,
                      padding: "4px 8px",
                      backgroundColor: customerType === "B2C" ? "#e3f2fd" : "transparent",
                      borderRadius: "6px",
                      border: customerType === "B2C" ? "1px solid #0b6bff" : "1px solid #dee2e6",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (customerType !== "B2C") {
                        e.currentTarget.style.borderColor = "#0b6bff";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (customerType !== "B2C") {
                        e.currentTarget.style.borderColor = "#dee2e6";
                      }
                    }}
                  >
                    <input
                      type="radio"
                      name="customerType"
                      value="B2C"
                      checked={customerType === "B2C"}
                      onChange={(e) => {
                        setCustomerType(e.target.value);
                        // If B2C is selected, End Consumer details become mandatory
                        if (e.target.value === "B2C" && !showEndCustomer) {
                          setShowEndCustomer(true);
                        }
                      }}
                      style={{
                        marginRight: "8px",
                        cursor: "pointer",
                        width: "14px",
                        height: "14px",
                        accentColor: "#0b6bff",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: customerType === "B2C" ? "600" : "500",
                        color: customerType === "B2C" ? "#0b6bff" : "#666",
                      }}
                    >
                      <strong>End Consumer</strong>
                    </span>
                  </label>
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <label className="input-label">Customer Name</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  value={invoiceDetails.customerName || ""}
                  readOnly
                />
              </div>
              <div className="col-lg-3 col-md-6">
                <label className="input-label">Customer Contact Person</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  value={invoiceDetails.representativeName || ""}
                  readOnly
                />
              </div>
              <div className="col-lg-3 col-md-6">
                <label className="input-label">Customer Address</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  value={invoiceDetails.customerAddress || ""}
                  readOnly
                />
              </div>
              <div className="col-lg-3 col-md-6">
                <label className="input-label">Customer City</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  value={invoiceDetails.customerCity || ""}
                  readOnly
                />
              </div>
              <div className="col-lg-3 col-md-6">
                <label className="input-label">Customer State</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  value={invoiceDetails.customerState || ""}
                  readOnly
                />
              </div>

              <div className="col-lg-3 col-md-6">
                <label className="input-label">Customer Postal Code</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  value={invoiceDetails.customerPinCode || ""}
                  readOnly
                />
              </div>
              {/* <div className="col-lg-3 col-md-6">
                <label className="input-label">Contact Person</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  value=""
                  readOnly
                />
              </div> */}
              <div className="col-lg-3 col-md-6">
                <label className="input-label">Mobile Number</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  value={invoiceDetails.customerMobileNo || ""}
                  readOnly
                />
              </div>

              {/* <div className="col-lg-3 col-md-6">
                <label className="input-label">Customer Code</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  value={invoiceDetails.customerCode || ""}
                  readOnly
                />
              </div> */}

              <div className="col-lg-3 col-md-6">
                <label className="input-label">Phone Number</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  value={invoiceDetails.customerPhone || ""}
                  readOnly
                />
              </div>

              <div className="col-lg-3 col-md-6">
                <label className="input-label">Customer Email</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  value={
                    invoiceDetails.customerEmail ||
                    invoiceDetails.CustomerEmail ||
                    ""
                  }
                  readOnly
                />
              </div>

              {customerType === "B2B" && (
                <div className="col-lg-3 col-md-6">
                  <label className="input-label">Dealer/Distributor</label>
                  <input
                    type="text"
                    className="form-control form-control-light"
                    placeholder="Enter dealer/distributor"
                    value={dealerDistributor}
                    maxLength={250}
                    onChange={(e) =>
                      setDealerDistributor((e.target.value || "").slice(0, 250))
                    }
                  />
                </div>
              )}

              {/* Add End Customer Button */}
              <div className="col-12 mt-3">
                <button
                  type="button"
                  className="btn btn-primary endCustBtn search-btn-variant"
                  onClick={() => {
                    // If B2C is selected, don't allow hiding End Consumer section
                    if (customerType === "B2C" && showEndCustomer) {
                      Swal.fire({
                        title: "Cannot Hide",
                        html: `<div style="text-align:left">End Consumer Details are mandatory for End Consumer customer type. You cannot hide this section.</div>`,
                        icon: "warning",
                        confirmButtonColor: "#ff9800",
                      });
                      return;
                    }
                    setShowEndCustomer(!showEndCustomer);
                  }}
                  disabled={customerType === "B2C" && showEndCustomer}
                  style={{
                    background: customerType === "B2C" && showEndCustomer
                      ? "linear-gradient(135deg, #6c757d 0%, #5a6268 100%)"
                      : "linear-gradient(135deg, #2a9dff 0%, #0b6bff 100%)",
                    borderColor: "transparent",
                    color: "#ffffff",
                    boxShadow: "0 8px 16px rgba(11, 107, 255, 0.22)",
                    padding: "9px 18px",
                    borderRadius: "8px",
                    fontWeight: 700,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    transition: "all 0.2s ease-in-out",
                    cursor: customerType === "B2C" && showEndCustomer ? "not-allowed" : "pointer",
                    opacity: customerType === "B2C" && showEndCustomer ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!(customerType === "B2C" && showEndCustomer)) {
                      e.target.style.transform = "translateY(-1px)";
                      e.target.style.boxShadow = "0 10px 18px rgba(11, 107, 255, 0.28)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "0 8px 16px rgba(11, 107, 255, 0.22)";
                  }}
                >
                  {showEndCustomer ? (
                    <>
                      <CloseIcon fontSize="small" />
                      HIDE END CONSUMER
                    </>
                  ) : (
                    <>
                      <AddCircleOutlineIcon fontSize="small" />
                      ADD END CONSUMER
                    </>
                  )}
                </button>
              </div>

              {/* End Customer Details Card */}
              {showEndCustomer && (
                <div className="col-12 mt-3">
                  <div
                    className="card"
                    style={{ border: "1px solid #dee2e6", borderRadius: "8px" }}
                  >
                    <div
                      className="card-header"
                      style={{
                        backgroundColor: "#f8f9fa",
                        padding: "12px 20px",
                      }}
                    >
                      <h6
                        className="mb-0"
                        style={{ fontSize: "16px", fontWeight: "600" }}
                      >
                        End Consumer Details
                      </h6>
                    </div>
                    <div className="card-body" style={{ padding: "20px" }}>
                      <div className="row g-3">
                        {/* Mobile Number first - required, searchable; lookup fills rest or user enters new */}
                        <div className="col-lg-12">
                          <label className={`input-label ${customerType === "B2C" ? "required" : ""}`}>
                            Mobile Number
                          </label>
                          <div className="d-flex gap-2 align-items-center">
                            <input
                              type="text"
                              className="form-control form-control-light end-consumer-mobile-input"
                              placeholder="Enter the mobile number and click Search to retrieve existing customer details, or continue to create a new customer."
                              value={endCustomerData.mobileNumber}
                              required={customerType === "B2C"}
                              maxLength={10}
                              inputMode="numeric"
                              pattern="\d{10}"
                              onChange={(e) =>
                                setEndCustomerData({
                                  ...endCustomerData,
                                  mobileNumber: (e.target.value || "").replace(/\D/g, "").slice(0, 10),
                                })
                              }
                              onBlur={handleEndConsumerMobileLookup}
                              disabled={endConsumerLookupLoading}
                            />
                            <button
                              type="button"
                              className="btn btn-search btnsrchHt"
                              onClick={handleEndConsumerMobileLookup}
                              disabled={
                                endConsumerLookupLoading ||
                                !/^\d{10}$/.test(String(endCustomerData.mobileNumber || "").trim())
                              }
                              title="Search by mobile number"
                              style={{
                                background:
                                  "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)",
                                border: "none",
                                color: "white",
                                fontWeight: 600,
                                boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                                borderRadius: "4px",
                                padding: "8px 24px",
                                marginTop: "0px",
                                transition: "all 0.3s ease",
                              }}
                              onMouseEnter={(e) => {
                                if (!endConsumerLookupLoading) {
                                  e.currentTarget.style.background =
                                    "linear-gradient(135deg, #0f00f5 0%, #0f00f5 100%)";
                                  e.currentTarget.style.transform = "translateY(-2px)";
                                  e.currentTarget.style.boxShadow =
                                    "0 6px 20px rgba(102, 126, 234, 0.6)";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!endConsumerLookupLoading) {
                                  e.currentTarget.style.background =
                                    "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)";
                                  e.currentTarget.style.transform = "translateY(0)";
                                  e.currentTarget.style.boxShadow =
                                    "0 4px 15px rgba(102, 126, 234, 0.4)";
                                }
                              }}
                              onMouseDown={(e) => {
                                if (!endConsumerLookupLoading) {
                                  e.currentTarget.style.transform = "translateY(0)";
                                }
                              }}
                              onMouseUp={(e) => {
                                if (!endConsumerLookupLoading) {
                                  e.currentTarget.style.transform = "translateY(-2px)";
                                }
                              }}
                            >
                              {endConsumerLookupLoading ? (
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                              ) : (
                                <SearchIcon fontSize="small" className="me-2" />
                              )}
                              Search
                            </button>
                          </div>
                        </div>
                        <div className="col-lg-3">
                          <label className={`input-label ${customerType === "B2C" ? "required" : ""}`}>
                            Consumer Name
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            placeholder="Enter customer name"
                            value={endCustomerData.name}
                            required={customerType === "B2C"}
                            onChange={(e) =>
                              setEndCustomerData({
                                ...endCustomerData,
                                name: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="col-lg-3">
                          <label className={`input-label ${customerType === "B2C" ? "required" : ""}`}>
                            Consumer Address
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            placeholder="Enter address"
                            value={endCustomerData.address}
                            required={customerType === "B2C"}
                            onChange={(e) =>
                              setEndCustomerData({
                                ...endCustomerData,
                                address: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="col-lg-3">
                          <label className={`input-label ${customerType === "B2C" ? "required" : ""}`}>
                            Country
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            placeholder="Enter country"
                            value={endCustomerData.countryCode || ""}
                            required={customerType === "B2C"}
                            onKeyDown={handleEndConsumerNoDigitKeyDown}
                            onChange={(e) =>
                              setEndCustomerData({
                                ...endCustomerData,
                                countryCode: stripDigitsFromEndConsumerText(e.target.value),
                              })
                            }
                          />
                        </div>
                        <div className="col-lg-3">
                          <label className={`input-label ${customerType === "B2C" ? "required" : ""}`}>
                            Zone/Province
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            placeholder="Enter zone / province"
                            value={endCustomerData.zoneCode || ""}
                            required={customerType === "B2C"}
                            onKeyDown={handleEndConsumerNoDigitKeyDown}
                            onChange={(e) =>
                              setEndCustomerData({
                                ...endCustomerData,
                                zoneCode: stripDigitsFromEndConsumerText(e.target.value),
                              })
                            }
                          />
                        </div>
                        <div className="col-lg-3">
                          <label className={`input-label ${customerType === "B2C" ? "required" : ""}`}>
                            State/City
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            placeholder="Enter state / city"
                            value={endCustomerData.stateCode || ""}
                            required={customerType === "B2C"}
                            onKeyDown={handleEndConsumerNoDigitKeyDown}
                            onChange={(e) => {
                              const stateCity = stripDigitsFromEndConsumerText(e.target.value);
                              setEndCustomerData({
                                ...endCustomerData,
                                stateCode: stateCity,
                                state: stateCity,
                                city: stateCity,
                                cityCode: stateCity,
                              });
                            }}
                          />
                        </div>
                        <div className="col-lg-3">
                          <label className={`input-label ${customerType === "B2C" ? "required" : ""}`}>
                            Postal Code
                          </label>
                          <input
                            type="text"
                            className={`form-control form-control-light ${
                              endCustomerPinCodeError ? "is-invalid" : ""
                            }`}
                            placeholder="Enter 6-digit postal code"
                            value={endCustomerData.pinCode}
                            required={customerType === "B2C"}
                            maxLength={6}
                            inputMode="numeric"
                            pattern="\d{6}"
                            onChange={(e) => {
                              const pinCode = (e.target.value || "")
                                .replace(/\D/g, "")
                                .slice(0, 6);
                              setEndCustomerPinCodeError("");
                              setEndCustomerData({
                                ...endCustomerData,
                                pinCode,
                              });
                            }}
                            onBlur={() => {
                              const pin = String(endCustomerData.pinCode || "").trim();
                              if (pin && !/^\d{6}$/.test(pin)) {
                                setEndCustomerPinCodeError(
                                  "Postal Code must be exactly 6 digits (numbers only)."
                                );
                              }
                            }}
                          />
                          {endCustomerPinCodeError && (
                            <div className="invalid-feedback d-block">
                              {endCustomerPinCodeError}
                            </div>
                          )}
                        </div>
                        <div className="col-lg-3">
                          <label className="input-label">Project/Site Details</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            placeholder="Enter project/site details"
                            value={endCustomerData.projectSiteDetails || ""}
                            maxLength={250}
                            onChange={(e) =>
                              setEndCustomerData({
                                ...endCustomerData,
                                projectSiteDetails: (e.target.value || "").slice(0, 250),
                              })
                            }
                          />
                        </div>
                      </div>

                      {/* Secondary Invoice Upload - same thumbnail grid + remove (X) as Invoice Copy; draft edit mode unchanged */}
                      <div className="col-12 mt-3">
                        <InvoiceUpload
                          className="file-upload-panel-layout--invoice"
                          title="Secondary Invoice Upload"
                          value={secondaryInvoiceFiles}
                          onChange={setSecondaryInvoiceFiles}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* <div className="col-lg-4">
                <label className="input-label">Customer Country</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  value={invoiceDetails.customerCountry || ""}
                  readOnly
                />
              </div> */}
            </div>
          </div>
        </>
      )}

      {invoiceDetails && (
        <div className="card-panel mt-3">
          <div className="section-heading">Customer Billing Details</div>
          <div className="row g-3">
            <div className="col-lg-3">
              <label className="input-label">Invoice Number</label>
              <input
                type="text"
                className="form-control form-control-light"
                value={
                  invoiceDetails.invoiceNumber ||
                  invoiceDetails.invoiceNo ||
                  invoiceDetails.InvoiceNo ||
                  formData.invoiceNo ||
                  ""
                }
                readOnly
              />
            </div>
            <div className="col-lg-3">
              <label className="input-label">Invoice Date</label>
              <input
                type="text"
                className="form-control form-control-light"
                value={formatDateDisplay(
                  invoiceDetails.invoiceDate ||
                  invoiceDetails.InvoiceDate ||
                  ""
                )}
                readOnly
              />
            </div>
            <div className="col-lg-3">
              <label className="input-label">PO / PI / SO No.</label>
              <input
                type="text"
                className="form-control form-control-light"
                value={
                  invoiceDetails.CustomerPONo ||
                  invoiceDetails.customerPONo ||
                  ""
                }
                readOnly
              />
            </div>
            <div className="col-lg-3">
              <label className="input-label">PO / PI / SO No. Date</label>
              <input
                type="text"
                className="form-control form-control-light"
                value={formatDateDisplay(
                  invoiceDetails.customerPODate ||
                  invoiceDetails.CustomerPODate ||
                  invoiceDetails.customerPoPiDate ||
                  invoiceDetails.CustomerPoPiDate ||
                  invoiceDetails.pODate ||
                  invoiceDetails.PODate ||
                  ""
                )}
                readOnly
              />
            </div>
          </div>
        </div>
      )}

      {invoiceDetails &&
        Array.isArray(invoiceDetails.products) && (
          <div className="card-panel mt-3 material-section-panel">
            <div className="section-heading">Material Section</div>
            {materialSectionError && (
              <div className="alert alert-warning mt-2 mb-2" role="alert">
                {materialSectionError}
              </div>
            )}
            <div style={{ maxWidth: "100%", overflowX: "auto" }}>
              <div className="table-responsive">
                <table
                  ref={productTableRef}
                  className="table table-striped table-bordered complaint-table"
                  style={{ width: "100%" }}
                >
                  <thead>
                    <tr>
                      <th style={{ width: "50px" }}></th>
                      <th>Business Vertical</th>
                      <th style={{ display: "none" }}>Product Category</th>
                      <th>Plant Name</th>
                      <th>Item Description</th>
                      <th>Product Details</th>
                      <th>Invoice Quantity</th>
                      <th>UOM</th>
                      <th>Invoice Unit Price</th>
                      <th>Total Price</th>
                      <th style={{ display: showMaterialComponentDetailsColumn ? undefined : "none" }}>
                        Component Details
                      </th>
                      <th>SR Category <span className="text-danger">*</span></th>
                      <th style={{ display: "none" }}>Remain Qty</th>
                      <th style={{ display: showMaterialSrQtyValueColumns ? undefined : "none" }}>
                        SR Qty
                      </th>
                      <th style={{ width: "50px" }}>Action</th>
                      <th style={{ display: showMaterialSrQtyValueColumns ? undefined : "none" }}>
                        Sr VALUE
                      </th>
                    </tr>
                  </thead>
                  <tbody></tbody>
                  <tfoot>
                    <tr className="material-section-total-row">
                      <th colSpan="6" className="text-end">Total</th>
                      <th className="text-end dt-numeric-col" id="material-total-invoice-qty"></th>
                      <th></th>
                      <th></th>
                      <th className="text-end dt-numeric-col" id="material-total-total-price"></th>
                      <th style={{ display: showMaterialComponentDetailsColumn ? undefined : "none" }}></th>
                      <th></th>
                      <th className="text-end dt-numeric-col" id="material-total-remain-qty" style={{ display: "none" }}></th>
                      <th
                        className="text-end dt-numeric-col"
                        id="material-total-comp-qty"
                        style={{ display: showMaterialSrQtyValueColumns ? undefined : "none" }}
                      ></th>
                      <th></th>
                      <th
                        className="text-end dt-numeric-col"
                        id="material-total-comp-value"
                        style={{ display: showMaterialSrQtyValueColumns ? undefined : "none" }}
                      ></th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}

      {invoiceDetails && (
        <div className="card-panel mt-3">
          <div className="section-heading">
            <div className="upload-card-header mb-0">
              <span>Sample Information</span>
              <span className="upload-note">
                (A piece of complaint (sample) board should be sent to Factory for analysis for all the Product Quality complaints, whenever possible)
              </span>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-lg-3 col-md-4 col-sm-6 sample-sent-field-col">
              <div className="d-flex align-items-center gap-2 mb-1 sample-sent-label-row">
                <label
                  className="form-label checklist-form-label mb-0 sample-sent-label"
                  style={{ width: "auto" }}
                >
                  Sample Sent
                </label>
                <span
                  className="text-muted sample-sent-hint"
                  style={{ fontSize: "0.9rem", fontWeight: 400 }}
                >
                  {sampleInfo.sampleSent === "Yes"
                    ? "(Provide courier details for the sent sample)"
                    : '(Select "Yes" to capture courier details)'}
                </span>
              </div>
              <select
                className="form-select checklist-form-control"
                value={sampleInfo.sampleSent}
                onChange={handleSampleSentChange}
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
          </div>

          {sampleInfo.sampleSent === "Yes" && (
            <div className="row g-3 mt-1">
              <div className="col-lg-3 col-md-6">
                <label className="input-label required">Courier Agency</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  value={sampleInfo.courierAgency}
                  onChange={handleSampleInfoChange("courierAgency")}
                  placeholder="Enter courier agency"
                />
              </div>

              <div className="col-lg-3 col-md-6">
                <label className="input-label required">Courier Date</label>
                <input
                  type="date"
                  className="form-control form-control-light"
                  value={sampleInfo.courierDate}
                  onChange={handleSampleInfoChange("courierDate")}
                />
              </div>

              <div className="col-lg-3 col-md-6">
                <label className="input-label required">Docket No.</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  value={sampleInfo.docketNo}
                  onChange={handleSampleInfoChange("docketNo")}
                  placeholder="Enter Docket No."
                />
              </div>

              <div className="col-lg-3 col-md-6">
                <label className="input-label required">Sent To</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  value={sampleInfo.sentTo}
                  onChange={handleSampleInfoChange("sentTo")}
                  placeholder="Enter recipient"
                />
              </div>

              <div className="col-12">
                <label className="input-label required">Sample Details</label>
                <textarea
                  rows="3"
                  className="form-control form-control-light"
                  value={sampleInfo.sampleDetails}
                  onChange={handleSampleInfoChange("sampleDetails")}
                  placeholder="Add any remarks"
                ></textarea>
              </div>
            </div>
          )}
        </div>
      )}

      {/* SR Information section - Hidden */}
      {false && invoiceDetails && (
        <div className="card-panel mt-3">
          <div className="section-heading">S R Information</div>
          <div className="row g-3">
            <div className="col-lg-3 col-md-6" style={{ display: 'none' }}>
              <label className="input-label">Plant Name</label>
              <input
                type="text"
                className="form-control form-control-light"
                value={srInfo.plantName}
                onChange={handleSrInfoChange("plantName")}
                placeholder="Enter plant name"
              />
            </div>
            <div className="col-lg-3 col-md-6">
              <label className="input-label">Corrective Action Taken</label>
              <textarea
                rows="2"
                className="form-control form-control-light"
                value={srInfo.correctiveAction}
                onChange={handleSrInfoChange("correctiveAction")}
                placeholder="Add corrective action remarks"
              ></textarea>
            </div>
            <div className="col-lg-3 col-md-6">
              <label className="input-label">Comments Visit Report</label>
              <textarea
                rows="2"
                className="form-control form-control-light"
                value={srInfo.visitReportComments}
                onChange={handleSrInfoChange("visitReportComments")}
                placeholder="Enter visit report comments"
              ></textarea>
            </div>
            <div className="col-lg-3 col-md-6">
              <label className="input-label">
                Other fields (visible post Commercial Approval)
              </label>
              <textarea
                rows="2"
                className="form-control form-control-light"
                value={srInfo.postCommercialNotes}
                onChange={handleSrInfoChange("postCommercialNotes")}
                placeholder="Capture notes visible after commercial approval"
              ></textarea>
            </div>
          </div>
        </div>
      )}

      {/* Commercial Details section - visible only for Export market */}
      {invoiceDetails &&
        (
          (market || "").toString().toUpperCase() === "EXPORT" ||
          (
            (
              invoiceDetails.marketName ||
              invoiceDetails.MarketName ||
              invoiceDetails.marketCode ||
              invoiceDetails.MarketCode ||
              ""
            )
              .toString()
              .toUpperCase()
              .includes("EXP")
          )
        ) && (
        <div className="card-panel mt-3">
          <div className="section-heading">Commercial Details</div>
          <div className="row g-3">
            <div className="col-lg-2 col-md-6">
              <label className="input-label">Business Vertical</label>
              <input
                type="text"
                className="form-control form-control-light"
                value={commercialDetails.productCategory}
                onChange={handleCommercialDetailsChange("productCategory")}
                placeholder={
                  isExportMarketActive()
                    ? "Business vertical from invoice"
                    : "Enter product category"
                }
                readOnly={isExportMarketActive()}
                title={
                  isExportMarketActive()
                    ? "Filled from invoice Business Vertical"
                    : undefined
                }
              />
            </div>
            <div className="col-lg-2 col-md-6">
              <label className="input-label">Exchange Rate</label>
              <input
                type="text"
                className="form-control form-control-light text-end"
                value={
                  commercialDetails.exchangeRate ||
                  invoiceDetails?.exchangeRate ||
                  invoiceDetails?.ExchangeRate ||
                  ""
                }
                readOnly
                placeholder="0.0000"
                title="From invoice exchange rate"
              />
            </div>
            <div className="col-lg-2 col-md-6">
              <label className="input-label">Invoice / Doc. Currency</label>
              <input
                type="text"
                className="form-control form-control-light"
                value={commercialDetails.invoiceCurrency}
                onChange={handleCommercialDetailsChange("invoiceCurrency")}
                placeholder={
                  isExportMarketActive()
                    ? "From SAP invoice header"
                    : "Enter invoice / doc. currency"
                }
                readOnly={isExportMarketActive()}
                title={
                  isExportMarketActive()
                    ? "From Greenlam.SAPInvoiceHeader currency"
                    : undefined
                }
              />
            </div>
            <div className="col-lg-2 col-md-6">
              <label className="input-label">Local Currency</label>
              <input
                type="text"
                className="form-control form-control-light"
                value={commercialDetails.documentCurrency}
                onChange={handleCommercialDetailsChange("documentCurrency")}
                placeholder={
                  isExportMarketActive() ? "INR" : "Enter local currency"
                }
                readOnly={isExportMarketActive()}
                title={
                  isExportMarketActive()
                    ? "Fixed to INR for export invoices"
                    : undefined
                }
              />
            </div>
            <div className="col-lg-2 col-md-6">
              <label className="input-label">Reporting Currency</label>
              <input
                type="text"
                className="form-control form-control-light"
                value={commercialDetails.reportingCurrency}
                readOnly
                placeholder="USD"
                title="Fixed reporting currency"
              />
            </div>
            <div className="col-lg-2 col-md-6">
              <label className="input-label">Local Currency Amount</label>
              <input
                type="text"
                className="form-control form-control-light text-end"
                value={commercialDetails.complaintValueUsd}
                readOnly
                placeholder="0.00"
                title="Local Currency Amount (total SR Value ÷ Exchange Rate)"
              />
            </div>
            <div className="col-lg-2 col-md-6">
              <label className="input-label">Reporting Currency Amount</label>
              <input
                type="text"
                className="form-control form-control-light text-end"
                value={commercialDetails.reportingAmount}
                readOnly
                placeholder="0.00"
                title="Exchange Rate × Invoice / Doc. Currency Amount"
              />
            </div>
            {shouldShowShortPayment && (
              <div className="col-lg-2 col-md-6">
                <label className="input-label">Short Payment</label>
                <div className="short-payment-dropdown-wrapper">
                  <select
                    className="form-select form-control-light short-payment-dropdown-select"
                    value={commercialDetails.shortPayment}
                    onChange={handleCommercialDetailsChange("shortPayment")}
                  >
                    <option value="">Select Reason</option>
                    <option value="Price Difference">Price Difference</option>
                    <option value="Freight Surcharge">Freight Surcharge</option>
                    <option value="Demurrage Detention">Demurrage Detention</option>
                    <option value="Wrong pricing">Wrong pricing</option>
                    <option value="Freight Loss">Freight Loss</option>
                    <option value="Miscellaneous">Miscellaneous</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Draft Save and Next Buttons - same design as /raise-complaint-checklist (AddEditUser style) */}
      {invoiceDetails && (
        <div className="card-panel mt-3">
          <div className="row">
            <div className="col-12">
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  type="button"
                  className="add-edit-user-back-button raise-complaint-save-draft-btn"
                  onMouseDown={() => {
                    suppressNextScrollLockArmRef.current = true;
                    releaseRaiseComplaintActionLocks();
                  }}
                  onClick={handleSaveDraft}
                  disabled={savingDraft || isDraftViewMode}
                  startIcon={
                    pendingDraftAction === "save" ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  sx={{
                    background: savingDraft ? "#bdbdbd" : "linear-gradient(135deg, #ffc107 0%, #ffb300 35%, #f9a825 70%, #f57f17 100%)",
                    boxShadow: savingDraft ? "none" : "0 4px 15px rgba(245, 127, 23, 0.45)",
                    "&:hover": {
                      background: savingDraft ? "#bdbdbd" : "linear-gradient(135deg, #ffc107 0%, #ffb300 35%, #f9a825 70%, #f57f17 100%)",
                      boxShadow: savingDraft ? "none" : "0 4px 15px rgba(245, 127, 23, 0.45)",
                    },
                  }}
                >
                  {pendingDraftAction === "save" ? "Saving..." : "Save Draft"}
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  onMouseDown={() => {
                    suppressNextScrollLockArmRef.current = true;
                    releaseRaiseComplaintActionLocks();
                  }}
                  onClick={handleNextChecklist}
                  disabled={savingDraft || isDraftViewMode}
                  className="btn-save-user checklist-submit-btn-green funbtns"
                  endIcon={
                    pendingDraftAction === "next" ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <ArrowForwardIcon />
                    )
                  }
                  sx={{
                    background: savingDraft ? "#bdbdbd" : "linear-gradient(135deg, #43a047 0%, #388e3c 35%, #2e7d32 70%, #1b5e20 100%)",
                    boxShadow: savingDraft ? "none" : "0 4px 15px rgba(46, 125, 50, 0.45)",
                    "&:hover": {
                      background: savingDraft ? "#bdbdbd" : "linear-gradient(135deg, #43a047 0%, #388e3c 35%, #2e7d32 70%, #1b5e20 100%)",
                      boxShadow: savingDraft ? "none" : "0 4px 15px rgba(46, 125, 50, 0.45)",
                    },
                  }}
                >
                  {pendingDraftAction === "next" ? "Please wait..." : "Next"}
                </Button>
              </Stack>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop */}
      {checklistOpen && (
        <div
          className="modal-backdrop fade show"
          onClick={handleChecklistClose}
          style={{ zIndex: 1040 }}
        ></div>
      )}

      {/* Checklist Modal - Bootstrap 5 */}
      <div
        className={`modal fade checklist-modal ${checklistOpen ? "show" : ""}`}
        id="checklistModal"
        ref={checklistModalRef}
        tabIndex="-1"
        aria-labelledby="checklistModalLabel"
        aria-hidden={!checklistOpen}
        style={{
          display: checklistOpen ? "block" : "none",
          zIndex: 1050,
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content checklist-modal-content">
            <div className="modal-header checklist-modal-header">
              <h5
                className="modal-title checklist-modal-title"
                id="checklistModalLabel"
              >
                SR Checklist
              </h5>
              <button
                type="button"
                className="btn-close-checklist checklist-modal-close"
                aria-label="Close"
                onClick={handleChecklistClose}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "0.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CloseIcon style={{ fontSize: "24px", color: "#666" }} />
              </button>
            </div>
            <div className="modal-body checklist-modal-body">
              {/* Product Information Section */}
              <div className="row g-3 mb-4">
                <div className="col-12 col-sm-6 col-md-3">
                  <label className="form-label checklist-form-label">
                    Item Category
                  </label>
                  <input
                    type="text"
                    className="form-control checklist-form-control"
                    value={checklistData.category}
                    readOnly
                  />
                </div>
                <div className="col-12 col-sm-6 col-md-3">
                  <label className="form-label checklist-form-label">
                    Item Name
                  </label>
                  <input
                    type="text"
                    className="form-control checklist-form-control"
                    value={checklistData.productName}
                    readOnly
                  />
                </div>
                <div className="col-12 col-sm-6 col-md-3">
                  <label className="form-label checklist-form-label">
                    Item Code
                  </label>
                  <input
                    type="text"
                    className="form-control checklist-form-control"
                    value={checklistData.productCode}
                    readOnly
                  />
                </div>
                <div className="col-12 col-sm-6 col-md-3">
                  <label className="form-label checklist-form-label">
                    SR Qty
                  </label>
                  <input
                    type="text"
                    className="form-control checklist-form-control text-end"
                    value={checklistData.quantity}
                    readOnly
                  />
                </div>
              </div>

              {/* SR Lines Section */}
              <div className="checklist-sr-lines-section">
                <div className="checklist-sr-lines-header">
                  {/* <h6 className="checklist-sr-lines-title">SR Lines</h6> */}
                </div>
                <div className="checklist-sr-lines-content mt-3">
                  <div className="row g-3">
                    {complaintLines.map((line) => (
                      <React.Fragment key={line.id}>
                        <div className="col-12 col-sm-6 col-md-5">
                          <label className="form-label checklist-form-label">
                            SR Category <span className="text-danger">*</span>
                          </label>
                          <select
                            className="form-select checklist-form-control"
                            value={line.type}
                            onChange={(e) =>
                              updateComplaintLine(
                                line.id,
                                "type",
                                e.target.value
                              )
                            }
                          >
                            <option value="">Select SR Category</option>
                            {loadingSRTypes ? (
                              <option value="" disabled>Loading SR Categories...</option>
                            ) : (
                              srTypesOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.text}
                                </option>
                              ))
                            )}
                          </select>
                        </div>
                        <div className="col-12 col-sm-4 col-md-3">
                          <label className="form-label checklist-form-label">
                            Qty
                          </label>
                          <input
                            type="number"
                            className="form-control checklist-form-control text-end"
                            min="0"
                            value={line.qty}
                            onChange={(e) =>
                              updateComplaintLine(
                                line.id,
                                "qty",
                                e.target.value
                              )
                            }
                          />
                        </div>
                        <div className="col-12 col-sm-12 col-md-2 d-flex gap-2 align-items-end justify-content-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-icon-checklist btn-add-checklist"
                            onClick={addComplaintLine}
                            title="Add Line"
                          >
                            <AddCircleOutlineIcon fontSize="medium" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-icon-checklist btn-remove-checklist"
                            onClick={() => removeComplaintLine(line.id)}
                            disabled={complaintLines.length === 1}
                            title="Remove Line"
                          >
                            <DeleteOutlineIcon fontSize="medium" />
                          </button>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer checklist-modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary checklist-btn-close"
                onClick={handleCloseWithDraftPrompt}
              >
                <CloseIcon fontSize="small" className="me-1" />
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary checklist-btn-save"
                onClick={handleDraftSave}
                disabled={savingDraft}
              >
                {savingDraft ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Saving...
                  </>
                ) : (
                  "Draft Save"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Product Details Modal Backdrop */}
      {productDetailsOpen && (
        <div
          className="modal-backdrop fade show"
          onClick={handleProductDetailsClose}
          style={{ zIndex: 1040 }}
        ></div>
      )}

      {/* Product Details Modal - Bootstrap 5 */}
      <div
        className={`modal fade checklist-modal ${productDetailsOpen ? "show" : ""}`}
        id="productDetailsModal"
        ref={productDetailsModalRef}
        tabIndex="-1"
        aria-labelledby="productDetailsModalLabel"
        aria-hidden={!productDetailsOpen}
        style={{
          display: productDetailsOpen ? "block" : "none",
          zIndex: 1050,
        }}
      >
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "800px" }}>
          <div className="modal-content checklist-modal-content">
            <div className="modal-header checklist-modal-header">
              <h5
                className="modal-title checklist-modal-title"
                id="productDetailsModalLabel"
              >
                Product Details
              </h5>
              {/* <button
                type="button"
                className="btn-close-checklist checklist-modal-close"
                aria-label="Close"
                onClick={handleProductDetailsClose}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "0.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CloseIcon style={{ fontSize: "24px", color: "#666" }} />
              </button> */}
            </div>
            <div className="modal-body checklist-modal-body" style={{ padding: "1.5rem" }}>
              {selectedProductData && (
                <>
                  {/* Helper function to get value from productData with fallback */}
                  {(() => {
                    const getValue = (key, altKeys = []) => {
                      const value = selectedProductData[key] ||
                        altKeys.map(k => selectedProductData[k]).find(v => v) ||
                        "";
                      return value || "";
                    };

                    return (
                      <>
                        {/* Row 1 */}
                        <div className="row g-3 mb-3">
                          <div className="col-12 col-sm-6 col-md-4">
                            <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left", fontWeight: "bold" }}>
                              Product Code
                            </label>
                            <input
                              type="text"
                              className="form-control checklist-form-control component-details-input"
                              value={getValue("ProductCode", ["productCode", "Productcode", "PRODUCTID"])}
                              readOnly
                              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                            />
                          </div>
                          <div className="col-12 col-sm-6 col-md-4">
                            <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left", fontWeight: "bold" }}>
                              Product Name
                            </label>
                            <input
                              type="text"
                              className="form-control checklist-form-control component-details-input"
                              value={getValue("ProductName", ["productName", "Productname"])}
                              readOnly
                              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                            />
                          </div>
                          <div className="col-12 col-sm-6 col-md-4">
                            <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left", fontWeight: "bold" }}>
                              Product Group
                            </label>
                            <input
                              type="text"
                              className="form-control checklist-form-control component-details-input"
                              value={getValue("ProductCategory", ["productCategory", "Productcategory", "ProductCategory"])}
                              readOnly
                              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                            />
                          </div>
                        </div>

                        {/* Row 2 */}
                        <div className="row g-3 mb-3">
                          <div className="col-12 col-sm-6 col-md-4">
                            <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left", fontWeight: "bold" }}>
                              Design Number
                            </label>
                            <input
                              type="text"
                              className="form-control checklist-form-control component-details-input"
                              value={getValue("DesignNo", ["DesignNumber", "designNo", "Designnumber"])}
                              readOnly
                              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                            />
                          </div>
                          <div className="col-12 col-sm-6 col-md-4">
                            <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left", fontWeight: "bold" }}>
                              Product Brand
                            </label>
                            <input
                              type="text"
                              className="form-control checklist-form-control component-details-input"
                              value={getValue("ProductBrand", ["productBrand", "Productbrand"])}
                              readOnly
                              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                            />
                          </div>
                          <div className="col-12 col-sm-6 col-md-4">
                            <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left", fontWeight: "bold" }}>
                              Product Finish
                            </label>
                            <input
                              type="text"
                              className="form-control checklist-form-control component-details-input"
                              value={getValue("ProductFinish", ["productFinish", "Productfinish"])}
                              readOnly
                              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                            />
                          </div>
                        </div>

                        {/* Row 3 */}
                        <div className="row g-3 mb-3">
                          <div className="col-12 col-sm-6 col-md-4">
                            <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left", fontWeight: "bold" }}>
                              Product Thickness (mm)
                            </label>
                            <input
                              type="text"
                              className="form-control checklist-form-control component-details-input"
                              value={getValue("ProductThickness", ["productThickness", "Productthickness"])}
                              readOnly
                              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                            />
                          </div>
                          <div className="col-12 col-sm-6 col-md-4">
                            <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left", fontWeight: "bold" }}>
                              Size (mm)
                            </label>
                            <input
                              type="text"
                              className="form-control checklist-form-control component-details-input"
                              value={getValue("Size", ["size"])}
                              readOnly
                              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                            />
                          </div>
                          <div className="col-12 col-sm-6 col-md-4">
                            <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left", fontWeight: "bold" }}>
                              Material Group Code
                            </label>
                            <input
                              type="text"
                              className="form-control checklist-form-control component-details-input"
                              value={getValue("MaterialGroupCode", ["materialGroupCode", "MaterialGroup", "materialGroup"])}
                              readOnly
                              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                            />
                          </div>
                        </div>

                        {/* Row 4 - Full width for Product Description */}
                        <div className="row g-3">
                          <div className="col-12">
                            <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left", fontWeight: "bold" }}>
                              Product Description
                            </label>
                            <input
                              type="text"
                              className="form-control checklist-form-control component-details-input"
                              value={getValue("ProductDescription", ["productDescription"])}
                              readOnly
                              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                            />
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
            <div className="modal-footer checklist-modal-footer" style={{ justifyContent: "flex-end" }}>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleProductDetailsClose}
                style={{ 
                  backgroundColor: "#dc3545", 
                  borderColor: "#dc3545",
                  color: "white",
                  fontWeight: "bold",
                  padding: "0.5rem 1.5rem"
                }}
              >
                <CloseIcon fontSize="small" className="me-1" style={{ color: "white" }} />
                CLOSE
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Component Details Modal Backdrop */}
      {componentDetailsOpen && (
        <div
          className="modal-backdrop fade show"
          onClick={handleComponentDetailsCancel}
          style={{ zIndex: 1040 }}
        ></div>
      )}

      {/* Component Details Modal - Bootstrap 5 */}
      <div
        className={`modal fade checklist-modal component-details-modal ${componentDetailsOpen ? "show" : ""}`}
        id="componentDetailsModal"
        ref={componentDetailsModalRef}
        tabIndex="-1"
        aria-labelledby="componentDetailsModalLabel"
        aria-hidden={!componentDetailsOpen}
        style={{
          display: componentDetailsOpen ? "flex" : "none",
          zIndex: 1050,
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable component-details-modal-dialog">
          <div className="modal-content checklist-modal-content">
            <div className="modal-header checklist-modal-header">
              <h5
                className="modal-title checklist-modal-title"
                id="componentDetailsModalLabel"
              >
                Component Details
              </h5>
              <button
                type="button"
                className="btn-close-checklist checklist-modal-close"
                aria-label="Close"
                onClick={handleComponentDetailsCancel}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "0.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CloseIcon style={{ fontSize: "24px", color: "#666" }} />
              </button>
            </div>
            <div className="modal-body checklist-modal-body">
              {loadingComponentDropdowns && (
                <div className="text-center mb-2">
                  <CircularProgress size={20} />
                  <span className="ms-2">Loading dropdowns...</span>
                </div>
              )}

              <div className="material-section-panel component-details-grid-panel">
                <div className="component-details-table-scroll">
                  <div className="table-responsive">
                    <table className="table table-striped table-bordered complaint-table component-details-inline-grid">
                      <thead>
                        <tr>
                          <th className="col-serial">#</th>
                          <th className="col-material-group">Material Group Desc.</th>
                          <th className="col-category">Component Category</th>
                          <th className="col-component-list">Component List/Name</th>
                          <th className="col-qty">Component Qty.</th>
                          <th className="col-issue">Component Issue</th>
                          <th className="col-material-code">Material Code (Plant Team)</th>
                          <th className="col-remarks">
                            Component Issue Remarks
                            {isComponentIssueRemarksRequired && <span className="text-danger"> *</span>}
                          </th>
                          <th className="col-action">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {componentDetailsRowsList.map((row, idx) => {
                          const rowOpts = componentRowOptionsMap[row._rowId] || {};
                          const materialGroupOptions = mergeSavedSelectOption(
                            filteredMaterialGroupsOptions,
                            row.materialGroupCode,
                            row.materialGroupDesc
                          );
                          const categoryOptions = mergeSavedSelectOption(
                            rowOpts.categories || [],
                            row.componentCategoryCode,
                            row.componentCategory
                          );
                          const nameOptions = mergeSavedSelectOption(
                            rowOpts.names || [],
                            row.componentListCode,
                            row.componentList
                          );
                          const issueOptions = mergeSavedSelectOption(
                            componentIssuesOptions,
                            row.componentIssueCode,
                            row.componentIssue
                          );
                          const hasMaterialGroup = Boolean(String(row.materialGroupCode || "").trim());
                          const hasCategory = Boolean(String(row.componentCategoryCode || "").trim());
                          const addIcon = (
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ pointerEvents: "none" }}>
                              <line x1="12" y1="5" x2="12" y2="19" />
                              <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                          );
                          const removeIcon = (
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ pointerEvents: "none" }}>
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          );
                          return (
                            <tr key={row._rowId}>
                              <td className="text-center align-middle component-details-grid-cell col-serial">{idx + 1}</td>
                              <td className="component-details-grid-cell col-material-group">
                                <select
                                  className="form-select form-select-sm component-grid-select"
                                  value={row.materialGroupCode || ""}
                                  onChange={(e) => handleComponentGridMaterialGroupChange(row._rowId, e.target.value)}
                                >
                                  <option value="">Select Material Group</option>
                                  {materialGroupOptions.map((o) => (
                                    <option key={o.code || o.Code} value={o.code || o.Code}>
                                      {o.name ?? o.Name ?? o.code ?? o.Code}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="component-details-grid-cell col-category">
                                <select
                                  className="form-select form-select-sm component-grid-select"
                                  value={row.componentCategoryCode || ""}
                                  disabled={!hasMaterialGroup}
                                  onChange={(e) => {
                                    const label = e.target.options[e.target.selectedIndex]?.text || "";
                                    handleComponentGridCategoryChange(row._rowId, e.target.value, label);
                                  }}
                                >
                                  <option value="">Select Category</option>
                                  {categoryOptions.map((o) => (
                                    <option key={o.code || o.Code} value={o.code || o.Code}>
                                      {o.name ?? o.Name ?? o.code ?? o.Code}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="component-details-grid-cell col-component-list">
                                <select
                                  className="form-select form-select-sm component-grid-select"
                                  value={row.componentListCode || ""}
                                  disabled={!hasCategory}
                                  onChange={(e) => {
                                    const label = e.target.options[e.target.selectedIndex]?.text || "";
                                    handleComponentGridListChange(row._rowId, e.target.value, label);
                                  }}
                                >
                                  <option value="">Select Component</option>
                                  {nameOptions.map((o) => (
                                    <option key={o.code || o.Code} value={o.code || o.Code}>
                                      {o.name ?? o.Name ?? o.code ?? o.Code}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="component-details-grid-cell col-qty">
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  className="form-control form-control-sm component-details-input component-qty-input"
                                  value={row.componentQty ?? ""}
                                  placeholder="Digits only"
                                  onChange={(e) => {
                                    const v = sanitizeCompQtyInput(e.target.value);
                                    updateComponentGridRow(row._rowId, { componentQty: v });
                                  }}
                                  onKeyDown={handleComponentQtyKeyDown}
                                />
                              </td>
                              <td className="component-details-grid-cell col-issue">
                                <select
                                  className="form-select form-select-sm component-grid-select"
                                  value={row.componentIssueCode || ""}
                                  onChange={(e) => handleComponentGridIssueChange(row._rowId, e.target.value)}
                                >
                                  <option value="">Select Issue</option>
                                  {issueOptions.map((o) => (
                                    <option key={o.code || o.Code} value={o.code || o.Code}>
                                      {o.name ?? o.Name ?? o.code ?? o.Code}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="component-details-grid-cell col-material-code">
                                <input
                                  type="text"
                                  className="form-control form-control-sm component-details-input"
                                  value={row.doorMaterialCode ?? ""}
                                  placeholder="To be entered by Plant Team"
                                  readOnly={isPlantTeamComponentFieldsReadOnly}
                                  onChange={(e) => {
                                    if (isPlantTeamComponentFieldsReadOnly) return;
                                    updateComponentGridRow(row._rowId, { doorMaterialCode: e.target.value });
                                  }}
                                  style={
                                    isPlantTeamComponentFieldsReadOnly
                                      ? { backgroundColor: "#f8f9fa", cursor: "default" }
                                      : undefined
                                  }
                                />
                              </td>
                              <td className="component-details-grid-cell col-remarks">
                                <textarea
                                  rows={2}
                                  className="form-control form-control-sm component-details-input component-grid-remarks"
                                  value={row.componentIssueRemarks ?? ""}
                                  placeholder="Enter component issue remarks"
                                  onChange={(e) =>
                                    updateComponentGridRow(row._rowId, { componentIssueRemarks: e.target.value })
                                  }
                                />
                              </td>
                              <td className="text-center align-middle material-add-remove-cell col-action">
                                {idx === componentDetailsRowsList.length - 1 ? (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary material-row-add d-inline-flex align-items-center justify-content-center"
                                    title="Add component row"
                                    style={{ padding: "6px 10px", minWidth: "40px", minHeight: "40px", borderRadius: "6px" }}
                                    onClick={() => addComponentGridRow(row._rowId)}
                                  >
                                    {addIcon}
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger material-row-remove d-inline-flex align-items-center justify-content-center"
                                    title="Remove row"
                                    style={{ padding: "6px 10px", minWidth: "40px", minHeight: "40px", borderRadius: "6px" }}
                                    onClick={() => removeComponentGridRow(row._rowId)}
                                  >
                                    {removeIcon}
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="material-section-total-row">
                          <th colSpan="4" className="text-center">
                            Total
                          </th>
                          <th className="text-center dt-numeric-col">
                            {componentDetailsRowsList.reduce(
                              (sum, r) => sum + (parseFloat(r.componentQty) || 0),
                              0
                            )}
                          </th>
                          <th colSpan="4"></th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer checklist-modal-footer d-flex gap-2 justify-content-end">
              <button
                type="button"
                className="btn btn-outline-secondary checklist-btn-cancel"
                onClick={handleComponentDetailsCancel}
              >
                <CancelIcon fontSize="small" className="me-1" />
                Cancel
              </button>
              <button
                type="button"
                className="component-details-btn-save"
                onClick={handleComponentDetailsSave}
              >
                <SaveIcon fontSize="small" className="me-1" />
                Save
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Previous SRs detailed view modal */}
      {showPreviousComplaintsDetailModal && (
        <div
          className="previous-srs-modal-backdrop"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            overflow: "auto",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowPreviousComplaintsDetailModal(false);
              setSelectedComplaintIndex(null);
            }
          }}
        >
          <div
            className="previous-srs-modal-container"
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              width: "95%",
              maxWidth: "1100px",
              maxHeight: "90vh",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Modal Header */}
            <div
              className="previous-srs-modal-header"
              style={{
                borderBottom: "1px solid #dee2e6",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px 8px 0 0",
                flexShrink: 0,
              }}
            >
              <h3 className="previous-srs-modal-title" style={{
                margin: 0,
                color: "#333",
                fontWeight: 600,
              }}>
                Previous SRs - Detailed View
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowPreviousComplaintsDetailModal(false);
                  setSelectedComplaintIndex(null);
                }}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#6c757d",
                  padding: "0",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CloseIcon />
              </button>
            </div>

            {/* Modal Body */}
            <div
              className="previous-srs-modal-body"
              style={{
                overflowY: "auto",
                overflowX: "hidden",
                flex: "1 1 auto",
                minHeight: 0,
                WebkitOverflowScrolling: "touch",
              }}
            >
              {previousComplaintsData.length === 0 || selectedComplaintIndex === null ? (
                <div style={{ textAlign: "center", padding: "24px", color: "#6c757d" }}>
                  No previous SRs data available.
                </div>
              ) : (
                (() => {
                  const complaint = previousComplaintsData[selectedComplaintIndex];
                  if (!complaint) return null;
                  // API returns camelCase (invoiceNumber); DB column is InvoiceNumber
                  const invoiceDisplay =
                    complaint.invoiceNumber ??
                    complaint.InvoiceNumber ??
                    complaint.invoiceNo ??
                    complaint.InvoiceNo ??
                    formData.invoiceNo ??
                    "";
                  const invoiceDisplayStr = (invoiceDisplay != null && invoiceDisplay !== undefined)
                    ? String(invoiceDisplay).trim()
                    : "";
                  const currentStatus = String(
                    complaint.statusName || complaint.StatusName || ""
                  ).trim();
                  const finalActionTaken =
                    currentStatus.toLowerCase() === "closed" ? "Closed" : "Open";
                  return (
                    <div className="previous-srs-details-card">
                      <h4 className="previous-srs-details-title">SR Details</h4>

                      <div className="row g-2">
                        <div className="col-md-3">
                          <label className="input-label">SR Number</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={complaint.srNumber || complaint.SRNumber || "N/A"}
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">Invoice Number</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={invoiceDisplayStr || "N/A"}
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">SR Date</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={
                              complaint.srDate || complaint.SRDate
                                ? (() => {
                                  const date = new Date(complaint.srDate || complaint.SRDate);
                                  const day = String(date.getDate()).padStart(2, '0');
                                  const month = String(date.getMonth() + 1).padStart(2, '0');
                                  const year = date.getFullYear();
                                  return `${day}-${month}-${year}`;
                                })()
                                : "N/A"
                            }
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">SR Type</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={complaint.complaintType || complaint.ComplaintType || "N/A"}
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">Product Name & Code</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={complaint.productsInvolved || complaint.ProductsInvolved || "N/A"}
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">SR Quantity</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={
                              complaint.srQuantity !== undefined
                                ? complaint.srQuantity.toString()
                                : complaint.SRQuantity !== undefined
                                  ? complaint.SRQuantity.toString()
                                  : "N/A"
                            }
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">Approved Quantity</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={
                              complaint.approvedQuantity !== undefined && complaint.approvedQuantity !== null
                                ? complaint.approvedQuantity.toString()
                                : complaint.ApprovedQuantity !== undefined && complaint.ApprovedQuantity !== null
                                  ? complaint.ApprovedQuantity.toString()
                                  : "0"
                            }
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">Approved Value</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={
                              complaint.approvedValue !== undefined && complaint.approvedValue !== null
                                ? `₹${parseFloat(complaint.approvedValue).toLocaleString("en-IN", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`
                                : complaint.ApprovedValue !== undefined && complaint.ApprovedValue !== null
                                  ? `₹${parseFloat(complaint.ApprovedValue).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}`
                                  : "₹0.00"
                            }
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">SR Value</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={
                              complaint.srValue !== undefined
                                ? `₹${parseFloat(complaint.srValue).toLocaleString("en-IN", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`
                                : complaint.SRValue !== undefined
                                  ? `₹${parseFloat(complaint.SRValue).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}`
                                  : "N/A"
                            }
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">Final Action Taken</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={finalActionTaken}
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">Current / Final Status</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={currentStatus || "N/A"}
                            readOnly
                          />
                        </div>

                        <div className="col-12">
                          <label className="input-label">Latest Comment</label>
                          <textarea
                            className="form-control form-control-light"
                            value={
                              String(
                                complaint.latestComment ||
                                  complaint.LatestComment ||
                                  ""
                              ).trim() || "N/A"
                            }
                            readOnly
                            rows="2"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })()
              )}
            </div>

            {/* Modal Footer */}
            <div
              className="previous-srs-modal-footer"
              style={{
                borderTop: "1px solid #dee2e6",
                display: "flex",
                justifyContent: "flex-end",
                backgroundColor: "#f8f9fa",
                borderRadius: "0 0 8px 8px",
                flexShrink: 0,
              }}
            >
              <button
                type="button"
                className="btn-close-prev-sr"
                onClick={() => {
                  setShowPreviousComplaintsDetailModal(false);
                  setSelectedComplaintIndex(null);
                }}
                style={{
                  background: "linear-gradient(135deg, #6c757d 0%, #5a6268 100%)",
                  border: "none",
                  color: "white",
                  fontWeight: 500,
                  borderRadius: "4px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(108, 117, 125, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default RaiseComplaint;
